/**
 * LECTURA DE PLANOS — Fase 0 (WIP, oculto / solo staff Ecosoft).
 *
 * Cimiento de "de un plano DWG/DXF a la Hoja de Presupuesto": abrir un plano de
 * un proyecto, verlo (zoom/pan) y SELECCIONAR elementos —por bloque ("todas las
 * iguales") y por capa (muros/pisos/vanos)—. SIN catálogo ni HP todavía (Fase 1).
 *
 * El backend convierte el DWG/DXF a un SVG "tagueado": cada inserción de bloque
 * es `<g class="ins" data-bloque data-id data-capa>…</g>` y la geometría suelta
 * va en `<g class="capa" data-capa>…</g>`. El SVG se inyecta inline (DOM real)
 * para poder resaltar por `querySelector`.
 *
 * Ver `docs/spec-plano-dwg-a-hp.md` (§10 y "Hallazgos de la revisión de selección"):
 * el PANEL DE CAPAS es el selector primario de geometría (clic sobre la línea fina
 * es ambiguo); el panel de bloques y el clic directo cubren los contables.
 */
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Dropdown,
  Option,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Input,
  Textarea,
  Slider,
  Switch,
  Tooltip,
  Spinner,
  MessageBar,
  MessageBarBody,
  Toast,
  ToastTitle,
  ToastBody,
  useToastController,
  makeStyles,
  mergeClasses,
  Dialog,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tab,
  TabList,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuButton,
  SplitButton,
  type MenuButtonProps,
} from '@fluentui/react-components';
import {
  Sparkle24Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
  ChevronLeft16Regular,
  ChevronUp16Regular,
  ZoomInRegular,
  ZoomOutRegular,
  ArrowResetRegular,
  DismissRegular,
  DeleteRegular,
  SearchRegular,
  CubeRegular,
  LayerRegular,
  TagRegular,
  ReceiptRegular,
  LightbulbRegular,
  Sparkle16Regular,
  LibraryRegular,
  EditRegular,
  PlugConnectedRegular,
  CheckmarkCircle16Filled,
  MoreHorizontal20Regular,
  SplitHorizontal20Regular,
  Table20Regular,
  WeatherPartlyCloudyDay20Regular,
  ArrowSwapRegular,
  ScalesRegular,
  AddRegular,
  Print20Regular,
  Eye16Regular,
  EyeOff16Regular,
} from '@fluentui/react-icons';
import { alternarCapaOculta, alternarGrupoOculto, todosOcultos } from '../lib/visibilidadPlano.js';
import { etiquetaDeNodo, nodosElegibles, ordenPreorden, partidaDeRenglonPorObra, type ObraReferenciaDTO, type ObraReferenciaResumen } from '@supernova/shared';
import type { FilaReemplazoHoja, TrabajoEnRiesgoPlano, CapasClasificadasDTO, DecisionDeCapa, EspecificacionVinculada, PlanoCapa, PlanoInventario, PlanoMagnitudTipo, PlanoOrigen, PlanoRenglon, PlanoRolArchivo, PlanoSeleccion, TipoAgrupacion } from '@supernova/shared';
import { AppTopBar } from '../components/AppTopBar.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { usePlanoAHpVisible, PLANO_VIEWER_MLIGHTCAD, MEMORIA_TIPOS_ENABLED, DIAGNOSTICO_DIBUJO_ENABLED, DIAGNOSTICO_MODELO_ENABLED } from '../lib/planoGate.js';
import { useDiagnosticoDelDibujo } from '../lib/useDiagnosticoDelDibujo.js';
import { reconocerTipos as reconocerTiposDeMemoria, leerTiposDelPlano, sumarUsosDeMemoria } from '../api/memoriaTipos.js';
import { ajustarMarcados, resueltoDeLinea, particionarPorExcepciones, clavesDelElemento, conceptosResueltos, notaDeOrigen, planDeEnvio, recetaAplicable, resolverElemento, type ElementoParaEnvio, type PlanDeEnvio } from '../lib/armadoConTipos.js';
import { recetaDesdeLaHoja } from '@supernova/shared';
import type { ClaveDeTipo, ReconocerTiposResponse, Resolucion, TiposDelPlanoResponse } from '@supernova/shared';
import { TiposDelPlano, type ElementoDelDialogo } from '../components/planos/TiposDelPlano.js';
import { listProjects, getProject, uploadFiles, type Project, type ProjectFile } from '../api/projects.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { PlanosGaleria } from '../components/planos/PlanosGaleria.js';
import { TiraDePlanos } from '../components/planos/TiraDePlanos.js';
import { PreguntaUnidad } from '../components/planos/PreguntaUnidad.js';
import { VistaGeneradores, claveDeMarca, numerosDeLineas, type CapturarCroquis } from '../components/planos/NumerosGeneradores.js';
import type { Croquis, PedidoCroquisIfc } from '../lib/croquis.js';
import { IndicadorNotas, NotasDelDetalle, resumenNotasDeLaHoja } from '../components/planos/NotasDelDetalle.js';
import { usePlanosGaleria } from '../components/planos/usePlanosGaleria.js';
import { etiquetasDeNiveles } from '../lib/nivelesIfc.js';
import {
  generarPlano,
  generarPlanoPdf,
  generarPlanoIfc,
  getPlanoIfcStatus,
  planoIfcUrl,
  getPlano,
  setDecisionCapa,
  planoSvgUrl,
  fetchPlanoSvg,
  asignarPlanoRenglon,
  listarPlanoRenglones,
  borrarPlanoRenglon,
  setPlanoEscala,
  sugerirConceptos,
  costoIaDelPlano,
  clasificarMagnitud,
  alturaPropuesta,
  getAltosVanos,
  setAltosVanos,
  proponerComplementarios,
  proponerComplementariosDeSintetico,
  revisarArmadoPlano,
  convertirCantidadPlano,
  proponerInstalacionesSupuesto,
  agregarRenglonPorDescripcion,
  reemplazarHojaPlano,
  trabajoEnRiesgoPlano,
  editarDescripcionRenglon,
  iaToggleRenglon,
  cotizarRenglonConIa,
  corregirCantidadRenglon,
  cambiarConceptoRenglon,
  detenerIaLote,
  getPlanoRenglonProcedimiento,
  cotizarIaLote,
  iaToggleTodos,
  agruparPlano,
  agruparPlanoPorObra,
  leerObraReferencia,
  listarObrasReferencia,
  borrarObraReferencia,
  ensurePlanoIfc,
  subirMiniaturaPlano,
  getPlanoCatalogs,
  setPlanoCatalogs,
  getPlanosHp,
  setPlanoRol,
  quitarNotaDeRenglon,
  listarOpcionesPlano,
  crearOpcionPlano,
  descartarOpcionPlano,
  usarOpcionPlano,
  editarOpcionPlano,
  crearGrupoOpcionesPlano,
  usarGrupoOpcionesPlano,
  descartarGrupoOpcionesPlano,
  type PlanoMagnitudConcepto,
  type PlanoAlturaPropuesta,
  type PlanoComplementario,
  type PlanoInstalacionSupuesta,
  pedirMaterialesPorGrupo,
} from '../api/planos.js';
import { GRUPOS_NO_DIBUJADO, MAX_LARGO_TIPO_MATERIAL, MAX_TIPOS_POR_GRUPO_MATERIAL, MENSAJE_MATERIAL_NO_REVISADO, type GrupoSinMaterial, type MaterialesPorGrupoResponse, type PreguntaDeMaterial } from '@supernova/shared';
import { bloqueaRehacer, lineasQueSePierden } from '../lib/trabajoQueSePierdeAlRehacerPlano.js';
import {
  listCatalogResources,
  listCatalogs,
  type CatalogResource,
  type Catalog,
  type ProjectCatalog,
} from '../api/catalogs.js';
import { ProjectCatalogsPicker, CATALOG_COLORS } from '../components/ProjectCatalogsPicker.js';
import { ProcedimientoDrawer } from '../components/ProcedimientoDrawer.js';
import { IfcViewer3D } from '../components/IfcViewer3D.js';
import { InterruptorIa } from '../components/InterruptorIa.js';
import { LoQueSeDedujo } from '../components/LoQueSeDedujo.js';
import type { NivelIfc, ObraIfc } from '../lib/medicionIfc.js';
import { coloresDeMaterial } from '../lib/resaltadoDeMaterial.js';
import type { MaterialDeducidoIfc } from '@supernova/shared';
import { alturaPorVistaDwg, escalaSinResolverDwg, factorEscalaDwg } from '../lib/contextoDwg.js';
import { asignablesDelPlano, fotoDeElemento as fotoDeElementoDelPlano, magnitudesDelPrincipal, vanosDelElemento, cuentaDeVanosDe, armarMuroVanos, rotuloDeVista, medirCapa2D, decisionIfc, type Asignable, type MedidaDePlanta } from '../lib/medirElemento.js';
// Visor CAD (WebGL, mlightcad) alternativo al SVG, detrás del flag VITE_PLANO_VIEWER_MLIGHTCAD.
// Perezoso: el motor (~16 MB) no entra al bundle principal (se carga solo al abrir un DXF con el flag).
const PlanoViewerMlightcad = lazy(() =>
  import('../components/PlanoViewerMlightcad.js').then((m) => ({ default: m.PlanoViewerMlightcad })),
);
import type { CajaEnPantalla, CensoDelPlano, ElementoParaCroquis } from '../components/PlanoViewerMlightcad.js';
import { QueMeFaltaPanel } from '../components/QueMeFaltaPanel.js';
import { ChequeoDelPlanoPastilla } from '../components/ChequeoDelPlano.js';
import {
  ArmarPresupuesto,
  agruparPorConcepto,
  filasParaAgregarDe,
  SIN_SALDO,
  sirveDelCatalogo,
  type BorradorArmado,
  type CandidatoArmado,
  type CantidadArmado,
  type Eleccion,
  type FilaParaAgregar,
  type OpcionArmado,
  type OpcionesDeArmado,
  type PreguntaArmado,
  type PropuestaArmado,
} from '../components/ArmarPresupuesto.js';
import { PROCEDIMIENTO_VARIANTE_DEFAULT, diagnosticarModelo, seleccionesDe, chequearModeloIfc, chequearPlanoDwg, chequearPlanoPdf, PARAMETROS_CHEQUEO_PLANO_DEFAULT, type ParametrosChequeoPlano, factorDeEscala, PLANO_ROLES_ARCHIVO, etiquetaRolArchivo, rolParaSelector, decisionDeCapa, decisionDeBloque, derivarCantidadComplementaria, datosDelPlano, describirTipoIfcParaSugerencia, GRUPOS_IFC, grupoDeCapaIfc, decisionIfc as decisionIfcDeClase, queMeFalta, type ElementoParaFalta, nombreLegibleTipoIfc, nombreDeclaradoDelTipoIfc, medidaPrincipalIfc, fmtMedidaIfc, nombrePiezaIfc, siguienteTipoQueFalta, CATEGORIA_IFC_ES, categoriaIfcSingular, muestraLargoIfc, cuentaDeMuroConVanos, proponerAltoDeVano, cantidadParaConcepto, cantidadDeMuroSinAltoDeVano, normalizarNecesidad, agregarNecesidadesNuevas, cantidadQueFalta, type AsignadoANecesidad, esAreaDePiso as esAreaDePisoDeConcepto, type TipoDeVano, type VanoDelPlano, capaSeMideComoMuro, capasQueParecenMuro, MENSAJE_IA_NO_RESPONDE, mensajeDeFalloIa, fotoDeRegla, fotoAMano, juntarTodas, seTocan, armarDocumentoGenerador, type DerivacionDeComplementario, porMedidaDelConcepto, SIN_FOTO_GENERADOR, claveDeElemento, type FotoGenerador, type LineaGenerador, motivoDelPesoDeducido, PARAMETROS_MEDICION_IFC_DEFAULT } from '@supernova/shared';
import { FieldHelp } from '../components/FieldHelp.js';
import { getQuoteConcepts, type QuoteConcept } from '../api/quotes.js';
import { ConceptPricePanel } from '../components/ConceptPricePanel.js';
import { marcaDeRenglon } from '../components/planos/marcaDeRenglon.js';
import { useCanMutate, useIsEcosoftStaff } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';
import { getParametrosChequeoPlano, getParametrosMedicionIfc } from '../api/calibracion.js';
import { MiniaturaIfcEnSegundoPlano } from '../components/planos/MiniaturaIfcEnSegundoPlano.js';
import { CambiarConceptoDialog, type ConceptoParaCambiar } from '../components/planos/CambiarConceptoDialog.js';
import { OpcionesDelConcepto, type ConceptoParaOpcion } from '../components/planos/OpcionesDelConcepto.js';
import { OpcionesDelGrupo } from '../components/planos/OpcionesDelGrupo.js';
import { AgregarAdicional } from '../components/planos/AgregarAdicional.js';
import { notaDelMasParecido } from '../lib/notaDelMasParecido.js';
import { CompartirConCliente } from '../components/planos/CompartirConCliente.js';
import { CambioDePlano, PastillaRevisionAplicada } from '../components/planos/CambioDePlano.js';
import { AvanceDeObra } from '../components/planos/AvanceDeObra.js';
import { elementosDeSeleccion, ladoActualDeLaHoja, mismaMedicion, type PlanoOpcion, type RenglonDeOpcion } from '@supernova/shared';
import { ApiClientError } from '../api/client.js';

const POLL_MS = 1500;
// Lo máximo que el servidor entrega por consulta (el `limit` de /catalogs/:id/resources).
const TOPE_RESULTADOS_POR_CATALOGO = 500;
/** Reintentos (con espera 1 s, 2 s, 4 s) antes de decir que no se pudo cargar la lista de planos. */
const REINTENTOS_ARCHIVOS = 3;
// Copy definitivo del ⓘ que explica el rol del plano (consolida de un vistazo lo que cada opción
// del dropdown ya dice inline). `whiteSpace:'pre-line'` en el estilo respeta estos saltos de línea.
const ROL_PLANO_AYUDA =
  'Cuando una obra tiene varios planos, esto decide cuáles suman cantidades a la hoja de presupuesto y cuáles solo sirven de consulta:\n\n' +
  '• Suma a la hoja — las cantidades de este plano entran a la hoja: plantas y cortes del edificio, urbanización y obra exterior.\n' +
  '• Solo referencia — láminas de detalle o especificaciones; ayudan a describir los conceptos pero no suman cantidad, para no cotizar dos veces el mismo elemento.\n\n' +
  'Te proponemos una opción según lo que trae el plano; cámbiala si hace falta.';
const DWG_DXF_RE = /\.(dwg|dxf)$/i;
const IFC_RE = /\.ifc$/i;
const PDF_RE = /\.pdf$/i;

interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Columnas de la hoja de un plano: concepto · unidad · cantidad · p. unitario · importe · IA · quitar. */
const HP_COLUMNAS = 'minmax(0,1fr) 60px 70px 100px 110px 72px 32px';

const useStyles = makeStyles({
  page: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-page)',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-m)',
    padding: '10px var(--space-xxl)',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
    flexWrap: 'wrap',
  },
  toolbarLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-tertiary)',
    marginRight: '2px',
  },
  // Botón de ayuda «?» + popover del rol del plano. Mismo patrón visual que FieldHelp
  // (disco con «?», hover/aria-expanded en acento) para coherencia con el resto de la app.
  rolHelpTrigger: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    borderRadius: '50%',
    border: '1.5px solid var(--border-default)',
    background: 'var(--bg-surface)',
    color: 'var(--text-tertiary)',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
    ':hover': {
      border: '1.5px solid var(--accent)',
      color: 'var(--accent)',
      background: 'var(--accent-weak)',
    },
    '&[aria-expanded="true"]': {
      border: '1.5px solid var(--accent)',
      color: 'var(--accent)',
      background: 'var(--accent-weak)',
    },
  },
  rolHelpSurface: {
    width: '360px',
    maxWidth: 'calc(100vw - 24px)',
    padding: '16px 18px 14px',
  },
  rolHelpTitle: { margin: '0 0 8px', fontSize: '14.5px', lineHeight: 1.35, fontWeight: 600 },
  rolHelpBody: {
    margin: 0,
    fontSize: '13.5px',
    lineHeight: 1.55,
    color: 'var(--text-secondary)',
    whiteSpace: 'pre-line',
  },
  wipTag: {
    marginLeft: 'auto',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-circular)',
    padding: '1px 7px',
  },
  body: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 280px) minmax(0, 1fr) var(--right-w, 390px)',
    // La fila puede quedar en 0 (preset «Solo la hoja»): sin esto la fila conservaba el alto mínimo de
    // su contenido y el cuerpo se desbordaba unos píxeles ENCIMA del encabezado de la hoja.
    gridTemplateRows: 'minmax(0, 1fr)',
    minHeight: 0,
    overflow: 'hidden',
  },
  sidePanel: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    borderRight: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
    overflowY: 'auto',
  },
  rightPanel: {
    borderRight: 'none',
    borderLeft: '1px solid var(--border-subtle)',
    position: 'relative',
    // Inspector de un solo scroll (rediseño 2026-09-21): propiedades + asignación +
    // complementarios se ven en secuencia; el panel entero scrollea (ya no acordeón
    // excluyente, para no perder de vista las cifras al asignar).
    overflowY: 'auto',
  },
  // Encabezado ESTÁTICO de una sección del inspector (ya no es toggle de acordeón).
  sectionHeadStatic: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px 4px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-tertiary)',
  },
  // Sección del acordeón que, abierta, ocupa el alto disponible del panel.
  sectionGrow: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Manija para redimensionar el panel derecho (arrastrar el borde izquierdo).
  resizer: {
    position: 'absolute',
    left: '-3px',
    top: 0,
    bottom: 0,
    width: '6px',
    cursor: 'col-resize',
    zIndex: 6,
    ':hover': { background: 'var(--accent-weak)' },
  },
  section: {
    borderBottom: '1px solid var(--border-subtle)',
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '12px 14px',
    font: 'inherit',
    color: 'var(--text-primary)',
    fontWeight: 600,
    fontSize: '13px',
    ':hover': { background: 'var(--bg-hover)' },
  },
  sectionCount: {
    marginLeft: 'auto',
    fontSize: '11px',
    color: 'var(--text-tertiary)',
    fontVariantNumeric: 'tabular-nums',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    padding: '2px 0 8px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '7px 14px 7px 30px',
    font: 'inherit',
    textAlign: 'left',
    color: 'var(--text-secondary)',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
  },
  rowSel: {
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    ':hover': { background: 'var(--accent-weak)', color: 'var(--accent)' },
  },
  rowName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '12.5px',
  },
  rowMeta: {
    marginLeft: 'auto',
    fontSize: '11px',
    color: 'var(--text-tertiary)',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  emptyHint: {
    padding: '4px 14px 10px 30px',
    fontSize: '12px',
    color: 'var(--text-tertiary)',
    lineHeight: 1.4,
  },
  // Subencabezado de categoría (IFC): agrupa los tipos/familias que cuelgan de una clase de elemento.
  capaCatHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px 3px 18px',
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
  },
  viewerWrap: {
    position: 'relative',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-page)',
    // Lo del visor no se pinta fuera de su caja. En el modo «eligiendo» la lista ancha y la miniatura
    // viven aquí; con la hoja abierta alta (Mitad y mitad / Solo la hoja) la caja queda baja y, sin
    // esto, la columna de la izquierda y la miniatura se desbordaban ENCIMA de la hoja.
    overflow: 'hidden',
  },
  viewerToolbar: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    zIndex: 5,
    display: 'flex',
    gap: '4px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    padding: '4px',
    boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.18))',
  },
  iconBtn: {
    width: '30px',
    height: '30px',
    borderRadius: 'var(--radius-sm)',
    display: 'grid',
    placeItems: 'center',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
  },
  /**
   * Contenedor del SVG inyectado. El resaltado de selección vive acá, apuntando a
   * las clases que emite el backend (`.ins`, `.capa`) más `.sel` que agrega esta
   * pantalla. El rojo del resaltado es un color FUNCIONAL constante (no cambia con
   * el tema) — por eso va como rgba fijo y no como token de superficie.
   */
  canvas: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    cursor: 'grab',
    touchAction: 'none',
    userSelect: 'none',
    display: 'flex',
    '& svg': {
      width: '100%',
      height: '100%',
      display: 'block',
    },
    // Bloques seleccionados: relleno rojo translúcido en el área de clic + trazo rojo.
    '& g.ins.sel > rect': {
      fill: 'rgba(229, 57, 53, 0.28)',
      stroke: 'rgba(229, 57, 53, 0.9)',
    },
    '& g.ins.sel polyline, & g.ins.sel line, & g.ins.sel path, & g.ins.sel circle': {
      stroke: 'rgba(229, 57, 53, 0.95)',
      strokeWidth: '1.6',
      vectorEffect: 'non-scaling-stroke',
    },
    // Capas seleccionadas: trazo rojo grueso, visible a cualquier zoom.
    '& g.capa.sel polyline, & g.capa.sel line, & g.capa.sel path, & g.capa.sel circle': {
      stroke: 'rgba(229, 57, 53, 0.95)',
      strokeWidth: '2',
      vectorEffect: 'non-scaling-stroke',
    },
    // Ya asignados a un concepto: se ATENÚAN (no se ocultan) para no perder el contexto
    // del dibujo; lo pendiente queda nítido. No aplica al que está seleccionado ahora.
    '& g.ins.asignado:not(.sel), & g.capa.asignado:not(.sel)': {
      opacity: 0.18,
    },
  },
  canvasGrabbing: { cursor: 'grabbing' },
  // Barra de estado fija al pie del plano (estilo AutoCAD): en escritorio, al pasar el mouse por
  // un elemento con concepto asignado, muestra aquí qué tiene (NO un tooltip flotante que sigue al
  // cursor — molesto). En iPad/touch no hay hover: el concepto se ve en el inspector al seleccionar.
  statusBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    pointerEvents: 'none',
    padding: '7px 12px',
    background: 'var(--bg-surface)',
    borderTop: '1px solid var(--border-subtle)',
    color: 'var(--text-primary)',
    fontSize: '12px',
    lineHeight: 1.4,
    // Descripción COMPLETA: envuelve en varias líneas (crece hacia arriba) en vez de recortar.
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxHeight: '40%',
    overflowY: 'auto',
  },
  centerState: {
    flex: 1,
    display: 'grid',
    placeItems: 'center',
    padding: '32px',
  },
  stateBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: 'var(--text-secondary)',
    fontSize: '13.5px',
    textAlign: 'center',
    maxWidth: '380px',
  },
  propGroup: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  propTitle: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
  },
  propItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 10px',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-raised)',
    border: '1px solid var(--border-subtle)',
    flexShrink: 0,
  },
  propName: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  propMeta: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    fontVariantNumeric: 'tabular-nums',
  },
  propEmpty: {
    fontSize: '12.5px',
    color: 'var(--text-tertiary)',
    lineHeight: 1.5,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12.5px',
    color: 'var(--text-secondary)',
    borderTop: '1px solid var(--border-subtle)',
    paddingTop: '10px',
  },
  msg: { margin: '10px 14px' },

  // --- Panel "Asignar concepto" (divulgación progresiva) ---
  assignGroup: {
    padding: '8px 14px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
    minHeight: 0,
    // Colchón: si aún así no entra (pantalla muy baja + HP grande), scrollea la
    // columna en vez de tapar el Asignar.
    overflowY: 'auto',
  },
  targetChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    flexShrink: 0,
  },
  targetChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-circular)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '4px 10px',
    font: 'inherit',
    fontSize: '12px',
    ':hover': { background: 'var(--bg-hover)' },
  },
  targetChipSel: {
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    border: '1px solid var(--accent)',
  },
  fieldLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    flexShrink: 0,
  },
  // Controles de tamaño fijo dentro de la columna de asignar: NO se encogen
  // cuando el panel pierde alto (ej. al abrir/agrandar la HP). Solo la lista de
  // resultados cede espacio; sin esto el botón "Sugerir" quedaba aplastado.
  noShrink: { flexShrink: 0 },
  // Entidad seleccionada, en UNA línea compacta (antes era una tarjeta alta que
  // comía el espacio de los conceptos).
  targetCompact: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    flexShrink: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  targetCompactName: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  targetCompactMeta: {
    fontSize: '12px',
    color: 'var(--text-tertiary)',
    flexShrink: 0,
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    // base 160px, crece con el espacio libre y puede ceder hasta 0 para que la
    // fila "Cantidad + Asignar" (footer) NUNCA quede tapada por la HP abierta.
    flex: '1 1 160px',
    // Con pantalla baja la lista no se aplasta a una sola fila: el panel entero hace scroll.
    minHeight: '180px',
    overflowY: 'auto',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  resultRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '100%',
    textAlign: 'left',
    border: 'none',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'transparent',
    cursor: 'pointer',
    padding: '8px 10px',
    font: 'inherit',
    color: 'var(--text-secondary)',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
  },
  resultRowSel: {
    background: 'var(--accent-weak)',
    ':hover': { background: 'var(--accent-weak)' },
  },
  resultDesc: {
    fontSize: '12.5px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    lineHeight: 1.4,
  },
  resultMeta: {
    fontSize: '11px',
    color: 'var(--text-tertiary)',
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    fontVariantNumeric: 'tabular-nums',
  },
  resultRazon: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
    fontStyle: 'italic',
    marginTop: '2px',
    lineHeight: 1.35,
  },
  // --- Modo «eligiendo»: la lista ancha ocupa el centro y el dibujo queda chico arriba a la izq. ---
  modoLista: {
    position: 'absolute',
    inset: 0,
    zIndex: 30,
    display: 'flex',
    background: 'var(--bg-page)',
  },
  modoListaIzq: {
    width: '296px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '14px',
    borderRight: '1px solid var(--border-subtle)',
    minHeight: 0,
  },
  modoListaDer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 18px 0',
    minHeight: 0,
  },
  miniatura: {
    flexShrink: 0,
    padding: 0,
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    textAlign: 'left',
    fontFamily: 'inherit',
    ':hover': { border: '1px solid var(--accent)' },
  },
  miniaturaPie: {
    padding: '7px 10px',
    fontSize: '11.5px',
    color: 'var(--text-secondary)',
    borderTop: '1px solid var(--border-subtle)',
  },
  listaAncha: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    paddingBottom: '16px',
  },
  filaAncha: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    padding: '11px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
    font: 'inherit',
    color: 'var(--text-primary)',
    flexShrink: 0,
    ':hover': { border: '1px solid var(--accent)' },
  },
  filaAnchaSel: {
    background: 'var(--accent-weak)',
    border: '1px solid var(--accent)',
  },
  filaAnchaDesc: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.45,
    color: 'var(--text-primary)',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  filaAnchaMeta: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    fontSize: '11.5px',
    color: 'var(--text-secondary)',
    fontVariantNumeric: 'tabular-nums',
  },
  filaAnchaRazon: {
    fontSize: '11.5px',
    color: 'var(--accent)',
    lineHeight: 1.4,
  },
  adicFila: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    padding: '9px 11px',
    borderRadius: '8px',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
    font: 'inherit',
    color: 'var(--text-primary)',
    flexShrink: 0,
    ':hover': { border: '1px solid var(--accent)' },
  },
  adicFilaActiva: {
    background: 'var(--accent-weak)',
    border: '1px solid var(--accent)',
  },
  adicFilaHecha: {
    border: '1px solid var(--ok)',
    background: 'var(--ok-weak)',
  },
  adicFilaNoVa: {
    border: '1px dashed var(--border-subtle)',
    background: 'transparent',
    opacity: 0.7,
  },
  cantidadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  cantidadInput: { width: '110px' },

  // --- HP: cajón inferior colapsable ---
  hpDrawer: {
    borderTop: '1px solid var(--border-default)',
    // Como la hoja de cotización: lo que rodea la tabla es el fondo de la página; la tabla, superficie.
    background: 'var(--bg-page)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'relative',
  },
  // Manija para redimensionar la hoja (arrastrar el borde superior del cajón).
  hpResizer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '-3px',
    height: '6px',
    cursor: 'row-resize',
    zIndex: 6,
    ':hover': { background: 'var(--accent-weak)' },
  },
  hpBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    width: '100%',
    boxSizing: 'border-box',
    padding: '6px 12px',
    color: 'var(--text-primary)',
    background: 'var(--bg-raised)',
    borderBottom: '1px solid var(--border-subtle)',
    whiteSpace: 'nowrap',
    // En pantallas angostas la barra pasa a dos líneas en vez de cortar las acciones de la derecha.
    flexWrap: 'wrap',
    rowGap: '6px',
  },
  hpBarToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px 6px',
    margin: '-4px -6px',
    borderRadius: 'var(--radius-sm)',
    font: 'inherit',
    color: 'inherit',
    ':hover': { background: 'var(--bg-hover)' },
  },
  hpPresets: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: 'auto',
  },
  hpPresetBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    border: '1px solid var(--border-subtle)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: '11.5px',
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: '999px',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--accent)', border: '1px solid var(--accent)' },
  },
  hpBarTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '13px',
  },
  hpBarStat: {
    fontSize: '12px',
    color: 'var(--text-tertiary)',
    fontVariantNumeric: 'tabular-nums',
  },
  hpBarTotal: {
    fontWeight: 700,
    fontSize: '13.5px',
    color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums',
  },
  hpBody: {
    overflowY: 'auto',
    padding: '0 12px 12px',
  },
  // Marco de la tabla (como `table` de la hoja de cotización). overflow CLIP, no hidden: 'hidden'
  // rompería el position:sticky de los encabezados.
  hpTabla: {
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-surface)',
    overflow: 'clip',
    marginTop: '6px',
  },
  hpEmpty: {
    padding: '18px 12px',
    fontSize: '13px',
    color: 'var(--text-tertiary)',
  },
  // Barra del selector "Tipo de agrupación" arriba de la hoja.
  // Acciones de la hoja en la fila del breadcrumb: en pantallas angostas, Vincular e Instalaciones se
  // van al menú "⋯ Más"; el segmentado de la obra y la vista quedan siempre visibles.
  accionAncha: { '@media (max-width: 1440px)': { display: 'none' } },
  // Botón de la barra con borde que se vea en los dos temas (el de Fluent casi desaparece en oscuro).
  botonBarra: {
    border: '1px solid var(--border-control)',
    ':hover': { border: '1px solid var(--accent)' },
  },
  accionMas: { '@media (min-width: 1441px)': { display: 'none' } },
  segmentado: {
    display: 'inline-flex',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
  },
  segBtn: {
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontFamily: 'inherit',
    fontSize: '12.5px',
    fontWeight: 600,
    padding: '4px 10px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
  },
  segBtnOn: { background: 'var(--accent-weak)', color: 'var(--accent)', ':hover': { background: 'var(--accent-weak)', color: 'var(--accent)' } },
  agrupBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '4px 0 10px',
  },
  agrupLabel: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  // Hoja con scroll como la de cotización (QuotePage + StickyGroupHeaders): el encabezado de
  // columnas va pegado arriba (top:0) y DEBAJO se apilan los agrupadores por nivel —la partida
  // general (nivel 0, nombre del proyecto) y la partida en la que estás (nivel 1)— con los mismos
  // colores por nivel (`--group-bg-N` / `--group-fg-N`). Aquí la hoja no está virtualizada, así que
  // basta `position: sticky` nativo: cada partida va envuelta en su propio <div>, y por eso la
  // siguiente EMPUJA a la saliente hacia arriba al llegar (igual que el overlay de cotización).
  // Los `top` se fijan inline con los altos medidos (ResizeObserver), no con números mágicos.
  hpColHead: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: 'var(--bg-surface)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  // Partida GENERAL de la hoja = nombre del proyecto (encima de las subpartidas).
  partidaGeneral: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 14px',
    borderBottom: '1px solid var(--border-subtle)',
    position: 'sticky',
    background: 'var(--group-bg-0)',
    color: 'var(--group-fg-0)',
    zIndex: 2,
    '& > span:first-child': {
      fontSize: '13px',
      fontWeight: 750,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'var(--group-fg-0)',
    },
    '& > span:last-child': { color: 'var(--group-fg-0)', fontSize: '12.5px', fontWeight: 700 },
  },
  partidaHead: {
    display: 'grid',
    gridTemplateColumns: HP_COLUMNAS,
    alignItems: 'center',
    gap: '10px',
    padding: '7px 14px 7px 30px',
    borderBottom: '1px solid var(--border-subtle)',
    position: 'sticky',
    background: 'var(--group-bg-1)',
    color: 'var(--group-fg-1)',
    zIndex: 1,
    boxShadow: '0 4px 8px -6px rgba(16,24,40,0.35)',
    '& > span:first-child > span:nth-child(2)': {
      fontSize: '12px',
      fontWeight: 650,
      letterSpacing: '0.01em',
      textTransform: 'none',
      color: 'var(--group-fg-1)',
    },
  },
  // En la columna Importe (la 4ª contando desde el final: importe · IA · quitar).
  partidaTotal: {
    gridColumn: '-4 / -3',
    textAlign: 'right',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    fontVariantNumeric: 'tabular-nums',
  },
  hpRow: {
    display: 'grid',
    gridTemplateColumns: HP_COLUMNAS,
    // 'start' como la fila de cotización: la descripción se ve COMPLETA (varias líneas) y el resto
    // de las columnas queda alineado a su primera línea.
    alignItems: 'start',
    gap: '10px',
    padding: '8px 14px',
    borderBottom: '1px solid var(--border-subtle)',
    cursor: 'pointer',
    fontSize: '12.5px',
    color: 'var(--text-secondary)',
    ':hover': { background: 'var(--bg-hover)' },
  },
  hpRowSel: {
    background: 'var(--accent-weak)',
    ':hover': { background: 'var(--accent-weak)' },
  },
  // El renglón al que se saltó desde «Lleva N adicionales» / «Adicional de…»: se marca un momento.
  hpRowDestacado: {
    boxShadow: 'inset 3px 0 0 var(--accent)',
    background: 'var(--accent-weak)',
  },
  // Leyenda que lleva a otro renglón: se toca (en iPad no hay tooltip).
  leyendaLink: {
    fontSize: '10.5px',
    color: 'var(--text-tertiary)',
    marginRight: '6px',
    whiteSpace: 'nowrap',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textDecoration: 'underline dotted',
    textUnderlineOffset: '2px',
    font: 'inherit',
    ':hover': { color: 'var(--accent)' },
  },
  // Descripción COMPLETA, envolviendo (igual que `desc` de la hoja de cotización): antes iba en
  // una línea con «…» y el concepto no se podía leer.
  hpDescCell: {
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: 1.45,
    color: 'var(--text-primary)',
  },
  hpNumCell: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  hpUnitCell: {
    textAlign: 'center',
    color: 'var(--text-tertiary)',
  },
  hpSinPrecio: {
    color: 'var(--text-tertiary)',
    fontStyle: 'italic',
  },
  delBtn: {
    width: '28px',
    height: '28px',
    borderRadius: 'var(--radius-sm)',
    display: 'grid',
    placeItems: 'center',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    ':hover': { background: 'var(--bg-hover)', color: 'rgb(229, 57, 53)' },
  },
  editDescBtn: {
    marginLeft: '6px',
    padding: '0 3px',
    verticalAlign: 'middle',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    ':hover': { color: 'var(--accent)' },
  },
});

/** DWG/DXF (se convierten a SVG 2D) e IFC (se ven en 3D) pueden abrirse en el visor de planos. */
function esArchivoCad(f: ProjectFile): boolean {
  return DWG_DXF_RE.test(f.originalName) || IFC_RE.test(f.originalName);
}

/** Un IFC (BIM) se ve en 3D directo, no pasa por la conversión a SVG. */
function esIfc(f: ProjectFile | null): boolean {
  return f != null && IFC_RE.test(f.originalName);
}

/** Un PDF de plano se lee por visión: cuantifica ESTIMADO (sin geometría), no se abre en el visor 2D. */
function esPdf(f: ProjectFile | null): boolean {
  return f != null && PDF_RE.test(f.originalName);
}

/** Archivos que se pueden trabajar en /planos: DWG/DXF/IFC (geometría) + PDF (visión, estimado). */
function esArchivoDePlano(f: ProjectFile): boolean {
  return esArchivoCad(f) || PDF_RE.test(f.originalName);
}

// Etiquetas del estándar IFC: viven en UN solo archivo (@supernova/shared/etiquetasIfc), el mismo que
// arma los nombres legibles y lo que se le cuenta a la IA al sugerir conceptos.
const categoriaSingular = categoriaIfcSingular;

/** Árbol de partidas de una obra de referencia (solo lectura): «clave descripción» como m1, en el orden de la obra. */
function ArbolDePartidas({ obra }: { obra: ObraReferenciaDTO }) {
  const hijosDe = (padreId: string | null) =>
    obra.nodos.filter((n) => n.padreId === padreId).sort((a, b) => a.orden - b.orden);
  // Clave en su propia columna (alineada) y nombre al lado; cada nivel cuelga de una línea guía para que se lea
  // qué es partida y qué es subpartida. Las que contienen subpartidas van en negrita, con su cuenta.
  const pintar = (padreId: string | null, nivel: number): JSX.Element[] =>
    hijosDe(padreId).map((n) => {
      const hijos = hijosDe(n.id);
      return (
        <div key={n.id}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '3.4em 1fr auto',
              gap: 8,
              alignItems: 'baseline',
              padding: '3px 6px',
              borderRadius: 4,
              color: 'var(--text-primary)',
              fontWeight: hijos.length > 0 ? 600 : 400,
              background: nivel === 0 && hijos.length > 0 ? 'var(--bg-subtle, rgba(127,127,127,0.10))' : 'transparent',
            }}
          >
            <span style={{ color: 'var(--text-tertiary)', fontWeight: 500, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
              {n.clave ?? ''}
            </span>
            <span>{n.nombre}</span>
            {hijos.length > 0 && (
              <span style={{ color: 'var(--text-tertiary)', fontWeight: 400, fontSize: 11 }}>
                {hijos.length} {nivel === 0 ? (hijos.length === 1 ? 'partida' : 'partidas') : hijos.length === 1 ? 'subpartida' : 'subpartidas'}
              </span>
            )}
          </div>
          {hijos.length > 0 && (
            <div style={{ marginLeft: 14, paddingLeft: 6, borderLeft: '1px solid var(--border-subtle)' }}>
              {pintar(n.id, nivel + 1)}
            </div>
          )}
        </div>
      );
    });
  return <>{pintar(null, 0)}</>;
}
const muestraLargo = muestraLargoIfc;

/**
 * Ejes por los que se puede agrupar la HP (desplegable "Tipo de agrupación"). 'familia' es el
 * default (sale del concepto, sin IA). Especialidad/Cronológico/Sistema/Material los infiere la IA.
 * Nivel y Ubicación (dependen de DÓNDE está el elemento) son etapa 2 — aún no se ofrecen.
 */
// `ia: true` = el eje se calcula con IA (consume tokens la PRIMERA vez; luego cacheado, instantáneo).
// 'familia' sale del propio concepto → nunca consume.
const AGRUPACIONES: { valor: TipoAgrupacion; label: string; ia: boolean }[] = [
  { valor: 'familia', label: 'Familia (default)', ia: false },
  { valor: 'especialidad', label: 'Especialidad', ia: true },
  { valor: 'cronologico', label: 'Proceso constructivo', ia: true },
  { valor: 'sistema', label: 'Sistema constructivo', ia: true },
  { valor: 'material', label: 'Material', ia: true },
  // Etapa 2 — dependen de DÓNDE está el elemento, no del concepto. En IFC/BIM salen del modelo al
  // asignar (gratis): "Niveles" del piso (IfcBuildingStorey) y "Ubicación" del espacio (IfcSpace).
  // 'nivel' no lleva IA (un piso no sale de la descripción; en DWG queda "Sin nivel"). 'ubicacion' sí:
  // si el modelo no trae la zona, la IA la infiere de la descripción en el mismo lote de clasificación.
  { valor: 'nivel', label: 'Niveles', ia: false },
  { valor: 'ubicacion', label: 'Ubicación / zona', ia: true },
  // Prueba staff: partidas de una obra de referencia. Abre el selector de obra (no cambia el eje hasta elegir).
  { valor: 'obra', label: 'Obra de referencia…', ia: true },
];
const AGRUPACION_LABEL: Record<TipoAgrupacion, string> = {
  familia: 'Familia (default)',
  especialidad: 'Especialidad',
  cronologico: 'Proceso constructivo',
  sistema: 'Sistema constructivo',
  material: 'Material',
  nivel: 'Niveles',
  ubicacion: 'Ubicación / zona',
  obra: 'Obra de referencia',
};

/** Etiqueta visible de un catálogo (displayName si trae, si no el nombre técnico). */
function etiquetaCatalogo(pc: ProjectCatalog): string {
  return (pc.catalog.displayName ?? '').trim() || pc.catalog.name;
}

const MONEDA = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Un importe/precio null NO es 0: se muestra como "s/precio" (concepto pelado). */
/** Para buscar en la hoja sin que importen mayúsculas ni acentos («excavacion» encuentra «Excavación»). */
function normalizarParaBuscar(t: string): string {
  return t.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
}

/**
 * useState que se acuerda entre visitas (localStorage, `supernova:planos-*`): cómo dejó el usuario la
 * pantalla —paneles plegados, hoja abierta, vista, eje de partidas—. Sin localStorage (privado,
 * bloqueado) funciona igual, solo que no se acuerda.
 */
function usePreferencia<T>(clave: string, inicial: T, valido: (v: unknown) => v is T): [T, (v: T | ((p: T) => T)) => void] {
  const [valor, setValor] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(`supernova:planos-${clave}`);
      if (raw != null) {
        const v: unknown = JSON.parse(raw);
        if (valido(v)) return v;
      }
    } catch {
      /* localStorage no disponible */
    }
    return inicial;
  });
  useEffect(() => {
    try {
      localStorage.setItem(`supernova:planos-${clave}`, JSON.stringify(valor));
    } catch {
      /* localStorage no disponible */
    }
  }, [clave, valor]);
  return [valor, setValor];
}
const esBool = (v: unknown): v is boolean => typeof v === 'boolean';

function fmtMoneda(n: number | null): string {
  return n == null ? '' : `$ ${MONEDA.format(n)}`;
}

/** Color del semáforo de confianza de un APU sintético (verde/amarillo/rojo). */
function semaforoColor(s: 'green' | 'yellow' | 'red' | null): string {
  return s === 'green' ? 'var(--ok)' : s === 'yellow' ? 'var(--warn)' : 'var(--bad)';
}

/**
 * Semilla de la descripción de «Generar con IA». Con lo que la IA entendió (al pedir «Recomendados»):
 * «{intención} ({nombre})». Sin eso, NO se consulta a la IA: el nombre del elemento y, si el plano lo
 * midió, el ancho del muro de doble trazo («A-MUROS · muro de 15 cm de ancho»). Sin inventar material.
 */
function semillaDescripcionSintetico(
  t: Pick<Asignable, 'esMuroDobleTrazo' | 'espesorSugerido'>,
  nombre: string,
  interpretacion: string | null,
): string {
  const interp = interpretacion?.trim();
  if (interp) return `${interp.charAt(0).toUpperCase()}${interp.slice(1)} (${nombre})`;
  if (t.esMuroDobleTrazo && t.espesorSugerido != null && t.espesorSugerido > 0) {
    const cm = Math.round(t.espesorSugerido * 1000) / 10;
    return `${nombre} · muro de ${cm.toLocaleString('es-MX')} cm de ancho`;
  }
  return nombre;
}

/** Corre tareas con un tope de `n` a la vez (las demás esperan turno, en orden). */
function crearLimitador(n: number) {
  let activas = 0;
  const cola: (() => void)[] = [];
  return async <T,>(tarea: () => Promise<T>): Promise<T> => {
    if (activas >= n) await new Promise<void>((r) => cola.push(r));
    activas++;
    try {
      return await tarea();
    } finally {
      activas--;
      cola.shift()?.();
    }
  };
}

/** Identidad de una selección (tipo + clave + vista): la misma que usa la hoja para sus renglones. */
/** Se acabó el saldo a mitad de armar una opción (distinto de cualquier otro fallo: permite guardar lo ya armado). */
class SaldoAgotadoAlArmar extends Error {}

function claveDeSeleccion(s: PlanoSeleccion): string {
  return `${s.tipo}:${s.clave}${s.vista ? `@${s.vista}` : ''}`;
}

/**
 * Un concepto candidato para la lista de resultados, venga de la búsqueda por
 * texto (catálogo) o de las sugerencias por IA. Unifica ambos orígenes para que
 * la lista y la selección (`conceptoElegido`) sean uno solo.
 */
interface ConceptoItem {
  id: string;
  /** Catálogo del que viene (multi-catálogo por prioridad); se usa al asignar. */
  catalogId: string;
  clave: string;
  descripcion: string;
  unidad: string | null;
  precio: number | null;
  familia?: string | null;
  /** Motivo corto (solo en sugerencias por IA). */
  razon?: string;
}

/** Números generadores de lo que no trae el plano: no sale de piezas dibujadas, sale de este supuesto. */
function fotoNoDibujado(it: PlanoInstalacionSupuesta, cantidad: number, unidad: string | null): FotoGenerador {
  return fotoDeRegla({
    origen: 'no_dibujado',
    unidad,
    total: cantidad,
    lineas: [{ elemento: null, localizacion: it.nombre || it.descripcion, detalle: it.deDonde || 'Supuesto con lo que trae el plano', parcial: cantidad }],
    supuesto: { deDonde: it.deDonde, base: [] },
  });
}

/** Números generadores: la operación de un complementario en palabras («Área 120.5 × 2»). */
function detalleDeRegla(derivaDe: string, base: number | null, factor: number, conVanos: boolean): string {
  const NOMBRE: Record<string, string> = { conteo: 'Piezas', largo: 'Largo', area: 'Área', volumen: 'Volumen' };
  const f3 = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 3 });
  return `${NOMBRE[derivaDe] ?? 'Medida'}${conVanos ? ' (con los huecos)' : ''} del elemento ${base != null ? f3(base) : '—'} × ${f3(factor)}`;
}

/** Formatea un número de metros para el panel de propiedades (es-MX, hasta 2 decimales). */
function fmtM(n: number): string {
  return n.toLocaleString('es-MX', { maximumFractionDigits: 2 });
}

/**
 * La cuenta del muro con huecos de una capa en VARIAS plantas, cada una con su alto propio (la misma que
 * usa el panel). null si alguna planta no tiene alto o no hay huecos. Para la lista de capas, que muestra
 * el área de cada planta sin que la capa sea la seleccionada.
 */
function cuentaPorPlantasConVanos(
  mv: Asignable['muroVanos'] | undefined,
  altosStr: Record<string, string>,
): ReturnType<typeof cuentaDeMuroConVanos> | null {
  if (!mv || mv.tramos.length < 2 || !mv.tramos.every((t) => t.altoPropioM != null)) return null;
  const altos: Record<string, number | null> = {};
  for (const t of mv.tipos) {
    const n = Number((altosStr[t.clave] ?? '').replace(',', '.'));
    altos[t.clave] = Number.isFinite(n) && n > 0 ? n : null;
  }
  return cuentaDeMuroConVanos(
    mv.tramos.map((t) => ({ ejeBrutoM: t.ejeBrutoM, altoM: t.altoPropioM!, vanos: t.vanos })),
    mv.factor,
    altos,
  );
}

/**
 * Nombre legible de un tipo de hueco: el bloque del dibujo con la primera letra en mayúscula; sin bloque pero
 * con trazos de puerta o ventana dibujados con líneas, «Puerta o ventana (dibujada con líneas)»; sin nada,
 * «Hueco sin puerta ni ventana».
 */
function nombreLegibleDeVano(bloque: string | null, dibujado?: boolean): string {
  const b = (bloque ?? '').trim();
  if (!b) return dibujado ? 'Puerta o ventana (dibujada con líneas)' : 'Hueco sin puerta ni ventana';
  return b.charAt(0).toUpperCase() + b.slice(1);
}

/**
 * "Puertas y ventanas de este muro" (DWG, muro con vanos, concepto por área o volumen): un alto por TIPO
 * de vano (bloque + ancho) y la cuenta del descuento renglón por renglón. Los altos se guardan al salir del
 * campo. El plano trae el ANCHO de cada hueco pero no su alto: cada fila dice cuántos huecos son y cuánto
 * miden de ancho, y la casilla pide solo la altura.
 */
function BloqueDescontarVanos({
  tipos,
  valores,
  onChange,
  onGuardar,
  cuenta,
}: {
  tipos: TipoDeVano[];
  valores: Record<string, string>;
  onChange: (clave: string, valor: string) => void;
  onGuardar: () => void;
  cuenta: ReturnType<typeof cuentaDeMuroConVanos> | null;
}) {
  const conAlto = tipos.filter((t) => {
    const n = Number((valores[t.clave] ?? '').replace(',', '.'));
    return Number.isFinite(n) && n > 0;
  }).length;
  const faltan = tipos.length - conAlto;

  // Los descuentos se nombran por su bloque (todos los anchos juntos).
  const descuentos: Array<{ nombre: string; m2: number }> = [];
  if (faltan === 0 && cuenta?.netoM2 != null) {
    const porNombre = new Map<string, number>();
    for (const d of cuenta.descuentos) {
      const k = d.bloque ? nombreLegibleDeVano(d.bloque) : d.dibujado ? 'Puertas o ventanas (dibujadas con líneas)' : 'Huecos sin puerta ni ventana';
      porNombre.set(k, (porNombre.get(k) ?? 0) + d.m2);
    }
    for (const [nombre, m2] of porNombre.entries()) {
      descuentos.push({ nombre, m2 });
    }
  }
  const tabular = { fontVariantNumeric: 'tabular-nums' as const };
  const filaCuenta = (texto: string, valor: string, fuerte = false) => (
    <div key={texto} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, fontWeight: fuerte ? 700 : 400, color: 'var(--text-primary)', ...tabular }}>
      <span>{texto}</span>
      <span>{valor}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>Puertas y ventanas de este muro</span>
        <span style={{ fontSize: 11, color: faltan > 0 ? 'var(--warn)' : 'var(--ok)', whiteSpace: 'nowrap' }}>
          {conAlto} de {tipos.length} con altura
        </span>
      </div>
      <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
        El plano dice cuánto mide de ancho cada hueco, pero no su altura. Escribe la altura de cada uno, en metros, para descontar solo lo que de verdad es hueco. Se guarda en este plano.
      </span>
      {tipos.map((t) => {
        const nombre = nombreLegibleDeVano(t.bloque, t.dibujado);
        return (
          <div key={t.clave} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 96px', gap: 8, alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 600, overflowWrap: 'anywhere' }}>{nombre}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                {t.n === 1 ? '1 hueco' : `${t.n} huecos`} · {fmtM(t.anchoM)} m de ancho{t.n === 1 ? '' : ' cada uno'}
              </div>
            </div>
            <Input
              size="small"
              type="number"
              aria-label={`Altura de ${nombre}, ${fmtM(t.anchoM)} m de ancho, en metros`}
              value={valores[t.clave] ?? ''}
              onChange={(_, d) => onChange(t.clave, d.value)}
              onBlur={onGuardar}
              placeholder="Altura"
              contentAfter={<span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>m</span>}
            />
          </div>
        );
      })}
      {faltan > 0 && (
        <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)' }}>
          {faltan === 1 ? 'Falta la altura de 1 tipo de hueco.' : `Faltan las alturas de ${faltan} tipos de hueco.`} Mientras tanto se descuentan de piso a techo y el renglón queda marcado para que lo revises.
        </span>
      )}
      {faltan === 0 && cuenta?.netoM2 == null && (
        <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)' }}>Escribe el alto del muro para ver la cuenta.</span>
      )}
      {faltan === 0 && cuenta?.netoM2 != null && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 6, borderTop: '1px solid var(--border-subtle)' }}>
          {filaCuenta('Muro completo, con sus huecos', `${fmtM(cuenta.brutoM2)} m²`)}
          {descuentos.map((d) => filaCuenta(`− ${d.nombre}`, `${fmtM(d.m2)} m²`))}
          {filaCuenta('Queda para la hoja', `${fmtM(cuenta.netoM2)} m²`, true)}
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4, paddingTop: 2 }}>
            Esta cantidad puede ser mayor que la de antes: sin las alturas, los huecos se descuentan de piso a techo; con ellas solo se descuenta lo que es hueco.
          </span>
        </div>
      )}
    </div>
  );
}

/** Abreviatura de 2 letras para la insignia del catálogo en el rail colapsado (misma fórmula que
 *  cotización — coherencia visual): iniciales de las 2 primeras palabras, o las 2 primeras letras. */
function catalogAbbrev(name: string): string {
  const words = (name ?? '').trim().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length >= 2) return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase();
  return (words[0] ?? name ?? '?').slice(0, 2).toUpperCase();
}

/** Riel fino que reemplaza a un panel lateral contraído; su botón lo vuelve a abrir. */
function RailColapsar({
  etiqueta,
  lado,
  onExpandir,
}: {
  etiqueta: string;
  lado: 'izq' | 'der';
  onExpandir: () => void;
}) {
  return (
    <aside
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0',
        background: 'var(--bg-surface)', minHeight: 0,
        borderRight: lado === 'izq' ? '1px solid var(--border-subtle)' : 'none',
        borderLeft: lado === 'der' ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <button
        type="button"
        onClick={onExpandir}
        title={`Mostrar ${etiqueta}`}
        aria-label={`Mostrar ${etiqueta}`}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
          borderRadius: 6, border: '1px solid var(--border-subtle)', background: 'var(--bg-canvas)',
          color: 'var(--text-secondary)', cursor: 'pointer',
        }}
      >
        {lado === 'izq' ? <ChevronRight16Regular /> : <ChevronLeft16Regular />}
      </button>
      <span
        style={{
          writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 11,
          color: 'var(--text-secondary)', letterSpacing: '0.04em', whiteSpace: 'nowrap',
        }}
      >
        {etiqueta}
      </span>
    </aside>
  );
}

/** Botón chico para contraer un panel lateral. */
function BtnContraer({ onClick, lado }: { onClick: () => void; lado: 'izq' | 'der' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Contraer panel"
      aria-label="Contraer panel"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26,
        borderRadius: 6, border: '1px solid var(--border-subtle)', background: 'transparent',
        color: 'var(--text-tertiary)', cursor: 'pointer',
      }}
    >
      {lado === 'izq' ? <ChevronLeft16Regular /> : <ChevronRight16Regular />}
    </button>
  );
}


/** Fila etiqueta→valor del grid de propiedades (estilo AutoCAD, curado). */
function FilaProp({ label, value, fuerte }: { label: string; value: string; fuerte?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline',
        padding: '5px 0', borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>{label}</span>
      <span
        style={{
          fontSize: 12.5, color: fuerte ? 'var(--accent)' : 'var(--text-primary)',
          fontWeight: fuerte ? 800 : 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** Unidad corta a mostrar en el label de Cantidad según la magnitud. */
function unidadDeMagnitud(m: PlanoMagnitudConcepto['magnitud']): string {
  switch (m) {
    case 'conteo':
      return 'piezas';
    case 'lineal':
      return 'metros';
    case 'area':
      return 'm²';
    case 'volumen':
      return 'm³';
    case 'peso':
      return 'kg';
    default:
      return 'a mano';
  }
}

/**
 * "1 · Lo que seleccionaste": el nombre de lo elegido y sus medidas en tarjetas, con la que MANDA (la
 * que va a la hoja) resaltada. La MISMA vista para un tipo de un modelo 3D, una capa o un bloque de un
 * dibujo: antes el dibujo mostraba renglones sueltos ("Largo del muro…") y el 3D tarjetas.
 */
function TarjetasMedida({
  titulo,
  subtitulo,
  filas,
  manda,
  estimadas,
}: {
  titulo: string;
  subtitulo?: string;
  filas: { k: string; label: string; value: string }[];
  manda: string | null;
  estimadas?: Set<string>;
}) {
  return (
    <>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
        {titulo}
        {subtitulo && <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}> · {subtitulo}</span>}
      </div>
      {filas.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6 }}>
          {filas.map((f) => {
            const esManda = f.k === manda;
            const esEstimada = estimadas?.has(f.k) ?? false;
            return (
              <div
                key={f.k}
                title={esEstimada ? `${f.label}: el modelo no lo declara, lo medimos de la forma de cada pieza` : undefined}
                style={{
                  padding: '6px 8px', borderRadius: 8, minWidth: 0,
                  border: `1px solid ${esManda ? 'var(--accent)' : 'var(--border-subtle)'}`,
                  background: esManda ? 'var(--accent-weak)' : 'transparent',
                }}
              >
                <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
                  {f.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: esManda ? 700 : 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {f.value}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * Alto de un nodo, medido. Para lo que va pegado arriba en la hoja (mismo apilado que la de
 * cotización): el encabezado de columnas (top:0), debajo la partida general (top = alto del
 * encabezado) y debajo la partida en curso (top = los dos). Se mide con ResizeObserver (no un
 * número mágico) vía callback ref, que reobserva el nodo real cada vez que la HP se abre/re-renderiza o cambia de vista.
 */
function useAltoMedido() {
  const [alto, setAlto] = useState(0);
  const roRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    if (!node) return;
    const medir = () => setAlto(node.getBoundingClientRect().height);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(node);
    roRef.current = ro;
  }, []);
  return [alto, ref] as const;
}

/** Como `useAltoMedido`, pero ancho y alto (para decidir si al visor le cabe algo encima). */
function useTamanoMedido() {
  const [tam, setTam] = useState({ w: 0, h: 0 });
  const roRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    if (!node) return;
    const medir = () => {
      const r = node.getBoundingClientRect();
      setTam((prev) => (prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(node);
    roRef.current = ro;
  }, []);
  return [tam, ref] as const;
}

/**
 * Alto mínimo del visor para mostrar sus barras flotantes: la de arriba (12px + ~36px) y la de pisos
 * del IFC abajo (38px + ~36px) deben caber sin encimarse entre ellas ni salirse del visor. Por debajo
 * de esto el dibujo prácticamente no se ve (preset «Solo la hoja») y las barras no sirven.
 */
const ALTO_MIN_VISOR_CON_BARRAS = 140;
/** Ancho mínimo para lo mismo: la barra de Aislar/Ocultar/Mostrar todo mide ~230px. */
const ANCHO_MIN_VISOR_CON_BARRAS = 240;

/** Columnas de la hoja en "Toda la obra": las mismas que en un plano + "Viene de" después del concepto. */
const HP_COLUMNAS_OBRA = 'minmax(0,1fr) 150px 60px 70px 100px 110px 72px 32px';

/** De qué medida del elemento sale la cantidad de un complementario, en palabras ('otro' = a mano). */
const DERIVA_DE_TEXTO: Record<PlanoComplementario['derivaDe'], string | null> = {
  area: 'del área del elemento',
  largo: 'del largo del elemento',
  volumen: 'del volumen del elemento',
  conteo: 'del número de piezas',
  vanos: 'de las puertas y ventanas que aloja',
  otro: null,
};

/**
 * «Ya asignaste X de Y unidad de {necesidad}»: lo que ya está en la hoja para una necesidad que se cubre con
 * más de un concepto. En otra unidad no se resta: se dicen las dos cantidades. null si no hay nada asignado.
 */
function avisoYaAsignado(nombre: string, objetivo: number | null, unidad: string | null, asig: AsignadoANecesidad[]): string | null {
  if (asig.length === 0) return null;
  const f = (n: number) => (Math.round(n * 100) / 100).toLocaleString('es-MX');
  const q = cantidadQueFalta(objetivo, unidad, asig);
  const u = unidad ? ` ${unidad}` : '';
  if (q.mismaUnidad) {
    return objetivo != null
      ? `Ya asignaste ${f(q.asignado)} de ${f(objetivo)}${u} de ${nombre}.`
      : `Ya asignaste ${f(q.asignado)}${u} de ${nombre}.`;
  }
  // Otra unidad: no se resta; se muestran las dos cantidades.
  const lista = asig.map((a) => `${f(a.cantidad)} ${a.unidad ?? ''}`.trim()).join(' + ');
  return `Ya asignaste ${lista} de ${nombre}${objetivo != null ? `; la cantidad estimada es ${f(objetivo)}${u}` : ''}. Revisa que no se cobre doble.`;
}

/** Agrega `valor` (clave o descripción del concepto) a lo resuelto de la necesidad `nombre`. */
function conResuelto(prev: Map<string, Set<string>>, nombre: string, valor: string): Map<string, Set<string>> {
  const k = normalizarNecesidad(nombre);
  const m = new Map(prev);
  m.set(k, new Set(m.get(k) ?? []).add(valor));
  return m;
}
/** Nombre con que se reconoce una instalación (el corto; si no vino, la descripción). */
const nombreInstal = (it: PlanoInstalacionSupuesta) => it.nombre || it.descripcion;
// Grupo de «Lo que no trae el plano» (una lista guardada de antes no lo trae: era de instalaciones).
const grupoDe = (it: PlanoInstalacionSupuesta) =>
  GRUPOS_NO_DIBUJADO.find((g) => g.id === it.grupo) ?? GRUPOS_NO_DIBUJADO.find((g) => g.id === 'instalaciones')!;

/**
 * La lista ANCHA de conceptos del modo «eligiendo» (centro de la pantalla). Es UNA sola para el
 * concepto principal y para cada adicional: solo cambia qué conceptos recibe y qué pasa al elegir.
 * La descripción va completa: dos conceptos se distinguen al final del texto (espesor, mezcla, acabado).
 */
function ListaConceptos({
  items,
  elegidoId,
  onElegir,
  cargando,
  cargandoTexto,
  conteo,
  vacio,
  aviso,
}: {
  items: ConceptoItem[];
  elegidoId: string | null;
  onElegir: (c: ConceptoItem) => void;
  cargando: boolean;
  cargandoTexto: string;
  conteo: React.ReactNode;
  vacio: React.ReactNode;
  /** Nota ARRIBA de la lista (p. ej. «Entre lo más parecido de tu catálogo no hay …»). Solo se ve con resultados. */
  aviso?: string | null;
}) {
  const styles = useStyles();
  return (
    <>
      <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', margin: '10px 0 8px', flexShrink: 0 }}>
        {cargando ? '' : items.length > 0 ? conteo : ''}
      </span>
      {!cargando && items.length > 0 && aviso && (
        <div
          role="note"
          style={{
            fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-primary)', padding: '8px 10px', marginBottom: 8,
            borderRadius: 6, border: '1px solid var(--border-subtle)', background: 'var(--bg-raised)',
            flexShrink: 0,
          }}
        >
          {aviso}
        </div>
      )}
      <div className={styles.listaAncha}>
        {cargando && (
          <div style={{ padding: 20 }}>
            <Spinner size="tiny" label={cargandoTexto} />
          </div>
        )}
        {!cargando && items.length === 0 && (
          <div style={{ padding: '20px 4px', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{vacio}</div>
        )}
        {!cargando &&
          items.map((r) => (
            <button
              key={r.id}
              type="button"
              className={mergeClasses(styles.filaAncha, elegidoId === r.id && styles.filaAnchaSel)}
              aria-pressed={elegidoId === r.id}
              onClick={() => onElegir(r)}
            >
              <span className={styles.filaAnchaDesc}>{r.descripcion}</span>
              <span className={styles.filaAnchaMeta}>
                <span>{r.clave}</span>
                {r.unidad && <span>· {r.unidad}</span>}
                {r.familia && <span>· {r.familia}</span>}
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  · {r.precio != null ? fmtMoneda(r.precio) : 'sin precio'}
                </span>
              </span>
              {r.razon && <span className={styles.filaAnchaRazon}>{r.razon}</span>}
            </button>
          ))}
      </div>
    </>
  );
}


/** Cuánto se espera al servidor antes de armar sin preguntar el material (el servidor tiene su propio tope). */
const ESPERA_MAXIMA_MATERIAL_MS = 45_000;

/** Lo que el servidor dijo sobre qué material preguntar (por clase IFC) para un conjunto de grupos. */
type EstadoPreguntasMaterial = {
  estado: 'cargando' | 'listo' | 'fallo';
  clave: string;
  porClase: Record<string, PreguntaDeMaterial>;
  aviso: string | null;
} | null;

/** Identidad de la consulta: el plano y los grupos (clase + tipos). Si cambia, se vuelve a preguntar al servidor. */
function claveDeGruposMaterial(fileId: string, grupos: GrupoSinMaterial[]): string {
  return `${fileId}|${grupos.map((g) => `${g.clase}:${g.tipos.join('/')}`).join(';')}`;
}

/**
 * Pide al servidor, una vez por consulta, de qué grupos conviene preguntar el material (los que el catálogo del
 * cliente distingue por material). No pinta nada: deja el resultado en el estado de la página. Vive aparte porque la
 * página tiene una salida anticipada y los efectos no pueden ir después de ella.
 */
function CargadorDePreguntasDeMaterial({
  activo,
  projectId,
  fileId,
  grupos,
  catalogId,
  onEstado,
}: {
  activo: boolean;
  projectId: string;
  fileId: string;
  grupos: GrupoSinMaterial[];
  /** El catálogo por defecto de la pantalla (si el proyecto aún no eligió los suyos). */
  catalogId: string;
  onEstado: (e: EstadoPreguntasMaterial) => void;
}) {
  const clave = `${claveDeGruposMaterial(fileId, grupos)}|${catalogId}`;
  const onEstadoRef = useRef(onEstado);
  onEstadoRef.current = onEstado;
  const gruposRef = useRef(grupos);
  gruposRef.current = grupos;
  const catalogIdRef = useRef(catalogId);
  catalogIdRef.current = catalogId;
  useEffect(() => {
    if (!activo || gruposRef.current.length === 0) return;
    let vivo = true;
    onEstadoRef.current({ estado: 'cargando', clave, porClase: {}, aviso: null });
    const sinRespuesta: MaterialesPorGrupoResponse = { grupos: [], avisoIa: MENSAJE_MATERIAL_NO_REVISADO };
    // Tope de espera (constante técnica): si el servidor no contesta, «Armar» no puede quedarse bloqueado; se arma sin preguntar.
    const vencimiento = new Promise<MaterialesPorGrupoResponse>((res) => setTimeout(() => res(sinRespuesta), ESPERA_MAXIMA_MATERIAL_MS));
    void Promise.race([
      pedirMaterialesPorGrupo(projectId, fileId, { grupos: gruposRef.current, ...(catalogIdRef.current ? { catalogId: catalogIdRef.current } : {}) }),
      vencimiento,
    ]).then((r) => {
      if (!vivo) return;
      const porClase: Record<string, PreguntaDeMaterial> = {};
      for (const g of r.grupos) porClase[g.clase] = g;
      onEstadoRef.current({ estado: r.avisoIa ? 'fallo' : 'listo', clave, porClase, aviso: r.avisoIa });
    });
    return () => {
      vivo = false;
    };
  }, [activo, clave, projectId, fileId]);
  return null;
}

export function PlanosPage() {
  const styles = useStyles();
  const visible = usePlanoAHpVisible();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  // --- Selección de proyecto / archivo ---
  const [projects, setProjects] = useState<Project[]>([]);
  // Entrando desde el proyecto (/project/:id/planos) el proyecto viene de la ruta y no se
  // elige con combo; en /planos (staff) se sigue eligiendo arriba.
  const { id: proyectoDeRuta, fileId: archivoDeRuta } = useParams<{ id?: string; fileId?: string }>();
  // Entrando por el proyecto (/project/:id/planos[/:fileId]): sin combos; la tira de planos manda y el
  // plano se abre solo. Sin :fileId es la GALERÍA de planos del proyecto.
  const modoProyecto = !!proyectoDeRuta;
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState<string | null>(proyectoDeRuta ?? null);
  useEffect(() => {
    if (proyectoDeRuta) setProjectId(proyectoDeRuta);
  }, [proyectoDeRuta]);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  // Sube cuando hay archivos nuevos (subidos desde la tira) → recargar la lista.
  const [archivosVersion, setArchivosVersion] = useState(0);
  // «Cambió el plano»: al aplicar (o volver) la hoja pasa a otro archivo; tras recargar la lista se abre ese.
  const abrirTrasRecargarRef = useRef<string | null>(null);
  // La lista de archivos no se pudo cargar (tras reintentar): NO es "el plano ya no está".
  const [errorArchivos, setErrorArchivos] = useState(false);
  // Nombre del proyecto de la ruta (para el breadcrumb), del mismo pedido que trae los archivos.
  const [nombreProyectoRuta, setNombreProyectoRuta] = useState<string | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  // Galería/tira de planos del proyecto (solo entrando por el proyecto).
  const galeria = usePlanosGaleria(modoProyecto ? proyectoDeRuta : null);
  // DWG con escala dudosa: la unidad que eligió el usuario (manda sobre la del lector) y si todavía
  // hay que preguntarla. Viene en la ficha de la galería (un solo criterio: `hayQuePreguntarUnidad`).
  const fichaAbierta = galeria.planos?.find((p) => p.fileId === fileId) ?? null;
  const escalaElegida = fichaAbierta?.escalaElegida ?? null;
  const preguntarUnidadAbierto = fichaAbierta?.preguntarUnidad === true && !!fichaAbierta.escala;
  const elegirUnidadDelPlano = useCallback(
    async (fid: string, unidad: string) => {
      const pid = proyectoDeRuta ?? null;
      if (!pid) return;
      await setPlanoEscala(pid, fid, unidad);
      await galeria.recargar();
    },
    [proyectoDeRuta, galeria],
  );
  const puedeEditar = useCanMutate();
  const [subiendoPlano, setSubiendoPlano] = useState(false);
  const [progresoSubida, setProgresoSubida] = useState(0);
  // Corrección del usuario a la cubeta de una capa (se cotiza / por revisar / ruido). Override sobre la
  // decisión de la IA. Por ahora vive en pantalla (se reinicia al cambiar de plano); persistir = follow-up.
  const [decisionOverride, setDecisionOverride] = useState<Record<string, DecisionDeCapa>>({});
  useEffect(() => { setDecisionOverride({}); }, [fileId]);
  // El ⋯ se GUARDA por plano (migr 166): sobrevive a recargar. Optimista; si no se guarda, se avisa.
  const guardarDecision = useCallback(
    (clave: string, d: DecisionDeCapa) => {
      setDecisionOverride((o) => ({ ...o, [clave]: d }));
      if (!projectId || !fileId || d === 'dudoso') return;
      setDecisionCapa(projectId, fileId, clave, d).catch(() => {
        dispatchToast(
          <Toast>
            <ToastTitle>No se pudo guardar tu corrección: se perderá al recargar.</ToastTitle>
          </Toast>,
          { intent: 'warning', timeout: 4000 },
        );
      });
    },
    [projectId, fileId, dispatchToast],
  );
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // --- Estado del plano ---
  const [status, setStatus] = useState<'idle' | 'pending' | 'ready' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [inventario, setInventario] = useState<PlanoInventario | null>(null);
  // Origen del inventario: 'pdf_vision' = leído de un PDF por visión → cantidades ESTIMADAS (se avisa
  // en pantalla). 'cad'/'ifc' = geometría real (exacto). Default 'cad' (planos históricos).
  const [origenPlano, setOrigenPlano] = useState<PlanoOrigen>('cad');
  // Clasificación de capas por IA (naturaleza: muro/vano/ventana/anotación). ADITIVO: si es null
  // (plano sin clasificar / sin saldo / viejo) la medición 2D corre como siempre. La usa la
  // cuantificación para no proponer ejes/mobiliario/instalaciones como muros medibles.
  const [capasClasificadas, setCapasClasificadas] = useState<CapasClasificadasDTO | null>(null);
  // ¿Es «solo dibujo»? La corrección del ⋯ manda; si no la hay, la clasificación (IA en DWG, la clase
  // en IFC). Lo que es solo dibujo NO se puede seleccionar: no se presupuesta.
  const esSoloDibujo = useCallback(
    (clave: string, esBloque: boolean): boolean => {
      const o = decisionOverride[clave];
      if (o) return o === 'ruido';
      if (esBloque) return decisionDeBloque(clave, capasClasificadas) === 'ruido';
      const c = inventario?.capas.find((x) => x.capa === clave);
      const d = c?.categoria != null ? decisionIfc(c.categoria, c.ifc?.supertipos) : decisionDeCapa(clave, capasClasificadas);
      return d === 'ruido';
    },
    [decisionOverride, capasClasificadas, inventario],
  );
  const avisarSoloDibujo = useCallback(
    (nombre: string) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{`${nombre} es solo dibujo: no se presupuesta.`}</ToastTitle>
          <ToastBody>¿Sí se construye? Cámbialo a «Se construye» con el ⋯ de su fila, a la izquierda.</ToastBody>
        </Toast>,
        { intent: 'info', timeout: 4000 },
      );
    },
    [dispatchToast],
  );
  const [svgReady, setSvgReady] = useState(false);
  // fileId del IFC abierto en el visor 3D (null = no hay IFC en pantalla). El IFC no usa el
  // pipeline de SVG; se ve en 3D directo, así que va por su propio camino.
  const [ifcAbierto, setIfcAbierto] = useState<string | null>(null);
  // IFC muy pesado: se pregunta antes de abrirlo (el tope está en Calibración). Los ya confirmados
  // en esta visita no vuelven a preguntar.
  const [ifcGrandeConfirmados, setIfcGrandeConfirmados] = useState<ReadonlySet<string>>(new Set());
  const [ifcAvisoTamanoMb, setIfcAvisoTamanoMb] = useState<number | null>(null);
  useEffect(() => {
    if ((!ifcAbierto && !modoProyecto) || ifcAvisoTamanoMb != null) return;
    void getParametrosMedicionIfc()
      .then((p) => setIfcAvisoTamanoMb(p.ifc_aviso_tamano_mb))
      .catch(() => setIfcAvisoTamanoMb(PARAMETROS_MEDICION_IFC_DEFAULT.ifc_aviso_tamano_mb));
  }, [ifcAbierto, modoProyecto, ifcAvisoTamanoMb]);
  // VISOR 3D EXPERIMENTAL (DWG/DXF → IFC): cuando un DWG/DXF se convierte a IFC para verlo en 3D.
  // Es SOLO visualización: NO alimenta cantidades a la HP (por eso NO se conecta al inventario/HP).
  // `ifcExpUrl` = URL del IFC generado (se pasa a IfcViewer3D como srcUrl) o null si el 3D no está a la vista.
  const [ifcExpUrl, setIfcExpUrl] = useState<string | null>(null);
  const [ifcExpStatus, setIfcExpStatus] = useState<'idle' | 'generando' | 'listo' | 'error'>('idle');
  const [ifcExpError, setIfcExpError] = useState<string | null>(null);
  const ifcExpPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Selección de elementos ---
  const [idsSel, setIdsSel] = useState<Set<string>>(new Set());
  // «Saltar» del recorrido «Empieza por lo grande»: lo saltado queda para el final (vuelve cuando no
  // quede otro). Por plano y solo en esta visita.
  const [saltados, setSaltados] = useState<Set<string>>(() => new Set());
  useEffect(() => setSaltados(new Set()), [fileId]);
  const [capasSel, setCapasSel] = useState<Set<string>>(new Set());
  // IFC (BIM): instancias SACADAS del tipo activo (por expressID del elemento). Con un tipo activo el
  // usuario excluye en el visor 3D las piezas que no van; la cantidad se recalcula sin ellas. El DWG 2D
  // no usa esto (selecciona por bloque con idsSel).
  const [instExcluidas, setInstExcluidas] = useState<Set<number>>(new Set());
  // IFC: pieza ENFOCADA desde el panel (por expressID) para ubicarla en el 3D sin sacarla del conteo.
  // Es un realce visual (no toca la selección). null = ninguna.
  const [instEnfoque, setInstEnfoque] = useState<number | null>(null);
  // IFC: modo de visibilidad del visor (comandos "ocultar/aislar/ocultar asignadas"). Es solo VISTA:
  // no cambia la selección ni la asignación. 'todo' = nada oculto.
  const [visMode, setVisMode] = useState<'todo' | 'ocultar-sel' | 'aislar-sel' | 'ocultar-asig'>('todo');
  // IFC: "Ver por piso" + corte de planta + ocultar techo + piezas ocultadas con clic derecho. Todo es
  // solo VISTA (no toca la selección ni las cantidades) y se combina con `visMode`.
  const [nivelesIfc, setNivelesIfc] = useState<NivelIfc[]>([]);
  // "Nivel 1 · +0.00 m": los pisos por su elevación real (el nombre del modelo va en chico).
  const etiquetaNivel = useMemo(() => etiquetasDeNiveles(nivelesIfc), [nivelesIfc]);
  const soloPartesIfc = nivelesIfc.length > 0 && nivelesIfc.every((n) => n.tipo === 'parte');
  const [nivelDeElemento, setNivelDeElemento] = useState<Map<number, number>>(new Map());
  const [hayTechoIfc, setHayTechoIfc] = useState(false);
  // Obras del modelo (IfcBridge, IfcRoad, IfcBuilding…): "Agrupar por obra" solo si hay más de una.
  const [obrasIfc, setObrasIfc] = useState<ObraIfc[]>([]);
  const [obraDeElemento, setObraDeElemento] = useState<Map<number, number>>(new Map());
  const [nivelVista, setNivelVista] = useState<number | null>(null);
  const [alturaCorte, setAlturaCorte] = useState(1.2);
  // "Cortar" el nivel como planta (arquitectura) o verlo COMPLETO (estructura/instalaciones: con el
  // corte a 1.2 m solo quedaban los arranques de las columnas). null = aún no lo tocó el usuario → el
  // default sale del modelo (ver `cortarPiso` más abajo).
  const [cortarElegido, setCortarElegido] = useState<boolean | null>(null);
  // Default de "Cortar": si el modelo trae puertas o ventanas es de arquitectura → se corta como planta;
  // si no (estructura, instalaciones) se ve el nivel completo. Sale de las clases del propio modelo.
  const modeloArquitectonico = (inventario?.capas ?? []).some((c) => /^Ifc(Door|Window)/.test(c.categoria ?? ''));
  const cortarPiso = cortarElegido ?? modeloArquitectonico;
  useEffect(() => setCortarElegido(null), [fileId]);
  const [ocultarTecho, setOcultarTecho] = useState(false);
  const [ocultasClic, setOcultasClic] = useState<Set<number>>(new Set());
  // Aislar desde el árbol (una categoría de un piso): SOLO esas piezas a la vista. null = sin aislar.
  const [aisladasArbol, setAisladasArbol] = useState<Set<number> | null>(null);
  // Ojos (solo VISTA, no se recuerdan entre sesiones): DWG = capas y tramas apagadas; IFC = piezas apagadas por
  // categoría o tipo. Se suman a lo oculto con clic derecho / «Ocultar selección» en un solo conjunto.
  const [capasOcultasDwg, setCapasOcultasDwg] = useState<Set<string>>(new Set());
  const [tramasOcultasDwg, setTramasOcultasDwg] = useState(false);
  const [ocultasOjo, setOcultasOjo] = useState<Set<number>>(new Set());
  // Panel izquierdo del IFC: por tipo (como siempre) o por piso (árbol Piso → Categoría → Tipo → Pieza).
  const [panelIfcPor, setPanelIfcPor] = useState<'tipo' | 'piso' | 'obra'>('tipo');
  const [pisosAbiertos, setPisosAbiertos] = useState<Set<string>>(new Set());
  const [ordenCamara, setOrdenCamara] = useState<{ n: number; vista: 'seleccion' | 'arriba' | 'todo' | 'resaltadas' } | null>(null);
  const pedirCamara = useCallback(
    (vista: 'seleccion' | 'arriba' | 'todo') => setOrdenCamara((o) => ({ n: (o?.n ?? 0) + 1, vista })),
    [],
  );
  // Escala del plano: el $INSUNITS del DWG no es confiable (algunos declaran mm pero están
  // dibujados en metros — p.ej. cs_05: una "puerta 0.90" mide 0.87 u). El usuario puede
  // corregir la unidad y las longitudes se recalculan. 'archivo' = usar lo que declara el DWG.
  const [unidadPlanoSel, setUnidadPlanoSel] = useState<'archivo' | 'm' | 'cm' | 'mm'>('archivo');

  // --- Paneles colapsables ---
  const [openCapas, setOpenCapas] = useState(true);
  const [openBloques, setOpenBloques] = useState(true);
  // Paneles laterales contraíbles: al colapsar, el plano gana ancho (baja la densidad).
  const [leftAbierto, setLeftAbierto] = usePreferencia('izq-abierto', true, esBool);
  // Panel de CATÁLOGOS (columna propia a la izquierda de Capas), colapsable independiente.
  const [catAbierto, setCatAbierto] = usePreferencia('catalogos-abierto', true, esBool);
  const [derechaAbierta, setDerechaAbierta] = usePreferencia('der-abierta', true, esBool);
  // Acordeón del panel derecho: una sección abierta a la vez.
  // Ancho del panel derecho (redimensionable, persistido por usuario).
  const [rightW, setRightW] = useState<number>(() => {
    try {
      const v = parseInt(localStorage.getItem('supernova:planos-right-w') ?? '', 10);
      if (Number.isFinite(v) && v >= 280 && v <= 640) return v;
    } catch {
      /* localStorage no disponible */
    }
    // El ancho donde los tres botones del paso 2 caben en una línea (Sebastián, 2026-10-02).
    return 390;
  });
  // Ancho del panel izquierdo ("Lo que trae el modelo" / Capas): redimensionable como el derecho,
  // para leer nombres largos de tipos y capas. Persistido por usuario.
  const [leftW, setLeftW] = useState<number>(() => {
    try {
      const v = parseInt(localStorage.getItem('supernova:planos-left-w') ?? '', 10);
      if (Number.isFinite(v) && v >= 220 && v <= 640) return v;
    } catch {
      /* localStorage no disponible */
    }
    return 280;
  });
  const startResizeLeft = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Durante el arrastre solo se mueve la variable CSS del grid (sin re-render de React: con el
    // modelo 3D abierto, un setState por píxel congelaba la pestaña). El estado se fija al soltar.
    const aside = e.currentTarget.parentElement;
    const grid = aside?.parentElement ?? null;
    const inicio = aside?.getBoundingClientRect().left ?? 0;
    let ultimo = aside?.getBoundingClientRect().width ?? 280;
    const onMove = (ev: MouseEvent) => {
      ultimo = Math.min(640, Math.max(220, ev.clientX - inicio));
      grid?.style.setProperty('--left-w', `${ultimo}px`);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setLeftW(ultimo);
      try {
        localStorage.setItem('supernova:planos-left-w', String(ultimo));
      } catch {
        /* localStorage no disponible */
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);
  // Grupos de la lista izquierda que el usuario cerró (Muros, Losas, Se construye…).
  const [gruposCerrados, setGruposCerrados] = useState<Set<string>>(() => new Set());
  const alternarGrupo = useCallback((g: string) => {
    setGruposCerrados((prev) => {
      const s = new Set(prev);
      if (s.has(g)) s.delete(g);
      else s.add(g);
      return s;
    });
  }, []);
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const onMove = (ev: MouseEvent) => {
      const w = Math.min(640, Math.max(280, window.innerWidth - ev.clientX));
      setRightW(w);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setRightW((w) => {
        try {
          localStorage.setItem('supernova:planos-right-w', String(w));
        } catch {
          /* localStorage no disponible */
        }
        return w;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Altos de lo que va pegado arriba en la hoja (ver `useAltoMedido`): el encabezado de columnas
  // (top:0), debajo la partida general y debajo la partida en curso.
  const [hpColHeadH, hpColHeadRef] = useAltoMedido();
  const [partidaGeneralH, partidaGeneralRef] = useAltoMedido();
  // Tamaño REAL del visor (lo que queda del centro tras el cajón de la hoja y los paneles). Con
  // «Solo la hoja» el dibujo no se ve y sus barras flotantes (zoom, Aislar/Ocultar, la de pisos y la
  // barra de estado del IFC) se salían del visor: quedaban ENCIMA del encabezado de la hoja, o de las
  // pestañas; y con una ventana angosta, encima del panel derecho. Si el dibujo no se ve, sus barras
  // no se muestran: se condicionan a este tamaño, no se tapan con z-index.
  const [tamVisor, visorWrapRef] = useTamanoMedido();
  const visorConEspacio = tamVisor.h >= ALTO_MIN_VISOR_CON_BARRAS && tamVisor.w >= ANCHO_MIN_VISOR_CON_BARRAS;

  // Alto de la hoja de presupuesto (cajón inferior, redimensionable + persistido).
  const [hpH, setHpH] = useState<number>(() => {
    try {
      const v = parseInt(localStorage.getItem('supernova:planos-hp-h') ?? '', 10);
      if (Number.isFinite(v) && v >= 120 && v <= 900) return v;
    } catch {
      /* localStorage no disponible */
    }
    return 200;
  });
  const startResizeHp = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const maxH = Math.round(window.innerHeight * 0.75);
    const startH = hpH;
    const onMove = (ev: MouseEvent) => {
      // Arrastrar hacia arriba agranda la hoja.
      const h = Math.min(maxH, Math.max(120, startH + (startY - ev.clientY)));
      setHpH(h);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setHpH((h) => {
        try {
          localStorage.setItem('supernova:planos-hp-h', String(h));
        } catch {
          /* localStorage no disponible */
        }
        return h;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [hpH]);

  // Presets rápidos de reparto vertical plano/HP (para enseñar el plano en grande, mitad y mitad,
  // o la hoja en grande sin tener que arrastrar). Abre la HP si estaba cerrada y persiste el alto.
  const aplicarPresetHp = useCallback((preset: 'plano' | 'mitad' | 'hoja') => {
    const maxH = Math.round(window.innerHeight * 0.75);
    const h =
      preset === 'plano' ? 150
        : preset === 'mitad' ? Math.min(maxH, Math.max(120, Math.round(window.innerHeight * 0.42)))
          : maxH; // 'hoja' → lo más alta permitida
    setHpAbierto(true);
    setHpH(h);
    try { localStorage.setItem('supernova:planos-hp-h', String(h)); } catch { /* localStorage n/d */ }
  }, []);

  // --- Catálogos de /planos (selección PROPIA) + buscador de conceptos ---
  // La selección NO se hereda de la cotización del proyecto: se elige aquí (puede
  // llegar a ser una app aparte). Vive en `plano_catalogos` (migr 114).
  const [catalogos, setCatalogos] = useState<ProjectCatalog[]>([]);
  const [catalogSel, setCatalogSel] = useState<string | null>(null);
  // Todos los catálogos disponibles del tenant (para el selector con prioridad).
  const [catalogosDisponibles, setCatalogosDisponibles] = useState<Catalog[]>([]);
  const [catalogosDialogAbierto, setCatalogosDialogAbierto] = useState(false);
  const [guardandoCatalogos, setGuardandoCatalogos] = useState(false);
  // Borrador de la selección mientras el diálogo está abierto (el picker emite onChange).
  const [catalogosBorrador, setCatalogosBorrador] = useState<string[]>([]);
  // Catálogos por prioridad (índice 0 = mayor): buscar/sugerir abarcan todos.
  const catalogosPrioridad = useMemo(() => catalogos.map((c) => c.catalogId), [catalogos]);
  // id de catálogo → nombre, para mostrar en la HP de qué catálogo salió cada renglón.
  const catalogoNombrePorId = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of catalogos) m.set(c.catalogId, c.catalog.name);
    return m;
  }, [catalogos]);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [resultados, setResultados] = useState<ConceptoItem[]>([]);
  // Algún catálogo tenía más coincidencias que el tope que entrega el servidor por consulta.
  const [resultadosRecortados, setResultadosRecortados] = useState(false);
  const [buscando, setBuscando] = useState(false);
  // Un catálogo ECOSTOS es ~96% materiales; para armar la HP lo que queremos
  // ofrecer son CONCEPTOS (partidas de obra con su análisis), no insumos sueltos.
  // Por eso el buscador arranca filtrado a conceptos (tipo 7). El cotizador puede
  // apagarlo si necesita asignar un material/insumo directo.
  // A la HP van conceptos/partidas, nunca materiales sueltos → el buscador filtra SIEMPRE a partidas
  // (tipo 7). Antes era un interruptor; se quitó porque en planos nunca se asignan materiales.
  const [conceptoElegido, setConceptoElegido] = useState<ConceptoItem | null>(null);

  // --- Fase 2: la IA decide la MAGNITUD del concepto + la ALTURA del muro ---
  // magnitudCache: por resourceId, para no re-llamar a la IA por el mismo concepto.
  // alturaProp: propuesta una vez por plano (leída de sus atributos). altoStr/espesorStr:
  // editables, para calcular m² (largo×alto) y m³ (largo×alto×espesor).
  const [magnitudCache, setMagnitudCache] = useState<Map<string, PlanoMagnitudConcepto | null>>(
    new Map(),
  );
  const [clasificando, setClasificando] = useState(false);
  const [alturaProp, setAlturaProp] = useState<PlanoAlturaPropuesta | null>(null);
  // Si la propuesta de altura falló con un motivo del servidor (p. ej. la IA tardó demasiado), se dice.
  const [alturaPropError, setAlturaPropError] = useState<string | null>(null);
  // Mismo caso para la magnitud de cada concepto (id → motivo del servidor).
  const [magnitudErrorPorId, setMagnitudErrorPorId] = useState<Map<string, string>>(() => new Map());
  const [altoStr, setAltoStr] = useState('');
  // Alto de cada TIPO de vano (clave de tipo → texto del campo), guardado por plano. Se captura una vez.
  const [altosVanosStr, setAltosVanosStr] = useState<Record<string, string>>({});
  const [espesorStr, setEspesorStr] = useState('');
  // Fase 3: conceptos complementarios que la IA propone para el concepto elegido.
  const [complementarios, setComplementarios] = useState<PlanoComplementario[]>([]);
  const [sugiriendoComp, setSugiriendoComp] = useState(false);
  const [compNota, setCompNota] = useState<string | null>(null);
  // Resolución EN LÍNEA de complementarios (acordeón): índice activo + sus candidatos + resueltos.
  const [compActivo, setCompActivo] = useState<number | null>(null);
  const [compCandidatos, setCompCandidatos] = useState<ConceptoItem[]>([]);
  const [compBuscando, setCompBuscando] = useState(false);
  // índice del complementario → clave del concepto con que se resolvió (para verificar contra la HP).
  // nombre normalizado de la necesidad → claves (o descripciones) de los conceptos con que se resolvió.
  // Por NOMBRE, no por posición: «Proponer más» agrega al final y el ✓ no cambia de fila. Una necesidad
  // puede resolverse con MÁS de un concepto (excavación tipo I + tipo II).
  const [compResueltos, setCompResueltos] = useState<Map<string, Set<string>>>(new Map());
  // Cantidad editable del complementario activo (pre-cargada con la derivada del elemento).
  const [compCantidadStr, setCompCantidadStr] = useState('');
  // Texto de búsqueda del complementario activo (arranca con lo que propuso la IA; editable).
  const [compQ, setCompQ] = useState('');
  // --- Modo «eligiendo» (rediseño 2026-10-01): al elegir el concepto (paso 2) o resolver los adicionales
  // (paso 3), la lista ancha toma el centro y el dibujo queda chico arriba a la izquierda. El visor NO se
  // desmonta: se encoge con `transform` (sin cambiar el tamaño de su canvas), así al volver está igual.
  const [modoLista, setModoLista] = useState<null | 'principal' | 'adicionales' | 'instalaciones'>(null);
  // Pestaña del adicional activo: buscar (gratis, arranca con lo que propuso la IA), sugeridos (IA, a
  // pedido) o generar (sintético con IA).
  const [compTab, setCompTab] = useState<'sugeridos' | 'buscar' | 'generar'>('buscar');
  const [compSugeridos, setCompSugeridos] = useState<ConceptoItem[] | null>(null);
  const [compSugeridosFaltante, setCompSugeridosFaltante] = useState<string | null>(null);
  const [compSugiriendo, setCompSugiriendo] = useState(false);
  // Concepto elegido en la lista ancha para el adicional activo (se agrega con el botón del panel derecho).
  const [compElegido, setCompElegido] = useState<ConceptoItem | null>(null);
  // Adicionales que el usuario marcó «No va en esta obra» (solo vista: no se guarda).
  const [compDescartados, setCompDescartados] = useState<Set<string>>(new Set());
  const [compAgregando, setCompAgregando] = useState(false);
  // Última búsqueda lanzada para el adicional activo y su número de turno (descarta respuestas viejas).
  const compUltimaBusquedaRef = useRef<string | null>(null);
  const compTurnoRef = useRef(0);
  // El "escenario" del visor (DWG/IFC/SVG) y su tamaño en modo normal: la miniatura lo escala.
  const [escenarioEl, setEscenarioEl] = useState<HTMLDivElement | null>(null);
  const modoListaRef = useRef<typeof modoLista>(null);
  // Dónde está lo seleccionado en el visor CAD (lo informa el visor), medido al entrar al modo.
  const leerCajaMiniRef = useRef<((todo?: boolean) => CajaEnPantalla | null) | null>(null);
  const [cajaMini, setCajaMini] = useState<CajaEnPantalla | null>(null);
  const registrarCajaMini = useCallback((leer: ((todo?: boolean) => CajaEnPantalla | null) | null) => {
    leerCajaMiniRef.current = leer;
  }, []);
  // Números generadores: el visor abierto (IFC o DWG) entrega con qué tomar el croquis de unos elementos.
  const croquisIfcRef = useRef<((p: PedidoCroquisIfc) => Promise<Croquis | null>) | null>(null);
  const croquisDwgRef = useRef<
    ((els: ElementoParaCroquis[], marcas?: { k: string; elementos: ElementoParaCroquis[] }[]) => Promise<Croquis | null>) | null
  >(null);
  // Croquis ya tomados (data URL) por plano + elementos: el plano no cambia mientras está abierto.
  const croquisTomadosRef = useRef(new Map<string, Croquis>());
  useEffect(() => croquisTomadosRef.current.clear(), [fileId]);
  const registrarCroquisIfc = useCallback((f: typeof croquisIfcRef.current) => {
    croquisIfcRef.current = f;
    if (import.meta.env.DEV) (window as unknown as { __croquisIfc?: unknown }).__croquisIfc = f;
  }, []);
  const registrarCroquisDwg = useCallback((f: typeof croquisDwgRef.current) => {
    croquisDwgRef.current = f;
    if (import.meta.env.DEV) (window as unknown as { __croquisDwg?: unknown }).__croquisDwg = f;
  }, []);
  const [tamEscenario, setTamEscenario] = useState<{ w: number; h: number } | null>(null);
  // Ancho real del cuerpo de la página (no de la ventana): decide cuánto se angostan los paneles en el modo.
  const [cuerpoEl, setCuerpoEl] = useState<HTMLDivElement | null>(null);
  const [anchoCuerpo, setAnchoCuerpo] = useState(1600);
  useEffect(() => {
    if (!cuerpoEl) return;
    const ro = new ResizeObserver(() => setAnchoCuerpo(cuerpoEl.clientWidth));
    ro.observe(cuerpoEl);
    return () => ro.disconnect();
  }, [cuerpoEl]);
  // Pantalla angosta (≈1280): miniatura más chica y paneles laterales más angostos mientras se elige.
  const cuerpoAngosto = anchoCuerpo < 1400;

  // --- Sugerencias por IA para la selección activa (Capa 2, bajo demanda) ---
  // No se disparan solas al cambiar la selección: cada sugerencia usa IA. El
  // cotizador aprieta "Sugerir conceptos" y ahí se llama al backend.
  const [sugerencias, setSugerencias] = useState<ConceptoItem[]>([]);
  const [sugiriendo, setSugiriendo] = useState(false);
  const [sugerenciaNota, setSugerenciaNota] = useState<string | null>(null);
  const [sugerenciaFaltante, setSugerenciaFaltante] = useState<string | null>(null);
  // Para qué selección son las sugerencias mostradas (se limpian si cambia).
  const [sugerenciasParaKey, setSugerenciasParaKey] = useState<string | null>(null);

  // --- Asignación / renglones de la HP ---
  const [targetKey, setTargetKey] = useState<string | null>(null);
  const [cantidadStr, setCantidadStr] = useState('1');
  const [asignando, setAsignando] = useState(false);
  // Lo último que se agregó a la hoja desde el panel: "✓ Agregado a la hoja … [Cambiar] [Deshacer]" y,
  // debajo, "Siguiente que falta". `key` = la selección para la que se agregó.
  const [ultimoAgregado, setUltimoAgregado] = useState<{ key: string; renglon: PlanoRenglon } | null>(null);
  // El tipo ya tiene concepto en la hoja y el usuario pidió cambiarlo (abre el buscador del paso 2).
  const [cambiandoPrincipal, setCambiandoPrincipal] = useState(false);
  const [deshaciendo, setDeshaciendo] = useState(false);
  // "2 · ¿Qué concepto es?": tres formas de la MISMA pregunta, en pestañas.
  const [formaConcepto, setFormaConcepto] = useState<'sugeridos' | 'buscar' | 'crear'>('sugeridos');
  const [renglones, setRenglones] = useState<PlanoRenglon[]>([]);
  // 'ai_unavailable' si el último «Generar con IA» se cortó porque la IA no respondía (fail-fast).
  const [avisoIa, setAvisoIa] = useState<'ai_unavailable' | null>(null);
  const [hpAbierto, setHpAbierto] = usePreferencia('hoja-abierta', false, esBool);
  // Eje por el que se agrupa la HP (subpartidas). 'familia' = default (sin IA). Los demás los
  // clasifica la IA por concepto (una vez, cacheado en el servidor + reportado a eko27).
  const [tipoAgrupacion, setTipoAgrupacion] = usePreferencia<TipoAgrupacion>(
    'eje-partidas',
    'familia',
    (v): v is TipoAgrupacion => AGRUPACIONES.some((a) => a.valor === v),
  );
  const [agrupando, setAgrupando] = useState(false);
  // Obra de referencia elegida (solo se acuerda su id; el árbol se carga al usarla) y su selector.
  const [obraIdPref, setObraIdPref] = usePreferencia<string>('obra-referencia', '', (v): v is string => typeof v === 'string');
  const [obraRef, setObraRef] = useState<ObraReferenciaDTO | null>(null);
  const [obrasDialogAbierto, setObrasDialogAbierto] = useState(false);
  const [obrasLista, setObrasLista] = useState<ObraReferenciaResumen[] | null>(null);
  const [obrasListaError, setObrasListaError] = useState(false);
  // «Ver partidas» del diálogo: qué obra tiene el árbol desplegado y los árboles ya traídos (sin elegirla).
  const [obraDesplegada, setObraDesplegada] = useState<string | null>(null);
  const [arbolesDeObra, setArbolesDeObra] = useState<Record<string, ObraReferenciaDTO | 'error'>>({});
  // Basura de una obra subida desde m1: primero pide confirmar (id de la obra que espera el «Sí, borrar»).
  const [obraPorBorrar, setObraPorBorrar] = useState<string | null>(null);
  const [borrandoObra, setBorrandoObra] = useState(false);
  const borrarObra = useCallback(
    async (obraId: string) => {
      setBorrandoObra(true);
      try {
        await borrarObraReferencia(obraId);
        setObrasLista((l) => (l ? l.filter((o) => o.id !== obraId) : l));
        if (obraId === obraIdPref) {
          setObraIdPref('');
          setObraRef(null);
          setTipoAgrupacion('familia');
        }
        setObraPorBorrar(null);
      } catch (e) {
        dispatchToast(
          <Toast>
            <ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo borrar la obra.'}</ToastTitle>
          </Toast>,
          { intent: 'error', timeout: 4000 },
        );
      } finally {
        setBorrandoObra(false);
      }
    },
    [obraIdPref, setObraIdPref, setTipoAgrupacion, dispatchToast],
  );
  const alternarArbolDeObra = useCallback(
    async (obraId: string) => {
      if (obraDesplegada === obraId) {
        setObraDesplegada(null);
        return;
      }
      setObraDesplegada(obraId);
      if (arbolesDeObra[obraId] && arbolesDeObra[obraId] !== 'error') return;
      try {
        const obra = await leerObraReferencia(obraId);
        setArbolesDeObra((a) => ({ ...a, [obraId]: obra }));
      } catch {
        setArbolesDeObra((a) => ({ ...a, [obraId]: 'error' }));
      }
    },
    [obraDesplegada, arbolesDeObra],
  );
  const ordenObra = useMemo(() => (obraRef ? ordenPreorden(obraRef) : null), [obraRef]);
  // Partida principal de la hoja con una obra de referencia: la raíz ÚNICA de esa obra (se respeta tal cual, no
  // se reemplaza por el nombre del proyecto). Con varias raíces no hay una principal: se queda el proyecto.
  const raizDeObra = useMemo(() => {
    if (!obraRef) return null;
    const raices = obraRef.nodos.filter((n) => n.padreId === null);
    return raices.length === 1 && obraRef.nodos.some((n) => n.padreId === raices[0]!.id) ? etiquetaDeNodo(raices[0]!) : null;
  }, [obraRef]);
  const etiquetaAgrupacion = tipoAgrupacion === 'obra' && obraRef ? `Obra: ${obraRef.nombre}` : AGRUPACION_LABEL[tipoAgrupacion];
  // Descripción del sintético (cuando el concepto NO está en catálogo): se SIEMBRA con el nombre
  // del elemento del CAD y el usuario la puede editar antes de mandarlo a la HP / cotizar con IA.
  const [descSinteticoStr, setDescSinteticoStr] = useState('');
  // ¿El usuario ya escribió en la descripción de «Generar con IA»? Entonces la semilla no lo pisa.
  const descEditadaRef = useRef(false);
  // Lo que la IA entendió de cada selección al pedir «Recomendados» (ya pagado): semilla de «Generar con IA».
  const [interpretacionPorKey, setInterpretacionPorKey] = useState<Map<string, string>>(() => new Map());
  const [agregandoSintetico, setAgregandoSintetico] = useState(false);
  // Edición inline de la descripción de un renglón sintético ya en la HP (antes de generarlo).
  const [editandoDescId, setEditandoDescId] = useState<string | null>(null);
  const [editDescStr, setEditDescStr] = useState('');
  // Confirmación antes de generar el lote con IA (es lento y consume la bolsa de tokens; se
  // puede apretar sin querer — igual que en las cotizaciones).
  const [confirmarLoteOpen, setConfirmarLoteOpen] = useState(false);
  const [generandoLote, setGenerandoLote] = useState(false);
  // Cantidad corregida a mano (edición en línea en la hoja).
  const [editandoCantId, setEditandoCantId] = useState<string | null>(null);
  const [editCantStr, setEditCantStr] = useState('');
  // «Cambiar concepto» de un renglón ya puesto.
  const [cambiarConceptoDe, setCambiarConceptoDe] = useState<PlanoRenglon | null>(null);
  // «Dos opciones con precio» (idea 7): las opciones de los principales del plano y el renglón cuyas
  // opciones se están viendo. Una opción no suma a la hoja (vive en plano_opciones, no en la hoja).
  const [opcionesPlano, setOpcionesPlano] = useState<PlanoOpcion[]>([]);
  const [opcionesDe, setOpcionesDe] = useState<string | null>(null);
  // «Comparar todos los muros»: el grupo de opciones que se está viendo, y el principal al que se le agrega un adicional.
  const [grupoAbierto, setGrupoAbierto] = useState<string | null>(null);
  const [adicionalDe, setAdicionalDe] = useState<PlanoRenglon | null>(null);
  // Números generadores: la vista con todos los conceptos de la hoja (de ahí se imprime).
  const [imprimiendoGeneradores, setImprimiendoGeneradores] = useState(false);
  // Panel de análisis de precio (el MISMO de la cotización): el renglón de planos es un quote_concept de
  // la cotización interna del plano. Clic en el renglón = resaltarlo + abrir el panel a la derecha.
  const [panelRenglon, setPanelRenglon] = useState<PlanoRenglon | null>(null);
  const [panelConcepto, setPanelConcepto] = useState<QuoteConcept | null>(null);
  const [panelW, setPanelW] = useState<number>(() => {
    try {
      const n = Number(localStorage.getItem('supernova:planos-panel-w'));
      return Number.isFinite(n) && n >= 340 ? n : 440;
    } catch {
      return 440;
    }
  });
  const cambiarPanelW = useCallback((n: number) => {
    setPanelW(n);
    try {
      localStorage.setItem('supernova:planos-panel-w', String(Math.round(n)));
    } catch {
      /* sin storage: solo esta sesión */
    }
  }, []);
  const cargarPanelConcepto = useCallback(async (r: PlanoRenglon) => {
    try {
      const { items } = await getQuoteConcepts(r.quoteId, { limit: 5000 });
      const c = items.find((x) => x.id === r.id) ?? null;
      setPanelConcepto((prev) => (c ? c : prev && prev.id === r.id ? prev : null));
    } catch {
      /* el panel muestra lo último que tenía */
    }
  }, []);
  const [cambiandoConcepto, setCambiandoConcepto] = useState(false);
  // Renglón cuyo Procedimiento constructivo se está viendo (drawer). Solo staff.
  const [procedimientoRenglon, setProcedimientoRenglon] = useState<{ id: string; descripcion: string } | null>(null);
  const isStaff = useIsEcosoftStaff();

  // --- Rol del archivo abierto dentro de la HP por-proyecto (mide edificio / mide sitio / solo
  // referencia). Decide si el archivo aporta cantidad o solo enriquece (anti-doble-conteo). Se
  // carga con el plano; el usuario lo confirma/cambia en el selector de la barra. ---
  const [rolArchivo, setRolArchivo] = useState<PlanoRolArchivo | null>(null);
  const [rolSugerido, setRolSugerido] = useState<{ rol: PlanoRolArchivo; razon: string } | null>(null);
  const [guardandoRol, setGuardandoRol] = useState(false);

  // --- Vista de la HP: por-archivo (default, editable) vs POR-PROYECTO (todos los planos, lectura). ---
  // La hoja es UNA por proyecto: se abre en "Toda la obra"; "Este plano" es el filtro.
  const [vistaProyecto, setVistaProyecto] = usePreferencia('toda-la-obra', true, esBool);
  const [renglonesProyecto, setRenglonesProyecto] = useState<PlanoRenglon[]>([]);
  const [cargandoProyecto, setCargandoProyecto] = useState(false);

  // --- Instalaciones NO dibujadas (frente #5·B): la IA las propone desde los recintos del plano y
  //     el usuario elige cuáles agregar a la hoja como partida "supuesto". Bajo demanda (botón). ---
  const [instalPropuestas, setInstalPropuestas] = useState<PlanoInstalacionSupuesta[] | null>(null);
  const [instalCargando, setInstalCargando] = useState(false);
  const [instalAgregando, setInstalAgregando] = useState(false);
  // Modo «eligiendo» de instalaciones: la activa, las marcadas «No va» y las que ya se agregaron
  // (índice → clave del concepto o su descripción, para verificar contra la hoja real).
  const [instalActivo, setInstalActivo] = useState<number | null>(null);
  const [instalDescartados, setInstalDescartados] = useState<Set<string>>(new Set());
  const [instalResueltos, setInstalResueltos] = useState<Map<string, Set<string>>>(new Map());

  // --- Refs del visor ---
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgElRef = useRef<SVGSVGElement | null>(null);
  const vbRef = useRef<ViewBox | null>(null);
  const vbInicialRef = useRef<ViewBox | null>(null);
  const dragRef = useRef<{ x: number; y: number; vb: ViewBox } | null>(null);
  const movedRef = useRef(0);
  const [grabbing, setGrabbing] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Atenuar en el plano los elementos ya asignados (para ver qué falta). Default: sí.
  const [atenuarAsignados, setAtenuarAsignados] = useState(true);
  // Mostrar las cotas y los nombres de local del plano (la capa `<g class="anotaciones">` que el SVG
  // trae oculta por default, para no ensuciar la selección/medición). Default: no.
  const [mostrarAnotaciones, setMostrarAnotaciones] = useState(false);
  // Hover del plano (solo escritorio): mapa selección→renglón + tooltip por DOM directo.
  const renglonMapRef = useRef<Map<string, PlanoRenglon>>(new Map());
  const tipRef = useRef<HTMLDivElement>(null);
  const hoverKeyRef = useRef<string | null>(null);

  // ---------------------------------------------------------------------------
  // Carga inicial de proyectos
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let vivo = true;
    setLoadingProjects(true);
    listProjects()
      .then((ps) => {
        if (!vivo) return;
        setProjects(ps);
      })
      .catch(() => {
        if (vivo) setProjects([]);
      })
      .finally(() => {
        if (vivo) setLoadingProjects(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Archivos del proyecto elegido (reusa el detalle de proyecto; filtra CAD).
  useEffect(() => {
    setIfcAbierto(null);
    setStatus('idle');
    if (!projectId) {
      setFiles([]);
      setFileId(null);
      return;
    }
    let vivo = true;
    let reintento: ReturnType<typeof setTimeout> | null = null;
    // La pantalla vuelve a "sin plano" mientras recarga: el plano de la ruta se debe volver a abrir.
    abiertoDeRutaRef.current = null;
    setLoadingFiles(true);
    setErrorArchivos(false);
    setFiles([]);
    setFileId(null);
    // Si la petición falla (p. ej. el servidor se está reiniciando) NO se concluye que el plano no
    // existe: se reintenta sola unas veces y, si sigue fallando, se ofrece "Reintentar".
    const cargar = (intento: number): Promise<void> =>
      getProject(projectId)
      .then((res) => {
        if (!vivo) return;
        setNombreProyectoRuta(res.project.name);
        const planos = res.files.filter(esArchivoDePlano);
        setFiles(planos);
        if (modoProyecto) {
          // El plano viene de la ruta (la galería o la tira eligieron cuál).
          if (archivoDeRuta && planos.some((f) => f.id === archivoDeRuta)) setFileId(archivoDeRuta);
        } else if (abrirTrasRecargarRef.current && planos.some((f) => f.id === abrirTrasRecargarRef.current)) {
          setFileId(abrirTrasRecargarRef.current);
          abrirTrasRecargarRef.current = null;
        } else if (planos.length === 1) {
          // Un solo plano: no hay nada que elegir.
          setFileId(planos[0]!.id);
        }
        setLoadingFiles(false);
      })
      .catch(() => {
        if (!vivo) return;
        if (intento < REINTENTOS_ARCHIVOS) {
          reintento = setTimeout(() => void cargar(intento + 1), 1000 * 2 ** intento);
          return;
        }
        setErrorArchivos(true);
        setLoadingFiles(false);
      });
    void cargar(0);
    return () => {
      vivo = false;
      if (reintento) clearTimeout(reintento);
    };
    // archivoDeRuta NO va aquí: cambiar de plano con la tira no recarga los archivos (efecto de abajo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, archivosVersion]);

  // Cambiar de plano con la tira (misma pantalla, otra :fileId en la ruta).
  useEffect(() => {
    if (!modoProyecto || loadingFiles) return;
    const siguiente = archivoDeRuta && files.some((f) => f.id === archivoDeRuta) ? archivoDeRuta : null;
    setFileId((prev) => (prev === siguiente ? prev : siguiente));
  }, [modoProyecto, archivoDeRuta, files, loadingFiles]);

  // Catálogos de /planos (selección propia) + lista de disponibles para el selector.
  // Se ordenan por prioridad (índice 0 = mayor). Si aún no hay selección propia,
  // arranca con ECOSTOS (el catálogo público) como default — NO se hereda de la
  // cotización. El default no se persiste hasta que el usuario guarde en el selector.
  useEffect(() => {
    if (!projectId) {
      setCatalogos([]);
      setCatalogSel(null);
      setCatalogosDisponibles([]);
      return;
    }
    let vivo = true;
    Promise.all([listCatalogs(), getPlanoCatalogs(projectId)])
      .then(([disponibles, cats]) => {
        if (!vivo) return;
        setCatalogosDisponibles(disponibles);
        const ordenados = [...cats].sort((a, b) => a.priority - b.priority);
        if (ordenados.length > 0) {
          setCatalogos(ordenados);
          setCatalogSel(ordenados[0]!.catalogId);
          return;
        }
        // Sin selección propia todavía: proponer ECOSTOS por default (sin persistir).
        const eco = disponibles.find((c) => c.kind === 'ecostos');
        if (eco) {
          const synthetic: ProjectCatalog = {
            catalogId: eco.id,
            catalog: eco,
            priority: 0,
            addedAt: new Date().toISOString(),
          };
          setCatalogos([synthetic]);
          setCatalogSel(eco.id);
        } else {
          setCatalogos([]);
          setCatalogSel(null);
        }
      })
      .catch(() => {
        if (vivo) {
          setCatalogos([]);
          setCatalogSel(null);
          setCatalogosDisponibles([]);
        }
      });
    return () => {
      vivo = false;
    };
  }, [projectId]);

  // Guarda la selección de catálogos de /planos (reemplazo total, en orden de prioridad).
  // Guardado INLINE (como cotización): el ProjectCatalogsPicker del panel izquierdo guarda en vivo al
  // agregar/quitar/reordenar, sin diálogo ni toast (silencioso).
  const guardarCatalogosInline = useCallback(
    (ids: string[]) => {
      if (!projectId) return;
      void setPlanoCatalogs(projectId, ids)
        .then((cats) => {
          const ord = [...cats].sort((a, b) => a.priority - b.priority);
          setCatalogos(ord);
          setCatalogSel((prev) => (prev && ord.some((c) => c.catalogId === prev) ? prev : ord[0]?.catalogId ?? null));
        })
        .catch(() => { /* silencioso: el picker ya refleja el cambio local */ });
    },
    [projectId],
  );

  const guardarCatalogos = useCallback(
    async (ids: string[]) => {
      if (!projectId) return;
      setGuardandoCatalogos(true);
      try {
        const cats = await setPlanoCatalogs(projectId, ids);
        const ordenados = [...cats].sort((a, b) => a.priority - b.priority);
        setCatalogos(ordenados);
        setCatalogSel((prev) =>
          prev && ordenados.some((c) => c.catalogId === prev) ? prev : ordenados[0]?.catalogId ?? null,
        );
        setCatalogosDialogAbierto(false);
        dispatchToast(
          <Toast>
            <ToastTitle>Catálogos actualizados.</ToastTitle>
          </Toast>,
          { intent: 'success', timeout: 2500 },
        );
      } catch {
        dispatchToast(
          <Toast>
            <ToastTitle>No se pudieron guardar los catálogos.</ToastTitle>
          </Toast>,
          { intent: 'error', timeout: 4000 },
        );
      } finally {
        setGuardandoCatalogos(false);
      }
    },
    [projectId, dispatchToast],
  );

  // Renglones ya armados del plano (HP). Se recarga tras asignar/borrar.
  const refrescarRenglones = useCallback(async () => {
    if (!projectId || !fileId) return;
    try {
      const rs = await listarPlanoRenglones(projectId, fileId);
      setRenglones(rs.renglones);
      setAvisoIa(rs.avisoIa ?? null);
    } catch {
      /* silencioso: la HP simplemente no se actualiza */
    }
    // La hoja de toda la obra también cambió (es la misma hoja, vista entera).
    void refrescarProyectoRef.current?.();
  }, [projectId, fileId]);

  // Las opciones de los principales (idea 7): se releen cuando cambia la hoja (un renglón que se va se lleva
  // las suyas). Es una lectura barata, sin IA.
  const refrescarOpciones = useCallback(async () => {
    if (!projectId || !fileId) return;
    try {
      setOpcionesPlano(await listarOpcionesPlano(projectId, fileId));
    } catch {
      /* silencioso: sin pastillas de opciones */
    }
  }, [projectId, fileId]);
  const huellaHoja = renglones.map((r) => r.id).join(',');
  useEffect(() => {
    void refrescarOpciones();
  }, [refrescarOpciones, huellaHoja]);
  const opcionesPorPrincipal = useMemo(() => {
    const m = new Map<string, PlanoOpcion[]>();
    for (const o of opcionesPlano) (m.get(o.principalId) ?? m.set(o.principalId, []).get(o.principalId)!).push(o);
    return m;
  }, [opcionesPlano]);

  // Mientras haya un sintético generándose (queued/running), refrescar cada 4 s para
  // reflejar cuando termine (el worker actualiza la fila; no hay WS para esto todavía).
  useEffect(() => {
    const generando = renglones.some(
      (r) => r.apuIaStatus === 'queued' || r.apuIaStatus === 'running',
    );
    if (!generando) return;
    const t = setTimeout(() => void refrescarRenglones(), 4000);
    return () => clearTimeout(t);
  }, [renglones, refrescarRenglones]);

  // Renglones marcados para IA que aún no tienen precio (para el botón de lote de la HP).
  // El lote en curso del plano abierto: lo que espera turno y lo que se está generando.
  const loteIaEnCurso = useMemo(
    () => ({
      enCola: renglones.filter((r) => r.apuIaStatus === 'queued').length,
      generandose: renglones.filter((r) => r.apuIaStatus === 'running').length,
    }),
    [renglones],
  );
  const [deteniendoLote, setDeteniendoLote] = useState(false);
  // Marcados en la hoja que se ve (el plano abierto o toda la obra), con o sin precio.
  const marcadosIaHoja = useMemo(
    () =>
      (vistaProyecto ? renglonesProyecto : renglones).filter(
        (r) => r.apuIaRequested && r.apuIaStatus !== 'queued' && r.apuIaStatus !== 'running',
      ),
    [vistaProyecto, renglonesProyecto, renglones],
  );
  const marcadosParaIa = marcadosIaHoja.length;
  const reemplazanPrecio = useMemo(() => marcadosIaHoja.filter((r) => r.precioUnitario != null).length, [marcadosIaHoja]);
  // Planos a los que pertenece lo que se ve: el lote y «Marcar todos» van por plano.
  const planosDeLaHoja = useCallback(
    (lista: PlanoRenglon[]) => [...new Set(lista.map((r) => r.sourceFileId ?? fileId).filter((x): x is string => !!x))],
    [fileId],
  );

  // Carga el ROL del archivo abierto (mide edificio / mide sitio / solo referencia) + la sugerencia
  // del sistema, para el selector de la barra. Se relee cada vez que se abre un plano distinto.
  // Es también el ÚNICO lugar donde se aplican las correcciones guardadas del ⋯ (migr 166), para DWG
  // e IFC por igual: los dos tipos de plano pasan por aquí al quedar 'ready'.
  useEffect(() => {
    if (!projectId || !fileId || status !== 'ready') {
      setRolArchivo(null);
      setRolSugerido(null);
      return;
    }
    let cancelado = false;
    void (async () => {
      try {
        const p = await getPlano(projectId, fileId);
        if (cancelado) return;
        setRolArchivo(p.rol);
        setRolSugerido({ rol: p.rolSugerido, razon: p.rolSugeridoRazon });
        // Lo que la persona tocó mientras cargaba gana sobre lo guardado.
        setDecisionOverride((o) => ({ ...(p.decisionesCapa ?? {}), ...o }));
      } catch {
        /* silencioso: el selector simplemente no aparece */
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [projectId, fileId, status]);

  // Cambia el rol del archivo abierto en la HP por-proyecto y lo persiste. Refresca la vista de
  // proyecto (si está abierta) porque cambiar el rol cambia qué renglones aportan al total.
  const cambiarRolArchivo = useCallback(
    async (rol: PlanoRolArchivo) => {
      if (!projectId || !fileId) return;
      const previo = rolArchivo;
      setRolArchivo(rol); // optimista
      setGuardandoRol(true);
      try {
        const { rol: guardado, notasPegadas } = await setPlanoRol(projectId, fileId, rol);
        setRolArchivo(guardado);
        // Entrar o salir de «Solo referencia» pega o despega sus notas en los conceptos: se refresca la hoja.
        void refrescarRenglones();
        if (vistaProyecto) void refrescarProyectoRef.current?.();
        void galeria.recargar();
        // Lo que cambia al marcarla "Solo referencia" no se ve en el plano: se dice.
        if (guardado === 'referencia_spec' && previo !== 'referencia_spec') {
          const n = notasPegadas?.conceptos ?? 0;
          const ejemplos = (notasPegadas?.ejemplos ?? []).map((d) => `«${d.length > 40 ? `${d.slice(0, 40).trimEnd()}…` : d}»`);
          dispatchToast(
            <Toast>
              <ToastTitle>Esta lámina ya no suma cantidades</ToastTitle>
              <ToastBody>
                {n === 0
                  ? 'Sus notas se van a pegar solas a los conceptos que describa, cuando los agregues.'
                  : `Sus notas se van a pegar solas a los conceptos que describen. Hoy: ${n} ${n === 1 ? 'concepto' : 'conceptos'} (${ejemplos.join(', ')}${n > ejemplos.length ? '…' : ''}).`}
              </ToastBody>
            </Toast>,
            { intent: 'info', timeout: 7000 },
          );
        }
      } catch (e) {
        setRolArchivo(previo);
        dispatchToast(
          <Toast><ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo cambiar el rol del plano.'}</ToastTitle></Toast>,
          { intent: 'error', timeout: 3500 },
        );
      } finally {
        setGuardandoRol(false);
      }
    },
    [projectId, fileId, rolArchivo, vistaProyecto, dispatchToast, galeria, refrescarRenglones],
  );
  // HP POR-PROYECTO: la unión de los renglones de TODOS los planos del proyecto (lectura).
  const refrescarProyecto = useCallback(async () => {
    if (!projectId) return;
    setCargandoProyecto(true);
    try {
      setRenglonesProyecto(await getPlanosHp(projectId));
    } catch {
      /* silencioso: la HP del proyecto simplemente no se actualiza */
    } finally {
      setCargandoProyecto(false);
    }
  }, [projectId]);
  // Ref para poder llamar refrescarProyecto desde cambiarRolArchivo sin ciclo de dependencias.
  const refrescarProyectoRef = useRef<(() => Promise<void>) | null>(null);
  refrescarProyectoRef.current = refrescarProyecto;

  // Al prender la vista de proyecto (o cambiar de proyecto con ella abierta), cargar la HP unificada.
  useEffect(() => {
    if (vistaProyecto) void refrescarProyecto();
  }, [vistaProyecto, refrescarProyecto]);

  // Quitar UNA nota de lámina pegada a un renglón (panel del concepto o marca «REF.» de la hoja). La
  // quitada ya no vuelve a pegarse sola en ese renglón. $0, sin IA.
  const quitarNota = useCallback(
    async (r: PlanoRenglon, nota: EspecificacionVinculada) => {
      const archivo = r.sourceFileId ?? fileId;
      if (!projectId || !archivo) return;
      try {
        await quitarNotaDeRenglon(projectId, archivo, r.id, { detalleId: nota.detalleId, texto: nota.texto });
        await refrescarRenglones();
        if (vistaProyecto) await refrescarProyecto();
        dispatchToast(
          <Toast><ToastTitle>Nota quitada. Ya no se pega sola a este concepto.</ToastTitle></Toast>,
          { intent: 'success', timeout: 3000 },
        );
      } catch (e) {
        dispatchToast(
          <Toast><ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo quitar la nota.'}</ToastTitle></Toast>,
          { intent: 'error', timeout: 3500 },
        );
      }
    },
    [projectId, fileId, refrescarRenglones, refrescarProyecto, vistaProyecto, dispatchToast],
  );

  // Frente #5·B: pedir a la IA las instalaciones que el plano NO dibuja (las infiere de los recintos).
  // Bajo demanda para no frenar la carga (igual criterio que complementarios). Cada una se resuelve
  // después en la lista ancha (modo «instalaciones»), igual que un adicional.
  // `actuales` = «Proponer más»: se le dice a la IA lo que ya está y las nuevas se agregan al final.
  const proponerInstalaciones = useCallback(async (
    actuales?: PlanoInstalacionSupuesta[],
  ): Promise<{ lista: PlanoInstalacionSupuesta[]; primeraNueva: number | null } | null> => {
    if (!projectId || !fileId) return null;
    setInstalCargando(true);
    try {
      const previas = actuales ?? [];
      const nuevas = await proponerInstalacionesSupuesto(
        projectId, fileId, previas.length > 0 ? previas.map(nombreInstal) : undefined,
      );
      const r = agregarNecesidadesNuevas(previas, nuevas, nombreInstal);
      setInstalPropuestas(r.lista);
      if (previas.length === 0) {
        setInstalActivo(null);
        setInstalDescartados(new Set());
        setInstalResueltos(new Map());
      }
      if (r.agregadas === 0) {
        dispatchToast(
          <Toast>
            <ToastTitle>
              {previas.length > 0
                ? 'No encontramos nada más que suela hacer falta.'
                : 'El plano no trae nombres de recinto ni superficies medidas para proponer lo que falta.'}
            </ToastTitle>
          </Toast>,
          { intent: 'warning', timeout: 4000 },
        );
      }
      return { lista: r.lista, primeraNueva: r.primeraNueva };
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo proponer lo que falta.'}</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
      return null;
    } finally {
      setInstalCargando(false);
    }
  }, [projectId, fileId, dispatchToast]);

  // Otro plano → las instalaciones propuestas eran del anterior.
  useEffect(() => {
    setInstalPropuestas(null);
    setInstalActivo(null);
    setInstalDescartados(new Set());
    setInstalResueltos(new Map());
  }, [fileId]);
  // Sin instalaciones que resolver (cambió el plano a medio pedido, o se limpiaron), no hay lista ancha.
  useEffect(() => {
    if (modoLista === 'instalaciones' && !(instalPropuestas && instalPropuestas.length > 0)) setModoLista(null);
  }, [modoLista, instalPropuestas]);

  // Generar con IA TODOS los marcados de golpe (tras confirmar en el diálogo).
  const generarLote = useCallback(async () => {
    if (!projectId || !fileId) return;
    setGenerandoLote(true);
    try {
      let encolados = 0;
      for (const fid of planosDeLaHoja(marcadosIaHoja)) {
        encolados += (await cotizarIaLote(projectId, fid)).encolados;
      }
      setConfirmarLoteOpen(false);
      await refrescarRenglones();
      dispatchToast(
        <Toast><ToastTitle>{`Generando ${encolados} concepto${encolados === 1 ? '' : 's'} con IA…`}</ToastTitle></Toast>,
        { intent: 'success', timeout: 2500 },
      );
    } catch (e) {
      const msg =
        e instanceof ApiClientError && e.status === 429
          ? 'No te alcanzan los créditos para generar todos.'
          : e instanceof ApiClientError && e.status === 400
            ? 'No hay renglones marcados para IA.'
            : 'No se pudo generar el lote.';
      dispatchToast(<Toast><ToastTitle>{msg}</ToastTitle></Toast>, { intent: 'warning', timeout: 4000 });
    } finally {
      setGenerandoLote(false);
    }
  }, [projectId, fileId, refrescarRenglones, dispatchToast, planosDeLaHoja, marcadosIaHoja]);

  // «Marcar todos con IA» / «Quitar» (como la cotización): el toggle en todos los renglones sin
  // precio del plano abierto. Solo marca; «Generar con IA» es el que genera y cobra.
  const [marcandoTodos, setMarcandoTodos] = useState(false);
  const marcarTodosIa = useCallback(
    async (requested: boolean) => {
      if (!projectId || !fileId) return;
      setMarcandoTodos(true);
      try {
        let cambiados = 0;
        for (const fid of planosDeLaHoja(vistaProyecto ? renglonesProyecto : renglones)) {
          cambiados += (await iaToggleTodos(projectId, fid, requested)).cambiados;
        }
        await refrescarRenglones();
        if (cambiados === 0) {
          dispatchToast(
            <Toast><ToastTitle>{requested ? 'Ya estaban todos marcados para IA.' : 'No había ninguno marcado para IA.'}</ToastTitle></Toast>,
            { intent: 'info', timeout: 2500 },
          );
        }
      } catch {
        dispatchToast(
          <Toast><ToastTitle>No se pudieron cambiar los interruptores. Intenta de nuevo.</ToastTitle></Toast>,
          { intent: 'error', timeout: 3000 },
        );
      } finally {
        setMarcandoTodos(false);
      }
    },
    [projectId, fileId, refrescarRenglones, dispatchToast, planosDeLaHoja, vistaProyecto, renglonesProyecto, renglones],
  );

  // ---------------------------------------------------------------------------
  // Aplicar el viewBox al SVG (zoom/pan)
  // ---------------------------------------------------------------------------
  const applyViewBox = useCallback(() => {
    const svg = svgElRef.current;
    const vb = vbRef.current;
    if (svg && vb) svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
  }, []);

  // Repinta el resaltado de la selección sobre el SVG inyectado.
  const repaint = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Conjunto de selecciones ya asignadas (para atenuarlas), desde los renglones vivos.
    const asignadas = new Set<string>();
    if (atenuarAsignados) {
      for (const r of renglones) {
        for (const s of seleccionesDe(r)) asignadas.add(`${s.tipo}:${s.clave}`);
      }
    }
    c.querySelectorAll('g.ins').forEach((el) => {
      const id = el.getAttribute('data-id');
      const bloque = el.getAttribute('data-bloque');
      el.classList.toggle('sel', !!id && idsSel.has(id));
      el.classList.toggle('asignado', !!bloque && asignadas.has(`bloque:${bloque}`));
    });
    c.querySelectorAll('g.capa').forEach((el) => {
      const capa = el.getAttribute('data-capa');
      el.classList.toggle('sel', !!capa && capasSel.has(capa));
      el.classList.toggle('asignado', !!capa && asignadas.has(`capa:${capa}`));
    });
  }, [idsSel, capasSel, renglones, atenuarAsignados]);

  useEffect(() => {
    if (svgReady) repaint();
  }, [svgReady, repaint]);

  // Prender/apagar la capa de anotaciones (cotas + nombres de local). El SVG la trae oculta por un
  // <style> interno (`.anotaciones{display:none}`); acá se pone el `display` INLINE en el grupo, que
  // gana sobre ese stylesheet. Es solo VISTA: el grupo lleva pointer-events:none, no toca la selección.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !svgReady) return;
    c.querySelectorAll<SVGGElement>('g.anotaciones').forEach((g) => {
      g.style.display = mostrarAnotaciones ? 'block' : 'none';
    });
  }, [svgReady, mostrarAnotaciones]);

  // Mapa selección→renglón para el hover (se lee por ref en el handler, sin re-render).
  useEffect(() => {
    const m = new Map<string, PlanoRenglon>();
    for (const r of renglones) {
      for (const s of seleccionesDe(r)) m.set(`${s.tipo}:${s.clave}`, r);
    }
    renglonMapRef.current = m;
  }, [renglones]);

  // Al quedar listo el plano, traer sus renglones ya armados (si los hubiera).
  useEffect(() => {
    if (status === 'ready') void refrescarRenglones();
  }, [status, refrescarRenglones]);

  // Buscador del catálogo: debounce del texto (mismo patrón que CatalogDetailPage).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Búsqueda por TEXTO (override manual). Solo corre cuando el usuario teclea:
  // con la caja vacía NO se vuelca el catálogo entero (ese era el bug: "Acarreo
  // de tierra" salía primero para cualquier selección). Vacía → manda la
  // sugerencia por IA de la selección (o su vacío).
  useEffect(() => {
    const ids = catalogosPrioridad;
    if (ids.length === 0 || !debouncedQ) {
      setResultados([]);
      setResultadosRecortados(false);
      setBuscando(false);
      return;
    }
    let vivo = true;
    setBuscando(true);
    setResultadosRecortados(false);
    // Texto sobre TODOS los catálogos del proyecto por prioridad (gratis). Cada resultado
    // conserva su catalogId; se dedup por id y se preserva el orden de prioridad.
    // Se muestran TODAS las coincidencias, y solo las que tienen todas las palabras: cada palabra
    // que se agrega afina («muro hueco» → los muros de block hueco), nunca abre la búsqueda.
    void Promise.all(
      ids.map((cid) =>
        listCatalogResources(cid, { q: debouncedQ, limit: TOPE_RESULTADOS_POR_CATALOGO, tipos: [7], todasLasPalabras: true })
          .then((res) => {
            if (vivo && res.total > res.resources.length) setResultadosRecortados(true);
            return res.resources.map((r) => ({ ...r, __catalogId: cid }));
          })
          .catch(() => [] as (CatalogResource & { __catalogId: string })[]),
      ),
    )
      .then((listas) => {
        if (!vivo) return;
        const vistos = new Set<string>();
        const merged: ConceptoItem[] = [];
        for (const lista of listas) {
          for (const r of lista) {
            if (vistos.has(r.id)) continue;
            vistos.add(r.id);
            merged.push({
              id: r.id, catalogId: r.__catalogId, clave: r.clave, descripcion: r.descripcion,
              unidad: r.unidad, precio: r.precio, familia: r.familia,
            });
          }
        }
        setResultados(merged);
      })
      .finally(() => {
        if (vivo) setBuscando(false);
      });
    return () => {
      vivo = false;
    };
  }, [catalogosPrioridad, debouncedQ]);

  // ---------------------------------------------------------------------------
  // Abrir plano: generar → poll → inyectar SVG
  // ---------------------------------------------------------------------------
  const limpiarPoll = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => () => limpiarPoll(), []);

  async function inyectarSvg(url: string) {
    const svgText = await fetchPlanoSvg(url);
    const c = canvasRef.current;
    if (!c) return;
    c.innerHTML = svgText;
    const svg = c.querySelector('svg');
    if (!svg) {
      setError('El plano no se pudo mostrar (respuesta sin SVG).');
      setStatus('failed');
      return;
    }
    svgElRef.current = svg as SVGSVGElement;
    // viewBox inicial: el que trae el SVG, o uno derivado de width/height.
    const vbAttr = svg.getAttribute('viewBox');
    let vb: ViewBox | null = null;
    if (vbAttr) {
      const [x, y, w, h] = vbAttr.split(/[\s,]+/).map(Number);
      if ([x, y, w, h].every((n) => Number.isFinite(n)) && w! > 0 && h! > 0) {
        vb = { x: x!, y: y!, w: w!, h: h! };
      }
    }
    if (!vb) {
      const w = Number(svg.getAttribute('width')) || 1000;
      const h = Number(svg.getAttribute('height')) || 1000;
      vb = { x: 0, y: 0, w, h };
    }
    vbRef.current = { ...vb };
    vbInicialRef.current = { ...vb };
    applyViewBox();
    setSvgReady(true);
  }

  const abrirPlano = useCallback(async () => {
    if (!projectId || !fileId) return;
    limpiarPoll();
    setError(null);
    setInventario(null);
    setOrigenPlano('cad');
    setCapasClasificadas(null);
    setSvgReady(false);
    setIdsSel(new Set());
    setCapasSel(new Set());
    setInstExcluidas(new Set());
    setInstEnfoque(null);
    setVisMode('todo');
    setRenglones([]);
    setConceptoElegido(null);
    setSugerencias([]);
    setSugerenciaNota(null);
    setSugerenciasParaKey(null);
    setInterpretacionPorKey(new Map());
    setQ('');
    setTargetKey(null);
    setHpAbierto(false);
    setMostrarAnotaciones(false);
    setUltimoAgregado(null);
    svgElRef.current = null;
    if (canvasRef.current) canvasRef.current.innerHTML = '';
    setIfcAbierto(null);
    // Cerrar el visor 3D experimental al cambiar de archivo.
    if (ifcExpPollRef.current) clearTimeout(ifcExpPollRef.current);
    ifcExpPollRef.current = null;
    setIfcExpUrl(null);
    setIfcExpStatus('idle');
    setIfcExpError(null);

    // IFC (BIM): no se convierte a SVG, se ve en 3D directo desde el archivo. Montamos el visor
    // 3D y salimos del pipeline de plano 2D. Aseguramos la fila project_planos (padre de la HP),
    // que en IFC no existe porque no hay pipeline que la cree.
    const archivo = files.find((f) => f.id === fileId) ?? null;
    if (esIfc(archivo)) {
      // Montamos el visor 3D ya (el modelo carga en paralelo), pero NO marcamos 'ready'
      // hasta que la fila `project_planos` exista. Los efectos que dependen de `status==='ready'`
      // —cargar el ROL del archivo, los renglones y la altura— leen esa fila con getPlano. Si
      // marcáramos 'ready' ANTES de terminar `ensure-ifc`, el SELECT de getPlano corre en paralelo
      // al INSERT y a veces gana el SELECT → 404 → el selector de rol ("Este plano") no aparecía
      // hasta reabrir el modelo. Esperar el INSERT vuelve el arranque determinista.
      setIfcAbierto(fileId);
      setStatus('pending');
      try {
        await ensurePlanoIfc(projectId, fileId);
      } catch (e) {
        setError(e instanceof ApiClientError ? e.message : 'No se pudo preparar el modelo IFC.');
      }
      setStatus('ready');
      return;
    }

    // PDF: no tiene geometría (ni SVG ni 3D). Se lee por VISIÓN y cuantifica ESTIMADO; el visor no
    // muestra dibujo, solo el inventario estimado + el aviso. Mismo patrón "abrir si ya está / si no,
    // generar", pero por la cola plano-pdf y SIN inyectar SVG.
    if (esPdf(archivo)) {
      setStatus('pending');
      try {
        const ex = await getPlano(projectId, fileId);
        if (ex.status === 'ready') {
          setInventario(ex.inventario);
          setCapasClasificadas(ex.capasClasificadas);
          setOrigenPlano(ex.origen);
          setStatus('ready');
          return;
        }
      } catch {
        // aún no existe / no accesible por este flujo → seguimos a generar
      }
      try {
        await generarPlanoPdf(projectId, fileId);
      } catch (e) {
        setError(
          e instanceof ApiClientError ? e.message : 'No se pudo iniciar la lectura del plano en PDF.',
        );
        setStatus('failed');
        return;
      }
      const pollPdf = async () => {
        if (!projectId || !fileId) return;
        try {
          const res = await getPlano(projectId, fileId);
          if (res.status === 'failed') {
            setError(res.error ?? 'El plano en PDF no se pudo leer.');
            setStatus('failed');
            return;
          }
          if (res.status === 'ready') {
            setInventario(res.inventario);
            setCapasClasificadas(res.capasClasificadas);
            setOrigenPlano(res.origen);
            setStatus('ready');
            return;
          }
          pollRef.current = setTimeout(() => void pollPdf(), POLL_MS);
        } catch (e) {
          setError(
            e instanceof ApiClientError ? e.message : 'No se pudo consultar el estado del plano.',
          );
          setStatus('failed');
        }
      };
      pollRef.current = setTimeout(() => void pollPdf(), POLL_MS);
      return;
    }

    setStatus('pending');

    // Si el plano YA está listo, ábrelo directo sin pasar por /generar. Necesario para el
    // proyecto de EJEMPLO (es de solo lectura y de otro tenant: /generar no aplica ahí), y de
    // paso evita reprocesar cualquier plano ya generado.
    try {
      const ex = await getPlano(projectId, fileId);
      if (ex.status === 'ready') {
        setInventario(ex.inventario);
        setOrigenPlano(ex.origen);
        setCapasClasificadas(ex.capasClasificadas);
        // Con el visor CAD nuevo (mlightcad) el plano se dibuja desde el DXF en su propio componente,
        // no inyectamos el SVG (evita bajar y montar el SVG que quedaría oculto).
        if (!PLANO_VIEWER_MLIGHTCAD) {
          await inyectarSvg(ex.svgUrl ?? planoSvgUrl(projectId, fileId));
        }
        setStatus('ready');
        return;
      }
    } catch {
      // aún no existe / no accesible por este flujo → seguimos al pipeline de generar
    }

    try {
      await generarPlano(projectId, fileId);
    } catch (e) {
      const msg =
        e instanceof ApiClientError ? e.message : 'No se pudo iniciar el procesamiento del plano.';
      setError(msg);
      setStatus('failed');
      return;
    }

    const poll = async () => {
      if (!projectId || !fileId) return;
      try {
        const res = await getPlano(projectId, fileId);
        if (res.status === 'failed') {
          setError(res.error ?? 'El plano no se pudo procesar.');
          setStatus('failed');
          return;
        }
        if (res.status === 'ready') {
          setInventario(res.inventario);
          setOrigenPlano(res.origen);
          setCapasClasificadas(res.capasClasificadas);
          if (!PLANO_VIEWER_MLIGHTCAD) {
            const url = res.svgUrl ?? planoSvgUrl(projectId, fileId);
            await inyectarSvg(url);
          }
          setStatus('ready');
          return;
        }
        // pending → seguir esperando
        pollRef.current = setTimeout(() => void poll(), POLL_MS);
      } catch (e) {
        const msg =
          e instanceof ApiClientError ? e.message : 'No se pudo consultar el estado del plano.';
        setError(msg);
        setStatus('failed');
      }
    };
    pollRef.current = setTimeout(() => void poll(), POLL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, fileId, files]);

  // Entrando por el proyecto, el plano se abre SOLO (IFC → modelo 3D, DWG → dibujo): no hay botón
  // "Abrir". Una vez por plano elegido; al cambiar de pestaña en la tira se abre el nuevo.
  const abiertoDeRutaRef = useRef<string | null>(null);
  // Tipos (capas) de ESTE plano que ya tienen concepto PRINCIPAL en la hoja: palomita verde en la
  // lista, avance "N de M" y "Siguiente que falta".
  const capasConPrincipal = useMemo(
    () =>
      new Set(
        renglones
          .filter((r) => r.rol === 'principal')
          .flatMap((r) => seleccionesDe(r).filter((s) => s.tipo === 'capa').map((s) => s.clave)),
      ),
    [renglones],
  );

  // Último inventario (el visor 3D lo entrega justo antes de su primera vista).
  const inventarioRef = useRef<PlanoInventario | null>(null);
  inventarioRef.current = inventario;
  useEffect(() => {
    if (!modoProyecto || !projectId || !fileId || loadingFiles) return;
    if (!files.some((f) => f.id === fileId)) return;
    if (abiertoDeRutaRef.current === fileId) return;
    abiertoDeRutaRef.current = fileId;
    void abrirPlano();
  }, [modoProyecto, projectId, fileId, files, loadingFiles, abrirPlano]);

  // IFC: la primera vista del modelo sirve de miniatura para la galería. Solo si todavía no tiene
  // (no se re-sube en cada apertura). Best-effort: nunca bloquea ni avisa error.
  const onPrimeraVistaIfc = useCallback(
    (imagen: Blob) => {
      if (!modoProyecto || !projectId || !fileId) return;
      // La cuenta de la ficha sale de lo que midió ESTE visor (lo que el usuario ve), no del servidor.
      const datos = datosDelPlano('ifc', inventarioRef.current);
      const item = galeria.planos?.find((p) => p.fileId === fileId);
      if (item?.miniatura === 'ready' && item.datos === datos) return;
      subirMiniaturaPlano(projectId, fileId, imagen, datos)
        .then(() => galeria.recargar())
        .catch(() => { /* la ficha queda con el ícono */ });
    },
    [modoProyecto, projectId, fileId, galeria],
  );

  // Vista previa de los IFC que nadie abrió todavía: la galería los pasa de a uno por el visor fuera de
  // pantalla. Solo los que se abren sin pedir confirmación por tamaño (el tope de «IFC grande» de
  // Calibración) y solo quien puede escribir en el proyecto. Cada uno se intenta una vez por visita.
  const [miniaturasIfcIntentadas, setMiniaturasIfcIntentadas] = useState<ReadonlySet<string>>(new Set());
  const miniaturaIfcEnCurso = useMemo(() => {
    // Hace falta poder escribir (la foto se sube). En el proyecto de ejemplo el servidor la rechaza y
    // la ficha queda como está: se intenta una vez y se sigue.
    if (!modoProyecto || archivoDeRuta || !puedeEditar || ifcAvisoTamanoMb == null) return null;
    const p = (galeria.planos ?? []).find((x) => {
      if (x.tipo !== 'ifc' || x.miniatura === 'ready' || miniaturasIfcIntentadas.has(x.fileId)) return false;
      const mb = (files.find((f) => f.id === x.fileId)?.sizeBytes ?? Infinity) / (1024 * 1024);
      return mb < ifcAvisoTamanoMb;
    });
    return p?.fileId ?? null;
  }, [modoProyecto, archivoDeRuta, puedeEditar, ifcAvisoTamanoMb, galeria.planos, miniaturasIfcIntentadas, files]);

  // Subir planos desde la tira ("+") o desde la galería de esta pantalla.
  const subirPlanos = useCallback(
    async (lista: File[]) => {
      if (!projectId) return;
      setSubiendoPlano(true);
      setProgresoSubida(0);
      try {
        await uploadFiles(projectId, lista, setProgresoSubida);
        setArchivosVersion((v) => v + 1);
        await galeria.recargar();
      } catch (e) {
        dispatchToast(
          <Toast>
            <ToastTitle>No se pudo subir el plano</ToastTitle>
            <ToastBody>{e instanceof ApiClientError ? e.message : 'Inténtalo de nuevo.'}</ToastBody>
          </Toast>,
          { intent: 'error', timeout: 6000 },
        );
      } finally {
        setSubiendoPlano(false);
        setProgresoSubida(0);
      }
    },
    [projectId, galeria, dispatchToast],
  );

  // ---------------------------------------------------------------------------
  // Visor 3D EXPERIMENTAL (DWG/DXF → IFC): SOLO visualización, NO toca la HP
  // ---------------------------------------------------------------------------
  // Convierte el plano CAD abierto a un modelo IFC (en el backend) y lo muestra en el visor 3D que
  // ya existe. El resultado es experimental (la lectura 3D no funciona con todos los dibujos) y no
  // afecta las cantidades ni la Hoja de Presupuesto.
  const verEn3dExperimental = useCallback(async () => {
    if (!projectId || !fileId) return;
    if (ifcExpPollRef.current) clearTimeout(ifcExpPollRef.current);
    setIfcExpError(null);
    setIfcExpStatus('generando');
    try {
      await generarPlanoIfc(projectId, fileId);
    } catch (e) {
      setIfcExpStatus('error');
      setIfcExpError(
        e instanceof ApiClientError ? e.message : 'No se pudo iniciar la vista 3D del plano.',
      );
      return;
    }
    const poll = async () => {
      if (!projectId || !fileId) return;
      try {
        const res = await getPlanoIfcStatus(projectId, fileId);
        if (res.status === 'failed') {
          setIfcExpStatus('error');
          setIfcExpError(res.error ?? 'No se pudo generar la vista 3D de este plano.');
          return;
        }
        if (res.status === 'ready' && res.ifcUrl) {
          setIfcExpUrl(planoIfcUrl(projectId, fileId));
          setIfcExpStatus('listo');
          return;
        }
        // pending / none → seguir esperando
        ifcExpPollRef.current = setTimeout(() => void poll(), POLL_MS);
      } catch (e) {
        setIfcExpStatus('error');
        setIfcExpError(
          e instanceof ApiClientError ? e.message : 'No se pudo consultar el estado de la vista 3D.',
        );
      }
    };
    ifcExpPollRef.current = setTimeout(() => void poll(), POLL_MS);
  }, [projectId, fileId]);

  const cerrarVisor3dExp = useCallback(() => {
    if (ifcExpPollRef.current) clearTimeout(ifcExpPollRef.current);
    ifcExpPollRef.current = null;
    setIfcExpUrl(null);
    setIfcExpStatus('idle');
    setIfcExpError(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Selección
  // ---------------------------------------------------------------------------
  // Ids de un bloque, del INVENTARIO (no del DOM del SVG): el visor mlightcad (WebGL) no dibuja los
  // <g class="ins">, así que leerlos del SVG devolvía vacío y NO dejaba seleccionar bloques. El
  // inventario trae `ids` por bloque y lo comparten ambos visores.
  const idsDeBloque = useCallback((nombre: string): string[] => {
    return inventario?.bloques.find((b) => b.nombre === nombre)?.ids ?? [];
  }, [inventario]);

  // Nombre de bloque de una instancia (por su id), también desde el inventario (no del SVG).
  const nombreDeId = useCallback((id: string): string => {
    const enInv = inventario?.bloques.find((b) => b.ids.includes(id))?.nombre;
    if (enInv) return enInv;
    const c = canvasRef.current;
    if (!c) return '';
    const el = c.querySelector(`g.ins[data-id="${CSS.escape(id)}"]`);
    return el?.getAttribute('data-bloque') ?? '';
  }, [inventario]);

  // Regla: SE TRABAJA UN TIPO A LA VEZ. La selección contiene o bien instancias
  // de UN solo bloque, o bien UNA sola capa. Mezclar tipos distintos (muros +
  // puertas) no tiene sentido para sugerir/asignar un concepto único, así que al
  // agregar algo de otro tipo se REEMPLAZA la selección previa. `tipoActivoRef`
  // guarda el tipo vigente para decidir cuándo limpiar.
  const tipoActivoRef = useRef<string | null>(null);
  useEffect(() => {
    if (idsSel.size > 0) {
      const first = idsSel.values().next().value as string;
      tipoActivoRef.current = `bloque:${nombreDeId(first)}`;
    } else if (capasSel.size > 0) {
      tipoActivoRef.current = `capa:${capasSel.values().next().value as string}`;
    } else {
      tipoActivoRef.current = null;
    }
  }, [idsSel, capasSel, nombreDeId]);

  // Se sigue trabajando un tipo a la vez (al elegir otro se reemplaza la selección), pero SIN avisar
  // con un toast: el cambio ya se ve en el panel y en el plano, el aviso solo estorbaba.
  const avisarCambioDeTipo = useCallback(() => {}, []);

  const toggleBloque = useCallback(
    (nombre: string) => {
      if (esSoloDibujo(nombre, true)) {
        avisarSoloDibujo(nombre);
        return;
      }
      const ids = idsDeBloque(nombre);
      if (ids.length === 0) return;
      const key = `bloque:${nombre}`;
      if (tipoActivoRef.current !== null && tipoActivoRef.current !== key) {
        // Cambio de tipo → limpiar lo anterior y seleccionar este bloque entero.
        setCapasSel(new Set());
        setInstExcluidas(new Set());
        setInstEnfoque(null);
        setIdsSel(new Set(ids));
        avisarCambioDeTipo();
        return;
      }
      setIdsSel((prev) => {
        const next = new Set(prev);
        const todas = ids.every((id) => next.has(id));
        for (const id of ids) {
          if (todas) next.delete(id);
          else next.add(id);
        }
        return next;
      });
    },
    [idsDeBloque, avisarCambioDeTipo, esSoloDibujo, avisarSoloDibujo],
  );

  const toggleId = useCallback(
    (id: string) => {
      const bloqueDeId = nombreDeId(id);
      if (bloqueDeId && esSoloDibujo(bloqueDeId, true)) {
        avisarSoloDibujo(bloqueDeId);
        return;
      }
      const key = `bloque:${bloqueDeId}`;
      if (tipoActivoRef.current !== null && tipoActivoRef.current !== key) {
        setCapasSel(new Set());
        setInstExcluidas(new Set());
        setInstEnfoque(null);
        setIdsSel(new Set([id]));
        avisarCambioDeTipo();
        return;
      }
      setIdsSel((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [nombreDeId, avisarCambioDeTipo, esSoloDibujo, avisarSoloDibujo],
  );

  // DWG con varias plantas: la capa SOLO dentro de una vista (p. ej. A-MUROS · Planta baja). null =
  // la capa completa (las plantas que suman).
  const [vistaSel, setVistaSel] = useState<{ capa: string; vista: string } | null>(null);
  useEffect(() => {
    setVistaSel(null);
  }, [fileId]);

  const toggleCapa = useCallback(
    (capa: string) => {
      if (esSoloDibujo(capa, false)) {
        avisarSoloDibujo(capa);
        return;
      }
      setVistaSel(null);
      const key = `capa:${capa}`;
      if (tipoActivoRef.current !== null && tipoActivoRef.current !== key) {
        setIdsSel(new Set());
        setInstExcluidas(new Set());
        setInstEnfoque(null);
        // Los modos de visibilidad basados en la selección dejan de tener sentido al cambiar de tipo.
        setVisMode((v) => (v === 'ocultar-sel' || v === 'aislar-sel' ? 'todo' : v));
        setCapasSel(new Set([capa]));
        avisarCambioDeTipo();
        return;
      }
      setCapasSel((prev) => {
        const next = new Set(prev);
        if (next.has(capa)) next.delete(capa);
        else next.add(capa);
        // Al soltar el tipo activo, sus exclusiones/enfoque por instancia dejan de tener sentido.
        if (next.size === 0) {
          setInstExcluidas(new Set());
          setInstEnfoque(null);
          setVisMode((v) => (v === 'ocultar-sel' || v === 'aislar-sel' ? 'todo' : v));
        }
        return next;
      });
    },
    [avisarCambioDeTipo, esSoloDibujo, avisarSoloDibujo],
  );

  const limpiarSeleccion = useCallback(() => {
    setVistaSel(null);
    setIdsSel(new Set());
    setCapasSel(new Set());
    setInstExcluidas(new Set());
    setInstEnfoque(null);
    // Los modos de visibilidad atados a la selección se sueltan; "ocultar asignadas" (global) se respeta.
    setVisMode((v) => (v === 'ocultar-sel' || v === 'aislar-sel' ? 'todo' : v));
  }, []);

  // El visor CAD nuevo (mlightcad) emite una PlanoSeleccion completa (misma que el SVG); la traducimos
  // al estado de selección de siempre (idsSel/capasSel) para que TODO el flujo posterior —cantidades,
  // asignar concepto, HP— corra idéntico. Semántica de "reemplazar" (clic = nueva selección). Los
  // `ids` son handles DXF, ya alineados con el inventario, así `asignables` reconstruye la selección
  // final (con el nombre limpio del bloque y las medidas de la capa) sin más traducción.
  const onSeleccionMlightcad = useCallback((sel: PlanoSeleccion) => {
    setInstExcluidas(new Set());
    setInstEnfoque(null);
    setVisMode((v) => (v === 'ocultar-sel' || v === 'aislar-sel' ? 'todo' : v));
    if (sel.tipo === 'bloque') {
      setVistaSel(null);
      setCapasSel(new Set());
      setIdsSel(new Set(sel.ids ?? []));
    } else {
      setIdsSel(new Set());
      setCapasSel(new Set([sel.clave]));
      // Clic sobre la capa DENTRO de una planta → esa planta (el visor dice en cuál cayó el clic).
      setVistaSel(sel.vista ? { capa: sel.clave, vista: sel.vista } : null);
    }
  }, []);

  // Elegir la capa solo dentro de una vista (fila de la planta en la lista de la izquierda).
  const elegirVistaDeCapa = useCallback((capa: string, vista: string) => {
    setIdsSel(new Set());
    setInstExcluidas(new Set());
    setInstEnfoque(null);
    setCapasSel(new Set([capa]));
    setVistaSel({ capa, vista });
  }, []);

  // IFC: sacar/reingresar una instancia (por expressID) del tipo activo. Por default TODAS las
  // instancias del tipo están incluidas; este set guarda las EXCLUIDAS, así "sacar una de 13" deja el
  // set en {esa} y la cantidad baja a 12. Reingresarla la quita del set.
  const toggleInstancia = useCallback((expressID: number) => {
    setInstExcluidas((prev) => {
      const next = new Set(prev);
      if (next.has(expressID)) next.delete(expressID);
      else next.add(expressID);
      return next;
    });
  }, []);

  // IFC: ENFOCAR una pieza desde el panel (resaltarla en el 3D para ubicarla) SIN sacarla del conteo.
  // Clic en la misma pieza la desenfoca. Sacar/reingresar es una acción aparte (botón explícito).
  const enfocarInstancia = useCallback((expressID: number) => {
    setInstEnfoque((prev) => (prev === expressID ? null : expressID));
  }, []);

  // Clic sobre el plano: bloque → todas las iguales; Ctrl/⌘ → esa sola;
  // si no hay bloque, capa (secundario, hay que pegarle a la línea).
  const onCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (movedRef.current > 5) return; // fue un arrastre, no un clic
      const target = e.target as Element | null;
      if (!target) return;
      const ins = target.closest('g.ins');
      if (ins) {
        const id = ins.getAttribute('data-id');
        const bloque = ins.getAttribute('data-bloque');
        if ((e.ctrlKey || e.metaKey) && id) {
          toggleId(id);
        } else if (bloque) {
          toggleBloque(bloque);
        } else if (id) {
          toggleId(id);
        }
        return;
      }
      const capaEl = target.closest('g.capa');
      const capa = capaEl?.getAttribute('data-capa');
      if (capa) toggleCapa(capa);
    },
    [toggleBloque, toggleId, toggleCapa],
  );

  // ---------------------------------------------------------------------------
  // Zoom (rueda) y pan (arrastrar) sobre el viewBox
  // ---------------------------------------------------------------------------
  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      const svg = svgElRef.current;
      const vb = vbRef.current;
      const c = canvasRef.current;
      if (!svg || !vb || !c) return;
      e.preventDefault();
      const rect = c.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
      const nw = vb.w * factor;
      const nh = vb.h * factor;
      // Mantener el punto bajo el cursor fijo.
      const nx = vb.x + (vb.w - nw) * px;
      const ny = vb.y + (vb.h - nh) * py;
      vbRef.current = { x: nx, y: ny, w: nw, h: nh };
      applyViewBox();
    },
    [applyViewBox],
  );

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const vb = vbRef.current;
    if (!vb) return;
    dragRef.current = { x: e.clientX, y: e.clientY, vb: { ...vb } };
    movedRef.current = 0;
    setGrabbing(true);
  }, []);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const drag = dragRef.current;
      const c = canvasRef.current;
      if (!c) return;
      if (drag) {
        const rect = c.getBoundingClientRect();
        const dxPx = e.clientX - drag.x;
        const dyPx = e.clientY - drag.y;
        movedRef.current = Math.max(movedRef.current, Math.abs(dxPx) + Math.abs(dyPx));
        const dx = (dxPx / rect.width) * drag.vb.w;
        const dy = (dyPx / rect.height) * drag.vb.h;
        vbRef.current = { x: drag.vb.x - dx, y: drag.vb.y - dy, w: drag.vb.w, h: drag.vb.h };
        applyViewBox();
        if (tipRef.current) tipRef.current.style.display = 'none';
        return;
      }
      // Hover SOLO escritorio (en iPad/touch no hay hover; ahí el concepto asignado se ve en el
      // inspector al seleccionar). Muestra el concepto solo si el elemento TIENE uno asociado.
      const tip = tipRef.current;
      if (!tip) return;
      const g = (e.target as Element | null)?.closest?.('g.ins, g.capa') as Element | null;
      const esCapa = g?.classList.contains('capa');
      const clave = g ? (esCapa ? g.getAttribute('data-capa') : g.getAttribute('data-bloque')) : null;
      const r = clave ? renglonMapRef.current.get(`${esCapa ? 'capa' : 'bloque'}:${clave}`) : undefined;
      if (!r) {
        tip.style.display = 'none';
        hoverKeyRef.current = null;
        return;
      }
      const key = `${esCapa ? 'capa' : 'bloque'}:${clave}`;
      if (hoverKeyRef.current !== key) {
        hoverKeyRef.current = key;
        tip.textContent = `${clave}: ${r.conceptoDescripcion} · ${r.cantidad} ${r.unidad ?? ''}`.trim();
      }
      tip.style.display = 'block';
    },
    [applyViewBox],
  );

  const endDrag = useCallback(() => {
    dragRef.current = null;
    setGrabbing(false);
  }, []);

  const zoomBtn = useCallback(
    (factor: number) => {
      const vb = vbRef.current;
      if (!vb) return;
      const nw = vb.w * factor;
      const nh = vb.h * factor;
      vbRef.current = {
        x: vb.x + (vb.w - nw) / 2,
        y: vb.y + (vb.h - nh) / 2,
        w: nw,
        h: nh,
      };
      applyViewBox();
    },
    [applyViewBox],
  );

  const resetView = useCallback(() => {
    if (!vbInicialRef.current) return;
    vbRef.current = { ...vbInicialRef.current };
    applyViewBox();
  }, [applyViewBox]);

  // ---------------------------------------------------------------------------
  // Factor de escala vigente (unidad elegida por el usuario, con el $INSUNITS como default).
  // Único punto: lo usan el panel de propiedades y el cálculo de cantidades.
  // La regla vive en `lib/contextoDwg.ts` (la usa también «Cambió el plano»).
  const factorEscala = useMemo<number | null>(
    () => factorEscalaDwg(inventario, unidadPlanoSel, escalaElegida),
    [unidadPlanoSel, inventario, escalaElegida],
  );
  // DWG con varias plantas: la altura de cada planta y de dónde sale. Nunca un valor típico.
  const alturaPorVista = useMemo(() => alturaPorVistaDwg(inventario, alturaProp), [inventario, alturaProp]);

  // Escala dudosa sin respuesta: no hay medidas que mostrar hasta que el usuario diga la unidad.
  const escalaSinResolver = useMemo(
    () => escalaSinResolverDwg(inventario, unidadPlanoSel, escalaElegida),
    [unidadPlanoSel, inventario, escalaElegida],
  );

  // ¿Las cotas contradicen al $INSUNITS declarado? → chip "revisar unidades" (solo con "según el archivo").
  const escalaRevisar = useMemo<boolean>(
    () => unidadPlanoSel === 'archivo' && factorDeEscala(inventario).revisar,
    [unidadPlanoSel, inventario],
  );

  // Resumen de la selección para el panel de propiedades
  // ---------------------------------------------------------------------------
  const resumenBloques = useMemo(() => {
    if (!inventario) return [];
    return inventario.bloques
      .map((b) => ({
        nombre: b.nombre,
        sel: b.ids.filter((id) => idsSel.has(id)).length,
        total: b.n,
        capas: b.capas,
        // Dimensiones representativas del bloque (bbox mediana), convertidas a metros si hay escala.
        ancho: b.ancho != null && factorEscala != null ? b.ancho * factorEscala : null,
        alto: b.alto != null && factorEscala != null ? b.alto * factorEscala : null,
      }))
      .filter((b) => b.sel > 0);
  }, [inventario, idsSel, factorEscala]);

  const capasSeleccionadas = useMemo(() => Array.from(capasSel), [capasSel]);
  const totalPiezas = idsSel.size;
  // Nada seleccionado aún → el panel derecho muestra una GUÍA de qué hacer, no un formulario vacío.
  const nadaSeleccionado = resumenBloques.length === 0 && capasSeleccionadas.length === 0;

  // ---------------------------------------------------------------------------
  // Asignación: qué selecciones hay activas (bloque o capa) y a cuál se le asigna
  // ---------------------------------------------------------------------------
  // Se arma para la selección del visor (`asignables`) y, con «Armar el presupuesto», para cada tipo que
  // falta sin seleccionarlo: la MISMA medición en los dos caminos.
  const construirAsignables = useCallback(
    (
      capasSel: ReadonlySet<string>,
      idsSel: ReadonlySet<string>,
      instExcluidas: ReadonlySet<number>,
      vistaSel: { capa: string; vista: string } | null,
    ): Asignable[] =>
      asignablesDelPlano({ inventario, factorEscala, capasClasificadas, decisionOverride, alturaPorVista, escalaSinResolver }, capasSel, idsSel, instExcluidas, vistaSel),
    [inventario, factorEscala, capasClasificadas, decisionOverride, alturaPorVista, escalaSinResolver],
  );
  const asignables = useMemo(
    () => construirAsignables(capasSel, idsSel, instExcluidas, vistaSel),
    [construirAsignables, capasSel, idsSel, instExcluidas, vistaSel],
  );

  // Cada pieza del modelo IFC por su expressID (su clase y su caja): para nombrar y medir las puertas y
  // ventanas que llenan los vanos de un muro.
  const piezaDelModelo = useMemo(() => {
    const m = new Map<number, { clase: string | null; dims?: { largo: number; ancho: number; alto: number } }>();
    for (const c of inventario?.capas ?? []) for (const x of c.instancias ?? []) m.set(x.id, { clase: c.categoria ?? null, dims: x.dims });
    return m;
  }, [inventario]);
  const fotoDeElemento = useCallback(
    (
      a: Asignable,
      o: {
        magnitud: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null | undefined;
        unidadConcepto: string | null | undefined;
        altoStr: string;
        espesorStr: string;
        altosVanos: Readonly<Record<string, string>>;
        altoDeDonde?: string | null;
      },
    ): FotoGenerador | null =>
      fotoDeElementoDelPlano({ inventario, nivelDeElemento, etiquetaNivel, factorEscala, capasClasificadas, decisionOverride, piezaDelModelo }, a, o),
    [inventario, nivelDeElemento, etiquetaNivel, factorEscala, capasClasificadas, decisionOverride, piezaDelModelo],
  );
  /**
   * La foto de un renglón que se agrega desde el panel: la medición del elemento con el alto/espesor del
   * panel. Si la cantidad que se manda no es la medida, la persona la escribió (queda «corregida a mano»);
   * si el plano no da la cantidad con esa unidad, es una cantidad escrita a mano.
   */
  const fotoManual = useCallback(
    (
      a: Asignable,
      magnitud: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null | undefined,
      unidad: string | null,
      cantidad: number,
    ): { generador: FotoGenerador; cantidadEscritaAMano?: boolean } => {
      const foto = fotoDeElemento(a, { magnitud, unidadConcepto: unidad, altoStr, espesorStr, altosVanos: altosVanosStr, altoDeDonde: 'El que tenía el panel al agregarlo.' });
      if (!foto) return { generador: fotoAMano(cantidad, unidad) };
      return Math.abs(foto.total - cantidad) < 1e-9 ? { generador: foto } : { generador: foto, cantidadEscritaAMano: true };
    },
    [fotoDeElemento, altoStr, espesorStr, altosVanosStr],
  );

  // La selección a la que se le asigna concepto: la elegida, o la primera activa.
  const target = useMemo(
    () => asignables.find((a) => a.key === targetKey) ?? asignables[0] ?? null,
    [asignables, targetKey],
  );

  // El PRINCIPAL que ya tiene la selección actual en la HP (matcheado o sintético), si existe. Con
  // esto el botón «¿Qué más lleva?» aparece para CUALQUIER principal —recién agregado
  // o ya en la hoja— y no solo justo después de agregar el sintético. Los complementarios se piden
  // por la descripción (la IA solo la necesita), así que sirve igual para catálogo y sintético.
  const principalActual = useMemo(() => {
    if (!target) return null;
    // Un renglón por concepto: el principal puede juntar varios elementos (`ademas`), no solo el primero. Dos
    // piezas del mismo tipo IFC (dos losas) son dos elementos: se comparan por sus piezas (AI-71).
    const tocan = renglones.filter(
      (x) =>
        x.rol === 'principal' &&
        x.seleccion != null &&
        elementosDeSeleccion(x.seleccion).some((e) => seTocan(e, target.seleccion)),
    );
    // Con varios que se tocan, el que tiene exactamente las piezas elegidas; si ninguno, no hay principal.
    const mismas = (a?: readonly string[], b?: readonly string[]) =>
      (a?.length ?? 0) === (b?.length ?? 0) && (a ?? []).every((id) => (b ?? []).includes(id));
    const r =
      tocan.length <= 1
        ? tocan[0]
        : tocan.find((x) =>
            elementosDeSeleccion(x.seleccion).some(
              (e) => seTocan(e, target.seleccion) && mismas(e.ids, target.seleccion.ids),
            ),
          );
    if (!r) return null;
    return { id: r.id, clave: r.resourceClave ?? null, descripcion: r.conceptoDescripcion, unidad: r.unidad, esIa: r.origen === 'apu_ia' };
  }, [renglones, target]);

  // Agregar a la HP un concepto que NO está en el catálogo. A la HP va el NOMBRE del elemento del
  // CAD (sembrado, editable por el usuario en `descSinteticoStr`), NO lo tecleado en el buscador;
  // con la cantidad medida y la selección para preservar el detalle del CAD para el APU. Entra
  // marcado para IA; el lote (o el botón del renglón) lo cotiza con el sintético.
  // Unidad de la medida con la que se llena la cantidad (la misma que dice la etiqueta «Cantidad para
  // la hoja»): bloque = piezas; IFC = su medida principal; DWG medido = metros. null = no se sabe.
  // Escrita como la escriben los catálogos (m2, m3), para que se compare igual al cambiar de concepto.
  const unidadDeLaMedida = useCallback(
    (t: Asignable): string | null => {
      if (t.seleccion.tipo === 'bloque') return 'pza';
      if (t.medidas) {
        const info = inventario?.capas.find((c) => c.capa === t.seleccion.clave);
        const mp = medidaPrincipalIfc(t.medidas, info?.categoria, info?.ifc?.predefinido, info?.ifc?.supertipos);
        return mp ? mp.unidad.replace('²', '2').replace('³', '3') : null;
      }
      return t.medidoEnMetros ? 'm' : null;
    },
    [inventario],
  );

  const agregarSinteticoPorDescripcion = useCallback(async () => {
    if (!projectId || !fileId || !target) return;
    const descripcion = descSinteticoStr.trim();
    if (!descripcion) return;
    const cantidad = Number(cantidadStr.replace(',', '.')) || target.cantidadDefault || 1;
    setAgregandoSintetico(true);
    try {
      await agregarRenglonPorDescripcion(projectId, fileId, {
        descripcion,
        // La unidad de la medida de donde sale la cantidad. Sin ella la IA tenía que adivinar a qué
        // se refería el número (136.28 ¿qué?) y el renglón quedaba sin unidad en la hoja.
        unidad: unidadDeLaMedida(target) ?? undefined,
        cantidad,
        ...fotoManual(target, undefined, unidadDeLaMedida(target) ?? null, cantidad),
        nivel: target.nivel,
        ubicacion: target.ubicacion,
        seleccion: target.seleccion,
      });
      setQ('');
      setConceptoElegido(null);
      setHpAbierto(true);
      await refrescarRenglones();
      setModoLista((m) => (m === 'principal' ? 'adicionales' : m));
      dispatchToast(
        <Toast><ToastTitle>Agregado a la hoja. Cotízalo con IA cuando quieras.</ToastTitle></Toast>,
        { intent: 'success', timeout: 3000 },
      );
    } catch {
      dispatchToast(
        <Toast><ToastTitle>No se pudo agregar el concepto.</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    } finally {
      setAgregandoSintetico(false);
    }
  }, [projectId, fileId, target, descSinteticoStr, cantidadStr, refrescarRenglones, dispatchToast, unidadDeLaMedida, fotoManual]);

  // Prender/apagar el toggle "generar con IA" de un renglón. El botón GLOBAL de la
  // cabecera de la HP genera de una vez todos los que quedan prendidos (no hay generación por
  // renglón: es la decisión de Sebastián). Optimista: refresca al terminar.
  const toggleIaRenglon = useCallback(
    async (renglon: PlanoRenglon, requested: boolean) => {
      const fid = renglon.sourceFileId ?? fileId;
      if (!projectId || !fid) return;
      const renglonId = renglon.id;
      const pintar = (v: boolean) => (prev: PlanoRenglon[]) =>
        prev.map((x) => (x.id === renglonId ? { ...x, apuIaRequested: v } : x));
      // Pinta el cambio de inmediato (el switch responde sin esperar al backend), en las dos vistas.
      setRenglones(pintar(requested));
      setRenglonesProyecto(pintar(requested));
      try {
        await iaToggleRenglon(projectId, fid, renglonId, requested);
      } catch {
        // Si falla, revierte y avisa.
        setRenglones(pintar(!requested));
        setRenglonesProyecto(pintar(!requested));
        dispatchToast(
          <Toast><ToastTitle>No se pudo cambiar el interruptor.</ToastTitle></Toast>,
          { intent: 'error', timeout: 3000 },
        );
      }
    },
    [projectId, fileId, dispatchToast],
  );

  // «Recomendados» de «Cambiar concepto»: lo mismo que al elegir el concepto la primera vez (lo que
  // el renglón mide en el plano, con lo que el modelo dice del tipo). Sin selección (renglón que
  // entró por descripción) se recomienda por su descripción, como un adicional.
  const recomendarParaRenglon = useCallback(
    async (r: PlanoRenglon | null) => {
      const fid = r?.sourceFileId ?? fileId;
      if (!r || !projectId || !fid) return { sugerencias: [] };
      const sel = r.seleccion;
      const capaInfo =
        sel?.tipo === 'capa' && fid === fileId ? inventario?.capas.find((c) => c.capa === sel.clave) : undefined;
      const ifc = capaInfo ? describirTipoIfcParaSugerencia(capaInfo) : null;
      return await sugerirConceptos(projectId, fid, {
        catalogId: catalogSel ?? catalogosPrioridad[0] ?? '',
        seleccion: sel
          ? { tipo: sel.tipo, clave: sel.clave, ...(ifc ? { ifc } : {}) }
          : { tipo: 'capa', clave: r.conceptoDescripcion },
      });
    },
    [projectId, fileId, inventario, catalogSel, catalogosPrioridad],
  );

  // Corregir a mano la cantidad de un renglón. Vacío o igual = no cambia nada.
  const guardarCantidadEditada = useCallback(async (r: PlanoRenglon) => {
    const fid = r.sourceFileId ?? fileId;
    setEditandoCantId(null);
    const n = Number(editCantStr.replace(/\s/g, '').replace(',', '.'));
    if (!projectId || !fid || !Number.isFinite(n) || n <= 0 || n === r.cantidad) return;
    try {
      await corregirCantidadRenglon(projectId, fid, r.id, n);
      await refrescarRenglones();
    } catch {
      dispatchToast(
        <Toast><ToastTitle>No se pudo guardar la cantidad. Intenta de nuevo.</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    }
  }, [editCantStr, projectId, fileId, refrescarRenglones, dispatchToast]);

  // Cambiar el concepto de un renglón por otro del catálogo (misma cantidad, partida y selección).
  const confirmarCambioDeConcepto = useCallback(async (nuevo: ConceptoParaCambiar) => {
    const r = cambiarConceptoDe;
    const fid = r?.sourceFileId ?? fileId;
    if (!r || !projectId || !fid) return;
    setCambiandoConcepto(true);
    try {
      const res = await cambiarConceptoRenglon(projectId, fid, r.id, { catalogId: nuevo.catalogId, resourceClave: nuevo.clave });
      setCambiarConceptoDe(null);
      await refrescarRenglones();
      if (res.unidadCambio) {
        dispatchToast(
          <Toast>
            <ToastTitle>
              Concepto cambiado. Antes se cobraba en {res.unidadCambio.antes ?? 'otra unidad'} y ahora en{' '}
              {res.unidadCambio.despues ?? 'otra unidad'}: revisa la cantidad, quedó marcada para revisar.
            </ToastTitle>
          </Toast>,
          { intent: 'warning', timeout: 8000 },
        );
      }
    } catch {
      dispatchToast(
        <Toast><ToastTitle>No se pudo cambiar el concepto. Intenta de nuevo.</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    } finally {
      setCambiandoConcepto(false);
    }
  }, [cambiarConceptoDe, projectId, fileId, refrescarRenglones, dispatchToast]);

  // Guardar la descripción editada de un renglón sintético ya en la HP (antes de generarlo).
  const guardarDescripcionEditada = useCallback(async () => {
    if (!projectId || !fileId || !editandoDescId) return;
    const descripcion = editDescStr.trim();
    if (!descripcion) { setEditandoDescId(null); return; }
    // El renglón puede ser de otro plano (vista de toda la obra): se edita en SU plano.
    const fid = [...renglones, ...renglonesProyecto].find((x) => x.id === editandoDescId)?.sourceFileId ?? fileId;
    try {
      await editarDescripcionRenglon(projectId, fid, editandoDescId, { descripcion });
      setEditandoDescId(null);
      await refrescarRenglones();
    } catch {
      dispatchToast(
        <Toast><ToastTitle>No se pudo guardar la descripción.</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    }
  }, [projectId, fileId, editandoDescId, editDescStr, refrescarRenglones, dispatchToast, renglones, renglonesProyecto]);

  // Reset del selector de unidad al cambiar de plano (cada DWG trae su propia escala).
  useEffect(() => {
    setUnidadPlanoSel('archivo');
  }, [fileId]);

  // Pre-llenar el espesor con el detectado del muro de doble trazo al cambiar de selección
  // (el usuario lo puede sobrescribir). Solo si la capa trae un espesor sugerido.
  useEffect(() => {
    if (target?.espesorSugerido != null) setEspesorStr(String(target.espesorSugerido));
    // DWG: la altura de ESA planta (de la fachada) manda sobre la propuesta general del plano.
    if (target?.vista?.altoM != null && target.esMuroDobleTrazo) setAltoStr(String(target.vista.altoM));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.key, target?.vista?.altoM]);

  // Pre-llenar el ALTO con la altura que la IA leyó del plano (atributos/cotas), para USARLA en vez de
  // dejar el campo vacío (Sebastián: "la altura está en la elevación, ¿por qué no la usas?"). Solo si
  // aún no hay un valor escrito, para no pisar lo que el usuario capturó. Leer la altura de la GEOMETRÍA
  // de la lámina de elevación es una mejora aparte (pendiente); esto usa lo que ya se cosechó.
  useEffect(() => {
    if (alturaProp?.alturaM != null) setAltoStr((prev) => (prev.trim() ? prev : String(alturaProp.alturaM)));
  }, [alturaProp]);

  // Sembrar la descripción del sintético (lo que va a la HP si el concepto no está en catálogo; editable).
  // Si ya se pidió «Recomendados», con lo que la IA entendió; si no, con el nombre del elemento y lo que el
  // plano mide (sin consultar a la IA). Al cambiar de selección se vuelve a sembrar; si llega la
  // interpretación después, solo reemplaza la semilla si el usuario todavía no escribió nada.
  const semillaDesc = useMemo(() => {
    if (!target) return '';
    const capaIfc = target.medidas ? inventario?.capas.find((c) => c.capa === target.seleccion.clave) : undefined;
    const nombre = capaIfc ? nombreLegibleTipoIfc(capaIfc) : target.seleccion.clave;
    return semillaDescripcionSintetico(target, nombre, interpretacionPorKey.get(target.key) ?? null);
  }, [target, inventario, interpretacionPorKey]);
  const semillaKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = target?.key ?? null;
    if (semillaKeyRef.current !== key) {
      semillaKeyRef.current = key;
      descEditadaRef.current = false;
      setDescSinteticoStr(semillaDesc);
    } else if (!descEditadaRef.current) {
      setDescSinteticoStr(semillaDesc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.key, semillaDesc]);

  // Fase 2 · A) Proponer la ALTURA una vez por plano (la IA la lee de sus atributos).
  // Se resetea la altura y el cache de magnitud al cambiar de plano.
  useEffect(() => {
    setAlturaProp(null);
    setAlturaPropError(null);
    setMagnitudCache(new Map());
    setMagnitudErrorPorId(new Map());
    setAltoStr('');
    setEspesorStr('');
    if (!projectId || !fileId || status !== 'ready') return;
    // Un IFC ya trae sus medidas del modelo: no hay alto que proponer (y cada propuesta es IA pagada).
    if (esIfc(files.find((f) => f.id === fileId) ?? null)) return;
    let alive = true;
    void alturaPropuesta(projectId, fileId)
      .then((a) => {
        if (!alive) return;
        setAlturaProp(a);
        if (a?.alturaM != null) setAltoStr(String(a.alturaM));
      })
      .catch((e) => {
        // Sin altura propuesta el usuario la pone a mano; si el servidor dijo por qué, se muestra.
        if (alive && e instanceof ApiClientError) setAlturaPropError(e.message);
      });
    return () => {
      alive = false;
    };
  }, [projectId, fileId, status, escalaElegida?.unidad]);

  // Altos de los vanos guardados en este plano (se capturan una vez por tipo y no se vuelven a pedir).
  useEffect(() => {
    setAltosVanosStr({});
    if (!projectId || !fileId || status !== 'ready') return;
    if (esIfc(files.find((f) => f.id === fileId) ?? null)) return;
    let alive = true;
    void getAltosVanos(projectId, fileId)
      .then((altos) => {
        if (alive) setAltosVanosStr(Object.fromEntries(Object.entries(altos).map(([k, v]) => [k, String(v)])));
      })
      .catch(() => {
        /* best-effort: sin lo guardado, se proponen desde el plano o se escriben */
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, fileId, status]);

  // Proponer desde el plano el alto de los tipos que todavía no tienen uno (sin IA, sin valores típicos).
  useEffect(() => {
    const mv = target?.muroVanos;
    if (!mv) return;
    setAltosVanosStr((prev) => {
      let cambio = false;
      const next = { ...prev };
      for (const t of mv.tipos) {
        if (next[t.clave] != null) continue;
        const p = proponerAltoDeVano(t, inventario?.anotaciones, mv.factor);
        if (p != null) {
          next[t.clave] = String(p);
          cambio = true;
        }
      }
      return cambio ? next : prev;
    });
  }, [target?.muroVanos, inventario]);

  const guardarAltosVanos = useCallback(() => {
    if (!projectId || !fileId) return;
    const altos: Record<string, number> = {};
    for (const [k, v] of Object.entries(altosVanosStr)) {
      const n = Number(v.replace(',', '.'));
      if (v.trim() && Number.isFinite(n) && n > 0) altos[k] = n;
    }
    void setAltosVanos(projectId, fileId, altos).catch(() => {
      /* se reintenta en el siguiente cambio; la cuenta en pantalla no depende de guardarlo */
    });
  }, [projectId, fileId, altosVanosStr]);

  // La cuenta del muro con sus vanos (bruto − Σ ancho × alto). Los tramos con alto propio (cada planta
  // con el suyo) lo usan si TODAS lo tienen; si no, todos usan el alto general, como el área de hoy.
  const cuentaVanos = useMemo(
    () => cuentaDeVanosDe(target?.muroVanos, altoStr, altosVanosStr),
    [target?.muroVanos, altoStr, altosVanosStr],
  );

  // Fase 2 · B) Clasificar la MAGNITUD del concepto elegido (la IA), cacheado por concepto.
  useEffect(() => {
    const c = conceptoElegido;
    if (!c || !projectId || !fileId || !catalogSel) return;
    if (magnitudCache.has(c.id)) return; // ya clasificado
    let alive = true;
    setClasificando(true);
    void clasificarMagnitud(projectId, fileId, catalogSel, c.id)
      .then((m) => {
        if (alive) setMagnitudCache((prev) => new Map(prev).set(c.id, m));
      })
      .catch((e) => {
        if (!alive) return;
        setMagnitudCache((prev) => new Map(prev).set(c.id, null));
        // El texto del servidor para «no existe» es técnico: se deja el mensaje de la pantalla. Los avisos pensados para quien usa
        // (sin saldo, IA lenta) sí se muestran.
        if (e instanceof ApiClientError && e.code !== 'PLANO_NOT_FOUND') setMagnitudErrorPorId((prev) => new Map(prev).set(c.id, e.message));
      })
      .finally(() => {
        if (alive) setClasificando(false);
      });
    return () => {
      alive = false;
    };
  }, [conceptoElegido, projectId, fileId, catalogSel, magnitudCache]);

  // Texto visible del selector de unidad del plano.
  const unidadArchivoNombre = inventario?.unidades?.nombre ?? null;
  const unidadPlanoLabel =
    unidadPlanoSel === 'archivo'
      ? `Según el archivo${unidadArchivoNombre ? ` (${unidadArchivoNombre})` : ''}`
      : unidadPlanoSel === 'm'
        ? 'Metros'
        : unidadPlanoSel === 'cm'
          ? 'Centímetros'
          : 'Milímetros';

  // Al cambiar de selección se olvida el concepto elegido (era de otra selección).
  useEffect(() => {
    setConceptoElegido(null);
    setCambiandoPrincipal(false);
    // Lo que se buscó para el elemento anterior no aplica a este (antes quedaba "muro…" al pasar a la losa).
    setQ('');
  }, [target?.key]);

  // Magnitud del concepto elegido (la decide la IA). undefined = aún sin clasificar / cargando;
  // null = la IA no pudo decidir; objeto = clasificado.
  const magnitudElegida = conceptoElegido ? magnitudCache.get(conceptoElegido.id) : undefined;

  // ¿El área es de PISO/LOSA (horizontal, sale directa del plano) o de MURO (vertical =
  // largo × alto)? Lo decide la IA por `inputExtra`: si NO pide alto, es área horizontal.
  // El piso confiable exige además que la capa tenga área de recintos cerrados medida.
  const esAreaDePiso = esAreaDePisoDeConcepto(magnitudElegida, target?.areaM2);

  // Cantidad calculada según la magnitud del concepto (regla pura y probada en
  // @supernova/shared/cantidadParaConcepto). '' cuando falta un dato → Asignar queda bloqueado.
  const cantidadCalculada = useMemo(() => {
    if (!target) return '';
    const info = target.medidas ? inventario?.capas.find((c) => c.capa === target.seleccion.clave) : undefined;
    return cantidadParaConcepto({
      magnitud: magnitudElegida,
      conceptoElegido: !!conceptoElegido,
      unidadConcepto: conceptoElegido?.unidad,
      cantidadDefault: target.cantidadDefault,
      baseEsConteo: target.seleccion.tipo === 'bloque',
      medidas: target.medidas,
      mandaIfc: info?.medidas?.manda,
      areaM2: target.areaM2,
      sinEscala: target.sinEscala,
      areaMuroPorPlantas: target.areaMuroPorPlantas,
      netoVanosM2: cuentaVanos?.netoM2 ?? null,
      altoStr,
      espesorStr,
    });
  }, [target, magnitudElegida, altoStr, espesorStr, inventario, conceptoElegido?.unidad, !!conceptoElegido, cuentaVanos]);

  // ¿La cantidad del principal descansa en un hueco sin alto (descontado de piso a techo)? → 'revisar'.
  const principalSinAltoDeVano =
    !!target?.muroVanos &&
    cantidadDeMuroSinAltoDeVano({
      magnitud: magnitudElegida,
      areaM2: target.areaM2,
      sinEscala: target.sinEscala,
      tiposVanosSinAlto: cuentaVanos?.tiposSinAlto,
    });

  // El campo Cantidad sigue a la calculada; el usuario la puede sobrescribir a mano (su
  // edición persiste hasta que cambie la selección, el concepto, el alto o el espesor).
  useEffect(() => {
    setCantidadStr(cantidadCalculada);
  }, [cantidadCalculada]);

  // Renglones resaltados = los cuya selección está activa en el plano (bidireccional).
  const renglonesResaltados = useMemo(() => {
    const keys = new Set(asignables.map((a) => a.key));
    const out = new Set<string>();
    for (const r of renglones) {
      if (seleccionesDe(r).some((s) => keys.has(claveDeSeleccion(s)) && asignables.some((a) => claveDeSeleccion(a.seleccion) === claveDeSeleccion(s) && seTocan(s, a.seleccion)))) out.add(r.id);
    }
    return out;
  }, [asignables, renglones]);

  // Subpartida (grupo) de un renglón según el eje elegido. 'familia' sale del propio concepto (la
  // familia del catálogo); uno que no la tiene —el generado con IA— toma su especialidad, que es la
  // misma idea (el oficio, tipo ECOSTOS). Los demás ejes salen de la clasificación por IA
  // (`agrupacion`); si aún no está clasificado → "Sin clasificar".
  const grupoDeRenglon = useCallback(
    (r: PlanoRenglon): { nombre: string; orden: number; clave?: string } => {
      const ag = r.agrupacion;
      switch (tipoAgrupacion) {
        case 'familia': {
          // El servidor nombra «Sin clasificar» la partida vacía (planoRenglonService): eso NO es una
          // familia, y ahí entra la especialidad (mismo criterio que la ruta de agrupar).
          const fam = (r.partida || '').trim();
          return { nombre: fam && fam !== 'Sin clasificar' ? fam : ag?.especialidad || 'Sin clasificar', orden: 0 };
        }
        case 'especialidad':
          return { nombre: ag?.especialidad || 'Sin clasificar', orden: 0 };
        case 'cronologico':
          return { nombre: ag?.faseObra || 'Sin clasificar', orden: ag?.ordenFase ?? 99 };
        case 'sistema':
          return { nombre: ag?.sistema || 'Sin clasificar', orden: 0 };
        case 'material':
          return { nombre: ag?.material || 'N/A', orden: 0 };
        case 'nivel':
          return { nombre: ag?.nivel || 'Sin nivel', orden: 0 };
        case 'ubicacion':
          return { nombre: ag?.ubicacion || 'Sin zona', orden: 0 };
        case 'obra': {
          // Partida de la obra de referencia elegida. Se agrupa por `clave` (dos hojas con el mismo nombre en
          // ramas distintas no se funden); sin obra cargada aún, todo cae en «Sin clasificar».
          if (!obraRef || !ordenObra) return { nombre: 'Sin clasificar', orden: 1e6 + 1, clave: '__sin_clasificar' };
          const x = partidaDeRenglonPorObra(ag, r.conceptoDescripcion, obraRef, ordenObra);
          if (x.estado === 'asignada') return { nombre: x.ruta.join(' › '), orden: x.orden, clave: x.nodoId };
          if (x.estado === 'sin_partida') return { nombre: 'Sin partida', orden: 1e6, clave: '__sin_partida' };
          return { nombre: 'Sin clasificar', orden: 1e6 + 1, clave: '__sin_clasificar' };
        }
        default:
          return { nombre: 'Sin clasificar', orden: 0 };
      }
    },
    [tipoAgrupacion, obraRef, ordenObra],
  );

  // HP agrupada por el eje elegido. Cronológico ordena por la fase (orden); los demás por el ORDEN
  // DE LLEGADA de los renglones (que el backend ya sirve en orden de obra/almacenamiento, mismo que
  // el export/m1) — NO alfabético (el alfabético no tiene sentido en un presupuesto y desalineaba
  // m0 vs m1). `seq` = orden de primera aparición de cada partida.
  // Complementarios que cuelgan de un principal presente (liga explícita, migr 146): van en SU partida
  // según el eje elegido, como cualquier renglón (igual que en lo que se manda a OPUS 27, que agrupa
  // por la familia del propio concepto); el vínculo se ve en la fila («por A-MUROS»). Uno sin principal
  // presente (de antes, o cuyo principal se quitó) lleva la marca «compl.».
  // La hoja es UNA por proyecto: "Toda la obra" (default) muestra los renglones de todos los planos,
  // "Este plano" solo los del abierto. Las dos se dibujan IGUAL (mismas partidas, mismas marcas
  // «por …», mismas acciones); la de toda la obra agrega la columna "Viene de".
  const filasHoja = vistaProyecto ? renglonesProyecto : renglones;
  // «Especificaciones de L lámina(s) en N conceptos» arriba de la hoja (solo si hay alguna pegada).
  const resumenNotasHoja = resumenNotasDeLaHoja(filasHoja);
  const hijosDe = useMemo(() => {
    const ids = new Set(filasHoja.map((r) => r.id));
    const m = new Map<string, PlanoRenglon[]>();
    for (const r of filasHoja) {
      if (r.rol !== 'complemento' || !r.principalId || !ids.has(r.principalId)) continue;
      (m.get(r.principalId) ?? m.set(r.principalId, []).get(r.principalId)!).push(r);
    }
    return m;
  }, [filasHoja]);
  const esHijo = useCallback(
    (r: PlanoRenglon) => r.rol === 'complemento' && !!r.principalId && filasHoja.some((x) => x.id === r.principalId),
    [filasHoja],
  );
  // Inventario de los OTROS planos de la obra que son IFC, para nombrar al principal de un adicional
  // que vino de ahí. En un IFC la capa es el nombre CRUDO del tipo («Leichtbeton 102890359 240»); el
  // nombre legible («Muro 24 cm») sale de su inventario, y con otro plano abierto ese inventario no
  // está en memoria. Se pide UNA vez por archivo y solo si hace falta (adicional cuyo principal es de
  // un IFC distinto al abierto). Un DWG no lo necesita: su capa ya es el nombre que se muestra.
  const [inventariosDeOtros, setInventariosDeOtros] = useState<ReadonlyMap<string, PlanoInventario>>(new Map());
  const inventariosPedidosRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!projectId) return;
    const porId = new Map(filasHoja.map((r) => [r.id, r] as const));
    const faltan = new Set<string>();
    for (const r of filasHoja) {
      if (r.rol !== 'complemento' || !r.principalId) continue;
      const src = porId.get(r.principalId)?.sourceFileId ?? r.sourceFileId;
      if (!src || src === fileId || inventariosPedidosRef.current.has(src)) continue;
      if (!esIfc(files.find((f) => f.id === src) ?? null)) continue;
      faltan.add(src);
    }
    for (const src of faltan) {
      inventariosPedidosRef.current.add(src);
      getPlano(projectId, src)
        .then((res) => {
          const inv = res.inventario;
          if (inv) setInventariosDeOtros((prev) => new Map(prev).set(src, inv));
        })
        .catch(() => {
          inventariosPedidosRef.current.delete(src); // se reintenta en el próximo cambio de la hoja
        });
    }
  }, [projectId, fileId, filasHoja, files]);
  // «por A-MUROS»: el nombre de lo seleccionado en el plano para el principal del adicional (en un IFC,
  // el nombre legible del tipo, el mismo de la lista de la izquierda: `nombreLegibleTipoIfc`, un solo
  // lugar). El inventario es el del plano DEL PRINCIPAL: el abierto o, en toda la obra, el de otro IFC.
  const nombreDelPrincipal = useCallback(
    (r: PlanoRenglon): string => {
      const p = filasHoja.find((x) => x.id === r.principalId);
      const sel = p?.seleccion ?? r.seleccion;
      if (!sel) return 'su concepto';
      const src = p?.sourceFileId ?? r.sourceFileId;
      const inv = !src || src === fileId ? inventario : inventariosDeOtros.get(src);
      const capaIfc = inv?.capas.find((c) => c.capa === sel.clave && c.categoria != null);
      if (capaIfc) return nombreLegibleTipoIfc(capaIfc);
      // Sin el inventario a mano (el modelo aún carga, o es de otro plano) NO se muestra la clave
      // cruda del modelo («Leichtbeton 102890359 240»): se nombra por su concepto.
      const desc = (p?.conceptoDescripcion ?? '').trim();
      return desc ? (desc.length > 40 ? `${desc.slice(0, 40).trimEnd()}…` : desc) : 'su concepto';
    },
    [filasHoja, inventario, inventariosDeOtros, fileId],
  );
  // Saltar a los renglones de un vínculo principal ↔ adicional: se ven y se marcan un momento. Si un
  // filtro los esconde, se quita (el usuario pidió verlos).
  const [destacados, setDestacados] = useState<Set<string>>(() => new Set());
  const irARenglones = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setDestacados(new Set(ids));
    const ir = () => {
      const el = document.querySelector(`[data-renglon-id="${ids[0]}"]`);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return !!el;
    };
    if (!ir()) {
      setFiltroHoja('todos');
      setTextoHoja('');
      setTimeout(ir, 50);
    }
    setTimeout(() => setDestacados(new Set()), 2500);
  }, []);
  // En toda la obra, lo de un plano de "solo referencia" no suma (anti-doble-conteo); en un plano, todo.
  const cuentaAlTotal = useCallback((r: PlanoRenglon) => !vistaProyecto || r.aportaCantidad, [vistaProyecto]);
  const partidas = useMemo(() => {
    const grupos = new Map<string, { items: PlanoRenglon[]; orden: number; seq: number; nombre: string; clave: string }>();
    let seq = 0;
    for (const r of filasHoja) {
      // Un adicional va en SU partida, como cualquier renglón (para eso están las partidas): el vínculo
      // con su principal se ve en la fila («por A-MUROS»), no por la posición.
      const g = grupoDeRenglon(r);
      const claveGrupo = g.clave ?? g.nombre;
      let cur = grupos.get(claveGrupo);
      if (!cur) {
        cur = { items: [], orden: g.orden, seq: seq++, nombre: g.nombre, clave: claveGrupo };
        grupos.set(claveGrupo, cur);
      }
      cur.items.push(r);
    }
    return Array.from(grupos.values())
      .map(({ items, orden, seq, nombre, clave }) => ({
        nombre,
        clave,
        items,
        orden,
        seq,
        total: items.reduce((s, r) => s + (cuentaAlTotal(r) ? r.importe ?? 0 : 0), 0),
      }))
      .sort((a, b) => (a.orden !== b.orden ? a.orden - b.orden : a.seq - b.seq));
  }, [filasHoja, grupoDeRenglon, cuentaAlTotal]);

  const totalHoja = useMemo(
    () => filasHoja.reduce((s, r) => s + (cuentaAlTotal(r) ? r.importe ?? 0 : 0), 0),
    [filasHoja, cuentaAlTotal],
  );

  // Filtros de la hoja: SOLO esconden filas; los totales (de partida y de la hoja) siguen siendo
  // de toda la hoja, y así se dice arriba cuando hay un filtro puesto.
  const [filtroHoja, setFiltroHoja] = useState<'todos' | 'revisar' | 'sin_cantidad' | 'sin_precio'>('todos');
  const [textoHoja, setTextoHoja] = useState('');
  const porRevisar = useCallback(
    (r: PlanoRenglon) => r.revisar || r.apuIaStatus === 'failed' || r.apuIaConfianza === 'red',
    [],
  );
  // Entró sin cantidad (el plano no la daba): cantidad 0, para completarla en la hoja.
  const sinCantidadHoja = useCallback((r: PlanoRenglon) => !(Number(r.cantidad) > 0), []);
  const sinPrecio = useCallback(
    (r: PlanoRenglon) => r.precioUnitario == null && r.apuIaStatus !== 'queued' && r.apuIaStatus !== 'running',
    [],
  );
  const conteoFiltros = useMemo(
    () => ({
      todos: filasHoja.length,
      revisar: filasHoja.filter(porRevisar).length,
      sin_cantidad: filasHoja.filter(sinCantidadHoja).length,
      sin_precio: filasHoja.filter(sinPrecio).length,
    }),
    [filasHoja, porRevisar, sinCantidadHoja, sinPrecio],
  );
  const conPrecioHoja = useMemo(() => filasHoja.filter((r) => r.precioUnitario != null).length, [filasHoja]);
  // «Imprimir análisis» es del plano abierto.
  const analizadosConIa = useMemo(
    () => renglones.filter((r) => r.apuIaStatus === 'done' && r.precioUnitario != null).length,
    [renglones],
  );
  const abrirImpresionAnalisis = useCallback(
    (modo: 'simple' | 'detallado') => {
      if (!projectId || !fileId) return;
      window.open(`/project/${projectId}/planos/${fileId}/analisis/imprimir?mode=${modo}`, '_blank', 'noopener');
    },
    [projectId, fileId],
  );
  const pasaFiltroHoja = useCallback(
    (r: PlanoRenglon) => {
      if (filtroHoja === 'revisar' && !porRevisar(r)) return false;
      if (filtroHoja === 'sin_cantidad' && !sinCantidadHoja(r)) return false;
      if (filtroHoja === 'sin_precio' && !sinPrecio(r)) return false;
      const t = normalizarParaBuscar(textoHoja);
      if (!t) return true;
      const hay = normalizarParaBuscar(`${r.resourceClave ?? ''} ${r.conceptoDescripcion}`);
      return t.split(/\s+/).every((w) => hay.includes(w));
    },
    [filtroHoja, textoHoja, porRevisar, sinPrecio],
  );
  const partidasVisibles = useMemo(() => {
    const base = partidas.map((p) => ({ ...p, visibles: p.items.filter(pasaFiltroHoja) }));
    const plano = (p: (typeof base)[number]) => ({
      ...p,
      etiqueta: p.nombre,
      nivel: 0,
      totalConHijos: p.total,
    });
    if (tipoAgrupacion !== 'obra' || !obraRef || !ordenObra) return base.filter((p) => p.visibles.length > 0).map(plano);

    // Con una obra de referencia la hoja muestra la ESTRUCTURA COMPLETA de la obra (partidas y subpartidas de
    // cualquier nivel, en su orden, con subtotal por nivel): una partida sin conceptos también sale, vacía.
    // Solo con un filtro puesto se esconden las que no tienen nada que mostrar (ni ellas ni sus subpartidas).
    const filtrandoAhora = filtroHoja !== 'todos' || textoHoja.trim() !== '';
    const elegibles = nodosElegibles(obraRef).sort((a, b) => (ordenObra.get(a.id) ?? 0) - (ordenObra.get(b.id) ?? 0));
    const ids = new Set(elegibles.map((n) => n.id));
    const padreDe = new Map(obraRef.nodos.map((n) => [n.id, n.padreId]));
    const porClave = new Map(base.map((p) => [p.clave, p]));
    const ancestros = (id: string): string[] => {
      const out: string[] = [];
      for (let p = padreDe.get(id) ?? null; p; p = padreDe.get(p) ?? null) if (ids.has(p)) out.push(p);
      return out;
    };
    const subtotal = new Map<string, number>();
    const visiblesDeRama = new Map<string, number>();
    for (const n of elegibles) {
      const own = porClave.get(n.id);
      for (const id of [n.id, ...ancestros(n.id)]) {
        subtotal.set(id, (subtotal.get(id) ?? 0) + (own?.total ?? 0));
        visiblesDeRama.set(id, (visiblesDeRama.get(id) ?? 0) + (own?.visibles.length ?? 0));
      }
    }
    const delaObra = elegibles
      .filter((n) => !filtrandoAhora || (visiblesDeRama.get(n.id) ?? 0) > 0)
      .map((n) => {
        const own = porClave.get(n.id);
        return {
          nombre: n.nombre,
          clave: n.id,
          items: own?.items ?? [],
          visibles: own?.visibles ?? [],
          orden: ordenObra.get(n.id) ?? 0,
          seq: 0,
          total: own?.total ?? 0,
          etiqueta: etiquetaDeNodo(n),
          nivel: ancestros(n.id).length,
          totalConHijos: subtotal.get(n.id) ?? 0,
        };
      });
    // Lo que no cayó en ninguna partida de la obra («Sin partida», «Sin clasificar») va al final, solo si hay algo.
    const sueltos = base.filter((p) => !ids.has(p.clave) && p.visibles.length > 0).map(plano);
    return [...delaObra, ...sueltos];
  }, [partidas, pasaFiltroHoja, tipoAgrupacion, obraRef, ordenObra, filtroHoja, textoHoja]);
  const filtrando = filtroHoja !== 'todos' || textoHoja.trim() !== '';
  const visiblesHoja = useMemo(
    () => partidasVisibles.reduce((s, p) => s + p.visibles.length, 0),
    [partidasVisibles],
  );
  // Cuántos planos distintos alimentan la hoja de toda la obra (para el conteo de la barra).
  const planosEnProyecto = useMemo(
    () => new Set(renglonesProyecto.map((r) => r.sourceFileId ?? r.sourceFileName ?? '')).size,
    [renglonesProyecto],
  );
  // ¿Todos los renglones ya tienen su clasificación de IA? (entonces cambiar de eje no consume nada).
  // Se mide por los ejes de IA (especialidad): un renglón IFC ya trae nivel/zona del modelo pero SIN
  // los ejes de IA, así que `agrupacion != null` no bastaría.
  const yaClasificado = renglones.length > 0 && renglones.every((r) => !!r.agrupacion?.especialidad);

  // Clasificar SOLO lo que falta, en cuanto entra a la hoja (una llamada en lote; el servidor revisa
  // saldo, cachea por descripción y reporta a eko27): así cualquier eje está listo para cambiar sin
  // esperar, y el concepto generado con IA tiene partida también en «Familia». Lo que ya se intentó
  // no se reintenta en esta pantalla (si la IA falla, no se insiste en cada refresco).
  const clasificacionIntentada = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!projectId || !fileId || agrupando) return;
    const faltan = renglones.filter(
      (r) => !r.agrupacion?.especialidad && !clasificacionIntentada.current.has(r.id),
    );
    if (faltan.length === 0) return;
    const t = setTimeout(() => {
      for (const r of faltan) clasificacionIntentada.current.add(r.id);
      setAgrupando(true);
      void agruparPlano(projectId, fileId)
        .then((rs) => {
          setRenglones(rs);
          void refrescarProyectoRef.current?.();
        })
        .catch(() => { /* sin clasificación: quedan en «Sin clasificar» como antes */ })
        .finally(() => setAgrupando(false));
    }, 1500);
    return () => clearTimeout(t);
  }, [projectId, fileId, renglones, agrupando]);

  // Cambiar el eje de agrupación. 'familia' y 'nivel' son instantáneos (salen del concepto / del modelo
  // BIM, sin IA). Para los ejes con IA, si algún renglón aún no está clasificado se pide UNA vez al
  // servidor (clasifica en lote, cachea y reporta a eko27); después el cambio de eje es instantáneo.
  const cambiarAgrupacion = useCallback(
    async (tipo: TipoAgrupacion, obraElegida?: ObraReferenciaDTO) => {
      setTipoAgrupacion(tipo);
      const meta = AGRUPACIONES.find((a) => a.valor === tipo);
      if (!meta?.ia || !projectId || !fileId) return; // 'familia'/'nivel' no consumen IA
      if (tipo === 'obra') {
        const obra = obraElegida ?? obraRef;
        if (!obra) return;
        const ord = ordenPreorden(obra);
        const faltan = renglones.filter((r) => partidaDeRenglonPorObra(r.agrupacion, r.conceptoDescripcion, obra, ord).estado === 'sin_clasificar');
        if (faltan.length === 0) return;
        for (const r of faltan) obraIntentada.current.add(`${obra.id}:${r.id}`);
        setAgrupando(true);
        try {
          const resp = await agruparPlanoPorObra(projectId, fileId, obra.id);
          setRenglones(resp.renglones);
          void refrescarProyectoRef.current?.(); // la hoja de «Todos los planos» sale de otra lista
          if (resp.avisoIa) {
            dispatchToast(
              <Toast>
                <ToastTitle>Por ahora no se pudo acomodar la hoja en las partidas de la obra. Intenta de nuevo en unos minutos.</ToastTitle>
              </Toast>,
              { intent: 'warning', timeout: 6000 },
            );
          } else if (resp.invalidos > 0) {
            dispatchToast(
              <Toast>
                <ToastTitle>{`${resp.invalidos} ${resp.invalidos === 1 ? 'concepto no se pudo acomodar' : 'conceptos no se pudieron acomodar'}; quedan sin clasificar.`}</ToastTitle>
              </Toast>,
              { intent: 'info', timeout: 5000 },
            );
          }
        } catch (e) {
          dispatchToast(
            <Toast>
              <ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo acomodar la hoja en las partidas de la obra.'}</ToastTitle>
            </Toast>,
            { intent: 'error', timeout: 4000 },
          );
        } finally {
          setAgrupando(false);
        }
        return;
      }
      if (!renglones.some((r) => !r.agrupacion?.especialidad)) return;
      setAgrupando(true);
      try {
        setRenglones(await agruparPlano(projectId, fileId));
      } catch (e) {
        dispatchToast(
          <Toast>
            <ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo agrupar con IA.'}</ToastTitle>
          </Toast>,
          { intent: 'error', timeout: 4000 },
        );
      } finally {
        setAgrupando(false);
      }
    },
    [projectId, fileId, renglones, dispatchToast, obraRef],
  );

  // «Obra de referencia…» no cambia el eje: abre el selector de obra (y trae la lista). Lo demás cambia directo.
  const alElegirAgrupacion = useCallback(
    async (tipo: TipoAgrupacion) => {
      if (tipo !== 'obra') return cambiarAgrupacion(tipo);
      setObrasDialogAbierto(true);
      setObrasListaError(false);
      try {
        setObrasLista(await listarObrasReferencia());
      } catch {
        setObrasListaError(true);
      }
    },
    [cambiarAgrupacion],
  );

  const elegirObra = useCallback(
    async (obraId: string) => {
      try {
        const obra = await leerObraReferencia(obraId);
        setObraIdPref(obra.id);
        setObraRef(obra);
        setObrasDialogAbierto(false);
        await cambiarAgrupacion('obra', obra);
      } catch (e) {
        dispatchToast(
          <Toast>
            <ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo abrir esa obra.'}</ToastTitle>
          </Toast>,
          { intent: 'error', timeout: 4000 },
        );
      }
    },
    [cambiarAgrupacion, dispatchToast, setObraIdPref],
  );

  // Con el eje en «Obra» y una obra guardada, se carga su árbol (si ya no existe: se limpia y se vuelve a «Familia»).
  useEffect(() => {
    if (tipoAgrupacion !== 'obra') return;
    if (!obraIdPref) {
      setTipoAgrupacion('familia');
      return;
    }
    if (obraRef?.id === obraIdPref) return;
    let vivo = true;
    void leerObraReferencia(obraIdPref)
      .then((o) => {
        if (vivo) setObraRef(o);
      })
      .catch(() => {
        if (!vivo) return;
        setObraIdPref('');
        setTipoAgrupacion('familia');
        dispatchToast(
          <Toast>
            <ToastTitle>Esa obra de referencia ya no está disponible. Se volvió a «Familia».</ToastTitle>
          </Toast>,
          { intent: 'warning', timeout: 5000 },
        );
      });
    return () => {
      vivo = false;
    };
  }, [tipoAgrupacion, obraIdPref, obraRef?.id, setObraIdPref, setTipoAgrupacion, dispatchToast]);

  // Auto: solo si el eje activo ES «Obra», lo que entra a la hoja sin partida vigente se acomoda (una vez por
  // obra+renglón en esta pantalla; si la IA falla no se insiste en cada refresco).
  const obraIntentada = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (tipoAgrupacion !== 'obra' || !obraRef || !ordenObra || !projectId || !fileId || agrupando) return;
    const faltan = renglones.filter(
      (r) =>
        partidaDeRenglonPorObra(r.agrupacion, r.conceptoDescripcion, obraRef, ordenObra).estado === 'sin_clasificar' &&
        !obraIntentada.current.has(`${obraRef.id}:${r.id}`),
    );
    if (faltan.length === 0) return;
    const t = setTimeout(() => void cambiarAgrupacion('obra', obraRef), 1500);
    return () => clearTimeout(t);
  }, [tipoAgrupacion, obraRef, ordenObra, projectId, fileId, renglones, agrupando, cambiarAgrupacion]);

  // Barra de CATÁLOGOS (qué catálogos se usan para buscar/sugerir conceptos). Se muestra en el panel
  // IZQUIERDO, arriba de Capas (Sebastián: los catálogos son la "fuente", van con las capas, no en el
  // panel derecho). El diálogo de selección/prioridad es un modal aparte (se abre con "Elegir").
  // Panel de CATÁLOGOS igual que en cotización (coherencia visual): el ProjectCatalogsPicker INLINE
  // (arrastrar para priorizar, colores por prioridad), no un diálogo. Guarda en vivo (silencioso).
  const panelCatalogos = (
    <aside className={styles.sidePanel}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '10px 12px 6px' }}>
        <span className={styles.fieldLabel}>Catálogos de referencia</span>
        <BtnContraer lado="izq" onClick={() => setCatAbierto(false)} />
      </div>
      {catalogos.length === 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--warn, #d08700)', padding: '0 12px 8px', lineHeight: 1.45 }}>
          Selecciona al menos un catálogo para buscar conceptos.
        </div>
      )}
      <div style={{ padding: '0 12px 12px', minHeight: 0, overflowY: 'auto' }}>
        <ProjectCatalogsPicker
          available={catalogosDisponibles}
          initialSelected={catalogosPrioridad}
          onChange={guardarCatalogosInline}
          disabled={guardandoCatalogos}
        />
      </div>
    </aside>
  );

  // Rail colapsado del panel de catálogos: una insignia (2 letras + color de prioridad) por catálogo en
  // uso, arriba y resaltada; las demás abajo, atenuadas. Clic = reabrir. Igual que cotización.
  const railCatalogos = (
    <aside
      className={styles.sidePanel}
      style={{ alignItems: 'center', padding: '8px 4px', gap: 8 }}
    >
      <button
        type="button"
        title="Mostrar los catálogos de referencia"
        aria-label="Mostrar los catálogos de referencia"
        // En el modo «eligiendo» los catálogos se pliegan solos (para dar ancho a la lista): abrirlos vuelve al dibujo.
        onClick={() => { setCatAbierto(true); setModoLista(null); }}
        style={{
          width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border-subtle)',
          background: 'var(--bg-canvas)', color: 'var(--text-secondary)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <ChevronRight16Regular />
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', alignItems: 'center', overflowY: 'auto', minHeight: 0 }}>
        {(() => {
          const enUso = new Set(catalogosPrioridad);
          const usados = catalogos;
          const otros = catalogosDisponibles.filter((c) => !enUso.has(c.id));
          const idxColor = new Map(catalogosDisponibles.map((c, i) => [c.id, i] as const));
          const insignia = (id: string, nombre: string, on: boolean) => {
            const color = CATALOG_COLORS[(idxColor.get(id) ?? 0) % CATALOG_COLORS.length]!;
            return (
              <Tooltip key={id} content={on ? `${nombre} · en uso` : nombre} relationship="label">
                <button
                  type="button" aria-label={nombre} onClick={() => setCatAbierto(true)}
                  style={{
                    width: 34, height: 30, borderRadius: 8, flexShrink: 0, padding: 0,
                    fontSize: 11, fontWeight: 800, letterSpacing: '0.02em', cursor: 'pointer',
                    border: `1.5px solid ${color}`, background: on ? color : 'transparent',
                    color: on ? '#fff' : color, opacity: on ? 1 : 0.55,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >{catalogAbbrev(nombre)}</button>
              </Tooltip>
            );
          };
          return (
            <>
              {usados.map((c) => insignia(c.catalogId, etiquetaCatalogo(c), true))}
              {usados.length > 0 && otros.length > 0 && (
                <div title="Arriba: en uso · Abajo: sin usar" style={{ width: '82%', height: 3, borderTop: '2px solid #e5484d', borderBottom: '2px solid #e5484d', flexShrink: 0, margin: '3px 0' }} />
              )}
              {otros.map((c) => insignia(c.id, c.displayName ?? c.name, false))}
            </>
          );
        })()}
      </div>
    </aside>
  );

  // Barra "Tipo de agrupación" (cómo se parten las partidas). Se extrae para poder mostrarla SIEMPRE
  // que la hoja esté abierta —aún sin renglones— y así se vea de entrada cómo se organizará el
  // presupuesto (Sebastián: visible por presentación). Los ejes con IA se calculan la 1ª vez.
  // «Cambió el plano»: el formato del plano de la hoja. IFC si se abrió en el visor 3D; DWG/DXF cuando el dibujo ya está leído.
  const archivoDeLaHoja = files.find((f) => f.id === fileId) ?? null;
  const formatoCambioDePlano: 'ifc' | 'dwg' | null =
    ifcAbierto != null && ifcAbierto === fileId
      ? 'ifc'
      : status === 'ready' && archivoDeLaHoja != null && esArchivoCad(archivoDeLaHoja) && !esIfc(archivoDeLaHoja)
        ? 'dwg'
        : null;
  const barraFiltros = (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, rowGap: 6, flex: '1 1 auto', minWidth: 0 }}>
      <span aria-hidden style={{ width: 1, alignSelf: 'stretch', margin: '2px 4px', background: 'var(--border-default)' }} />
      <span className={styles.segmentado} role="group" aria-label="Qué renglones ver" style={{ flexShrink: 0 }}>
        {(
          [
            { k: 'todos', t: 'Todos' },
            { k: 'revisar', t: 'Por revisar' },
            { k: 'sin_cantidad', t: 'Sin cantidad' },
            { k: 'sin_precio', t: 'Sin precio' },
          ] as const
        ).map((f) => (
          <button
            key={f.k}
            type="button"
            className={mergeClasses(styles.segBtn, filtroHoja === f.k && styles.segBtnOn)}
            aria-pressed={filtroHoja === f.k}
            title={
              f.k === 'revisar'
                ? 'Marcados para revisar, con confianza baja o cuyo análisis no se pudo generar'
                : f.k === 'sin_precio'
                  ? 'Todavía no tienen precio'
                  : f.k === 'sin_cantidad'
                    ? 'Entraron con cantidad 0 porque el plano no la da: escríbela'
                    : undefined
            }
            onClick={() => setFiltroHoja(f.k)}
          >
            {f.t} ({conteoFiltros[f.k]})
          </button>
        ))}
      </span>
      <Input
        size="small"
        contentBefore={<SearchRegular />}
        placeholder="Buscar en la hoja"
        title="Busca por clave o descripción"
        value={textoHoja}
        onChange={(_, d) => setTextoHoja(d.value)}
        style={{ minWidth: 120, flex: '0 1 220px' }}
      />
      {filtrando && (
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          <span title="Los totales son de toda la hoja">Mostrando {visiblesHoja} de {filasHoja.length}</span>
        </span>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 8, rowGap: 6, minWidth: 0 }}>
        {resumenNotasHoja && (
          <span
            title={resumenNotasHoja}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: 'var(--text-secondary)' }}
          >
            <TagRegular style={{ fontSize: 13, color: 'var(--accent)' }} />
            <FieldHelp helpKey="planos.vincular_especificaciones" />
          </span>
        )}
        {filasHoja.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            <b>{conPrecioHoja}</b> de {filasHoja.length} con precio
          </span>
        )}
        {isStaff && formatoCambioDePlano != null && filasHoja.length > 0 && projectId && fileId && puedeEditar && (
          <>
            <PastillaRevisionAplicada projectId={projectId} fileId={fileId} refrescar={renglones} onAbrir={() => setCambioAbierto(true)} />
          </>
        )}
        {isStaff && filasHoja.length > 0 && puedeEditar && (
          <Button
            appearance="subtle"
            size="small"
            style={{ color: 'var(--bad)' }}
            title="Solo Ecosoft: borra todos los conceptos de la hoja (con confirmación)"
            onClick={() => setBorrarHojaAbierto(true)}
          >
            Borrar toda la hoja
          </Button>
        )}
        {projectId && fileId && (
          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              <Button
                size="small"
                appearance="outline"
                disabled={analizadosConIa === 0}
                title={analizadosConIa === 0 ? 'Disponible cuando haya al menos un análisis hecho con IA' : undefined}
              >
                🖨 Imprimir análisis
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => abrirImpresionAnalisis('simple')}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Impresión simple</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Conceptos y su análisis por tipo de recurso.</div>
                  </div>
                </MenuItem>
                <MenuItem onClick={() => abrirImpresionAnalisis('detallado')}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Impresión detallada</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Incluye memoria de cálculo, razonamientos y validaciones.</div>
                  </div>
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
        {filasHoja.length > 0 && (
          <Menu positioning="above-end">
            <MenuTrigger disableButtonEnhancement>
              {(triggerProps: MenuButtonProps) => (
                <SplitButton
                  size="small"
                  appearance={marcadosParaIa > 0 ? 'primary' : 'outline'}
                  icon={<LightbulbRegular />}
                  menuButton={triggerProps}
                  primaryActionButton={{
                    disabled: marcadosParaIa === 0,
                    title:
                      marcadosParaIa === 0
                        ? 'Primero marca los conceptos que quieres generar con IA (flecha de al lado, o el interruptor de cada renglón)'
                        : `${marcadosParaIa} concepto${marcadosParaIa === 1 ? '' : 's'} marcado${marcadosParaIa === 1 ? '' : 's'} para IA`,
                    onClick: () => setConfirmarLoteOpen(true),
                  }}
                >
                  {marcadosParaIa > 0 ? `Generar con IA (${marcadosParaIa})` : 'Generar con IA'}
                </SplitButton>
              )}
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem disabled={marcandoTodos || marcadosParaIa >= filasHoja.length} onClick={() => void marcarTodosIa(true)}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Marcar todos con IA</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Solo los marca, también los de catálogo. «Generar con IA» es el que los arma y reemplaza el precio que tengan.</div>
                  </div>
                </MenuItem>
                <MenuItem disabled={marcandoTodos || marcadosParaIa === 0} onClick={() => void marcarTodosIa(false)}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Desmarcar todos</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Apaga el interruptor IA en todos los conceptos de la hoja.</div>
                  </div>
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </div>
    </div>
  );

  const barraAgrupacion = (
    <div className={styles.agrupBar}>
      <span className={styles.agrupLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        Tipo de partidas
        <FieldHelp helpKey="planos.tipo_partidas" />
      </span>
      <Dropdown
        size="small"
        aria-label="Tipo de agrupación de la hoja"
        value={etiquetaAgrupacion}
        selectedOptions={[tipoAgrupacion]}
        onOptionSelect={(_, d) => void alElegirAgrupacion((d.optionValue as TipoAgrupacion) ?? 'familia')}
        style={{ minWidth: 220 }}
      >
        {AGRUPACIONES.map((a) => (
          <Option key={a.valor} value={a.valor} text={a.label} title={a.valor === 'obra' && vistaProyecto ? 'Solo acomoda los conceptos del plano abierto; los de otros planos quedan «Sin clasificar».' : undefined}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <span>{a.label}</span>
              <span
                style={{
                  marginLeft: 'auto', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase',
                  borderRadius: 4, padding: '0 5px', whiteSpace: 'nowrap',
                  color: a.ia ? 'var(--privado, #6c54d6)' : 'var(--text-tertiary)',
                  border: `1px solid ${a.ia ? 'var(--privado, #6c54d6)' : 'var(--border-subtle)'}`,
                }}
              >
                {a.ia ? '✦ IA' : 'sin costo'}
              </span>
            </span>
          </Option>
        ))}
      </Dropdown>
      {agrupando ? (
        <Spinner size="tiny" label="Clasificando con IA…" />
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          {yaClasificado
            ? 'Cambiar de tipo es instantáneo.'
            : 'Elige cómo se agrupan las partidas del presupuesto.'}
        </span>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Acciones: asignar concepto, borrar renglón, y saltar del renglón al plano
  // ---------------------------------------------------------------------------
  const handleAsignar = useCallback(async () => {
    if (!projectId || !fileId || !target || !conceptoElegido || !catalogSel) return;
    const cantidad = Number(cantidadStr.replace(',', '.'));
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>La cantidad debe ser un número mayor que cero.</ToastTitle>
        </Toast>,
        { intent: 'warning', timeout: 3000 },
      );
      return;
    }
    setAsignando(true);
    try {
      const res = await asignarPlanoRenglon(projectId, fileId, {
        catalogId: conceptoElegido.catalogId || catalogSel || '',
        resourceClave: conceptoElegido.clave,
        cantidad,
        ...fotoManual(target, magnitudElegida, conceptoElegido.unidad ?? null, cantidad),
        // Medición dudosa, o un hueco sin alto descontado de piso a techo (supuesto: mismo 'revisar' que
        // un adicional con dimensiones supuestas). Si el usuario reescribió la cantidad, ya no es supuesto.
        revisar: target.revisar || (principalSinAltoDeVano && cantidadStr === cantidadCalculada),
        nivel: target.nivel,
        ubicacion: target.ubicacion,
        seleccion: target.seleccion,
      });
      await refrescarRenglones();
      // El panel lo dice ("✓ Agregado a la hoja") y ofrece Cambiar / Deshacer / Siguiente que falta.
      setUltimoAgregado({ key: target.key, renglon: res });
      setCambiandoPrincipal(false);
      setComplementarios([]);
      setCompNota(null);
      // En el modo «eligiendo» no se vuelve al dibujo: sigue el paso 3 (lo que este concepto implica).
      setModoLista((m) => (m === 'principal' ? 'adicionales' : m));
    } catch (e) {
      const msg = e instanceof ApiClientError ? e.message : 'No se pudo asignar el concepto.';
      dispatchToast(
        <Toast>
          <ToastTitle>{msg}</ToastTitle>
        </Toast>,
        { intent: 'error', timeout: 4000 },
      );
    } finally {
      setAsignando(false);
    }
  }, [projectId, fileId, target, conceptoElegido, catalogSel, cantidadStr, cantidadCalculada, principalSinAltoDeVano, refrescarRenglones, dispatchToast, fotoManual, magnitudElegida]);

  // Fase 3: pedir a la IA los conceptos complementarios/previos del principal (catálogo o sintético).
  const sugerirComplementarios = useCallback(async (): Promise<{ lista: PlanoComplementario[]; primeraNueva: number | null } | undefined> => {
    if (!projectId || !fileId || !target) return;
    // Prioridad: concepto de catálogo recién ELEGIDO en el panel; si no, el PRINCIPAL que ya tiene
    // la selección en la HP (matcheado o sintético) — por descripción, que es lo único que la IA usa.
    // Los complementarios cuelgan del principal que YA está en la hoja (principal_id): sin él no se piden.
    if (!principalActual) return;
    // El concepto elegido en el panel solo cuenta si es ESE principal (si el usuario eligió otro y no
    // lo agregó, se piden los del principal real, por su descripción).
    const elegidoEsElPrincipal = !!conceptoElegido && conceptoElegido.clave === principalActual.clave;
    setSugiriendoComp(true);
    setCompNota(null);
    // «Proponer más»: con lista ya hecha, se le dice a la IA lo que ya está y las nuevas van AL FINAL
    // (las de antes no se mueven: su ✓ y su «no va» siguen igual).
    const ya = complementarios.map((c) => c.necesidad);
    try {
      const list = elegidoEsElPrincipal && conceptoElegido && catalogSel
        ? await proponerComplementarios(
            projectId, fileId, catalogSel, conceptoElegido.id, target.seleccion.tipo, target.seleccion.clave,
            ya.length > 0 ? ya : undefined,
          )
        : await proponerComplementariosDeSintetico(
            projectId, fileId, principalActual!.descripcion, principalActual!.unidad,
            target.seleccion.tipo, target.seleccion.clave, ya.length > 0 ? ya : undefined,
          );
      const r = agregarNecesidadesNuevas(complementarios, list, (c) => c.necesidad);
      setComplementarios(r.lista);
      if (r.agregadas === 0) {
        setCompNota(
          complementarios.length > 0
            ? 'No encontramos más que suelan acompañarlo.'
            : 'No encontramos nada más que suela llevar este concepto.',
        );
      }
      return { lista: r.lista, primeraNueva: r.primeraNueva };
    } catch (e) {
      setCompNota(
        e instanceof ApiClientError && (e.status === 429 || e.code === 'IA_TIMEOUT')
          ? e.message
          : 'No se pudo consultar qué más lleva en este momento.',
      );
      return { lista: complementarios, primeraNueva: null };
    } finally {
      setSugiriendoComp(false);
    }
  }, [projectId, fileId, catalogSel, conceptoElegido, principalActual, target, complementarios]);


  // Al cambiar el concepto elegido o la selección, se olvidan los complementarios previos.
  useEffect(() => {
    setComplementarios([]);
    setCompNota(null);
    setCompActivo(null);
    setCompCandidatos([]);
    setCompResueltos(new Map());
    setCompDescartados(new Set());
    setCompSugeridos(null);
    setCompElegido(null);
  }, [conceptoElegido?.id, target?.key]);

  // Cantidad derivada del elemento para un complementario. La RELACIÓN (de qué magnitud sale, con
  // qué factor, y qué dimensiones supuso la IA) viene en el propio `comp` — NO hay tabla
  // concepto→fórmula (CLAUDE.md §11). Aquí solo se arman las magnitudes YA medidas del principal
  // (reusando lo del plano/IFC, sin re-medir) y se delega el cálculo a la función pura de
  // @supernova/shared. Devuelve { cantidad: null } = "a definir" cuando la magnitud de origen no
  // está medida (el usuario la pone a mano) y `esSupuesto=true` cuando la IA asumió una medida que
  // el plano no dibuja → el renglón entra 'revisar'.
  // Largo del muro CON sus huecos (eje bruto: largo medido + Σ ancho de los vanos), para un adicional que
  // la IA dice que pasa por encima o por debajo de los huecos (`incluyeVanos`). null = sin huecos.
  const largoConVanosM = useMemo(() => {
    const tr = target?.muroVanos?.tramos;
    return tr && tr.length > 0 ? Math.round(tr.reduce((s, t) => s + t.ejeBrutoM, 0) * 1000) / 1000 : null;
  }, [target?.muroVanos]);

  // Lo ya asignado en la HOJA REAL a un adicional (por su nombre): cantidad y unidad de cada renglón.
  // Sirve para el ✓ y para proponer lo que FALTA al agregarle otro concepto (no cobrar doble).
  const asignadoAComp = useCallback(
    (comp: PlanoComplementario): AsignadoANecesidad[] => {
      const hechos = compResueltos.get(normalizarNecesidad(comp.necesidad));
      if (!hechos || !target) return [];
      return renglones
        .filter(
          (r) =>
            r.rol === 'complemento' &&
            // catálogo: por clave de recurso; sintético (sin clave): por su descripción.
            ((r.resourceClave != null && hechos.has(r.resourceClave)) ||
              (r.origen === 'apu_ia' && hechos.has(r.conceptoDescripcion))) &&
            r.seleccion != null &&
            seTocan(r.seleccion, target.seleccion),
        )
        .map((r) => ({ cantidad: r.cantidad, unidad: r.unidad }));
    },
    [compResueltos, renglones, target],
  );
  // Lo mismo para una instalación (renglones sin selección: son de todo el plano).
  const asignadoAInstal = useCallback(
    (it: PlanoInstalacionSupuesta): AsignadoANecesidad[] => {
      const hechos = instalResueltos.get(normalizarNecesidad(nombreInstal(it)));
      if (!hechos) return [];
      return renglones
        .filter(
          (r) =>
            r.seleccion == null &&
            ((r.resourceClave != null && hechos.has(r.resourceClave)) || hechos.has(r.conceptoDescripcion)),
        )
        .map((r) => ({ cantidad: r.cantidad, unidad: r.unidad }));
    },
    [instalResueltos, renglones],
  );

  const cantidadDerivada = useCallback(
    (comp: PlanoComplementario) => {
      if (!target) return { cantidad: null as number | null, esSupuesto: false, usaLargoConVanos: false, base: null as number | null, factor: 1 };
      const md = target.medidas; // IFC (BIM): magnitudes ya medidas del modelo
      const mags = magnitudesDelPrincipal(target, { altoStr, espesorStr, cuentaVanos, largoConVanosM, capas: inventario?.capas });
      const r = derivarCantidadComplementaria(comp, mags);
      // Para el número generador: la medida del elemento de la que sale y su factor.
      const base =
        comp.derivaDe === 'conteo' ? mags.conteo
          : comp.derivaDe === 'largo' ? (r.usaLargoConVanos ? mags.largoConVanos : mags.largo)
            : comp.derivaDe === 'area' ? mags.area
              : comp.derivaDe === 'volumen' ? mags.volumen
                : null;
      const regla = { base: base ?? null, factor: typeof comp.factor === 'number' && comp.factor > 0 ? comp.factor : 1 };
      // Un área/volumen de muro con un hueco sin alto (descontado de piso a techo) también es supuesto.
      const sinAltoDeVano =
        md == null && target.esMuroDobleTrazo && (comp.derivaDe === 'area' || comp.derivaDe === 'volumen') &&
        (cuentaVanos?.tiposSinAlto ?? 0) > 0;
      return sinAltoDeVano ? { ...r, ...regla, esSupuesto: true } : { ...r, ...regla };
    },
    [target, altoStr, espesorStr, cuentaVanos, largoConVanosM],
  );

  // Buscar/sugerir candidatos del catálogo para UN complementario, en línea (acordeón).
  // Candidatos de la lista ancha para lo que se está resolviendo (un adicional o una instalación):
  // «Buscar» en todos los catálogos del proyecto (gratis) o «Recomendados» (IA, a pedido). Cada pestaña
  // tiene su lista: buscar no borra los recomendados ni al revés.
  const cargarCandidatos = useCallback(
    async (consulta: string, modo: 'buscar' | 'sugerir', textoBusqueda?: string, mismo = false) => {
      if (!projectId || !fileId || !catalogSel) return;
      const turno = modo === 'buscar' ? ++compTurnoRef.current : 0;
      if (modo === 'buscar') {
        setCompCandidatos([]);
        setCompBuscando(true);
        setCompQ(textoBusqueda ?? consulta);
        compUltimaBusquedaRef.current = (textoBusqueda ?? consulta).trim();
      } else {
        setCompSugiriendo(true);
        if (!mismo) setCompQ(consulta);
      }
      try {
        if (modo === 'buscar') {
          // Texto sobre TODOS los catálogos del proyecto por prioridad (gratis), dedup por id. El
          // texto arranca con lo propuesto y el usuario lo puede cambiar (igual que el principal).
          const texto = (textoBusqueda ?? consulta).trim();
          const listas = await Promise.all(
            catalogosPrioridad.map((cid) =>
              listCatalogResources(cid, { q: texto, tipos: [7], limit: 15 })
                .then((res) => res.resources.map((r) => ({ ...r, __catalogId: cid })))
                .catch(() => [] as (CatalogResource & { __catalogId: string })[]),
            ),
          );
          const vistos = new Set<string>();
          const merged: ConceptoItem[] = [];
          for (const lista of listas) {
            for (const r of lista) {
              if (vistos.has(r.id)) continue;
              vistos.add(r.id);
              merged.push({
                id: r.id, catalogId: r.__catalogId, clave: r.clave, descripcion: r.descripcion,
                unidad: r.unidad, precio: r.precio, familia: r.familia,
              });
            }
          }
          if (turno === compTurnoRef.current) setCompCandidatos(merged.slice(0, 25));
        } else {
          const res = await sugerirConceptos(projectId, fileId, {
            catalogId: catalogSel ?? '',
            seleccion: { tipo: 'capa', clave: consulta },
          });
          setCompSugeridosFaltante(res.notaFaltante ?? null);
          setCompSugeridos(
            res.sugerencias.map((s) => ({
              id: s.resourceId, catalogId: s.catalogId, clave: s.clave, descripcion: s.descripcion,
              unidad: s.unidad, precio: s.precio, razon: s.razonModelo,
            })),
          );
        }
      } catch {
        if (modo === 'buscar') {
          if (turno === compTurnoRef.current) setCompCandidatos([]);
        } else setCompSugeridos([]);
      } finally {
        if (modo === 'buscar') {
          if (turno === compTurnoRef.current) setCompBuscando(false);
        } else setCompSugiriendo(false);
      }
    },
    [projectId, fileId, catalogSel, catalogosPrioridad],
  );

  const resolverComp = useCallback(
    async (i: number, comp: PlanoComplementario, modo: 'buscar' | 'sugerir', textoBusqueda?: string) => {
      if (!projectId || !fileId || !catalogSel || !target) return;
      // Re-buscar con otro texto (o pedir sugeridos) en el MISMO complementario no toca la cantidad que
      // ya escribió.
      const mismo = compActivo === i && (textoBusqueda != null || modo === 'sugerir');
      setCompActivo(i);
      if (!mismo) {
        // Otro adicional: lo de antes era del anterior.
        setCompSugeridos(null);
        setCompElegido(null);
      }
      // Si no se puede derivar (null = "a definir"), dejar el campo vacío para que el usuario lo llene.
      // Si ya tiene algo asignado en la hoja, se propone lo que FALTA (en otra unidad no se resta).
      const der = cantidadDerivada(comp).cantidad;
      const asig = asignadoAComp(comp);
      const falta = asig.length > 0 ? cantidadQueFalta(der, comp.unidadEsperada, asig).faltante : null;
      const ini = falta ?? der;
      if (!mismo) setCompCantidadStr(ini != null ? String(Math.round(ini * 100) / 100) : '');
      await cargarCandidatos(comp.consultaCatalogo, modo, textoBusqueda, mismo);
    },
    [projectId, fileId, catalogSel, target, cantidadDerivada, asignadoAComp, compActivo, cargarCandidatos],
  );

  // Lo mismo para una instalación: su cantidad arranca con la que estimó la IA (vacía = «a definir»).
  const resolverInstal = useCallback(
    async (i: number, modo: 'buscar' | 'sugerir', textoBusqueda?: string) => {
      const it = instalPropuestas?.[i];
      if (!it) return;
      const mismo = instalActivo === i && (textoBusqueda != null || modo === 'sugerir');
      setInstalActivo(i);
      if (!mismo) {
        setCompSugeridos(null);
        setCompElegido(null);
        const asig = asignadoAInstal(it);
        const falta = asig.length > 0 ? cantidadQueFalta(it.cantidad, it.unidadEsperada, asig).faltante : null;
        const ini = falta ?? it.cantidad;
        setCompCantidadStr(ini != null ? String(ini) : '');
      }
      await cargarCandidatos(it.consultaCatalogo, modo, textoBusqueda, mismo);
    },
    [instalPropuestas, instalActivo, asignadoAInstal, cargarCandidatos],
  );

  // Números generadores de un complementario agregado desde el panel: sale del elemento con la regla de la IA.
  const fotoDeComplementarioManual = useCallback(
    (
      comp: PlanoComplementario,
      der: { cantidad: number | null; base: number | null; factor: number; usaLargoConVanos: boolean; esSupuesto?: boolean },
      cantidad: number,
      unidad: string | null,
    ): { generador: FotoGenerador; cantidadEscritaAMano?: boolean } => {
      if (der.cantidad == null || !target) return { generador: fotoAMano(cantidad, unidad) };
      const info = target.medidas ? inventario?.capas.find((c) => c.capa === target.seleccion.clave) : undefined;
      const generador = fotoDeRegla({
        origen: 'complementario',
        unidad,
        total: der.cantidad,
        regla: comp.razon || null,
        lineas: [
          {
            elemento: { tipo: target.seleccion.tipo, clave: target.seleccion.clave, ...(target.seleccion.vista ? { vista: target.seleccion.vista } : {}) },
            localizacion: info?.categoria ? nombreLegibleTipoIfc(info) : target.seleccion.clave,
            grupo: target.vista?.rotulo ?? '',
            detalle: detalleDeRegla(comp.derivaDe, der.base, der.factor, der.usaLargoConVanos),
            parcial: der.cantidad,
            // El contrato con que salió: con él «Cambió el plano» lo recalcula sin IA.
            ...(comp.derivaDe !== 'otro'
              ? { derivacion: { derivaDe: comp.derivaDe, factor: der.factor, ...(comp.incluyeVanos === true ? { incluyeVanos: true } : {}), ...(der.esSupuesto ? { supuesto: true } : {}) } }
              : {}),
          },
        ],
      });
      return Math.abs(der.cantidad - cantidad) < 1e-9 ? { generador } : { generador, cantidadEscritaAMano: true };
    },
    [target, inventario],
  );

  // Asignar un concepto del catálogo como COMPLEMENTO del elemento (rol='complemento').
  const asignarComplemento = useCallback(
    async (concepto: ConceptoItem, comp: PlanoComplementario, i: number) => {
      if (!projectId || !fileId || !catalogSel || !target) return false;
      // La cantidad la escribe el usuario o, si no la tocó, la derivada del elemento. Cuando no hay
      // ninguna (derivada = "a definir" y el campo vacío) NO se agrega con un número inventado: se pide.
      const der = cantidadDerivada(comp);
      const cantEdit = Number(compCantidadStr.replace(',', '.'));
      const cantidad = cantEdit > 0 ? cantEdit : der.cantidad;
      if (cantidad == null || !(cantidad > 0)) {
        dispatchToast(
          <Toast><ToastTitle>Escribe la cantidad del complemento antes de agregarlo.</ToastTitle></Toast>,
          { intent: 'error', timeout: 4000 },
        );
        return false;
      }
      try {
        await asignarPlanoRenglon(projectId, fileId, {
          catalogId: concepto.catalogId || catalogSel || '',
          resourceClave: concepto.clave,
          cantidad,
          unidad: comp.unidadEsperada ?? undefined,
          ...fotoDeComplementarioManual(comp, der, cantidad, concepto.unidad ?? comp.unidadEsperada ?? null),
          rol: 'complemento',
          principalId: principalActual?.id ?? null,
          // Si la cantidad descansa en una medida que la IA supuso (y el usuario no la reescribió),
          // el renglón entra a revisar además de por la medición dudosa del principal.
          revisar: target.revisar || (der.esSupuesto && !(cantEdit > 0)),
          nivel: target.nivel,
          ubicacion: target.ubicacion,
          seleccion: {
            tipo: target.seleccion.tipo,
            clave: target.seleccion.clave,
            ids: target.seleccion.ids,
          },
        });
        setCompResueltos((prev) => conResuelto(prev, comp.necesidad, concepto.clave));
        setCompActivo(null);
        setCompCandidatos([]);
        await refrescarRenglones();
        dispatchToast(
          <Toast>
            <ToastTitle>Complemento agregado a la hoja.</ToastTitle>
          </Toast>,
          { intent: 'success', timeout: 3000 },
        );
        return true;
      } catch (e) {
        const msg = e instanceof ApiClientError ? e.message : 'No se pudo agregar el complemento.';
        dispatchToast(
          <Toast>
            <ToastTitle>{msg}</ToastTitle>
          </Toast>,
          { intent: 'error', timeout: 4000 },
        );
        return false;
      }
    },
    [projectId, fileId, catalogSel, target, cantidadDerivada, compCantidadStr, refrescarRenglones, dispatchToast, principalActual],
  );

  // Agregar un COMPLEMENTO que NO está en catálogo como sintético (mismo patrón que el principal):
  // a la HP va el nombre de la necesidad que propuso la IA (editable luego en la HP), rol='complemento',
  // con la cantidad derivada del elemento. Se cotiza con IA en el lote, igual que los demás sintéticos.
  const agregarComplementoSintetico = useCallback(
    async (comp: PlanoComplementario, i: number, cantidadTexto?: string) => {
      if (!projectId || !fileId || !target) return false;
      const der = cantidadDerivada(comp);
      const cantEdit = Number((cantidadTexto ?? compCantidadStr).replace(',', '.'));
      const cantidad = cantEdit > 0 ? cantEdit : der.cantidad;
      if (cantidad == null || !(cantidad > 0)) {
        dispatchToast(
          <Toast><ToastTitle>Escribe la cantidad del complemento antes de agregarlo.</ToastTitle></Toast>,
          { intent: 'error', timeout: 4000 },
        );
        return false;
      }
      try {
        const r = await agregarRenglonPorDescripcion(projectId, fileId, {
          descripcion: comp.necesidad,
          unidad: comp.unidadEsperada ?? undefined,
          cantidad,
          ...fotoDeComplementarioManual(comp, der, cantidad, comp.unidadEsperada ?? null),
          rol: 'complemento',
          principalId: principalActual?.id ?? null,
          nivel: target.nivel,
          ubicacion: target.ubicacion,
          // Cantidad derivada de un supuesto de la IA (y no reescrita) → el renglón entra a revisar.
          revisar: der.esSupuesto && !(cantEdit > 0),
          seleccion: {
            tipo: target.seleccion.tipo,
            clave: target.seleccion.clave,
            ids: target.seleccion.ids,
          },
        });
        setCompResueltos((prev) => conResuelto(prev, comp.necesidad, r.resourceClave ?? comp.necesidad));
        setCompActivo(null);
        setCompCandidatos([]);
        setHpAbierto(true);
        await refrescarRenglones();
        dispatchToast(
          <Toast><ToastTitle>Complemento agregado a la hoja. Cotízalo con IA cuando quieras.</ToastTitle></Toast>,
          { intent: 'success', timeout: 3000 },
        );
        return true;
      } catch {
        dispatchToast(
          <Toast><ToastTitle>No se pudo agregar el complemento.</ToastTitle></Toast>,
          { intent: 'error', timeout: 4000 },
        );
        return false;
      }
    },
    [projectId, fileId, target, compCantidadStr, cantidadDerivada, refrescarRenglones, dispatchToast, principalActual],
  );

  // Sugerir conceptos por IA para la selección activa (bajo demanda, botón).
  const handleSugerir = useCallback(async () => {
    if (!projectId || !fileId || !catalogSel || !target) return;
    setSugiriendo(true);
    setSugerenciaNota(null);
    setConceptoElegido(null);
    setQ(''); // apagar el override de texto para que se vean las sugerencias
    try {
      const sel = target.seleccion;
      const res = await sugerirConceptos(projectId, fileId, {
        catalogId: catalogSel ?? '',
        seleccion: {
          tipo: sel.tipo,
          clave: sel.clave,
          // IFC: lo que el MODELO dice del tipo (clase, materiales, exterior, espesor, cantidad). Es el
          // mismo dato del nombre legible de la lista: sale de etiquetasIfc, un solo lugar.
          ...(() => {
            const capaInfo = sel.tipo === 'capa' ? inventario?.capas.find((c) => c.capa === sel.clave) : undefined;
            const ifc = capaInfo ? describirTipoIfcParaSugerencia(capaInfo) : null;
            return ifc ? { ifc } : {};
          })(),
        },
      });
      setSugerencias(
        res.sugerencias.map((s) => ({
          id: s.resourceId,
          catalogId: s.catalogId,
          clave: s.clave,
          descripcion: s.descripcion,
          unidad: s.unidad,
          precio: s.precio,
          razon: s.razonModelo,
        })),
      );
      setSugerenciaNota(res.nota ?? null);
      setSugerenciaFaltante(res.notaFaltante ?? null);
      setSugerenciasParaKey(target.key);
      // Lo que la IA entendió queda para proponer la descripción de «Generar con IA» ($0, ya pagado).
      const interp = res.interpretacion?.trim();
      if (interp) setInterpretacionPorKey((prev) => new Map(prev).set(target.key, interp));
    } catch (e) {
      const msg = e instanceof ApiClientError ? e.message : 'No se pudieron obtener sugerencias.';
      setSugerencias([]);
      setSugerenciaNota(msg);
      setSugerenciasParaKey(target.key);
    } finally {
      setSugiriendo(false);
    }
  }, [projectId, fileId, catalogSel, target, inventario]);

  // Quitar un renglón de la hoja. Un principal con complementarios colgados PREGUNTA qué hacer con
  // ellos (tres salidas: cancelar, quitar solo el principal, quitar todo). Sirve para el bote de cada
  // renglón y para "Deshacer"/"Cambiar" del panel. El renglón puede ser de otro plano (vista de toda
  // la obra): se borra en SU plano.
  const [borrarPendiente, setBorrarPendiente] = useState<
    { renglon: PlanoRenglon; hijos: number; alTerminar?: () => void } | null
  >(null);
  const ejecutarBorrado = useCallback(
    async (r: PlanoRenglon, conHijos: boolean, alTerminar?: () => void) => {
      const fid = r.sourceFileId ?? fileId;
      if (!projectId || !fid) return;
      setBorrarPendiente(null);
      try {
        await borrarPlanoRenglon(projectId, fid, r.id, conHijos);
        await refrescarRenglones();
        alTerminar?.();
      } catch (e) {
        const msg = e instanceof ApiClientError ? e.message : 'No se pudo quitar el renglón.';
        dispatchToast(
          <Toast>
            <ToastTitle>{msg}</ToastTitle>
          </Toast>,
          { intent: 'error', timeout: 4000 },
        );
      }
    },
    [projectId, fileId, refrescarRenglones, dispatchToast],
  );
  const handleBorrar = useCallback(
    async (r: PlanoRenglon, alTerminar?: () => void) => {
      const todas = [...renglones, ...renglonesProyecto];
      const hijos = new Set(todas.filter((x) => x.rol === 'complemento' && x.principalId === r.id).map((x) => x.id)).size;
      if (hijos > 0) {
        setBorrarPendiente({ renglon: r, hijos, alTerminar });
        return;
      }
      await ejecutarBorrado(r, false, alTerminar);
    },
    [renglones, renglonesProyecto, ejecutarBorrado],
  );

  // Clic en un renglón → resalta en el plano su selección (reusa idsSel/capasSel).
  const handleClickRenglon = useCallback((r: PlanoRenglon) => {
    // Abre su análisis de precio en el panel de la derecha (cualquier renglón, también de otro plano).
    setPanelRenglon(r);
    setPanelConcepto((prev) => (prev && prev.id === r.id ? prev : null));
    void cargarPanelConcepto(r);
    // Solo se resalta en el dibujo lo del plano abierto (un renglón de otro plano no está en este dibujo).
    if (!r.seleccion || (r.sourceFileId != null && r.sourceFileId !== fileId)) return;
    if (r.seleccion.tipo === 'bloque') {
      setIdsSel(new Set(r.seleccion.ids ?? []));
      setCapasSel(new Set());
    } else {
      setCapasSel(new Set([r.seleccion.clave]));
      setIdsSel(new Set());
    }
  }, [fileId, cargarPanelConcepto]);

  // --- Estado de asignación por pieza (IFC) ----------------------------------------------------
  // De los renglones ya armados sacamos, por capa (tipo), qué instancias tienen concepto. Si un renglón
  // guarda expressIDs en su selección → esas piezas están asignadas; si no trae ids (concepto del tipo
  // ENTERO, o IFC viejo sin instancias) → la capa cuenta como asignada completa.
  const asignacionPorCapa = useMemo(() => {
    const ids = new Map<string, Set<number>>();
    const completas = new Set<string>();
    for (const r of renglones) for (const s of seleccionesDe(r)) {
      // Un tipo "tiene concepto" por su PRINCIPAL; lo que lo acompaña no lo da por presupuestado.
      if (s.tipo !== 'capa' || r.rol !== 'principal') continue;
      if (s.ids && s.ids.length > 0) {
        let set = ids.get(s.clave);
        if (!set) {
          set = new Set<number>();
          ids.set(s.clave, set);
        }
        for (const id of s.ids) {
          const n = Number(id);
          if (Number.isFinite(n)) set.add(n);
        }
      } else {
        completas.add(s.clave);
      }
    }
    return { ids, completas };
  }, [renglones]);

  // Conjunto plano de expressIDs ya asignados (para pintar en el visor 3D).
  const instAsignadas = useMemo(() => {
    const s = new Set<number>();
    if (!inventario) return s;
    for (const c of inventario.capas) {
      const entera = asignacionPorCapa.completas.has(c.capa);
      const idset = asignacionPorCapa.ids.get(c.capa);
      for (const inst of c.instancias ?? []) {
        if (entera || idset?.has(inst.id)) s.add(inst.id);
      }
    }
    return s;
  }, [inventario, asignacionPorCapa]);

  // «¿Qué me falta?»: de lo que SE CONSTRUYE (clase IFC o clasificación del DWG, con las correcciones
  // del ⋯), cuánto ya tiene concepto principal — por piezas, área, largo y volumen — y qué falta.
  // Lo que nadie decidió si se construye no cuenta (se avisa aparte). Con «Ver en el plano» el visor
  // pinta de rojo lo que falta.
  const [verFalta, setVerFalta] = useState(false);
  const faltaDelPlano = useMemo(() => {
    if (!inventario || inventario.estimadoVision) return null;
    const esIfc = ifcAbierto != null || inventario.capas.some((c) => c.categoria != null);
    const elementos: ElementoParaFalta[] = [];
    let sinDecidir = 0;
    if (esIfc) {
      const ids = new Set<number>();
      for (const c of inventario.capas) {
        if (c.categoria == null || (c.medidas?.conteo ?? 0) <= 0) continue;
        const d = decisionOverride[c.capa] ?? decisionIfcDeClase(c.categoria, c.ifc?.supertipos);
        if (d === 'dudoso') sinDecidir++;
        if (d !== 'cotiza') continue;
        const entera = asignacionPorCapa.completas.has(c.capa);
        const conConcepto = asignacionPorCapa.ids.get(c.capa);
        const insts = c.instancias ?? [];
        for (const i of insts) ids.add(i.id);
        elementos.push({
          tipo: 'capa',
          clave: c.capa,
          nombre: nombreLegibleTipoIfc(c),
          piezas: insts.length
            ? insts.map((i) => ({ n: 1, area: i.area, volumen: i.volumen, asignada: entera || !!conConcepto?.has(i.id) }))
            : [{ n: c.medidas?.conteo ?? 0, area: c.medidas?.area, volumen: c.medidas?.volumen, asignada: entera }],
        });
      }
      return { avance: queMeFalta(elementos, ['piezas', 'area', 'volumen']), sinDecidir, instIfc: ids, dwg: null };
    }
    const capas: string[] = [];
    for (const c of inventario.capas) {
      const d = decisionOverride[c.capa] ?? decisionDeCapa(c.capa, capasClasificadas);
      if (d === 'dudoso') sinDecidir++;
      if (d !== 'cotiza') continue;
      const esMuro = capaSeMideComoMuro(c.capa, capasClasificadas, decisionOverride[c.capa]);
      const med = medirCapa2D(c, factorEscala, esMuro);
      const area = med.areaM2 ?? 0;
      const largo = med.baseLargo ?? 0;
      if (area <= 0 && largo <= 0) continue;
      capas.push(c.capa);
      // Cada capa suma en UNA medida, la que la mide: un muro por su largo (su «área» en planta es la
      // huella, no obra), lo demás por su área y, si no tiene, por su largo.
      const porLargo = esMuro || area <= 0;
      elementos.push({
        tipo: 'capa',
        clave: c.capa,
        nombre: c.capa,
        piezas: [{ n: 0, ...(porLargo ? { longitud: largo } : { area }), asignada: capasConPrincipal.has(c.capa) }],
      });
    }
    const bloques: string[] = [];
    for (const b of inventario.bloques) {
      const d = decisionOverride[b.nombre] ?? decisionDeBloque(b.nombre, capasClasificadas);
      if (d === 'dudoso') sinDecidir++;
      if (d !== 'cotiza' || (b.n ?? 0) <= 0) continue;
      bloques.push(b.nombre);
      // Un principal del bloque con piezas elegidas cubre esas; sin piezas, todas las de ese nombre.
      let entero = false;
      const conConcepto = new Set<string>();
      for (const r of renglones) {
        if (r.rol !== 'principal') continue;
        for (const sel of seleccionesDe(r)) {
          if (sel.tipo !== 'bloque' || sel.clave !== b.nombre) continue;
          if (sel.ids && sel.ids.length > 0) sel.ids.forEach((id) => conConcepto.add(id));
          else entero = true;
        }
      }
      elementos.push({
        tipo: 'bloque',
        clave: b.nombre,
        nombre: b.nombre,
        piezas: b.ids && b.ids.length > 0
          ? b.ids.map((id) => ({ n: 1, asignada: entero || conConcepto.has(id) }))
          : [{ n: b.n, asignada: entero }],
      });
    }
    return {
      avance: queMeFalta(elementos, ['piezas', 'area', 'longitud']),
      sinDecidir,
      instIfc: null,
      dwg: { capas, bloques },
    };
  }, [inventario, ifcAbierto, decisionOverride, asignacionPorCapa, capasClasificadas, factorEscala, capasConPrincipal, renglones]);
  const instAPresupuestar = verFalta ? (faltaDelPlano?.instIfc ?? null) : null;
  // «Ver dónde» (Lo que se dedujo): las piezas de UN material iluminadas en el visor 3D. Solo vista.
  const [materialVer, setMaterialVer] = useState<MaterialDeducidoIfc | null>(null);
  useEffect(() => setMaterialVer(null), [ifcAbierto]);
  const instColoresMaterial = useMemo(() => (capasSel.size === 0 ? coloresDeMaterial(materialVer?.expressIds) : null), [materialVer, capasSel]);
  const queMeFaltaDwg = verFalta ? (faltaDelPlano?.dwg ?? null) : null;

  // Cuántas piezas de una capa están asignadas (para el contador del panel).
  const contarAsignadas = useCallback(
    (c: PlanoCapa): { total: number; asignadas: number; sinAsignar: number } => {
      const insts = c.instancias ?? [];
      const total = insts.length || c.n_entidades;
      let asignadas = 0;
      if (asignacionPorCapa.completas.has(c.capa)) asignadas = total;
      else {
        const set = asignacionPorCapa.ids.get(c.capa);
        if (set) asignadas = insts.filter((i) => set.has(i.id)).length;
      }
      return { total, asignadas, sinAsignar: Math.max(0, total - asignadas) };
    },
    [asignacionPorCapa],
  );

  // --- Estado de asignación del TIPO seleccionado (target) --------------------------------------
  // Reúne, para lo que está seleccionado ahora: qué concepto(s) ya tiene en la HP y —en tipos con
  // piezas contables (instancias IFC o inserciones de un bloque DWG)— cuántas piezas ya están
  // asignadas de cuántas hay. Alimenta el indicador del panel derecho y el botón "solo las que
  // faltan". Una capa de geometría suelta (DWG) no tiene sub-piezas → `tienePiezas=false`.
  const estadoTarget = useMemo(() => {
    if (!target) return null;
    const key = target.key;
    const rs = renglones.filter(
      (r) => r.seleccion && claveDeSeleccion(r.seleccion) === key,
    );
    // expressID/data-id ya asignados a algún renglón de este tipo; `completa` = algún renglón cubre
    // el tipo ENTERO (sin ids: capa DWG, o IFC asignado como tipo completo).
    const idsAsignados = new Set<string>();
    let completa = false;
    for (const r of rs) {
      const s = r.seleccion!;
      if (s.ids && s.ids.length > 0) for (const id of s.ids) idsAsignados.add(String(id));
      else completa = true;
    }
    // Universo de piezas del tipo (para medir "N de T"): inserciones del bloque (DWG) o instancias
    // del tipo (IFC). Una capa de geometría suelta no las tiene.
    let total: number | null = null;
    let piezasIds: string[] = [];
    if (target.seleccion.tipo === 'bloque') {
      const b = inventario?.bloques.find((x) => x.nombre === target.seleccion.clave);
      if (b) {
        total = b.n;
        piezasIds = b.ids.map(String);
      }
    } else {
      const c = inventario?.capas.find((x) => x.capa === target.seleccion.clave);
      const insts = c?.instancias ?? [];
      if (insts.length > 0) {
        total = insts.length;
        piezasIds = insts.map((i) => String(i.id));
      }
    }
    const tienePiezas = total != null && piezasIds.length > 0;
    const asignadas =
      completa && total != null
        ? total
        : piezasIds.filter((id) => idsAsignados.has(id)).length;
    // Conceptos distintos ya asignados (por descripción+rol), para nombrarlos.
    const conceptos = rs.map((r) => ({
      descripcion: r.conceptoDescripcion,
      rol: r.rol,
      cantidad: r.cantidad,
      unidad: r.unidad,
      esIa: r.origen === 'apu_ia',
    }));
    return { rs, conceptos, total, asignadas, tienePiezas, completa, idsAsignados, piezasIds };
  }, [target, renglones, inventario]);

  // ¿Está activo el modo "solo las que faltan"? Se DERIVA del estado real (sin flag propio que
  // desincronice): IFC → todas las piezas asignadas están excluidas del conteo; bloque DWG →
  // ninguna pieza asignada sigue seleccionada. Así el botón sabe cuándo ofrecer "mostrar todas".
  const soloFaltantesActivo = useMemo(() => {
    if (!estadoTarget || !target || !estadoTarget.tienePiezas) return false;
    const asig = estadoTarget.idsAsignados;
    if (asig.size === 0) return false;
    if (target.seleccion.tipo === 'bloque') {
      return idsSel.size > 0 && ![...asig].some((id) => idsSel.has(id));
    }
    return [...asig].every((id) => instExcluidas.has(Number(id)));
  }, [estadoTarget, target, idsSel, instExcluidas]);

  // Deja en la selección/conteo SOLO las piezas del tipo activo que aún no tienen concepto. En DWG
  // se reduce la selección de bloques a las inserciones faltantes; en IFC se excluyen del conteo las
  // piezas ya asignadas (mismo mecanismo que sacar una a mano en el visor 3D). Reversible con abajo.
  const soloLasQueFaltan = useCallback(() => {
    if (!estadoTarget || !target) return;
    const faltantes = estadoTarget.piezasIds.filter((id) => !estadoTarget.idsAsignados.has(id));
    if (target.seleccion.tipo === 'bloque') {
      setIdsSel(new Set(faltantes));
    } else {
      const excl = new Set<number>();
      for (const id of estadoTarget.idsAsignados) {
        const n = Number(id);
        if (Number.isFinite(n)) excl.add(n);
      }
      setInstExcluidas(excl);
    }
  }, [estadoTarget, target]);

  // Vuelve a incluir TODAS las piezas del tipo activo (deshace "solo las que faltan").
  const incluirTodasLasPiezas = useCallback(() => {
    if (!target) return;
    if (target.seleccion.tipo === 'bloque') {
      const b = inventario?.bloques.find((x) => x.nombre === target.seleccion.clave);
      if (b) setIdsSel(new Set(b.ids));
    } else {
      setInstExcluidas(new Set());
    }
  }, [target, inventario]);

  // Clic en una pieza del visor 3D (IFC): además de sacarla/reingresarla del conteo (comportamiento
  // de siempre), la ENFOCA para que el panel derecho muestre su estado individual (qué concepto tiene,
  // si es que tiene). El clic desde la LISTA de instancias del panel sigue usando enfocar/toggle por
  // separado (casilla vs. fila).
  const toggleInstancia3D = useCallback(
    (expressID: number) => {
      toggleInstancia(expressID);
      setInstEnfoque(expressID);
    },
    [toggleInstancia],
  );

  // Clic en una pieza del 3D (IFC) = ESA pieza, como en un visor BIM. Se representa con lo que ya existe
  // (el tipo activo + las exclusiones), así las cantidades y "asignar concepto" funcionan igual:
  //  - clic: el tipo de la pieza con TODAS las demás de ese tipo excluidas;
  //  - Ctrl/⌘+clic sobre el mismo tipo: suma o quita esa pieza. Sobre OTRO tipo reemplaza: se trabaja un
  //    tipo a la vez (un concepto por selección);
  //  - clic en vacío (o en algo que no es del inventario, como el terreno): suelta la selección.
  const capaDeInst = useMemo(() => {
    const m = new Map<number, PlanoCapa>();
    for (const c of inventario?.capas ?? []) for (const i of c.instancias ?? []) m.set(i.id, c);
    return m;
  }, [inventario]);
  const seleccionarPieza3D = useCallback(
    (eid: number | null, sumar: boolean) => {
      const c = eid != null ? capaDeInst.get(eid) : undefined;
      if (eid == null || !c) {
        limpiarSeleccion();
        return;
      }
      if (sumar && capasSel.size === 1 && capasSel.has(c.capa)) {
        setInstExcluidas((prev) => {
          const n = new Set(prev);
          if (n.has(eid)) n.delete(eid);
          else n.add(eid);
          return n;
        });
        setInstEnfoque(eid);
        return;
      }
      setIdsSel(new Set());
      setCapasSel(new Set([c.capa]));
      setInstExcluidas(new Set((c.instancias ?? []).map((i) => i.id).filter((id) => id !== eid)));
      setInstEnfoque(eid);
      setVisMode((v) => (v === 'ocultar-sel' || v === 'aislar-sel' ? 'todo' : v));
    },
    [capaDeInst, capasSel, limpiarSeleccion],
  );
  // Elegir un tipo ENTERO o solo sus piezas de un piso (árbol): el tipo + excluir las que no van.
  const seleccionarPiezasDelTipo = useCallback((c: PlanoCapa, ids: readonly number[]) => {
    const quedan = new Set(ids);
    setIdsSel(new Set());
    setCapasSel(new Set([c.capa]));
    setInstExcluidas(new Set((c.instancias ?? []).map((i) => i.id).filter((id) => !quedan.has(id))));
    setInstEnfoque(null);
    setVisMode((v) => (v === 'ocultar-sel' || v === 'aislar-sel' ? 'todo' : v));
  }, []);

  // Info de la pieza ENFOCADA (por clic en 3D o en la lista del panel): nombre + si ya tiene concepto
  // y cuál(es). Solo aplica a IFC (las piezas individuales viven en las instancias de la capa activa).
  const piezaEnfocadaInfo = useMemo(() => {
    if (instEnfoque == null || !target || target.seleccion.tipo !== 'capa') return null;
    const capa = inventario?.capas.find((c) => c.capa === target.seleccion.clave);
    const insts = capa?.instancias ?? [];
    const idx = insts.findIndex((i) => i.id === instEnfoque);
    if (idx < 0) return null;
    const inst = insts[idx]!;
    const nombre = inst.nombre?.trim() || `${categoriaSingular(capa?.categoria)} ${idx + 1}`;
    const asignada =
      estadoTarget?.completa === true || estadoTarget?.idsAsignados.has(String(instEnfoque)) === true;
    const conceptos = [
      ...new Set(
        (estadoTarget?.rs ?? [])
          .filter((r) => {
            const s = r.seleccion!;
            return !s.ids || s.ids.length === 0 || s.ids.map(String).includes(String(instEnfoque));
          })
          .map((r) => r.conceptoDescripcion),
      ),
    ];
    return { nombre, asignada, conceptos };
  }, [instEnfoque, target, inventario, estadoTarget]);

  // --- Comandos de visibilidad (IFC): qué piezas se ocultan según el modo elegido -----------------
  // Instancias INCLUIDAS del/los tipo(s) activo(s) (las que hoy cuentan para la cantidad).
  const incluidasActivas = useMemo(() => {
    const s = new Set<number>();
    if (!inventario) return s;
    for (const c of inventario.capas) {
      if (!capasSel.has(c.capa)) continue;
      for (const inst of c.instancias ?? []) if (!instExcluidas.has(inst.id)) s.add(inst.id);
    }
    return s;
  }, [inventario, capasSel, instExcluidas]);

  const todasLasInst = useMemo(() => {
    const s = new Set<number>();
    if (!inventario) return s;
    for (const c of inventario.capas) for (const inst of c.instancias ?? []) s.add(inst.id);
    return s;
  }, [inventario]);

  const instOcultas = useMemo(() => {
    // Las ocultadas a mano (clic derecho) se suman a las del modo de visibilidad.
    const s = new Set<number>(ocultasClic);
    for (const id of ocultasOjo) s.add(id);
    if (visMode === 'ocultar-sel') {
      for (const id of incluidasActivas) s.add(id);
    } else if (visMode === 'ocultar-asig') {
      for (const id of instAsignadas) s.add(id);
    }
    return s;
  }, [visMode, incluidasActivas, instAsignadas, ocultasClic, ocultasOjo]);
  // Aislar (botón/tecla I, o una categoría del árbol): SOLO esas piezas a la vista — también se va el
  // terreno y lo que no es del inventario.
  const instSoloVisibles = useMemo(
    () => (visMode === 'aislar-sel' ? incluidasActivas : aisladasArbol),
    [visMode, incluidasActivas, aisladasArbol],
  );

  const hayTipoActivo = capasSel.size > 0;

  // Volver a ver TODO el modelo: suelta los modos de visibilidad, las piezas ocultadas con clic
  // derecho, el aislado del árbol, el piso elegido y el techo oculto.
  const mostrarTodo = useCallback(() => {
    setVisMode('todo');
    setOcultasClic(new Set());
    setOcultasOjo(new Set());
    setAisladasArbol(null);
    setNivelVista(null);
    setOcultarTecho(false);
  }, []);
  const hayAlgoOculto =
    visMode !== 'todo' || ocultasClic.size > 0 || ocultasOjo.size > 0 || aisladasArbol != null || nivelVista != null || ocultarTecho;

  // Otro IFC → la vista vuelve a empezar (los ids de piezas y pisos son de cada archivo).
  useEffect(() => {
    setNivelesIfc([]);
    setNivelDeElemento(new Map());
    setObrasIfc([]);
    setObraDeElemento(new Map());
    setHayTechoIfc(false);
    setNivelVista(null);
    setOcultarTecho(false);
    setOcultasClic(new Set());
    setOcultasOjo(new Set());
    setAisladasArbol(null);
  }, [ifcAbierto]);

  // Otro plano → todo se ve (los ojos no se recuerdan).
  useEffect(() => {
    setCapasOcultasDwg(new Set());
    setTramasOcultasDwg(false);
  }, [projectId, fileId]);

  // Atajos del visor 3D (IFC), como en un visor BIM. No se activan escribiendo en un campo.
  //  Esc = soltar la selección (si no hay nada elegido: mostrar todo) · H = ocultar lo elegido ·
  //  I = aislar lo elegido · F = acercar a lo elegido.
  useEffect(() => {
    // En el modo «eligiendo» el dibujo es solo una miniatura: sus atajos no corren (Esc lo maneja el modo).
    if (!ifcAbierto || modoLista) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.isContentEditable ||
          /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) ||
          t.closest('[role="combobox"],[role="listbox"],[role="dialog"],[role="slider"]'))
      )
        return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (e.key === 'Escape') {
        if (hayTipoActivo) limpiarSeleccion();
        else mostrarTodo();
      } else if (k === 'h' && hayTipoActivo) {
        setVisMode((v) => (v === 'ocultar-sel' ? 'todo' : 'ocultar-sel'));
      } else if (k === 'i' && hayTipoActivo) {
        setVisMode((v) => (v === 'aislar-sel' ? 'todo' : 'aislar-sel'));
      } else if (k === 'f') {
        pedirCamara('seleccion');
      } else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ifcAbierto, hayTipoActivo, limpiarSeleccion, mostrarTodo, pedirCamara, modoLista]);

  // ---------------------------------------------------------------------------
  // Modo «eligiendo»: entrar, salir y recorrer los adicionales.
  // ---------------------------------------------------------------------------
  const salirModoLista = useCallback(() => {
    setModoLista(null);
    setCompActivo(null);
    setInstalActivo(null);
    setCompElegido(null);
  }, []);
  // Mide el escenario del visor JUSTO antes de entrar al modo (con el diseño normal todavía): el
  // ResizeObserver no corre en una pestaña en segundo plano y la miniatura necesita el tamaño de antes.
  const medirEscenario = useCallback(() => {
    if (modoListaRef.current || !escenarioEl) return;
    const r = escenarioEl.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) setTamEscenario({ w: r.width, h: r.height });
  }, [escenarioEl]);
  const entrarModoPrincipal = useCallback((forma: 'sugeridos' | 'buscar' | 'crear') => {
    medirEscenario();
    setFormaConcepto(forma);
    setDerechaAbierta(true);
    setModoLista('principal');
  }, [medirEscenario]);

  // Esc vuelve al dibujo. Se escucha en captura para ganarle a los atajos del visor; no se mete con un
  // diálogo o un menú abierto (ahí Esc cierra eso).
  useEffect(() => {
    if (!modoLista) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const t = e.target instanceof Element ? e.target : null;
      if (t?.closest('[role="dialog"],[role="listbox"],[role="menu"],[role="combobox"]')) return;
      e.preventDefault();
      e.stopPropagation();
      salirModoLista();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [modoLista, salirModoLista]);

  // Si cambia (o se suelta) la selección, la lista era de otra cosa: de vuelta al dibujo.
  useEffect(() => {
    salirModoLista();
  }, [target?.key, salirModoLista]);

  // Tamaño del escenario del visor en modo normal (la miniatura lo escala). En el modo la caja queda
  // fija con ese tamaño, así que el observador no cambia nada mientras dura.
  useEffect(() => {
    const el = escenarioEl;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (modoListaRef.current) return; // en miniatura la caja está fija: no se mide
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setTamEscenario({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [escenarioEl]);
  useEffect(() => {
    modoListaRef.current = modoLista;
  }, [modoLista]);
  // Al entrar al modo se mide dónde está lo seleccionado (antes de pintar: sin parpadeo). La cámara del
  // visor no se toca.
  // En instalaciones la miniatura encuadra TODO el plano (son de toda la obra, no de la selección).
  const modoMini = modoLista == null ? null : modoLista === 'instalaciones' ? 'todo' : 'seleccion';
  useLayoutEffect(() => {
    setCajaMini(modoMini ? leerCajaMiniRef.current?.(modoMini === 'todo') ?? null : null);
  }, [modoMini]);

  // Se deshizo el principal estando en el paso 3: de vuelta a elegir el concepto.
  useEffect(() => {
    if (modoLista === 'adicionales' && !principalActual) setModoLista('principal');
  }, [modoLista, principalActual]);

  // Un adicional ya está en la hoja: se deriva de la HP real (si borras el renglón, el ✓ se limpia solo).
  const compEnLaHoja = useCallback(
    (i: number) => {
      const cmp = complementarios[i];
      return !!cmp && asignadoAComp(cmp).length > 0;
    },
    [complementarios, asignadoAComp],
  );
  const compNoVa = useCallback(
    (i: number) => {
      const cmp = complementarios[i];
      return !!cmp && compDescartados.has(normalizarNecesidad(cmp.necesidad));
    },
    [complementarios, compDescartados],
  );

  // Abrir un adicional: arranca en «Buscar» con lo que propuso la IA ya escrito (gratis).
  const abrirComp = useCallback(
    (i: number) => {
      const cmp = complementarios[i];
      if (!cmp) return;
      setCompTab('buscar');
      setCompElegido(null);
      void resolverComp(i, cmp, 'buscar');
    },
    [complementarios, resolverComp],
  );

  // Tras agregar o descartar el adicional `i`, sigue con el próximo pendiente (sin mirar `i`, que la HP de
  // este render todavía no refleja); si no queda ninguno, vuelve al dibujo.
  const avanzarComp = useCallback(
    (i: number) => {
      const n = complementarios.length;
      for (let paso = 1; paso < n; paso++) {
        const j = (i + paso) % n;
        if (!compNoVa(j) && !compEnLaHoja(j)) {
          abrirComp(j);
          return;
        }
      }
      salirModoLista();
    },
    [complementarios.length, compNoVa, compEnLaHoja, abrirComp, salirModoLista],
  );

  // Pedir a la IA los adicionales (botón explícito: cuesta IA) y abrir el primero.
  const pedirAdicionales = useCallback(async () => {
    const r = await sugerirComplementarios();
    if (r && r.primeraNueva != null) {
      medirEscenario();
      setModoLista('adicionales');
      setCompTab('buscar');
      setCompElegido(null);
      const cmp = r.lista[r.primeraNueva]!;
      void resolverComp(r.primeraNueva, cmp, 'buscar');
    }
  }, [sugerirComplementarios, resolverComp, medirEscenario]);

  // Buscar mientras se escribe (como el principal): cada palabra afina. Gratis.
  useEffect(() => {
    if (compTab !== 'buscar') return;
    const texto = compQ.trim();
    if (!texto || texto === compUltimaBusquedaRef.current) return;
    if (modoLista === 'adicionales' && compActivo != null) {
      const cmp = complementarios[compActivo];
      if (!cmp) return;
      const t = setTimeout(() => void resolverComp(compActivo, cmp, 'buscar', compQ), 350);
      return () => clearTimeout(t);
    }
    if (modoLista === 'instalaciones' && instalActivo != null) {
      const t = setTimeout(() => void resolverInstal(instalActivo, 'buscar', compQ), 350);
      return () => clearTimeout(t);
    }
  }, [compQ, modoLista, compActivo, instalActivo, compTab, complementarios, resolverComp, resolverInstal]);

  // ---------------------------------------------------------------------------
  // «Lo que no trae el plano», en la lista ancha (mismo patrón que los adicionales).
  // ---------------------------------------------------------------------------
  // Ya en la hoja: se verifica contra la HP real (renglón sin selección con esa clave o descripción).
  const instalEnLaHoja = useCallback(
    (i: number) => {
      const it = instalPropuestas?.[i];
      return !!it && asignadoAInstal(it).length > 0;
    },
    [instalPropuestas, asignadoAInstal],
  );
  const instalNoVa = useCallback(
    (i: number) => {
      const it = instalPropuestas?.[i];
      return !!it && instalDescartados.has(normalizarNecesidad(nombreInstal(it)));
    },
    [instalPropuestas, instalDescartados],
  );
  const abrirInstal = useCallback(
    (i: number) => {
      setCompTab('buscar');
      setCompElegido(null);
      void resolverInstal(i, 'buscar');
    },
    [resolverInstal],
  );
  // Tras agregar o descartar la instalación `i`, la siguiente pendiente; si no queda, vuelve al dibujo.
  const avanzarInstal = useCallback(
    (i: number) => {
      const n = instalPropuestas?.length ?? 0;
      for (let paso = 1; paso < n; paso++) {
        const j = (i + paso) % n;
        if (!instalNoVa(j) && !instalEnLaHoja(j)) {
          abrirInstal(j);
          return;
        }
      }
      salirModoLista();
    },
    [instalPropuestas, instalNoVa, instalEnLaHoja, abrirInstal, salirModoLista],
  );
  // «Lo que no trae el plano»: pide la lista (una sola vez por plano: cuesta IA) y la muestra agrupada.
  const abrirInstalaciones = useCallback(async () => {
    let lista = instalPropuestas;
    if (!lista || lista.length === 0) lista = (await proponerInstalaciones())?.lista ?? null;
    if (!lista || lista.length === 0) return;
    medirEscenario();
    setDerechaAbierta(true);
    setModoLista('instalaciones');
    // Primero la lista agrupada, sin elegir nada: el usuario toca el que quiere revisar.
    setInstalActivo(null);
    setCompElegido(null);
    setCompSugeridos(null);
  }, [instalPropuestas, proponerInstalaciones, medirEscenario]);

  // «Proponer más» instalaciones: las nuevas al final; abre la primera nueva.
  const proponerMasInstalaciones = useCallback(async () => {
    if (!instalPropuestas || instalPropuestas.length === 0) return;
    const r = await proponerInstalaciones(instalPropuestas);
    if (r && r.primeraNueva != null) {
      setModoLista('instalaciones');
      setCompTab('buscar');
      setCompElegido(null);
      const it = r.lista[r.primeraNueva]!;
      setInstalActivo(r.primeraNueva);
      setCompSugeridos(null);
      setCompCantidadStr(it.cantidad != null ? String(it.cantidad) : '');
      void cargarCandidatos(it.consultaCatalogo, 'buscar');
    }
  }, [instalPropuestas, proponerInstalaciones, cargarCandidatos]);

  // Atajo: TODAS las pendientes de una vez como concepto propio (se generan con IA en el lote), con la
  // cantidad estimada (o 1 si no se pudo estimar: entra marcada para revisar igual). Vuelve al dibujo.
  const agregarInstalacionesPendientes = useCallback(async () => {
    if (!projectId || !fileId || !instalPropuestas) return;
    const pendientes = instalPropuestas
      .map((it, i) => ({ it, i }))
      .filter(({ i }) => !instalNoVa(i) && !instalEnLaHoja(i));
    if (pendientes.length === 0) return;
    const sinCantidad = pendientes.filter(({ it }) => it.cantidad == null).length;
    setInstalAgregando(true);
    try {
      const hechas = new Map(instalResueltos);
      for (const { it, i } of pendientes) {
        await agregarRenglonPorDescripcion(projectId, fileId, {
          descripcion: it.descripcion,
          unidad: it.unidadEsperada ?? undefined,
          cantidad: it.cantidad ?? 1,
          ...(it.cantidad != null ? { generador: fotoNoDibujado(it, it.cantidad, it.unidadEsperada ?? null) } : {}),
          rol: 'principal',
          partida: grupoDe(it).partida,
          revisar: true,
        });
        hechas.set(normalizarNecesidad(nombreInstal(it)), new Set(hechas.get(normalizarNecesidad(nombreInstal(it))) ?? []).add(it.descripcion));
      }
      setInstalResueltos(hechas);
      setHpAbierto(true);
      await refrescarRenglones();
      salirModoLista();
      dispatchToast(
        <Toast>
          <ToastTitle>
            {`${pendientes.length} ${pendientes.length === 1 ? 'concepto agregado' : 'conceptos agregados'} como supuesto. ${pendientes.length === 1 ? 'Revísalo' : 'Revísalos'} en la hoja.${
              sinCantidad > 0 ? ` ${sinCantidad} ${sinCantidad === 1 ? 'entró' : 'entraron'} sin cantidad: escríbela en la hoja.` : ''
            }`}
          </ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 4000 },
      );
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudieron agregar a la hoja.'}</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    } finally {
      setInstalAgregando(false);
    }
  }, [projectId, fileId, instalPropuestas, instalNoVa, instalEnLaHoja, instalResueltos, refrescarRenglones, salirModoLista, dispatchToast]);

  // «¿Este plano sirve para presupuestar?» (idea 10): veredicto y razones leídos del archivo, $0. Los
  // umbrales salen de la calibración (staff).
  const [paramsChequeo, setParamsChequeo] = useState<ParametrosChequeoPlano | null>(null);
  useEffect(() => {
    if (status !== 'ready' || paramsChequeo) return;
    void getParametrosChequeoPlano().then(setParamsChequeo);
  }, [status, paramsChequeo]);
  // «Revisar el dibujo» (solo DWG/DXF, detrás de su bandera): avisos de lo que del dibujo cambia las cantidades.
  const [censoDwg, setCensoDwg] = useState<CensoDelPlano | null>(null);
  const [avisoVisto, setAvisoVisto] = useState<{ n: number; handles: string[]; titulo: string } | null>(null);
  const esDwgParaDiagnostico =
    DIAGNOSTICO_DIBUJO_ENABLED &&
    status === 'ready' &&
    !!inventario &&
    origenPlano !== 'pdf_vision' &&
    inventario.estimadoVision !== true &&
    ifcAbierto == null &&
    !inventario.capas.some((c) => c.categoria != null);
  const escalaDiagnostico = useMemo(() => factorDeEscala(inventario, escalaElegida), [inventario, escalaElegida]);
  const decisionCapaDiag = useCallback(
    (capa: string) => decisionOverride[capa] ?? decisionDeCapa(capa, capasClasificadas),
    [decisionOverride, capasClasificadas],
  );
  const decisionBloqueDiag = useCallback(
    (nombre: string) => decisionOverride[nombre] ?? decisionDeBloque(nombre, capasClasificadas),
    [decisionOverride, capasClasificadas],
  );
  const { diagnostico: diagnosticoDibujo, leyendo: leyendoDibujo } = useDiagnosticoDelDibujo({
    habilitado: esDwgParaDiagnostico,
    clave: `${projectId}:${fileId}`,
    censo: censoDwg,
    inventario,
    escala: escalaDiagnostico,
    params: paramsChequeo ?? PARAMETROS_CHEQUEO_PLANO_DEFAULT,
    decisionCapa: decisionCapaDiag,
    decisionBloque: decisionBloqueDiag,
  });
  useEffect(() => setAvisoVisto(null), [projectId, fileId]);
  // «Revisar el modelo» (solo IFC, detrás de su bandera): lo mismo que el del dibujo, con la lista de piezas de cada problema.
  const [piezasVer, setPiezasVer] = useState<{ n: number; ids: number[]; titulo: string } | null>(null);
  const esIfcParaDiagnostico =
    DIAGNOSTICO_MODELO_ENABLED && status === 'ready' && !!inventario && (ifcAbierto != null || inventario.capas.some((c) => c.categoria != null));
  const diagnosticoModelo = useMemo(() => {
    if (!esIfcParaDiagnostico || !inventario) return null;
    try {
      const porCapa = new Map(inventario.capas.map((c) => [c.capa, c] as const));
      return diagnosticarModelo(
        {
          inventario,
          decisionCapa: (capa) => {
            const c = porCapa.get(capa);
            return decisionOverride[capa] ?? (c?.categoria != null ? decisionIfc(c.categoria, c.ifc?.supertipos) : null);
          },
          obraDe: obrasIfc.length > 1 ? (id) => obraDeElemento.get(id) : undefined,
        },
        paramsChequeo ?? PARAMETROS_CHEQUEO_PLANO_DEFAULT,
      );
    } catch {
      return null; // el diagnóstico nunca tumba la pantalla
    }
  }, [esIfcParaDiagnostico, inventario, decisionOverride, paramsChequeo, obrasIfc, obraDeElemento]);
  useEffect(() => setPiezasVer(null), [projectId, fileId]);
  const instColoresVer = useMemo(() => coloresDeMaterial(piezasVer?.ids), [piezasVer]);
  const comparacionDiagnostico = useMemo(
    () => (avisoVisto ? new Map(avisoVisto.handles.map((h) => [h, 'modified' as const])) : null),
    [avisoVisto],
  );
  const chequeoDelPlano = useMemo(() => {
    if (status !== 'ready' || !inventario) return null;
    const p = paramsChequeo ?? PARAMETROS_CHEQUEO_PLANO_DEFAULT;
    // Mismo criterio que `esPdfEstimado` / `esInventarioIfc` (se calculan más abajo, después del gate).
    if (origenPlano === 'pdf_vision' || inventario.estimadoVision === true) return chequearPlanoPdf(inventario);
    if (ifcAbierto != null || inventario.capas.some((c) => c.categoria != null)) return chequearModeloIfc(inventario, p, diagnosticoModelo);
    return chequearPlanoDwg(inventario, p, escalaElegida, diagnosticoDibujo);
  }, [status, inventario, paramsChequeo, origenPlano, ifcAbierto, escalaElegida, diagnosticoDibujo, diagnosticoModelo]);

  // «Armar el presupuesto» (idea 1): el asistente y el alto de muros con que mide (arranca con el del
  // panel o el que la IA leyó del plano).
  const [armarAbierto, setArmarAbierto] = useState(false);
  // «Rehacer la hoja»: con la hoja ya armada se pregunta antes; en modo 'rehacer' se arma todo el plano y la
  // hoja anterior sigue intacta hasta el botón final (que la reemplaza en una sola transacción).
  const [modoArmado, setModoArmado] = useState<'faltante' | 'rehacer'>('faltante');
  const [renglonesVistosArmado, setRenglonesVistosArmado] = useState<string[]>([]);
  const [yaArmada, setYaArmada] = useState<TrabajoEnRiesgoPlano | null>(null);
  // Lo que se pierde al rehacer, guardado al elegir «Rehacer» (el diálogo de aviso se cierra y esto sigue).
  const [rehacerInfo, setRehacerInfo] = useState<TrabajoEnRiesgoPlano | null>(null);
  // Preguntas de «Antes de armar» que la persona contestó «no» (no se vuelven a hacer en este plano).
  const [descartadasArmado, setDescartadasArmado] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => setDescartadasArmado(new Set()), [fileId]);
  // Consultas de IA de complementarios y lo que no trae el plano: en lotes de a 4 a la vez.
  const limiteIaArmado = useRef(crearLimitador(4)).current;
  // La misma frase dentro de la corrida («aplanado» de dos muros) se consulta UNA vez.
  const cacheExtraArmado = useRef(new Map<string, Promise<{ opciones: OpcionArmado[]; noEsExacto: string | null }>>()).current;
  // Los complementarios de un mismo concepto se piden UNA vez por corrida (ver `proponerArmado`).
  const cacheComplementosArmado = useRef(new Map<string, Promise<PlanoComplementario[]>>()).current;
  const cacheMagnitudArmado = useRef(new Map<string, Promise<PlanoMagnitudConcepto | null>>()).current;
  // El material que el modelo no dice, contestado en «Antes de armar» (por clase IFC).
  const [materialArmado, setMaterialArmado] = useState<Record<string, string>>({});
  useEffect(() => setMaterialArmado({}), [fileId]);
  // Qué material vale la pena preguntar (lo que el catálogo del cliente distingue): lo pide CargadorDePreguntasDeMaterial.
  const [preguntasDeMaterial, setPreguntasDeMaterial] = useState<EstadoPreguntasMaterial>(null);
  useEffect(() => setPreguntasDeMaterial(null), [fileId]);
  // Lo que quien cotiza dijo que se construye en una superficie que el plano dibuja como contorno
  // («firme de concreto»): con eso se busca su concepto y «lo que no trae el plano» no lo repite.
  const [queEsArmado, setQueEsArmado] = useState<Record<string, string>>({});
  useEffect(() => setQueEsArmado({}), [fileId]);
  // Lo que ya se propuso como complementario en esta corrida y los conceptos de los principales: la IA no
  // lo repite en otro elemento (el dintel de la puerta cuando el muro ya lleva su cerramiento corrido).
  const yaPropuestoArmado = useRef<string[]>([]);
  const principalesArmado = useRef(new Set<string>());
  // Con qué datos pidió cada renglón sus complementarios: la revisión final le cuelga lo que falte con ellos.
  const paramsComplementosArmado = useRef(new Map<string, Parameters<typeof armarComplementarios>[0]>());
  // «Conceptos por tipo»: lo que la memoria y el plano ya saben de los tipos de esta corrida (una consulta por corrida).
  const memoriaArmado = useRef<Promise<{ reconocer: ReconocerTiposResponse | null; plano: TiposDelPlanoResponse | null } | null> | null>(null);
  const [decididosPorTipos, setDecididosPorTipos] = useState<Set<string>>(new Set());
  // El inventario para el que ya se leyó la memoria (mientras no coincida, «Antes de armar» espera: no pregunta de más).
  const [memoriaLeidaDe, setMemoriaLeidaDe] = useState<unknown>(null);
  // «Asignar conceptos a elementos»: el diálogo (su único punto de entrada es «Antes de armar»), un contador para releer la memoria
  // al aplicarlo y el resumen que se ve en «Antes de armar».
  const [tiposAbierto, setTiposAbierto] = useState(false);
  // Tipo con el que abre «Asignar conceptos» cuando se llega desde una fila del panel «Lo que trae el modelo».
  const [tipoInicialDelDialogo, setTipoInicialDelDialogo] = useState<string | null>(null);
  const [memoriaTick, setMemoriaTick] = useState(0);
  // Al abrir el plano se leen las asociaciones recordadas (sin comando): cuántos elementos ya tienen concepto.
  const [resumenPlano, setResumenPlano] = useState<{ reconocidos: number; total: number } | null>(null);
  const [resumenTipos, setResumenTipos] = useState<{ reconocidos: number; sugeridos: number; conflictos: number; noVigentes: number; total: number } | null>(null);
  useEffect(() => {
    // Con el armado cerrado no queda memoria cargada ni particiones por pieza (nada vivo que mida de más).
    memoriaResuelta.current = null;
    particiones.current.clear();
    if (armarAbierto) {
      memoriaArmado.current = null;
      cacheExtraArmado.clear();
      cacheComplementosArmado.clear();
      cacheMagnitudArmado.clear();
      yaPropuestoArmado.current = [];
      principalesArmado.current = new Set();
      paramsComplementosArmado.current = new Map();
    }
  }, [armarAbierto, cacheExtraArmado, cacheComplementosArmado, cacheMagnitudArmado]);
  // Staff: borrar la hoja de presupuesto COMPLETA del proyecto (todos sus planos), con confirmación.
  const [borrarHojaAbierto, setBorrarHojaAbierto] = useState(false);
  const [compartirAbierto, setCompartirAbierto] = useState(false);
  // «Cambió el plano» (idea 4, IFC): subir la revisión nueva del modelo y ver cuánto cambia la hoja.
  const [cambioAbierto, setCambioAbierto] = useState(false);
  // «Avance de obra» (idea 11, IFC): el video de cómo fue avanzando la obra, con lo que mandó OPUS 27.
  const [avanceAbierto, setAvanceAbierto] = useState(false);
  // Un diálogo con su propio visor (Avance de obra, Cambió el plano) tapa la página: el visor 2D destruye el motor del anterior y el 3D
  // duplicaría el modelo en memoria. Se desmonta el de atrás mientras el diálogo está abierto y se vuelve a montar al cerrarlo.
  const visorTapado = avanceAbierto || cambioAbierto;
  const [borrandoHoja, setBorrandoHoja] = useState(false);
  const [altoArmado, setAltoArmado] = useState('');
  // El alto con que se mide: el del asistente si ya se abrió; si no (p. ej. «Poner en la hoja lo asignado»), el que ya se sabe del plano.
  const altoEfectivoArmado = altoArmado.trim() ? altoArmado : altoStr;
  useEffect(() => {
    if (armarAbierto) setAltoArmado((prev) => (prev.trim() ? prev : altoStr));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armarAbierto]);
  useEffect(() => setAltoArmado(''), [fileId]);
  // De dónde salió el alto de muros con que se mide al armar (lo dice el número generador).
  const altoArmadoDeDonde =
    alturaProp?.alturaM != null && altoArmado === String(alturaProp.alturaM)
      ? alturaProp.razon || 'Se leyó de las anotaciones del plano.'
      : 'Lo escribiste tú al armar el presupuesto.';

  // ---------------------------------------------------------------------------
  // Gate: si el flag está off o no es staff, no existe la pantalla.
  // (El backend igual devuelve 404; esto es la otra mitad.)
  // ---------------------------------------------------------------------------
  if (!visible) return <Navigate to="/projects" replace />;

  const proyectoActual = projects.find((p) => p.id === projectId) ?? null;
  const archivoActual = files.find((f) => f.id === fileId) ?? null;
  // Números generadores: el croquis de un renglón (o de una de sus líneas) en el visor abierto. IFC: sus
  // piezas (o sus tipos); DWG: sus capas (en su planta) y bloques. Lo pinta y lo devuelve el visor.
  // El mismo croquis (los mismos elementos del mismo plano) se toma una vez: imprimir todo el plano repite
  // los de un principal en cada uno de sus adicionales.
  const croquisTomados = croquisTomadosRef.current;
  const capturarCroquisRenglon: CapturarCroquis = async (r, linea) => {
    const lineas = (linea ? [linea] : r.generador?.lineas ?? []).filter((l) => l.elemento);
    const sels = seleccionesDe(r);
    // Las líneas numeradas se rotulan en el croquis con su número (la clave de la marca es la de la tabla).
    const numeradas = [...numerosDeLineas(r.generador?.lineas ?? []).keys()].filter((l) => !linea || l === linea);
    const tomar = async <P, M>(capturar: ((p: P, m?: M) => Promise<Croquis | null>) | null, pedido: P, marcas?: M) => {
      if (!capturar) return null;
      const k = `${fileId}|${JSON.stringify(pedido)}|${JSON.stringify(marcas ?? null)}`;
      if (croquisTomados.has(k)) return croquisTomados.get(k)!;
      const c = await capturar(pedido, marcas);
      if (c) croquisTomados.set(k, c);
      return c;
    };
    const elementoDe = (e: { tipo: 'capa' | 'bloque'; clave: string; vista?: string }): ElementoParaCroquis => {
      const ids = e.tipo === 'bloque' ? sels.find((s) => s.tipo === 'bloque' && s.clave === e.clave)?.ids : undefined;
      return { tipo: e.tipo, clave: e.clave, ...(e.vista ? { vista: e.vista } : {}), ...(ids ? { ids } : {}) };
    };
    if (ifcAbierto) {
      const marcas = numeradas.flatMap((l) => {
        const k = claveDeMarca(l);
        if (!k) return [];
        return [l.pieza ? { k, piezas: [Number(l.pieza)] } : { k, etiquetas: [l.elemento!.clave] }];
      });
      const unicas = [...new Map(marcas.map((m) => [m.k, m])).values()];
      const ifc = (p: PedidoCroquisIfc) => (croquisIfcRef.current ? croquisIfcRef.current(p) : Promise.resolve(null));
      if (lineas.length) {
        return tomar(ifc, {
          piezas: lineas.flatMap((l) => (l.pieza ? [Number(l.pieza)] : [])),
          etiquetas: [...new Set(lineas.filter((l) => !l.pieza).map((l) => l.elemento!.clave))],
          marcas: unicas,
        });
      }
      return tomar(ifc, {
        piezas: sels.flatMap((s) => (s.ids ?? []).map(Number)).filter((n) => Number.isFinite(n)),
        etiquetas: sels.filter((s) => !s.ids?.length).map((s) => s.clave),
      });
    }
    const porClave = new Map<string, ElementoParaCroquis>();
    for (const e of lineas.length ? lineas.map((l) => l.elemento!) : sels) porClave.set(claveDeElemento(e), elementoDe(e));
    const marcas = [
      ...new Map(
        numeradas.flatMap((l) => {
          const k = claveDeMarca(l);
          return k && l.elemento ? [[k, { k, elementos: [elementoDe(l.elemento)] }] as [string, { k: string; elementos: ElementoParaCroquis[] }]] : [];
        }),
      ).values(),
    ];
    return tomar(croquisDwgRef.current, [...porClave.values()], marcas);
  };
  // El inventario viene de un IFC cuando sus capas traen categoría (agrupación por clase de elemento).
  // En IFC no hay dicotomía capas/bloques, así que la UI cambia (sin sección de Bloques).
  // Un IFC abierto usa SIEMPRE la redacción de IFC, aunque su lista salga vacía (antes caía en la de DWG,
  // "Capas / Bloques").
  const esInventarioIfc = ifcAbierto != null || (inventario?.capas ?? []).some((c) => c.categoria != null);

  // ¿Mostrar el visor CAD nuevo (mlightcad) en vez del render SVG? Solo con el flag prendido, plano
  // CAD 2D (no IFC) y listo. El IFC sigue con su visor 3D; el SVG queda como camino de siempre.
  const usarMlightcad =
    PLANO_VIEWER_MLIGHTCAD && status === 'ready' && !ifcAbierto && !esIfc(archivoActual) && !esPdf(archivoActual);

  // PDF leído por visión: cuantifica ESTIMADO. Se avisa en pantalla (Sebastián, opción (a)).
  const esPdfEstimado = origenPlano === 'pdf_vision' || inventario?.estimadoVision === true;

  // Entrada limpia: mientras NO haya plano abierto, los paneles laterales colapsan solos para que
  // el usuario nuevo vea una sola cosa que hacer (elegir plano). Al abrir uno vuelven a su estado.
  const sinPlano = status === 'idle' && !ifcAbierto;
  const leftAbiertoEff = leftAbierto && !sinPlano;
  const catAbiertoEff = catAbierto && !sinPlano;
  const derechaAbiertaEff = derechaAbierta && !sinPlano;

  // Lista de resultados: si el usuario tecleó, manda la búsqueda por texto;
  // si la caja está vacía, mandan las sugerencias por IA de la selección actual.
  // Pestaña "Buscar" = resultados del texto; "Sugeridos" = lo que propuso la IA para esta selección.
  // Concepto propio (no está en catálogo) → sintético con IA. A la HP va el NOMBRE del elemento del CAD
  // (sembrado, editable aquí); el detalle del CAD se conserva para armar el APU. Se muestra en el panel
  // derecho o en la lista ancha (modo «eligiendo»), nunca en los dos.
  const bloqueCrear = catalogSel ? (
<div
                      className={styles.noShrink}
                      style={{
                        marginTop: 6, padding: '10px 12px', borderRadius: 8,
                        background: 'var(--bg-surface, rgba(255,255,255,0.03))',
                        border: '1px dashed var(--border-subtle)',
                        display: 'flex', flexDirection: 'column', gap: 8,
                      }}
                    >
                      <span className={styles.fieldLabel} style={{ marginTop: 0 }}>
                        ¿No está en el catálogo? Agrégalo como concepto propio
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                        Tomamos el nombre del elemento del plano. Ajústalo si hace falta; con eso y el
                        detalle del plano se genera el análisis con IA.
                      </span>
                      <Textarea
                        aria-label="Descripción del concepto a generar con IA"
                        value={descSinteticoStr}
                        resize="vertical"
                        onChange={(_, d) => {
                          descEditadaRef.current = true;
                          setDescSinteticoStr(d.value);
                        }}
                        placeholder="Describe el concepto tal como debe ir en el presupuesto"
                      />
                      <NotasDelDetalle
                        projectId={projectId}
                        texto={descSinteticoStr}
                        nombre={target?.seleccion.clave ?? null}
                      />
                      <Button
                        appearance="primary"
                        icon={<LightbulbRegular />}
                        disabled={!target || !descSinteticoStr.trim() || agregandoSintetico}
                        onClick={() => void agregarSinteticoPorDescripcion()}
                      >
                        {agregandoSintetico ? 'Agregando…' : 'Agregar a la hoja (se cotiza con IA)'}
                      </Button>
                    </div>
  ) : null;
  const usandoTexto = formaConcepto === 'buscar';
  const sugerenciasVigentes = target != null && sugerenciasParaKey === target.key;
  const items: ConceptoItem[] = usandoTexto
    ? debouncedQ.length > 0 ? resultados : []
    : sugerenciasVigentes
      ? sugerencias
      : [];
  const cargandoLista = usandoTexto ? buscando : sugiriendo;
  const cantidadNum = Number(cantidadStr.replace(',', '.'));
  const importeAgregar =
    conceptoElegido?.precio != null && Number.isFinite(cantidadNum) && cantidadNum > 0
      ? conceptoElegido.precio * cantidadNum
      : null;

  // ---------------------------------------------------------------------------
  // Modo «eligiendo»: lo que se pinta (la lista ancha, la miniatura, el adicional activo).
  // ---------------------------------------------------------------------------
  // Nombre corto de lo seleccionado ("A-MUROS", "A-MUROS · Planta baja").
  // En un IFC, el mismo nombre legible que la lista de la izquierda ("Muro 24 cm"), no el del tipo del modelo.
  const capaIfcSel =
    target && target.medidas ? inventario?.capas.find((c) => c.capa === target.seleccion.clave) : undefined;
  const nombreSel = capaIfcSel
    ? nombreLegibleTipoIfc(capaIfcSel)
    : target
      ? target.etiqueta.replace(/^Capa:\s*/, '')
      : '';
  // La medida en una línea, para el paso 1 plegado.
  const resumenMedida = (() => {
    if (!target) return '';
    if (target.medidas) return target.detalle;
    const alto = Number(altoStr.replace(',', '.'));
    const largo = target.medidoEnMetros ? target.cantidadDefault : null;
    // Muro con huecos y sus altos: la misma área neta que las tarjetas y la cantidad.
    const area =
      (target.muroVanos ? cuentaVanos?.netoM2 ?? null : null) ??
      target.areaMuroPorPlantas ??
      (target.areaM2 != null && target.areaM2 > 0 && !target.esMuroDobleTrazo
        ? target.areaM2
        : largo != null && alto > 0
          ? largo * alto
          : null);
    const partes = [
      area != null ? `${fmtM(area)} m²` : largo != null ? `${fmtM(largo)} m` : target.detalle,
      espesorStr.trim() ? `ancho ${espesorStr.trim()}` : null,
    ].filter(Boolean);
    return partes.join(' · ');
  })();
  // ¿Hay visor que mostrar? (si no, el escenario no ocupa lugar y se ve el aviso que toque).
  const hayEscenario =
    (ifcAbierto != null && !!projectId) ||
    (usarMlightcad && !!projectId && !!fileId) ||
    (status === 'ready' && !ifcAbierto && !usarMlightcad && !esPdfEstimado);
  // Miniatura: caja fija (más chica en pantallas angostas, para dejarle ancho a la lista). El escenario
  // del visor se escala y se recorta (clip-path) para que lo SELECCIONADO llene la caja: el visor CAD dice
  // dónde está en pantalla (`cajaMini`); sin ese dato (IFC) se llena la caja con el centro de la vista.
  const MINI_W = cuerpoAngosto ? 208 : 268;
  const MINI_H = Math.round((MINI_W * 150) / 268);
  const mini = (() => {
    if (!tamEscenario) return null;
    const t = tamEscenario;
    let s = Math.max(MINI_W / t.w, MINI_H / t.h);
    let cx = t.w / 2;
    let cy = t.h / 2;
    if (cajaMini && cajaMini.w > 4 && cajaMini.h > 4) {
      // Un margen alrededor de lo seleccionado; sin agrandar más de 2× (se vería borroso).
      s = Math.min(MINI_W / (cajaMini.w * 1.12 + 12), MINI_H / (cajaMini.h * 1.12 + 12), 2);
      cx = cajaMini.x + cajaMini.w / 2;
      cy = cajaMini.y + cajaMini.h / 2;
    }
    const vw = MINI_W / s;
    const vh = MINI_H / s;
    const x0 = cx - vw / 2;
    const y0 = cy - vh / 2;
    const p0 = (n: number) => Math.max(0, n);
    return {
      s,
      left: 15 - x0 * s,
      top: 15 - y0 * s,
      clip: `inset(${p0(y0)}px ${p0(t.w - (x0 + vw))}px ${p0(t.h - (y0 + vh))}px ${p0(x0)}px)`,
    };
  })();

  const compAct = compActivo != null ? complementarios[compActivo] ?? null : null;
  const derAct = compAct ? cantidadDerivada(compAct) : null;
  const deQueAct = compAct ? DERIVA_DE_TEXTO[compAct.derivaDe] : null;
  const compCantEdit = Number(compCantidadStr.replace(',', '.'));
  const compCantidad = compCantEdit > 0 ? compCantEdit : derAct?.cantidad ?? null;
  const compImporte =
    compElegido?.precio != null && compCantidad != null && compCantidad > 0 ? compElegido.precio * compCantidad : null;
  const pendientesAdic = complementarios.filter((_, i) => !compEnLaHoja(i) && !compNoVa(i)).length;
  const avisoAsigComp = compAct
    ? avisoYaAsignado(compAct.necesidad, derAct?.cantidad ?? null, compAct.unidadEsperada, asignadoAComp(compAct))
    : null;

  const abrirAdicional = (i: number) => {
    medirEscenario();
    setDerechaAbierta(true);
    setModoLista('adicionales');
    const k = normalizarNecesidad(complementarios[i]?.necesidad ?? '');
    setCompDescartados((prev) => {
      if (!prev.has(k)) return prev;
      const n = new Set(prev);
      n.delete(k);
      return n;
    });
    abrirComp(i);
  };
  const agregarAdicActivo = async () => {
    if (compActivo == null || !compAct) return;
    const i = compActivo;
    setCompAgregando(true);
    try {
      const ok =
        compTab === 'generar'
          ? await agregarComplementoSintetico(compAct, i)
          : compElegido
            ? await asignarComplemento(compElegido, compAct, i)
            : false;
      if (ok) avanzarComp(i);
    } finally {
      setCompAgregando(false);
    }
  };
  const descartarAdicActivo = () => {
    if (compActivo == null) return;
    const i = compActivo;
    const k = normalizarNecesidad(complementarios[i]?.necesidad ?? '');
    setCompDescartados((prev) => new Set(prev).add(k));
    avanzarComp(i);
  };

  // Una fila de la lista de lo que se resuelve en la lista ancha (adicional o instalación): la misma en el
  // panel derecho y en la columna izquierda.
  const renderFila = (f: {
    key: string; titulo: string; badge: { texto: string; fuerte: boolean } | null; estado: string;
    enHoja: boolean; noVa: boolean; activo: boolean; onClick: () => void;
  }) => (
    <button
      key={f.key}
      type="button"
      className={mergeClasses(
        styles.adicFila,
        f.enHoja && styles.adicFilaHecha,
        f.noVa && styles.adicFilaNoVa,
        f.activo && styles.adicFilaActiva,
      )}
      aria-current={f.activo ? 'true' : undefined}
      onClick={f.onClick}
    >
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontWeight: 600, fontSize: 12.5 }}>
          {f.enHoja ? '✓ ' : ''}
          {f.titulo}
        </span>
        {f.badge && (
          <span style={{ fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap', color: f.badge.fuerte ? 'var(--accent)' : 'var(--text-tertiary)' }}>
            {f.badge.texto}
          </span>
        )}
      </span>
      <span style={{ fontSize: 11, color: f.enHoja ? 'var(--ok)' : 'var(--text-secondary)' }}>{f.estado}</span>
    </button>
  );
  const textoPendiente = (unidad: string | null, cantidad: number | null) =>
    `Pendiente${unidad ? ` · ${unidad}` : ''} · ${
      cantidad != null ? (Math.round(cantidad * 100) / 100).toLocaleString('es-MX') : 'cantidad a definir'
    }`;
  const renderFilaAdic = (cmp: PlanoComplementario, i: number) => {
    const enHoja = compEnLaHoja(i);
    const noVa = !enHoja && compNoVa(i);
    const activo = modoLista === 'adicionales' && compActivo === i;
    return renderFila({
      key: `${cmp.necesidad}:${i}`,
      titulo: cmp.necesidad,
      badge: { texto: cmp.tipico ? 'casi siempre va' : 'a veces va', fuerte: cmp.tipico },
      estado: enHoja
        ? 'En la hoja'
        : noVa ? 'No va en esta obra' : activo ? 'Eligiendo su concepto…' : textoPendiente(cmp.unidadEsperada, cantidadDerivada(cmp).cantidad),
      enHoja, noVa, activo,
      onClick: () => abrirAdicional(i),
    });
  };

  // Lo que se va a agregar: el concepto elegido en la lista, o el concepto propio («Generar con IA»).
  const renderBloqueElegidoSolo = (nombrePropio: string) =>
    compTab === 'generar' ? (
      <div style={{ padding: 12, borderRadius: 8, border: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
        Va a la hoja como concepto propio, «<b style={{ color: 'var(--text-primary)' }}>{nombrePropio}</b>», y se cotiza con IA.
      </div>
    ) : compElegido ? (
      <div style={{ padding: 12, borderRadius: 8, background: 'var(--accent-weak)', border: '1px solid var(--accent)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45, color: 'var(--text-primary)' }}>{compElegido.descripcion}</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {compElegido.clave}
          {compElegido.unidad ? ` · ${compElegido.unidad}` : ''}
          {` · ${compElegido.precio != null ? fmtMoneda(compElegido.precio) : 'sin precio'}`}
        </span>
      </div>
    ) : (
      <div style={{ padding: 12, borderRadius: 8, border: '1px dashed var(--border-subtle)', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
        Elige su concepto en la lista.
      </div>
    );
  // Lo elegido + las notas de las láminas que se le pegan (y si lo contradicen).
  const renderBloqueElegido = (nombrePropio: string) => {
    const texto = compTab === 'generar' ? nombrePropio : compElegido?.descripcion ?? '';
    return (
      <>
        {renderBloqueElegidoSolo(nombrePropio)}
        {texto && (
          <NotasDelDetalle
            projectId={projectId}
            texto={texto}
            nombre={modoLista === 'adicionales' ? target?.seleccion.clave ?? null : null}
          />
        )}
      </>
    );
  };

  // Panel derecho del paso 3 en el modo: el adicional activo, su cantidad y qué hacer con él.
  // Las notas de las láminas pegadas al concepto principal de la selección (con ✕ para quitar).
  const renglonPrincipal = principalActual ? renglones.find((x) => x.id === principalActual.id) ?? null : null;
  const notasDelPrincipal = renglonPrincipal ? (
    <div style={{ marginTop: 6 }}>
      <NotasDelDetalle
        projectId={projectId}
        texto={renglonPrincipal.conceptoDescripcion}
        nombre={renglonPrincipal.seleccion?.clave ?? null}
        notasPegadas={renglonPrincipal.specRefs}
        onQuitar={puedeEditar ? (n) => quitarNota(renglonPrincipal, n) : undefined}
      />
    </div>
  ) : null;
  const panelAdicional = (
    <div className={styles.section}>
      <div style={{ padding: '10px 40px 10px 14px', display: 'flex', flexDirection: 'column', gap: 3, borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ok)' }}>✓ En la hoja · {nombreSel}</span>
        {principalActual && (
          <span
            title={principalActual.descripcion}
            style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {principalActual.clave ? `${principalActual.clave} · ` : ''}
            {principalActual.descripcion}
          </span>
        )}
        {notasDelPrincipal}
      </div>
      {!compAct ? (
        <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {complementarios.length === 0
              ? 'Toca «¿Qué más lleva?» a la izquierda para ver lo que suele acompañar a este concepto. Si no hace falta, vuelve al dibujo.'
              : 'Elige un adicional de la lista de la izquierda.'}
          </span>
          <Button appearance="secondary" onClick={salirModoLista}>Volver al dibujo</Button>
        </div>
      ) : (
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span className={styles.fieldLabel}>
            Adicional {(compActivo ?? 0) + 1} de {complementarios.length}
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{compAct.necesidad}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{compAct.razon}</span>
          <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className={styles.fieldLabel}>
              Cantidad{compAct.unidadEsperada ? ` (${compAct.unidadEsperada})` : ''}
            </span>
            <Input
              aria-label="Cantidad del adicional"
              type="number"
              style={{ width: 150 }}
              value={compCantidadStr}
              placeholder="a definir"
              onChange={(_, d) => setCompCantidadStr(d.value)}
            />
            <span style={{ fontSize: 11, color: derAct?.cantidad != null ? 'var(--text-tertiary)' : 'var(--warn)', lineHeight: 1.4 }}>
              {derAct?.cantidad != null
                ? `Sale ${derAct.usaLargoConVanos && largoConVanosM != null ? `del largo del muro con sus huecos: ${fmtM(largoConVanosM)} m` : deQueAct ?? 'del elemento'}${compAct.factor && compAct.factor > 0 && compAct.factor !== 1 ? ` × ${compAct.factor}` : ''}. Ajústala si hace falta.`
                : 'A definir: el plano no la trae. Escríbela tú.'}
            </span>
            {avisoAsigComp && (
              <span style={{ fontSize: 11, color: 'var(--warn)', lineHeight: 1.4 }}>{avisoAsigComp}</span>
            )}
            {compAct.dimensionesFaltantes && compAct.dimensionesFaltantes.length > 0 && (
              <span style={{ fontSize: 11, color: 'var(--warn)', lineHeight: 1.4 }}>
                Supuse esto (revísalo):{' '}
                {compAct.dimensionesFaltantes
                  .map((d) => `${d.nombre} = ${d.valorSugerido}${d.unidad ? ` ${d.unidad}` : ''}`)
                  .join(' · ')}
              </span>
            )}
          </div>
          {renderBloqueElegido(compAct.necesidad)}
          <Button
            appearance="primary"
            disabled={compAgregando || (compTab !== 'generar' && !compElegido) || !(compCantidad != null && compCantidad > 0)}
            onClick={() => void agregarAdicActivo()}
          >
            {compAgregando
              ? 'Agregando…'
              : compTab !== 'generar' && !compElegido
                ? 'Elige un concepto de la lista'
                : !(compCantidad != null && compCantidad > 0)
                  ? 'Escribe la cantidad'
                  : `Agregar a la hoja${compTab === 'generar' ? '' : compImporte != null ? ` · ${fmtMoneda(compImporte)}` : ''}`}
          </Button>
          <Button appearance="secondary" disabled={compAgregando} onClick={descartarAdicActivo}>
            No va en esta obra
          </Button>
          <Button appearance="transparent" onClick={salirModoLista}>
            {pendientesAdic > 1 ? 'Dejar los demás para después y volver al dibujo' : 'Volver al dibujo'}
          </Button>
        </div>
      )}
    </div>
  );

  // Por qué «Lo que no trae el plano» está apagado (se dice al pasar el mouse, no se apaga en silencio).
  const motivoNoDibujadoApagado = !fileId
    ? 'Abre un plano: se propone a partir de lo que trae ese plano.'
    : !inventario
      ? 'El plano todavía se está leyendo.'
      : null;
  // --- Lo que no trae el plano (modo «instalaciones») ---
  const instAct = instalActivo != null ? instalPropuestas?.[instalActivo] ?? null : null;
  const instCantidad = compCantEdit > 0 ? compCantEdit : instAct?.cantidad ?? null;
  const instImporte =
    compElegido?.precio != null && instCantidad != null && instCantidad > 0 ? compElegido.precio * instCantidad : null;
  const pendientesInstal = (instalPropuestas ?? []).filter((_, i) => !instalEnLaHoja(i) && !instalNoVa(i)).length;
  const avisoAsigInstal = instAct
    ? avisoYaAsignado(nombreInstal(instAct), instAct.cantidad, instAct.unidadEsperada, asignadoAInstal(instAct))
    : null;
  // Las pendientes sin cantidad estimada: el atajo las agrega con 1 y lo avisa.
  const pendientesSinCantidad = (instalPropuestas ?? []).filter(
    (it, i) => !instalEnLaHoja(i) && !instalNoVa(i) && it.cantidad == null,
  ).length;
  // ¿La unidad del concepto elegido es otra que la de la cantidad estimada? Solo comparación de texto
  // (minúsculas, sin espacios ni puntos): no hay tabla de equivalencias.
  const normUnidad = (u: string) => u.toLowerCase().replace(/[\s.]/g, '');
  const unidadDistintaInstal =
    compTab !== 'generar' && !!compElegido?.unidad && !!instAct?.unidadEsperada &&
    normUnidad(compElegido.unidad) !== normUnidad(instAct.unidadEsperada);
  const abrirInstalacion = (i: number) => {
    const it = instalPropuestas?.[i];
    const k = it ? normalizarNecesidad(nombreInstal(it)) : '';
    setInstalDescartados((prev) => {
      if (!prev.has(k)) return prev;
      const n = new Set(prev);
      n.delete(k);
      return n;
    });
    abrirInstal(i);
  };
  // Números generadores de lo que no trae el plano: el supuesto, de dónde sale; o la cantidad escrita a mano.
  const fotoDeInstal = (it: PlanoInstalacionSupuesta, cantidad: number, unidad: string | null): { generador: FotoGenerador; cantidadEscritaAMano?: boolean } => {
    if (it.cantidad == null) return { generador: fotoAMano(cantidad, unidad) };
    const generador = fotoNoDibujado(it, it.cantidad, unidad);
    return Math.abs(it.cantidad - cantidad) < 1e-9 ? { generador } : { generador, cantidadEscritaAMano: true };
  };
  const agregarInstalActivo = async () => {
    if (instalActivo == null || !instAct || !projectId || !fileId) return;
    if (!(instCantidad != null && instCantidad > 0)) return;
    const i = instalActivo;
    const elegido = compElegido;
    setCompAgregando(true);
    try {
      if (compTab === 'generar') {
        await agregarRenglonPorDescripcion(projectId, fileId, {
          descripcion: instAct.descripcion,
          unidad: instAct.unidadEsperada ?? undefined,
          cantidad: instCantidad,
          ...fotoDeInstal(instAct, instCantidad, instAct.unidadEsperada ?? null),
          rol: 'principal',
          partida: grupoDe(instAct).partida,
          revisar: true,
        });
        setInstalResueltos((prev) => conResuelto(prev, nombreInstal(instAct), instAct.descripcion));
      } else if (elegido) {
        // Del catálogo, pero en la partida de su grupo y marcado para revisar (es un supuesto).
        await asignarPlanoRenglon(projectId, fileId, {
          catalogId: elegido.catalogId || catalogSel || '',
          resourceClave: elegido.clave,
          cantidad: instCantidad,
          ...fotoDeInstal(instAct, instCantidad, elegido.unidad ?? instAct.unidadEsperada ?? null),
          rol: 'principal',
          partida: grupoDe(instAct).partida,
          revisar: true,
        });
        setInstalResueltos((prev) => conResuelto(prev, nombreInstal(instAct), elegido.clave));
      } else return;
      await refrescarRenglones();
      dispatchToast(
        <Toast><ToastTitle>Agregado a la hoja, marcado para revisar.</ToastTitle></Toast>,
        { intent: 'success', timeout: 3000 },
      );
      avanzarInstal(i);
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo agregar a la hoja.'}</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    } finally {
      setCompAgregando(false);
    }
  };
  const descartarInstalActivo = () => {
    if (instalActivo == null) return;
    const i = instalActivo;
    const k = instalPropuestas?.[i] ? normalizarNecesidad(nombreInstal(instalPropuestas[i]!)) : '';
    setInstalDescartados((prev) => new Set(prev).add(k));
    avanzarInstal(i);
  };
  const renderFilaInstal = (it: PlanoInstalacionSupuesta, i: number) => {
    const enHoja = instalEnLaHoja(i);
    const noVa = !enHoja && instalNoVa(i);
    const activo = modoLista === 'instalaciones' && instalActivo === i;
    return renderFila({
      key: `${it.descripcion}:${i}`,
      titulo: it.nombre || it.descripcion,
      badge: null,
      estado: enHoja ? 'En la hoja, marcado para revisar' : noVa ? 'No va en esta obra' : activo ? 'Eligiendo su concepto…' : textoPendiente(it.unidadEsperada, it.cantidad),
      enHoja, noVa, activo,
      onClick: () => abrirInstalacion(i),
    });
  };
  // Grupos en su orden fijo; dentro de Instalaciones, disciplinas en el orden en que llegaron (abierta: la IA).
  const gruposInstal = GRUPOS_NO_DIBUJADO.filter((g) => (instalPropuestas ?? []).some((it) => grupoDe(it).id === g.id));
  const disciplinasInstal = [
    ...new Set((instalPropuestas ?? []).filter((it) => grupoDe(it).id === 'instalaciones').map((it) => it.disciplina.trim())),
  ];
  const panelInstal = (
    <div className={styles.section}>
      {!instAct ? (
        <div style={{ padding: '16px 40px 16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Elige un concepto de la lista de la izquierda.
          </span>
          <Button appearance="secondary" onClick={salirModoLista}>Volver al dibujo</Button>
        </div>
      ) : (
        <div style={{ padding: '14px 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span className={styles.fieldLabel} style={{ paddingRight: 26 }}>
            {grupoDe(instAct).titulo} · {(instalActivo ?? 0) + 1} de {instalPropuestas?.length ?? 0}
            {instAct.disciplina ? ` · ${instAct.disciplina}` : ''}
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{instAct.nombre || instAct.descripcion}</span>
          {instAct.razon && <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{instAct.razon}</span>}
          <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className={styles.fieldLabel}>
              Cantidad{instAct.unidadEsperada ? ` (${instAct.unidadEsperada})` : ''}
            </span>
            <Input
              aria-label="Cantidad"
              type="number"
              style={{ width: 150 }}
              value={compCantidadStr}
              placeholder="a definir"
              onChange={(_, d) => setCompCantidadStr(d.value)}
            />
            <span style={{ fontSize: 11, color: instAct.cantidad != null ? 'var(--text-tertiary)' : 'var(--warn)', lineHeight: 1.4 }}>
              {instAct.cantidad != null
                ? `${instAct.deDonde ? `${instAct.deDonde.replace(/\.$/, '')}.` : 'Estimada con lo que trae el plano.'} Ajústala si hace falta.`
                : 'A definir: el plano no trae con qué estimarla. Escríbela tú.'}
            </span>
            {avisoAsigInstal && (
              <span style={{ fontSize: 11, color: 'var(--warn)', lineHeight: 1.4 }}>{avisoAsigInstal}</span>
            )}
            <span style={{ fontSize: 11, color: 'var(--warn)', lineHeight: 1.4 }}>
              Es un supuesto: el plano no lo dibuja. Entra marcado para que lo revises.
            </span>
          </div>
          {renderBloqueElegido(instAct.descripcion)}
          {unidadDistintaInstal && compElegido?.unidad && instAct.unidadEsperada && (
            <span style={{ fontSize: 11.5, color: 'var(--warn)', lineHeight: 1.4 }}>
              Este concepto se cobra en {compElegido.unidad}; la cantidad se estimó en {instAct.unidadEsperada}. Revísala.
            </span>
          )}
          <Button
            appearance="primary"
            disabled={compAgregando || (compTab !== 'generar' && !compElegido) || !(instCantidad != null && instCantidad > 0)}
            onClick={() => void agregarInstalActivo()}
          >
            {compAgregando
              ? 'Agregando…'
              : compTab !== 'generar' && !compElegido
                ? 'Elige un concepto de la lista'
                : !(instCantidad != null && instCantidad > 0)
                  ? 'Escribe la cantidad'
                  : `Agregar a la hoja${compTab === 'generar' ? '' : instImporte != null ? ` · ${fmtMoneda(instImporte)}` : ''}`}
          </Button>
          <Button appearance="secondary" disabled={compAgregando} onClick={descartarInstalActivo}>
            No va en esta obra
          </Button>
          <Button appearance="transparent" onClick={salirModoLista}>
            {pendientesInstal > 1 ? 'Dejar las demás para después y volver al dibujo' : 'Volver al dibujo'}
          </Button>
        </div>
      )}
    </div>
  );
  // Lo que se resuelve en la lista ancha (adicional o instalación): su nombre y cómo se buscan sus candidatos.
  const itemAncho =
    modoLista === 'adicionales' && compAct && compActivo != null
      ? {
          nombre: compAct.necesidad,
          propio: compAct.necesidad,
          buscar: (t?: string) => void resolverComp(compActivo, compAct, 'buscar', t),
          sugerir: () => void resolverComp(compActivo, compAct, 'sugerir'),
        }
      : modoLista === 'instalaciones' && instAct && instalActivo != null
        ? {
            nombre: instAct.nombre || instAct.descripcion,
            propio: instAct.descripcion,
            buscar: (t?: string) => void resolverInstal(instalActivo, 'buscar', t),
            sugerir: () => void resolverInstal(instalActivo, 'sugerir'),
          }
        : null;

  // Lista ANCHA: ocupa el centro mientras se elige. Arriba a la izquierda queda el lugar de la miniatura
  // (el escenario del visor se encoge encima de ese botón; el clic lo atraviesa y vuelve al dibujo).
  const vistaAncha = (
    <div className={styles.modoLista}>
      <div className={styles.modoListaIzq} style={{ width: MINI_W + 28 }}>
        <button
          type="button"
          className={styles.miniatura}
          style={{ alignSelf: 'flex-start' }}
          onClick={salirModoLista}
          title="Volver al dibujo"
          aria-label="Agrandar el dibujo y volver"
        >
          <span style={{ display: 'block', width: MINI_W, height: MINI_H, background: 'var(--bg-page)' }} />
          <span className={styles.miniaturaPie} style={{ width: MINI_W, boxSizing: 'border-box' }}>
            {modoLista === 'instalaciones' ? 'Todo el plano · clic para volver' : `${nombreSel} marcado · clic para volver`}
          </span>
        </button>
        {modoLista === 'principal' && (
          <div style={{ padding: 12, borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0, overflowY: 'auto' }}>
            <span className={styles.fieldLabel}>Lo que seleccionaste</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{nombreSel}</span>
            {resumenMedida && <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{resumenMedida}</span>}
          </div>
        )}
        {modoLista === 'adicionales' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <span className={styles.fieldLabel}>3 · También lleva</span>
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
              Te proponemos lo que suele acompañarlo; tú decides cuáles van y con qué concepto.
            </span>
            {complementarios.map((cmp, i) => renderFilaAdic(cmp, i))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <Button
                appearance={complementarios.length > 0 ? 'subtle' : 'primary'}
                icon={<LightbulbRegular />}
                disabled={sugiriendoComp || !principalActual}
                onClick={() => void pedirAdicionales()}
                style={{ flexGrow: 1 }}
              >
                {sugiriendoComp
                  ? 'Consultando…'
                  : complementarios.length > 0
                    ? 'Proponer más'
                    : '¿Qué más lleva?'}
              </Button>
              <FieldHelp helpKey="planos.que_mas_lleva" />
            </div>
            {compNota && (
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>{compNota}</span>
            )}
          </div>
        )}
        {modoLista === 'instalaciones' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <span className={styles.fieldLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Lo que no trae el plano <FieldHelp helpKey="planos.instalaciones_faltan" />
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
              La obra lo lleva aunque el plano no lo dibuje: sale de sus cuartos y de su superficie. Tú decides qué va y con qué concepto.
            </span>
            {gruposInstal.map((g) => (
              <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginTop: 6 }}>{g.titulo}</span>
                {g.id === 'instalaciones'
                  ? disciplinasInstal.map((d) => (
                      <div key={d || '—'} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {d ? d.charAt(0).toUpperCase() + d.slice(1) : 'Otras'}
                        </span>
                        {(instalPropuestas ?? []).map((it, i) =>
                          grupoDe(it).id === 'instalaciones' && it.disciplina.trim() === d ? renderFilaInstal(it, i) : null,
                        )}
                      </div>
                    ))
                  : (instalPropuestas ?? []).map((it, i) => (grupoDe(it).id === g.id ? renderFilaInstal(it, i) : null))}
              </div>
            ))}
            <Button
              appearance="subtle"
              icon={<LightbulbRegular />}
              disabled={instalCargando}
              onClick={() => void proponerMasInstalaciones()}
              style={{ flexShrink: 0 }}
            >
              {instalCargando ? 'Consultando…' : 'Proponer más'}
            </Button>
            {pendientesInstal > 0 && (
              <Button
                appearance="secondary"
                disabled={instalAgregando}
                onClick={() => void agregarInstalacionesPendientes()}
                style={{ flexShrink: 0, marginTop: 6, height: 'auto', padding: '8px 10px', borderStyle: 'dashed', lineHeight: 1.35 }}
              >
                {instalAgregando
                  ? 'Agregando…'
                  : `Agregar las pendientes como concepto propio · se generan con IA${
                      pendientesSinCantidad > 0
                        ? ` · ${pendientesSinCantidad} ${pendientesSinCantidad === 1 ? 'entra' : 'entran'} sin cantidad`
                        : ''
                    }`}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className={styles.modoListaDer} style={cuerpoAngosto ? { padding: '14px 14px 0' } : undefined}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', minWidth: 0 }}>
            {modoLista === 'principal'
              ? '¿Qué concepto es?'
              : modoLista === 'instalaciones'
                ? instAct ? instAct.nombre || instAct.descripcion : 'Lo que no trae el plano'
                : compAct ? compAct.necesidad : 'También lleva'}
            <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>
              {modoLista === 'principal'
                ? ` · ${nombreSel}`
                : modoLista === 'instalaciones'
                  ? ` · ${instAct ? grupoDe(instAct).titulo.toLowerCase() : 'todo el plano'}`
                  : ` · para ${nombreSel}`}
            </span>
          </span>
          <Button appearance="secondary" onClick={salirModoLista} style={{ flexShrink: 0 }}>
            Volver al dibujo (Esc)
          </Button>
        </div>

        {modoLista === 'principal' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, flexShrink: 0, borderBottom: '1px solid var(--border-subtle)' }}>
              <TabList
                selectedValue={formaConcepto}
                onTabSelect={(_, d) => setFormaConcepto(d.value as typeof formaConcepto)}
              >
                <Tab value="buscar">Buscar</Tab>
                <FieldHelp helpKey="planos.buscar" variante="accion" />
                <Tab value="sugeridos">Recomendados</Tab>
                <FieldHelp helpKey="planos.recomendados" variante="accion" />
                <Tab value="crear">Generar con IA</Tab>
                <FieldHelp helpKey="planos.generar_ia" variante="accion" />
              </TabList>
            </div>
            {formaConcepto === 'crear' ? (
              <div style={{ marginTop: 8, maxWidth: 640 }}>
                {bloqueCrear ?? <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Elige un catálogo.</span>}
              </div>
            ) : (
              <>
                {formaConcepto === 'sugeridos' && !sugerenciasVigentes && (
                  <Button
                    appearance="secondary"
                    icon={<LightbulbRegular />}
                    disabled={!catalogSel || !target || sugiriendo}
                    onClick={() => void handleSugerir()}
                    style={{ marginTop: 12, alignSelf: 'flex-start', flexShrink: 0 }}
                  >
                    {sugiriendo ? 'Buscando conceptos…' : 'Ver recomendados de mis catálogos'}
                  </Button>
                )}
                {formaConcepto === 'buscar' && (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12, flexShrink: 0 }}>
                    <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                      Escribe qué buscas; cada palabra que agregas afina la lista.
                    </span>
                    <Input
                      aria-label="Buscar concepto en el catálogo"
                      autoFocus
                      placeholder="p. ej. muro de block"
                      contentBefore={<SearchRegular />}
                      value={q}
                      disabled={!catalogSel}
                      onChange={(_, d) => setQ(d.value)}
                    />
                  </label>
                )}
                <ListaConceptos
                  items={items}
                  elegidoId={conceptoElegido?.id ?? null}
                  onElegir={setConceptoElegido}
                  cargando={cargandoLista}
                  cargandoTexto={usandoTexto ? 'Buscando…' : 'Buscando conceptos…'}
                  conteo={
                    usandoTexto ? (
                      <>
                        {items.length === 1 ? '1 concepto' : `${items.length.toLocaleString('es-MX')} conceptos`}
                        {resultadosRecortados
                          ? ' (hay más: agrega otra palabra para afinar)'
                          : items.length > 1 ? ' · agrega otra palabra para afinar' : ''}
                      </>
                    ) : (
                      `Recomendados para ${nombreSel}, con el porqué de cada uno`
                    )
                  }
                  vacio={
                    !catalogSel
                      ? 'Elige un catálogo.'
                      : usandoTexto
                        ? debouncedQ.length === 0
                          ? 'Escribe arriba qué concepto buscas.'
                          : 'No hay conceptos con todas esas palabras. Quita una, o créalo en «Generar con IA».'
                        : sugerenciaNota ?? 'Pide sugerencias con el botón de arriba, o busca en «Buscar».'
                  }
                  aviso={usandoTexto ? null : sugerenciaFaltante}
                />
              </>
            )}
          </>
        ) : itemAncho ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, flexShrink: 0, borderBottom: '1px solid var(--border-subtle)' }}>
              <TabList
                selectedValue={compTab}
                onTabSelect={(_, d) => setCompTab(d.value as typeof compTab)}
              >
                <Tab value="buscar">Buscar</Tab>
                <FieldHelp helpKey="planos.buscar" variante="accion" />
                <Tab value="sugeridos">Recomendados</Tab>
                <FieldHelp helpKey="planos.recomendados" variante="accion" />
                <Tab value="generar">Generar con IA</Tab>
                <FieldHelp helpKey="planos.generar_ia" variante="accion" />
              </TabList>
            </div>
            {compTab === 'buscar' && (
              <>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12, flexShrink: 0 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                    Ya viene escrito lo que te proponemos; cámbialo si quieres. Cada palabra afina.
                  </span>
                  <Input
                    aria-label={`Buscar concepto para ${itemAncho.nombre}`}
                    contentBefore={<SearchRegular />}
                    value={compQ}
                    placeholder="Escribe qué buscas…"
                    onChange={(_, d) => setCompQ(d.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && compQ.trim()) {
                        e.preventDefault();
                        itemAncho.buscar(compQ);
                      }
                    }}
                  />
                </label>
                <ListaConceptos
                  items={compCandidatos}
                  elegidoId={compElegido?.id ?? null}
                  onElegir={setCompElegido}
                  cargando={compBuscando}
                  cargandoTexto="Buscando…"
                  conteo={
                    <>
                      {compCandidatos.length === 1 ? '1 concepto' : `${compCandidatos.length} conceptos`}
                      {compCandidatos.length > 1 ? ' · agrega otra palabra para afinar' : ''}
                    </>
                  }
                  vacio="No está en tus catálogos con esas palabras. Quita una, prueba «Recomendados» o «Generar con IA»."
                />
              </>
            )}
            {compTab === 'sugeridos' &&
              (compSugeridos == null && !compSugiriendo ? (
                <Button
                  appearance="secondary"
                  icon={<LightbulbRegular />}
                  onClick={() => itemAncho.sugerir()}
                  style={{ marginTop: 12, alignSelf: 'flex-start', flexShrink: 0 }}
                >
                  Ver recomendados de mis catálogos
                </Button>
              ) : (
                <ListaConceptos
                  items={compSugeridos ?? []}
                  elegidoId={compElegido?.id ?? null}
                  onElegir={setCompElegido}
                  cargando={compSugiriendo}
                  cargandoTexto="Buscando conceptos…"
                  conteo={`Recomendados para «${itemAncho.nombre}», con el porqué de cada uno`}
                  aviso={compSugeridosFaltante}
                  vacio="No encontramos conceptos para esto en tus catálogos. Prueba «Buscar» o «Generar con IA»."
                />
              ))}
            {compTab === 'generar' && (
              <div style={{ marginTop: 14, maxWidth: 560, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                ¿No está en tus catálogos? Agrégalo como concepto propio: a la hoja va
                «<b style={{ color: 'var(--text-primary)' }}>{itemAncho.propio}</b>» con su cantidad, y su análisis
                se genera con IA cuando cotices los conceptos propios. Lo agregas con el botón de la derecha.
              </div>
            )}
          </>
        ) : (
          <div style={{ marginTop: 16, maxWidth: 560, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {modoLista === 'instalaciones'
              ? 'Elige un concepto de la lista de la izquierda.'
              : complementarios.length === 0
                ? 'Toca «¿Qué más lleva?» a la izquierda para ver lo que suele acompañar a este concepto (aplanado, pintura, dala…).'
                : 'Elige un adicional de la lista de la izquierda.'}
          </div>
        )}
      </div>
    </div>
  );

  // Migas de pan (mismo componente y posición que la cotización). Sirven para VOLVER A LA LISTA DE
  // PLANOS: con un plano abierto, "Planos" es link a la galería y el último nivel es el plano.
  const nombrePlanoAbierto =
    archivoActual?.originalName ?? galeria.planos?.find((p) => p.fileId === archivoDeRuta)?.nombre ?? '…';
  const planosCrumbs = modoProyecto
    ? [
        { label: 'Inicio', to: '/projects' },
        { label: 'Proyectos', to: '/projects' },
        // Mientras carga (o reintenta) se ve «…», nunca un «Proyecto» genérico.
        { label: nombreProyectoRuta ?? proyectoActual?.name ?? '…', to: `/project/${proyectoDeRuta}` },
        archivoDeRuta ? { label: 'Planos', to: `/project/${proyectoDeRuta}/planos` } : { label: 'Planos' },
        ...(archivoDeRuta ? [{ label: nombrePlanoAbierto }] : []),
      ]
    : null;
  const puedeSubirPlanos = puedeEditar && !proyectoActual?.isExample;

  // "Empieza por lo grande" / "Siguiente que falta": lo que se presupuesta y aún no tiene concepto
  // principal, de lo que más pesa a lo que menos. IFC: por tipo, por volumen (siguienteTipoQueFalta).
  // DWG: capas que se construyen (por área medida y después por largo) y luego bloques (por cuántas
  // piezas); una capa que no se puede medir (sin escala) no se propone. Lo saltado va al final.
  type Siguiente = { tipo: 'capa' | 'bloque'; clave: string; texto: string };
  const candidatosSiguiente = (rehacer = false): Siguiente[] => {
    // «Rehacer la hoja»: todo el plano, como si la hoja estuviera vacía (la anterior sigue hasta reemplazarla).
    const capasConPrincipalAqui = rehacer ? new Set<string>() : capasConPrincipal;
    if (esInventarioIfc) {
      const fuera = (c: PlanoCapa) => decisionIfc(c.categoria, c.ifc?.supertipos) !== 'cotiza';
      const out: Siguiente[] = [];
      const ya = new Set(capasConPrincipalAqui);
      // Se piden en orden: el primero, luego el primero sin ese, etc. (hasta encontrar uno no saltado).
      for (;;) {
        const sig = siguienteTipoQueFalta(inventario?.capas ?? [], ya, fuera);
        if (!sig) break;
        out.push({ tipo: 'capa', clave: sig.capa.capa, texto: `${nombreLegibleTipoIfc(sig.capa)} · ${fmtMedidaIfc(sig.medida)}` });
        ya.add(sig.capa.capa);
        if (!saltados.has(`capa:${sig.capa.capa}`)) break;
      }
      return out;
    }
    if (!inventario) return [];
    const conPrincipalBloque = new Set(
      (rehacer ? [] : renglones).filter((r) => r.rol === 'principal').flatMap((r) => seleccionesDe(r).filter((s) => s.tipo === 'bloque').map((s) => s.clave)),
    );
    const capas: Array<Siguiente & { area: number; largo: number }> = [];
    for (const c of inventario.capas) {
      if (capasConPrincipalAqui.has(c.capa)) continue;
      const d = decisionOverride[c.capa] ?? decisionDeCapa(c.capa, capasClasificadas);
      if (d !== 'cotiza') continue;
      const med = medirCapa2D(c, factorEscala, capaSeMideComoMuro(c.capa, capasClasificadas, decisionOverride[c.capa]));
      const area = med.areaM2 ?? 0;
      const largo = med.baseLargo ?? 0;
      if (area <= 0 && largo <= 0) continue;
      const fmt = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 1 });
      capas.push({ tipo: 'capa', clave: c.capa, texto: area > 0 ? `${c.capa} · ${fmt(area)} m²` : `${c.capa} · ${fmt(largo)} m`, area, largo });
    }
    capas.sort((a, b) => b.area - a.area || b.largo - a.largo);
    const bloques = inventario.bloques
      .filter((b) => !conPrincipalBloque.has(b.nombre))
      .filter((b) => (decisionOverride[b.nombre] ?? decisionDeBloque(b.nombre, capasClasificadas)) === 'cotiza')
      .sort((a, b) => b.n - a.n)
      .map((b): Siguiente => ({ tipo: 'bloque', clave: b.nombre, texto: `${b.nombre} · ${b.n} pza` }));
    return [...capas, ...bloques];
  };
  const siguienteQueFalta: Siguiente | null = (() => {
    const todos = candidatosSiguiente();
    return todos.find((x) => !saltados.has(`${x.tipo}:${x.clave}`)) ?? todos[0] ?? null;
  })();
  const irA = (sig: Siguiente) => {
    limpiarSeleccion();
    if (sig.tipo === 'capa') toggleCapa(sig.clave);
    else setIdsSel(new Set(inventario?.bloques.find((b) => b.nombre === sig.clave)?.ids ?? []));
  };
  // «Armar el presupuesto»: TODO lo que se construye y no tiene concepto principal, en el mismo orden que
  // «Empieza por lo grande» (sin saltar nada: el asistente revisa todo junto). En IFC respeta las
  // correcciones del ⋯.
  const todosLosQueFaltan = (forzarFaltante = false): Siguiente[] => {
    // `forzarFaltante`: «Poner en la hoja lo asignado» nunca rehace la hoja, aunque el asistente haya quedado en «Rehacer».
    const rehacer = !forzarFaltante && modoArmado === 'rehacer';
    if (!esInventarioIfc) return candidatosSiguiente(rehacer);
    const fuera = (c: PlanoCapa) => (decisionOverride[c.capa] ?? decisionIfc(c.categoria, c.ifc?.supertipos)) !== 'cotiza';
    const ya = new Set(rehacer ? [] : capasConPrincipal);
    const out: Siguiente[] = [];
    for (;;) {
      const sig = siguienteTipoQueFalta(inventario?.capas ?? [], ya, fuera);
      if (!sig) break;
      out.push({ tipo: 'capa', clave: sig.capa.capa, texto: `${nombreLegibleTipoIfc(sig.capa)} · ${fmtMedidaIfc(sig.medida)}` });
      ya.add(sig.capa.capa);
    }
    return out;
  };
  // Lo que mide el plano con la unidad en que se presupuesta: un muro por su largo (su «área» en planta
  // es la huella), un piso por su área, un bloque por piezas. Es también lo que entra si se genera con IA.
  const medidaParaArmado = (a: Asignable): { cantidad: number; unidad: string | null } => {
    if (a.medidas) {
      // La medida principal del tipo con SU valor: una ventana va por piezas (9), no por su área (21.6).
      const info = inventario?.capas.find((c) => c.capa === a.seleccion.clave);
      const mp = medidaPrincipalIfc(a.medidas, info?.categoria, info?.ifc?.predefinido, info?.ifc?.supertipos);
      if (mp) return { cantidad: mp.valor, unidad: mp.unidad.replace('²', '2').replace('³', '3') };
    }
    if (a.medidas || a.seleccion.tipo === 'bloque') return { cantidad: a.cantidadDefault, unidad: unidadDeLaMedida(a) };
    // El muro va por su largo: su área depende del alto y de los vanos, y esa cuenta la hace el concepto.
    if (a.esMuroDobleTrazo) return { cantidad: a.cantidadDefault, unidad: unidadDeLaMedida(a) };
    if (a.areaM2 != null && a.areaM2 > 0) return { cantidad: a.areaM2, unidad: 'm2' };
    return { cantidad: a.cantidadDefault, unidad: unidadDeLaMedida(a) };
  };
  const fmtArmado = (m: { cantidad: number; unidad: string | null }) =>
    `${m.cantidad.toLocaleString('es-MX', { maximumFractionDigits: 2 })} ${(m.unidad ?? '').replace('m2', 'm²').replace('m3', 'm³')}`.trim();
  // El tipo entero, medido igual que si se seleccionara en el visor.
  // «Conceptos por tipo», excepciones por pieza: una pieza con concepto propio sale de su tipo y forma un elemento aparte
  // (`<tipo>#e0`, …). No hay estado que guardar: la partición sale SIEMPRE de lo asignado en este plano (memoria cargada), así
  // que un borrador retomado o una corrida nueva ven lo mismo. Con el armado cerrado la memoria cargada se descarta.
  const memoriaResuelta = useRef<{ reconocer: ReconocerTiposResponse | null; plano: TiposDelPlanoResponse | null } | null>(null);
  const particiones = useRef(new Map<string, ReturnType<typeof particionarPorExcepciones<number | string>> & { todas: (number | string)[] } | null>());
  // Todas las piezas de un tipo (con o sin GlobalId): IFC = las instancias de la capa; DWG = las inserciones del bloque.
  const piezasDelTipo = (key: string): { id: number | string; gid: string | null; nombre: string }[] => {
    const [tipo, ...resto] = key.split(':');
    const clave = resto.join(':');
    if (tipo === 'capa') return (inventario?.capas.find((x) => x.capa === clave)?.instancias ?? []).map((i, n) => ({ id: i.id, gid: i.gid ?? null, nombre: i.nombre?.trim() || `Pieza ${n + 1}` }));
    return (inventario?.bloques.find((x) => x.nombre === clave)?.ids ?? []).map((id, n) => ({ id, gid: String(id), nombre: `${clave} ${n + 1}` }));
  };
  // Qué piezas del tipo `key` tienen concepto propio (null = ninguna: el tipo va entero).
  const particionDeClave = (key: string) => {
    const mem = memoriaResuelta.current;
    if (!MEMORIA_TIPOS_ENABLED || !mem || (mem.plano?.asignaciones ?? []).every((x) => x.nivel !== 'pieza')) return null;
    if (particiones.current.has(key)) return particiones.current.get(key)!;
    const todas = piezasDelTipo(key);
    const conGid = todas.flatMap((p) => (p.gid ? [{ id: p.id, gid: p.gid }] : []));
    const res = resolverElemento(clavesDeCandidato({ key, tipo: key.startsWith('bloque:') ? 'bloque' : 'capa', nombre: '', medida: '' }), mem.plano?.asignaciones ?? [], mem.reconocer, conGid.map((p) => p.gid));
    const part = particionarPorExcepciones(res, conGid);
    const r = part.grupos.length ? { ...part, todas: todas.map((p) => p.id) } : null;
    particiones.current.set(key, r);
    return r;
  };
  useEffect(() => {
    particiones.current.clear();
  }, [inventario]);
  const baseVaciaPorExcepciones = (key: string): boolean => {
    const p = particionDeClave(key);
    return !!p && p.todas.every((id) => p.excluirDelTipo.has(id));
  };
  const asignableDeClave = (key: string): Asignable | null => {
    const m = /^(.*)#e(\d+)$/.exec(key);
    const baseKey = m ? m[1]! : key;
    const [tipo, ...resto] = baseKey.split(':');
    const clave = resto.join(':');
    const part = particionDeClave(baseKey);
    if (part) {
      if (m) {
        const g = part.grupos[Number(m[2])];
        if (!g) return null;
        const suyas = new Set<number | string>(g.ids);
        return tipo === 'capa'
          ? construirAsignables(new Set([clave]), new Set(), new Set(part.todas.filter((id) => !suyas.has(id)).map(Number)), null)[0] ?? null
          : construirAsignables(new Set(), new Set(g.ids.map(String)), new Set(), null)[0] ?? null;
      }
      if (baseVaciaPorExcepciones(baseKey)) return null;
      return tipo === 'capa'
        ? construirAsignables(new Set([clave]), new Set(), new Set([...part.excluirDelTipo].map(Number)), null)[0] ?? null
        : construirAsignables(new Set(), new Set(part.todas.filter((id) => !part.excluirDelTipo.has(id)).map(String)), new Set(), null)[0] ?? null;
    }
    if (tipo === 'capa') return construirAsignables(new Set([clave]), new Set(), new Set(), null)[0] ?? null;
    const b = inventario?.bloques.find((x) => x.nombre === clave);
    return b ? construirAsignables(new Set(), new Set(b.ids), new Set(), null)[0] ?? null : null;
  };
  // Los elementos que todavía no tienen concepto principal en la hoja, como candidatos del armado.
  const construirCandidatos = (forzarFaltante = false): CandidatoArmado[] =>
    todosLosQueFaltan(forzarFaltante).map((x) => {
      const key = `${x.tipo}:${x.clave}`;
      const i = x.texto.lastIndexOf(' · ');
      const nombre = i > 0 ? x.texto.slice(0, i) : x.clave;
      if (esInventarioIfc) return { key, tipo: x.tipo, nombre, medida: i > 0 ? x.texto.slice(i + 3) : '' };
      const a = asignableDeClave(key);
      return { key, tipo: x.tipo, nombre, medida: a ? `${fmtArmado(medidaParaArmado(a))}${a.esMuroDobleTrazo ? ' de muro' : ''}` : '' };
    });
  const candidatosArmado: CandidatoArmado[] = armarAbierto ? construirCandidatos() : [];
  const catalogoArmado = catalogSel ?? catalogosPrioridad[0] ?? '';
  // Complementarios y lo que no trae el plano: «Recomendados» por su frase (lo mismo que el botón del
  // panel para un adicional), SIN interpretarla —la frase ya la redactó la IA como concepto de obra: una
  // consulta menos por cada uno—. La búsqueda por texto casi nunca encuentra una frase así de corta, así que
  // no se intenta. En lotes con tope (`limiteIaArmado`) y la misma frase una sola vez por corrida.
  const buscarExtraArmado = (q: string, acompanaA?: string): Promise<{ opciones: OpcionArmado[]; noEsExacto: string | null }> => {
    const k = `${q.trim().toLowerCase().replace(/\s+/g, ' ')}|${(acompanaA ?? '').slice(0, 80).toLowerCase()}`;
    const ya = cacheExtraArmado.get(k);
    if (ya) return ya;
    const p = limiteIaArmado(async () => {
      if (!projectId || !fileId) return { opciones: [], noEsExacto: null };
      const res = await sugerirConceptos(projectId, fileId, {
        catalogId: catalogoArmado,
        yaEsConcepto: true,
        ...(acompanaA ? { acompanaA } : {}),
        seleccion: { tipo: 'capa', clave: q },
      });
      return {
        opciones: res.sugerencias.slice(0, 3).map((x) => ({
          resourceId: x.resourceId,
          catalogId: x.catalogId,
          clave: x.clave,
          descripcion: x.descripcion,
          unidad: x.unidad ?? null,
          precio: x.precio ?? null,
          razon: x.razonModelo ?? null,
          mismoElemento: x.mismoElemento ?? null,
          afectaPrecio: x.diferenciaAfectaPrecio ?? null,
          mismoSistema: x.mismoSistema ?? null,
        })),
        noEsExacto: res.notaFaltante ?? null,
      };
    });
    cacheExtraArmado.set(k, p);
    p.catch(() => cacheExtraArmado.delete(k));
    return p;
  };
  // Ingeniero: se cotiza con lo que hay en el catálogo. Si no está el concepto exacto, va el MÁS PARECIDO
  // que tenga precio (un equivalente sin precio no es equivalente, AI-36) y se marca para revisar; a «Generar
  // con IA» solo cuando el catálogo no tiene nada con precio. Devuelve el índice elegido o 'ia'.
  // Si no es exacto, solo vale uno que la IA juzgó el MISMO elemento (otra medida, mismo trabajo): «lo más
  // parecido» a secas trae otra cosa (el piso de mármol por el sellado de juntas).
  // El más parecido (no el exacto): el aviso dice QUÉ pide el plano y qué es lo que va, con el porqué de la
  // IA («el plano pide muro de 30 cm; va el 040601, de 12 cm»), no un «revísalo» genérico.
  // Va con IA porque lo único parecido del catálogo se construye con OTRO sistema: el aviso dice cuál y por qué
  // no se usó (sin esto quedaba un «no hay» genérico, como si el catálogo no tuviera nada).
  const notaOtroSistema = (notaFaltante: string, ops: OpcionArmado[]): string | null => {
    const o = ops.find((x) => x.mismoElemento === true && x.mismoSistema === false && x.precio != null && x.precio > 0);
    if (!o) return null;
    const falta = /no hay (.+?)\.(?:\s|$)/i.exec(notaFaltante)?.[1]?.trim();
    const va = `${o.clave} «${o.descripcion.replace(/\s+/g, ' ').trim().slice(0, 90)}${o.descripcion.length > 90 ? '…' : ''}»`;
    return `${falta ? `El plano pide ${falta}` : 'El plano pide otro sistema constructivo'}; lo más parecido de tu catálogo, ${va}, se construye de otra forma y cambia el precio: va generado con IA. Revísalo.`;
  };
  const elegirDelCatalogo = (ops: OpcionArmado[], exacto: boolean): Eleccion => {
    const i = ops.findIndex((o) => sirveDelCatalogo(o, exacto));
    return i >= 0 ? i : 'ia';
  };
  // Magnitud en que se cobra un concepto del catálogo (la misma consulta que usa el panel para medir),
  // una vez por concepto en la corrida. Con ella se comprueba que la cantidad de un complementario esté en
  // la misma magnitud que su concepto (sin tablas de unidades: las dos magnitudes las declara la IA).
  const lecturaDeConceptoArmado = (o: OpcionArmado): Promise<PlanoMagnitudConcepto | null> => {
    const ya = cacheMagnitudArmado.get(o.resourceId);
    if (ya) return ya;
    const p = limiteIaArmado(async () => {
      if (!projectId || !fileId) return null;
      return await clasificarMagnitud(projectId, fileId, o.catalogId || catalogoArmado, o.resourceId).catch(() => null);
    });
    cacheMagnitudArmado.set(o.resourceId, p);
    return p;
  };
  const magnitudDeConceptoArmado = async (o: OpcionArmado): Promise<PlanoMagnitudTipo | null> =>
    (await lecturaDeConceptoArmado(o))?.magnitud ?? null;
  const NOMBRE_MAGNITUD: Record<PlanoMagnitudTipo, string> = {
    conteo: 'piezas',
    lineal: 'largo',
    area: 'área',
    volumen: 'volumen',
    peso: 'peso',
    otro: 'otra medida',
  };
  // Magnitudes del elemento para derivar sus complementarios: la MISMA cuenta que el panel
  // (`cantidadDerivada`), con el alto de muros del asistente y los altos de vanos que conoce el plano.
  const magnitudesArmado = (a: Asignable) => {
    const md = a.medidas;
    const alto = Number(altoEfectivoArmado.replace(',', '.'));
    const espesor = a.espesorSugerido ?? 0;
    const esBloque = a.seleccion.tipo === 'bloque';
    const largo2d = md == null && !esBloque && a.medidoEnMetros === true ? a.cantidadDefault : null;
    const altosVanos: Record<string, string> = { ...altosVanosStr };
    for (const t of a.muroVanos?.tipos ?? []) {
      if (altosVanos[t.clave] != null) continue;
      const pr = proponerAltoDeVano(t, inventario?.anotaciones, a.muroVanos!.factor);
      if (pr != null) altosVanos[t.clave] = String(pr);
    }
    const vanos = md == null ? cuentaDeVanosDe(a.muroVanos, altoEfectivoArmado, altosVanos) : null;
    const areaMuro = vanos?.netoM2 ?? a.areaMuroPorPlantas ?? (largo2d != null && alto > 0 ? largo2d * alto : null);
    const area = md ? md.area ?? null : a.esMuroDobleTrazo ? areaMuro : a.areaM2 != null && a.areaM2 > 0 ? a.areaM2 : areaMuro;
    const volumen = md
      ? md.volumen ?? null
      : areaMuro != null && espesor > 0 && (a.esMuroDobleTrazo || !(a.areaM2 != null && a.areaM2 > 0))
        ? areaMuro * espesor
        : null;
    const tr = a.muroVanos?.tramos;
    return {
      conteo: md ? md.conteo : esBloque ? a.cantidadDefault : null,
      largo: md ? md.longitud ?? null : largo2d,
      largoConVanos: md == null && tr && tr.length > 0 ? Math.round(tr.reduce((x, t) => x + t.ejeBrutoM, 0) * 1000) / 1000 : null,
      area,
      volumen,
      sinAltoDeVano: md == null && !!a.esMuroDobleTrazo && (vanos?.tiposSinAlto ?? 0) > 0,
      vanos: vanosDelElemento(a, inventario?.capas),
    };
  };
  /**
   * Los complementarios de un concepto, con su cantidad: los de un elemento del plano y los de una partida
   * de «lo que no trae el plano» (la losa de azotea también lleva su impermeabilización).
   */
  const armarComplementarios = async (x: {
    key: string;
    selTipo: 'capa' | 'bloque';
    selClave: string;
    opciones: OpcionArmado[];
    eleccion: Eleccion;
    sintetico: { descripcion: string; unidad: string | null };
    contexto: string;
    conceptoK: string;
    mag: ReturnType<typeof magnitudesArmado>;
    porPlanta?: MedidaDePlanta[];
    partida?: string;
    /** Partida propia (de «lo que no trae el plano»): no se le quita lo ya propuesto a otros elementos. */
    sinYaPropuestas?: boolean;
    /** Las salidas se cuentan aparte (está prendido «lo que no trae el plano»). */
    salidasAparte?: boolean;
    /** Caras ya decididas (las de «lo que no trae el plano», que ve la obra completa). */
    caras?: string[];
    /** Lista ya dada (la de la revisión final): no se le pide a la IA. */
    lista?: PlanoComplementario[];
    /** Para las claves de los renglones («rev» en los de la revisión, para no chocar con los primeros). */
    sufijo?: string;
    /** Cómo se llama el principal (para el número generador del complementario). */
    nombrePadre?: string;
    /** «Otra opción»: la IA puede derivar de los vanos del elemento (en «Presupuestar todo el plano», no). */
    porVanos?: boolean;
  }): Promise<PropuestaArmado[]> => {
    if (!projectId || !fileId) return [];
    if (!x.lista) paramsComplementosArmado.current.set(x.key, x);
    const { opciones, eleccion: eleccionPrincipal, sintetico, contexto, conceptoK, mag } = x;
    // Cada elemento pide sus complementarios sin depender de lo que otros ya pidieron: con esa lista (y los
    // elementos en paralelo) la IA a veces se callaba el aplanado de los muros porque otro elemento ya llevaba
    // uno. Lo repetido con el mismo concepto se junta en un renglón al armar la hoja.
    const contextoFinal = contexto.slice(0, 1500);
    const yaPropuestas: string[] = [];
    let pedido = x.lista ? Promise.resolve(x.lista) : cacheComplementosArmado.get(conceptoK);
    if (!pedido) {
      pedido =
        typeof eleccionPrincipal === 'number'
          ? proponerComplementarios(projectId, fileId, opciones[eleccionPrincipal]!.catalogId || catalogoArmado, opciones[eleccionPrincipal]!.resourceId, x.selTipo, x.selClave, yaPropuestas.length ? yaPropuestas : undefined, contextoFinal || undefined, x.salidasAparte, x.caras, x.porVanos)
          : proponerComplementariosDeSintetico(projectId, fileId, sintetico.descripcion, sintetico.unidad, x.selTipo, x.selClave, yaPropuestas.length ? yaPropuestas : undefined, contextoFinal || undefined, x.salidasAparte, x.porVanos);
      void pedido.then((l) => yaPropuestoArmado.current.push(...l.map((x) => x.necesidad))).catch(() => undefined);
      cacheComplementosArmado.set(conceptoK, pedido);
      pedido.catch(() => cacheComplementosArmado.delete(conceptoK));
    }
    const lista = await pedido;
    const comps = await Promise.all(
      lista.map(async (comp, i): Promise<PropuestaArmado> => {
        const consulta = comp.consultaCatalogo || comp.necesidad;
        // Lo que no siempre aplica entra apagado y NO se busca (no se paga) hasta que se prenda.
        const acompanaA = typeof eleccionPrincipal === 'number' ? opciones[eleccionPrincipal]!.descripcion.slice(0, 300) : sintetico.descripcion;
        const encontrado = comp.tipico ? await buscarExtraArmado(consulta, acompanaA) : { opciones: [], noEsExacto: null };
        // Un complementario nunca es el mismo concepto que su principal: sería cobrarlo dos veces.
        const delPrincipal = typeof eleccionPrincipal === 'number' ? opciones[eleccionPrincipal]?.resourceId : undefined;
        // Ni el de su principal ni el de otro principal del plano: sería cotizar dos veces un elemento.
        const ops = encontrado.opciones.filter((o) => o.resourceId !== delPrincipal && !principalesArmado.current.has(o.resourceId));
        const noEsExacto = encontrado.noEsExacto;
        // Solo en las plantas donde va este trabajo (la IA las nombra con los rótulos del plano).
        const enPlantas = comp.plantas?.length && x.porPlanta?.length
          ? x.porPlanta.filter((p) => comp.plantas!.some((x) => x.trim().toLowerCase() === p.rotulo.trim().toLowerCase()))
          : [];
        const magDelTrabajo =
          enPlantas.length > 0 && enPlantas.length < (x.porPlanta?.length ?? 0) && enPlantas.every((p) => p.largoM != null)
            ? {
                ...mag,
                largo: enPlantas.reduce((t, p) => t + p.largoM!, 0),
                largoConVanos: null,
                area: enPlantas.every((p) => p.areaM2 != null) ? enPlantas.reduce((t, p) => t + p.areaM2!, 0) : mag.area,
              }
            : mag;
        const r = derivarCantidadComplementaria(comp, magDelTrabajo);
        // Lo que se apoya en el terreno va solo bajo la planta de abajo; si quedó medido con todas, se avisa.
        const todasLasPlantas = (comp.requiere ?? '').endsWith(':terreno') && enPlantas.length === 0 && (x.porPlanta?.length ?? 0) > 1;
        const supuesto = r.esSupuesto || (mag.sinAltoDeVano && (comp.derivaDe === 'area' || comp.derivaDe === 'volumen'));
        const supuestos = (comp.dimensionesFaltantes ?? []).map((d) => d.nombre).join(', ');
        // Sin concepto en el catálogo NO se manda solo a «Generar con IA»: la bolsa se gasta en los principales.
        // Queda en «No se agrega» con el porqué y se puede prender.
        const elegido: Eleccion = !comp.tipico ? 'fuera' : elegirDelCatalogo(ops, !noEsExacto);
        const eleccion: Eleccion = elegido === 'ia' ? 'fuera' : elegido;
        // Si la medida la supuso la IA (el plano y sus detalles no la dicen), el del catálogo vale tal cual.
        // Si la dice el proyecto y el catálogo no la tiene igual, va el más parecido marcado para revisar.
        const delProyecto =
          typeof eleccion === 'number' && !!noEsExacto && !(comp.dimensionesFaltantes ?? []).length && ops[eleccion]!.afectaPrecio !== false;
        // La cantidad del plano tiene que estar en la magnitud en que se cobra el concepto: un relleno de
        // tezontle por m³ no lleva el área de la losa. Si no coinciden, va sin cantidad (en rojo) y lo dice.
        const lectura = typeof eleccion === 'number' ? await lecturaDeConceptoArmado(ops[eleccion]!) : null;
        const magConcepto = lectura?.magnitud ?? null;
        const distinta =
          !!comp.magnitud && !!magConcepto && comp.magnitud !== 'otro' && magConcepto !== 'otro' && comp.magnitud !== magConcepto;
        // Si el concepto DICE la medida que falta («relleno de 10 cm promedio»), con ella se pasa del área al
        // volumen y lo dice la nota. Si no la dice, va sin cantidad, como antes.
        const conMedida =
          distinta && r.cantidad != null && lectura?.medidaDelConceptoM != null &&
          comp.magnitud === 'area' && magConcepto === 'volumen'
            ? porMedidaDelConcepto(r.cantidad, lectura.medidaDelConceptoM)
            : null;
        // Si no (o si con la medida que eligió la IA no sale cantidad), la IA dice de qué medida del elemento sale la cantidad en la unidad del concepto (el resane de
        // un vano por metro cuando el plano da la puerta por pieza) y la cuenta la hace la misma regla de siempre.
        const conversion =
          ((distinta && conMedida == null) || (!distinta && r.cantidad == null)) &&
          // El peso (kg, ton) no sale de la geometría del plano: se convertía el volumen de toda la pieza en acero.
          magConcepto !== 'peso' &&
          typeof eleccion === 'number' && projectId && fileId
            ? await limiteIaArmado(() =>
                convertirCantidadPlano(projectId, fileId, {
                  trabajo: comp.necesidad,
                  conceptoDescripcion: ops[eleccion]!.descripcion,
                  conceptoUnidad: ops[eleccion]!.unidad,
                  medidas: { conteo: magDelTrabajo.conteo, largo: magDelTrabajo.largo, area: magDelTrabajo.area, volumen: magDelTrabajo.volumen, vanos: magDelTrabajo.vanos },
                  datosElemento: x.contexto.slice(0, 800),
                }),
              )
                .then((res) => res.conversion)
                .catch(() => null)
            : null;
        const convertida = conversion
          ? derivarCantidadComplementaria(
              { ...comp, derivaDe: conversion.derivaDe, factor: conversion.factor, dimensionesFaltantes: conversion.dimensionesFaltantes, incluyeVanos: false },
              magDelTrabajo,
            ).cantidad
          : null;
        const otraMagnitud = distinta && conMedida == null && convertida == null;
        // Números generadores: de qué principal sale y con qué regla (la que dio la IA, no una tabla).
        const cantFinal = otraMagnitud ? null : conMedida ?? convertida ?? r.cantidad;
        const fotoComp = (() => {
          if (cantFinal == null) return null;
          const baseDe = (d: string, conVanos: boolean): number | null =>
            d === 'conteo' ? magDelTrabajo.conteo ?? null
              : d === 'largo' ? (conVanos ? magDelTrabajo.largoConVanos ?? magDelTrabajo.largo : magDelTrabajo.largo) ?? null
                : d === 'area' ? magDelTrabajo.area ?? null
                  : d === 'volumen' ? magDelTrabajo.volumen ?? null
                    : d === 'vanos' ? magDelTrabajo.vanos ?? null
                      : null;
          const NOMBRE_BASE: Record<string, string> = { conteo: 'Piezas', largo: 'Largo', area: 'Área', volumen: 'Volumen', vanos: 'Puertas y ventanas' };
          const f2 = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 3 });
          let detalle: string;
          if (convertida != null && conversion) {
            const b = baseDe(conversion.derivaDe, false);
            detalle = `${NOMBRE_BASE[conversion.derivaDe] ?? 'Medida'} ${b != null ? f2(b) : '—'} × ${f2(conversion.factor || 1)}${conversion.como ? ` (${conversion.como.replace(/.$/, '')})` : ''}`;
          } else {
            const b = baseDe(comp.derivaDe, r.usaLargoConVanos);
            const fct = typeof comp.factor === 'number' && comp.factor > 0 ? comp.factor : 1;
            detalle =
              `${NOMBRE_BASE[comp.derivaDe] ?? 'Medida'}${r.usaLargoConVanos ? ' (con los huecos)' : ''} ${b != null ? f2(b) : '—'} × ${f2(fct)}` +
              (conMedida != null ? ` × ${f2(lectura!.medidaDelConceptoM!)} m (la medida que dice el concepto)` : '');
          }
          const sp = asignableDeClave(x.key)?.seleccion;
          // El contrato EFECTIVO con que salió la cantidad (para recalcularla sin IA si cambia el plano). Si salió
          // de una parte de las plantas, la medida del elemento entero no la reproduce: sin contrato.
          const fctComp = typeof comp.factor === 'number' && comp.factor > 0 ? comp.factor : 1;
          const derivacion: DerivacionDeComplementario | undefined =
            magDelTrabajo !== mag || !sp
              ? undefined
              : convertida != null && conversion
                ? {
                    derivaDe: conversion.derivaDe,
                    factor: conversion.factor > 0 ? conversion.factor : 1,
                    ...(supuesto || conversion.dimensionesFaltantes.length > 0 ? { supuesto: true } : {}),
                  }
                : comp.derivaDe === 'otro'
                  ? undefined
                  : {
                      derivaDe: comp.derivaDe,
                      factor: fctComp,
                      ...(comp.incluyeVanos === true ? { incluyeVanos: true } : {}),
                      ...(conMedida != null ? { medidaM: lectura!.medidaDelConceptoM! } : {}),
                      ...(supuesto ? { supuesto: true } : {}),
                    };
          return fotoDeRegla({
            origen: 'complementario',
            unidad: (typeof eleccion === 'number' ? ops[eleccion]?.unidad : null) ?? comp.unidadEsperada ?? null,
            total: cantFinal,
            regla: comp.razon || null,
            lineas: [
              {
                elemento: sp ? { tipo: sp.tipo, clave: sp.clave, ...(sp.vista ? { vista: sp.vista } : {}) } : null,
                localizacion: x.nombrePadre ?? x.selClave,
                grupo: enPlantas.length > 0 && enPlantas.length < (x.porPlanta?.length ?? 0) ? enPlantas.map((p) => p.rotulo).join(', ') : '',
                detalle,
                parcial: cantFinal,
                ...(derivacion ? { derivacion } : {}),
              },
            ],
          });
        })();
        return {
          key: `${x.key}::${x.sufijo ?? 'comp'}:${i}`,
          tipo: 'complemento',
          elementoKey: x.key,
          padre: x.key,
          ...(x.partida ? { partida: x.partida } : {}),
          nombre: comp.necesidad,
          medida: '',
          opciones: ops,
          consulta,
          eleccion,
          nota: [
            comp.razon,
            !comp.tipico ? 'Depende de la obra: decide si va.' : null,
            delProyecto ? 'El proyecto lo pide con otra medida: va lo más parecido de tu catálogo, revísalo.' : null,
            typeof eleccion === 'number' && noEsExacto && !delProyecto ? 'Va el de tu catálogo (la medida no la dice el plano).' : null,
            elegido === 'ia' ? 'Tus catálogos no lo tienen.' : null,
          ].filter(Boolean).join(' '),
          sintetico: { descripcion: comp.necesidad, unidad: comp.unidadEsperada },
          cantidad: {
            cantidad: cantFinal,
            foto: fotoComp,
            revisar: supuesto || delProyecto || otraMagnitud || todasLasPlantas || conMedida != null || convertida != null,
            nota: convertida != null
              ? `${conversion!.como ? `${conversion!.como.replace(/\.$/, '')}` : 'Convertida a la unidad del concepto'}${
                  conversion!.dimensionesFaltantes.length ? ` (supone ${conversion!.dimensionesFaltantes.map((d) => d.nombre).join(', ')})` : ''
                }: revísalo.`
              : conMedida != null
              ? `${NOMBRE_MAGNITUD[comp.magnitud!][0]!.toUpperCase()}${NOMBRE_MAGNITUD[comp.magnitud!].slice(1)} del plano por ${Math.round(lectura!.medidaDelConceptoM! * 100)} cm, la medida que dice el concepto: revísalo.`
              : otraMagnitud
              ? `El plano da su ${NOMBRE_MAGNITUD[comp.magnitud!]} y este concepto se cobra por ${NOMBRE_MAGNITUD[magConcepto!]}: escribe la cantidad.`
              : todasLasPlantas
                ? 'Va solo bajo la planta que se apoya en el terreno, y la cantidad incluye todas las plantas: ajústala.'
              : r.cantidad == null
                ? 'No se puede sacar del plano: escribe la cantidad.'
                : supuestos
                  ? `Supone ${supuestos}: entra marcado para revisar.`
                  : supuesto
                    ? 'Descansa en un supuesto: entra marcado para revisar.'
                    : null,
          },
        };
      }),
    );
    return comps;
  };
  // Los complementarios que propone la IA para un principal ya decidido (el del motor y el recordado sin sus
  // complementarios): lo que antes vivía al final de `proponerArmado`, sin cambios.
  const complementosDeIa = async (p: {
    c: CandidatoArmado;
    a: Asignable;
    op: OpcionesDeArmado;
    opciones: OpcionArmado[];
    eleccion: Eleccion;
    sintetico: { descripcion: string; unidad: string | null };
    ifc: ReturnType<typeof describirTipoIfcParaSugerencia> | null;
    caras?: string[];
    materialSupuesto: boolean;
  }): Promise<PropuestaArmado[]> => {
    const { c, a, op, opciones, eleccion, sintetico, ifc } = p;

    // Sus complementarios: los del concepto elegido (o, si va a IA, los de su descripción).
    // El mismo concepto implica los mismos trabajos: la LISTA se pide una vez por concepto en la corrida
    // (antes, una vez por cada tipo del modelo). La cantidad de cada complementario se sigue sacando de las
    // medidas de CADA elemento, abajo.
    // Con sus caras: la misma losa del catálogo en la azotea y en el entrepiso no lleva los mismos trabajos.
    const conceptoK =
      (typeof eleccion === 'number'
        ? `cat:${opciones[eleccion]!.resourceId}`
        : `ia:${sintetico.descripcion.replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase()}|${sintetico.unidad ?? ''}`) +
      `|${(p.caras ?? []).join(',')}`;
    if (typeof eleccion === 'number') principalesArmado.current.add(opciones[eleccion]!.resourceId);
    // Lo que el MODELO dice del elemento (piso o azotea, material, exterior) y lo que el plano ya trae
    // como elemento propio: sin esto la IA impermeabilizaba losas de entrepiso o soldaba vigas de madera.
    // Con su clase y tipo IFC: una «Losa 20 cm» BASESLAB es la cimentación (los muros ya no llevan la suya).
    const otrosDelPlano = candidatosArmado
      .filter((x) => x.key !== c.key)
      .map((x) => {
        const ax = asignableDeClave(x.key);
        const cx = ax?.seleccion.tipo === 'capa' ? inventario?.capas.find((k) => k.capa === ax.seleccion.clave) : undefined;
        return cx?.categoria ? `${x.nombre} (${cx.categoria}${cx.ifc?.predefinido ? ` ${cx.ifc.predefinido}` : ''})` : x.nombre;
      });
    const contexto = [
      ifc
        ? `LO QUE EL MODELO DICE DEL ELEMENTO: ${ifc.etiqueta}; clase IFC ${ifc.clase}` +
          `${ifc.predefinido ? `; tipo ${ifc.predefinido}` : ''}` +
          `; material: ${ifc.materiales?.length ? ifc.materiales.join(', ') : 'no lo dice'}` +
          `${ifc.esExterior === true ? '; exterior' : ifc.esExterior === false ? '; interior' : ''}.`
        : null,
      // Plano 2D: en qué plantas está dibujado (con su largo). Con eso la IA sabe qué caras tiene (el piso de
      // un nivel no es una azotea) y en qué planta va cada trabajo (la cadena de desplante, solo abajo).
      !ifc && a.porPlanta?.length
        ? `LO QUE EL PLANO DICE DEL ELEMENTO: está dibujado en ${a.porPlanta
            .map((p) => {
              const m = [p.largoM != null ? `largo ${Math.round(p.largoM * 100) / 100} m` : null, p.altoM != null ? `alto ${p.altoM} m` : null].filter(Boolean);
              return `«${p.rotulo}»${m.length ? ` (${m.join(', ')})` : ''}`;
            })
            .join(', ')}.`
        : null,
      otrosDelPlano.length ? `EL PLANO YA TRAE COMO ELEMENTOS PROPIOS (se cotizan aparte): ${[...new Set(otrosDelPlano)].slice(0, 60).join('; ')}.` : null,
      // El material no está dicho ni se supuso: los trabajos que solo valen para un material (o sistema constructivo) en particular
      // serían una suposición sin avisar (p. ej. refuerzo de concreto en una viga que puede ser de otro material).
      !p.materialSupuesto && (ifc ? !ifc.materiales?.length : false)
        ? 'EL MATERIAL DE ESTE ELEMENTO NO ESTÁ DICHO: no propongas trabajos que solo tengan sentido para un material o sistema constructivo en particular; propón solo los que valen sea cual sea.'
        : null,
      // «Lo que no trae el plano» ya cuenta las salidas de toda la casa: aquí serían dos veces.
      op.noDibujado
        ? 'LAS SALIDAS DE INSTALACIONES (eléctricas, hidráulicas, sanitarias, de gas, de voz y datos) SE COTIZAN APARTE para toda la obra: no las propongas.'
        : null,
    ]
      .filter(Boolean)
      .join('\n');
    const comps = await armarComplementarios({
      key: c.key,
      nombrePadre: c.nombre,
      selTipo: a.seleccion.tipo,
      selClave: a.seleccion.clave,
      opciones,
      eleccion,
      sintetico,
      contexto,
      conceptoK,
      mag: magnitudesArmado(a),
      porPlanta: a.porPlanta,
      salidasAparte: op.noDibujado,
      // Las caras que dijo la interpretación del elemento (sus datos, sin las láminas de detalle).
      ...(p.caras?.length ? { caras: p.caras } : {}),
    });
    return comps;
  };
  // «Conceptos por tipo»: las claves de un candidato (IFC: tipo y clase del modelo; DWG: capa o bloque).
  const clavesDeCandidato = (c: CandidatoArmado) => {
    // Se lee de la clave directamente (sin pasar por `asignableDeClave`, que a su vez consulta las particiones por pieza).
    const i = c.key.indexOf(':');
    const capaInfo = c.key.slice(0, i) === 'capa' ? inventario?.capas.find((x) => x.capa === c.key.slice(i + 1)) : undefined;
    return clavesDelElemento(c.key, esInventarioIfc, capaInfo, inventario?.ifc?.materialesDeducidos);
  };
  // Una consulta por corrida: lo que recuerda la empresa de los tipos del plano + lo decidido en este plano.
  // Si falla, el armado sigue como siempre (null = motor).
  const cargarMemoriaArmado = async (candidatos: CandidatoArmado[] = candidatosArmado, usados: readonly string[] = catalogosPrioridad) => {
    if (!MEMORIA_TIPOS_ENABLED || !projectId || !fileId) return null;
    try {
      const vistas = new Map<string, ClaveDeTipo>();
      for (const c of candidatos) {
        const k = clavesDeCandidato(c);
        for (const x of [k.tipo, k.clase]) if (x) vistas.set(`${x.canonica}|${x.uso}`, x);
      }
      const [reconocer, plano] = await Promise.all([
        vistas.size ? reconocerTiposDeMemoria(projectId, fileId, [...vistas.values()]) : Promise.resolve(null),
        leerTiposDelPlano(projectId, fileId),
      ]);
      const r = ajustarMarcados({ reconocer, plano }, new Set(usados));
      memoriaResuelta.current = r;
      particiones.current.clear();
      return r;
    } catch {
      return null;
    }
  };
  // Al abrir el armado: se lee la memoria una vez y se anotan los elementos que ya tienen concepto decidido, para no
  // preguntar su material en «Antes de armar» (si todos los tipos de una clase están resueltos, la clase no se pregunta).
  useEffect(() => {
    if (!armarAbierto || !MEMORIA_TIPOS_ENABLED || !inventario) {
      setDecididosPorTipos((prev) => (prev.size ? new Set() : prev));
      setResumenTipos(null);
      return;
    }
    let vivo = true;
    const carga = cargarMemoriaArmado();
    memoriaArmado.current = carga;
    void carga.then((mem) => {
      if (!vivo) return;
      if (mem) {
        const resueltos = conceptosResueltos(mem.plano, mem.reconocer);
        const listos = new Set<string>();
        const resumen = { reconocidos: 0, sugeridos: 0, conflictos: 0, noVigentes: 0, total: candidatosArmado.length };
        for (const c of candidatosArmado) {
          const res = resolverElemento(clavesDeCandidato(c), mem.plano?.asignaciones ?? [], mem.reconocer);
          if (res.receta && recetaAplicable(res.receta, resueltos)) {
            listos.add(c.key);
            resumen.reconocidos++;
          }
          else if (res.pendiente?.estado === 'sugerencia') resumen.sugeridos++;
          else if (res.pendiente?.estado === 'conflicto') resumen.conflictos++;
        }
        setDecididosPorTipos(listos);
        setResumenTipos(resumen);
      }
      setMemoriaLeidaDe(inventario);
    });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armarAbierto, inventario, memoriaTick, candidatosArmado.map((c) => c.key).join('|'), catalogosPrioridad.join('|')]);
  // Los elementos que muestra «Asignar conceptos a elementos»: todo lo que se construye (con o sin concepto en la hoja),
  // con la misma medida y orden que «Armar el presupuesto».
  const elementosDelDialogo = (): ElementoDelDialogo[] => {
    if (!inventario) return [];
    const base: { key: string; tipo: 'capa' | 'bloque'; nombre: string; medida: string; grupo: string; claseIfc: string | null }[] = [];
    if (esInventarioIfc) {
      const fuera = (c: PlanoCapa) => (decisionOverride[c.capa] ?? decisionIfc(c.categoria, c.ifc?.supertipos)) !== 'cotiza';
      // Mismo orden que el panel «Lo que trae el modelo»: el del inventario, sin reordenar por tamaño.
      for (const c of inventario.capas) {
        if (c.categoria == null || fuera(c)) continue;
        const mp = medidaPrincipalIfc(c.medidas, c.categoria, c.ifc?.predefinido, c.ifc?.supertipos);
        base.push({
          key: `capa:${c.capa}`,
          tipo: 'capa',
          nombre: nombreLegibleTipoIfc(c),
          medida: mp ? fmtMedidaIfc(mp) : '',
          // Los mismos grupos y el mismo orden que el panel (Muros, Losas, Puertas y ventanas…).
          grupo: grupoDeCapaIfc(c),
          claseIfc: c.categoria,
        });
      }
    } else {
      for (const x of candidatosSiguiente(true)) {
        const key = `${x.tipo}:${x.clave}`;
        const i = x.texto.lastIndexOf(' · ');
        const a = asignableDeClave(key);
        base.push({
          key,
          tipo: x.tipo,
          nombre: i > 0 ? x.texto.slice(0, i) : x.clave,
          medida: a ? `${fmtArmado(medidaParaArmado(a))}${a.esMuroDobleTrazo ? ' de muro' : ''}` : '',
          grupo: x.tipo === 'bloque' ? 'Bloques' : 'Capas',
          claseIfc: null,
        });
      }
    }
    if (esInventarioIfc) base.sort((a, b) => GRUPOS_IFC.indexOf(a.grupo as (typeof GRUPOS_IFC)[number]) - GRUPOS_IFC.indexOf(b.grupo as (typeof GRUPOS_IFC)[number]));
    return base.map((b) => ({
      key: b.key,
      nombre: b.nombre,
      medida: b.medida,
      grupo: b.grupo,
      claseIfc: b.claseIfc,
      claves: clavesDeCandidato({ key: b.key, tipo: b.tipo, nombre: b.nombre, medida: b.medida }),
      nombreDelTipo: (() => {
        const t = inventario.capas.find((x) => `capa:${x.capa}` === b.key)?.tipoDelModelo;
        return t && t.origen !== 'clase' ? (t.uso ? `${t.nombre} · ${t.uso}` : t.nombre) : null;
      })(),
      nombreVisible: (() => {
        const c = inventario.capas.find((x) => `capa:${x.capa}` === b.key);
        return c ? nombreDeclaradoDelTipoIfc(c)?.tipo ?? null : null;
      })(),
      piezas: (() => {
        const c = esInventarioIfc ? inventario.capas.find((x) => `capa:${x.capa}` === b.key) : undefined;
        if (!c) return piezasDelTipo(b.key).flatMap((pz) => (pz.gid ? [{ gid: pz.gid, nombre: pz.nombre }] : []));
        // IFC: el mismo nombre de pieza y el mismo orden que la lista del panel.
        return (c.instancias ?? []).flatMap((inst, i) => {
          if (!inst.gid) return [];
          const nivelId = nivelDeElemento.get(inst.id);
          const mp = medidaPrincipalIfc({ conteo: 1, ...inst, manda: c.medidas?.manda }, c.categoria, c.ifc?.predefinido, c.ifc?.supertipos);
          return [{
            gid: inst.gid,
            nombre: nombrePiezaIfc({
              nombre: inst.nombre,
              clase: c.categoria,
              indice: i,
              nivel: nivelId != null ? etiquetaNivel.get(nivelId)?.corto : null,
              medida: mp && mp.magnitud !== 'conteo' ? mp : null,
            }),
          }];
        });
      })(),
      delaHoja: recetaDesdeLaHoja(renglones, [b.key.slice(b.key.indexOf(':') + 1)]),
    }));
  };
  useEffect(() => {
    if (!MEMORIA_TIPOS_ENABLED || !inventario || !projectId || !fileId) {
      setResumenPlano(null);
      return;
    }
    let vivo = true;
    const els = elementosDelDialogo();
    const vistas = new Map<string, ClaveDeTipo>();
    for (const e of els) for (const x of [e.claves.tipo, e.claves.clase]) if (x) vistas.set(`${x.canonica}|${x.uso}`, x);
    void Promise.all([vistas.size ? reconocerTiposDeMemoria(projectId, fileId, [...vistas.values()]) : Promise.resolve(null), leerTiposDelPlano(projectId, fileId)])
      .then(([rec, pl]) => {
        if (!vivo) return;
        const mem = ajustarMarcados({ reconocer: rec, plano: pl }, new Set(catalogosPrioridad));
        const resueltos = conceptosResueltos(mem.plano, mem.reconocer);
        let reconocidos = 0;
        for (const e of els) {
          const r = resolverElemento(e.claves, mem.plano?.asignaciones ?? [], mem.reconocer).receta;
          if (r && recetaAplicable(r, resueltos)) reconocidos++;
        }
        setResumenPlano({ reconocidos, total: els.length });
      })
      .catch(() => {
        if (vivo) setResumenPlano(null);
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventario, projectId, fileId, memoriaTick, catalogosPrioridad.join('|')]);
  // Lo recordado o asignado a un tipo (UN principal + sus complementarios) se arma SIN consultar al catálogo ni a la IA;
  // null = no se puede aplicar (algún concepto ya no existe) y el tipo va al motor de siempre.
  const proponerDeReceta = async (
    c: CandidatoArmado,
    a: Asignable,
    res: Resolucion,
    mem: { reconocer: ReconocerTiposResponse | null; plano: TiposDelPlanoResponse | null },
    op: OpcionesDeArmado,
    /**
     * «Poner en la hoja lo asignado»: `sinIa` = nunca se pide nada a la IA para elegir conceptos (una receta que no recordó
     * sus complementarios entra solo con su principal); `magnitudes` = lo que ya se preguntó por concepto (una consulta por
     * concepto, no por elemento).
     */
    opts?: { sinIa?: boolean; magnitudes?: ReadonlyMap<string, Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null> },
  ): Promise<PropuestaArmado[] | null> => {
    const receta = res.receta;
    if (!receta) return null;
    const capaInfo = a.seleccion.tipo === 'capa' ? inventario?.capas.find((x) => x.capa === a.seleccion.clave) : undefined;
    const ifc = capaInfo ? describirTipoIfcParaSugerencia(capaInfo) : null;
    const origen = notaDeOrigen(res);
    const deBase = {
      fuente: res.fuente === 'motor' ? ('tipo_plano' as const) : res.fuente,
      ...(res.memoria ? { memoriaId: res.memoria.id, version: res.memoria.version } : {}),
    };
    const medida = medidaParaArmado(a);
    if (receta.noPresupuestar) {
      return [
        {
          key: c.key,
          tipo: 'principal',
          elementoKey: c.key,
          padre: null,
          nombre: c.nombre,
          medida: c.medida,
          opciones: [],
          eleccion: 'fuera',
          nota: `Marcaste que este tipo no se presupuesta. ${origen}`.trim(),
          sintetico: { descripcion: c.nombre, unidad: medida.unidad },
          cantidad: { cantidad: null, revisar: false, nota: null },
          deTipos: deBase,
        },
      ];
    }
    // Cada concepto de la receta contra el catálogo de HOY (resuelto por el servidor al reconocer / leer el plano).
    const resueltos = conceptosResueltos(mem.plano, mem.reconocer);
    const opcionDe = (l: (typeof receta.lineas)[number]): OpcionArmado | null => {
      const r = resueltoDeLinea(resueltos, l);
      if (!r) return null;
      return {
        resourceId: r.resourceId,
        catalogId: l.catalogId,
        clave: l.clave,
        descripcion: r.descripcion ?? l.descripcion,
        unidad: r.unidad ?? l.unidad,
        precio: r.precio,
        razon: null,
        mismoElemento: null,
        afectaPrecio: null,
        mismoSistema: null,
      };
    };
    const lp = receta.lineas.find((l) => l.rol === 'principal');
    const opcion = lp ? opcionDe(lp) : null;
    if (!lp || !opcion) return null;
    const complementos = receta.lineas.filter((l) => l.rol === 'complemento');
    const opcionesComp = complementos.map((l) => opcionDe(l));
    if (receta.recuerda.complementarios && opcionesComp.some((o) => !o)) return null;

    const dePregunta = lp.magnitud ? undefined : opts?.magnitudes?.get(opcion.resourceId);
    const cantidad = await medirArmado(c.key, opcion, lp.magnitud ?? dePregunta ?? null, !lp.magnitud && opts?.magnitudes != null);
    principalesArmado.current.add(opcion.resourceId);
    const principal: PropuestaArmado = {
      key: c.key,
      tipo: 'principal',
      elementoKey: c.key,
      padre: null,
      nombre: c.nombre,
      medida: c.medida,
      opciones: [opcion],
      eleccion: 0,
      nota: origen,
      ...(ifc ? { uso: `${ifc.clase}|${ifc.predefinido ?? ''}` } : {}),
      sintetico: { descripcion: opcion.descripcion, unidad: opcion.unidad },
      cantidad,
      deTipos: { ...deBase, resourceId: opcion.resourceId, magnitud: lp.magnitud, agrupacion: receta.agrupacion },
    };
    if (!op.complementarios) return [principal];
    if (!receta.recuerda.complementarios) {
      if (opts?.sinIa) return [principal];
      // No se recordaron los complementarios: los propone la IA, como siempre, para el concepto recordado.
      const ia = await complementosDeIa({ c, a, op, opciones: [opcion], eleccion: 0, sintetico: principal.sintetico, ifc, materialSupuesto: false });
      return [principal, ...ia];
    }
    // Complementarios recordados: su cantidad sale de lo que mide ESTE elemento, con la derivación que se recordó.
    const mag = magnitudesArmado(a);
    const variasPlantas = (a.porPlanta?.length ?? 0) > 1;
    const comps: PropuestaArmado[] = complementos.map((l, i): PropuestaArmado => {
      const o = opcionesComp[i]!;
      const d = l.derivacion;
      let cant: number | null = null;
      if (d) {
        const r = derivarCantidadComplementaria(
          { derivaDe: d.derivaDe, factor: d.factor, incluyeVanos: d.incluyeVanos, dimensionesFaltantes: [] } as Parameters<typeof derivarCantidadComplementaria>[0],
          mag,
        );
        cant = r.cantidad != null && d.medidaM != null ? porMedidaDelConcepto(r.cantidad, d.medidaM) : r.cantidad;
      }
      const sp = a.seleccion;
      const foto =
        cant != null && d
          ? fotoDeRegla({
              origen: 'complementario',
              unidad: o.unidad,
              total: cant,
              regla: null,
              lineas: [
                {
                  elemento: { tipo: sp.tipo, clave: sp.clave, ...(sp.vista ? { vista: sp.vista } : {}) },
                  localizacion: c.nombre,
                  grupo: '',
                  detalle: `Lo recordado para este tipo: ${d.derivaDe} × ${d.factor}`,
                  parcial: cant,
                  derivacion: d,
                },
              ],
            })
          : null;
      const plantas = variasPlantas && d != null && d.derivaDe !== 'conteo' && d.derivaDe !== 'vanos';
      const revisar = l.supuesto || cant == null || plantas || d?.medidaM != null || (mag.sinAltoDeVano && d?.derivaDe !== 'conteo');
      return {
        key: `${c.key}|m${l.orden}`,
        tipo: 'complemento',
        elementoKey: c.key,
        padre: c.key,
        nombre: o.descripcion,
        medida: '',
        opciones: [o],
        eleccion: 0,
        nota: [
          origen,
          cant == null ? 'No se puede sacar del plano: escribe la cantidad.' : null,
          plantas ? 'Revisa la cantidad: este trabajo puede no ir en todas las plantas.' : null,
          l.supuesto ? 'Descansa en un supuesto: entra marcado para revisar.' : null,
        ].filter(Boolean).join(' '),
        sintetico: { descripcion: o.descripcion, unidad: o.unidad },
        cantidad: { cantidad: cant, revisar, nota: null, foto },
        deTipos: { ...deBase, resourceId: o.resourceId },
      };
    });
    return [principal, ...comps];
  };
  // Las piezas con concepto propio: cada grupo se arma con su receta como un elemento aparte (el tipo ya sale sin ellas).
  const proponerExcepciones = async (c: CandidatoArmado, op: OpcionesDeArmado, opts?: Parameters<typeof proponerDeReceta>[5]): Promise<PropuestaArmado[]> => {
    if (!MEMORIA_TIPOS_ENABLED) return [];
    if (!memoriaArmado.current) memoriaArmado.current = cargarMemoriaArmado();
    const mem = await memoriaArmado.current;
    const part = mem ? particionDeClave(c.key) : null;
    if (!mem || !part) return [];
    const out: PropuestaArmado[] = [];
    for (const [i, g] of part.grupos.entries()) {
      const key = `${c.key}#e${i}`;
      const a = asignableDeClave(key);
      if (!a) continue;
      const cx: CandidatoArmado = { key, tipo: c.tipo, nombre: `${c.nombre} · ${g.ids.length === 1 ? '1 pieza' : `${g.ids.length} piezas`} con concepto propio`, medida: fmtArmado(medidaParaArmado(a)) };
      const filas = await proponerDeReceta(cx, a, { fuente: 'pieza_plano', receta: g.receta, excepciones: [], pendiente: null }, mem, op, opts);
      if (filas) out.push(...filas);
    }
    return out;
  };
  const proponerArmado = async (c: CandidatoArmado, op: OpcionesDeArmado): Promise<PropuestaArmado[]> => {
    const extras = await proponerExcepciones(c, op);
    // Si TODAS las piezas del tipo tienen concepto propio, el tipo no queda con nada que armar.
    if (baseVaciaPorExcepciones(c.key)) return extras;
    return [...(await proponerBase(c, op)), ...extras];
  };
  const proponerBase = async (c: CandidatoArmado, op: OpcionesDeArmado): Promise<PropuestaArmado[]> => {
    const a = asignableDeClave(c.key);
    if (!a || !projectId || !fileId) throw new Error('sin selección');
    // «Conceptos por tipo»: lo que la persona asignó (en este plano o en otro proyecto) manda sobre el motor.
    if (MEMORIA_TIPOS_ENABLED) {
      if (!memoriaArmado.current) memoriaArmado.current = cargarMemoriaArmado();
      const mem = await memoriaArmado.current;
      if (mem) {
        const res = resolverElemento(clavesDeCandidato(c), mem.plano?.asignaciones ?? [], mem.reconocer);
        if (res.receta) {
          const deReceta = await proponerDeReceta(c, a, res, mem, op);
          if (deReceta) return deReceta;
        }
      }
    }
    const capaInfo = a.seleccion.tipo === 'capa' ? inventario?.capas.find((x) => x.capa === a.seleccion.clave) : undefined;
    const ifcBase = capaInfo ? describirTipoIfcParaSugerencia(capaInfo) : null;
    // El material que el modelo no dice y la persona contestó en «Antes de armar».
    const materialDicho = capaInfo?.categoria ? materialArmado[capaInfo.categoria]?.trim() : '';
    // Lo que contestó manda sobre un nombre que no dice el material.
    const ifc = ifcBase && materialDicho ? { ...ifcBase, materiales: [materialDicho] } : ifcBase;
    // Sin material contestado: lo que traen los OTROS tipos del modelo (y lo que quien cotiza contestó de otras
    // clases), para que la IA suponga el más probable y lo diga. Si el tipo ya declara su material, no se usa.
    const contextoModelo =
      ifc && !materialDicho && inventario
        ? [
            ...new Set(
              inventario.capas
                .filter((x) => x.categoria && x.capa !== capaInfo?.capa)
                .map((x) => {
                  const contestado = materialArmado[x.categoria!]?.trim();
                  const mats = contestado ? [contestado] : x.ifc?.materiales ?? [];
                  return `${CATEGORIA_IFC_ES[x.categoria!] ?? x.categoria} «${x.capa}»${mats.length ? ` (${mats.join(', ')})` : ''}`;
                }),
            ),
          ]
            .slice(0, 40)
            .join('; ')
        : '';
    // Capa del plano 2D que solo se mide por el largo de sus líneas (ni muro, ni área): la IA dice si ese
    // largo es la cantidad del elemento o solo la suma de su dibujo (los escalones de una escalera).
    const porLineas = a.seleccion.tipo === 'capa' && !a.medidas && !a.esMuroDobleTrazo && !(a.areaM2 != null && a.areaM2 > 0) && !!a.medidoEnMetros;
    const res = await sugerirConceptos(projectId, fileId, {
      catalogId: catalogoArmado,
      seleccion: {
        tipo: a.seleccion.tipo,
        clave: a.seleccion.clave,
        ...(ifc ? { ifc } : {}),
        ...(contextoModelo ? { contextoModelo } : {}),
        // Lo que quien cotiza dijo que se construye ahí (una superficie que el plano dibuja como contorno).
        ...(a.seleccion.tipo === 'capa' && queEsArmado[a.seleccion.clave]?.trim()
          ? { dimensiones: `lo que es, según quien cotiza: ${queEsArmado[a.seleccion.clave]!.trim()}` }
          : {}),
        ...(porLineas
          ? {
              dimensiones:
                `lo medido: ${Math.round(a.cantidadDefault * 100) / 100} m, la suma del largo de las líneas de la capa` +
                (a.porPlanta?.length
                  ? ` (${a.porPlanta.filter((p) => p.largoM != null).map((p) => `${p.rotulo}: ${Math.round(p.largoM! * 100) / 100} m`).join(', ')})`
                  : ''),
            }
          : {}),
      },
    });
    const interp = res.interpretacion?.trim() || null;
    if (interp) setInterpretacionPorKey((prev) => new Map(prev).set(a.key, interp));
    const nombre = capaInfo?.categoria ? nombreLegibleTipoIfc(capaInfo) : a.seleccion.clave;
    const opciones = res.sugerencias.slice(0, 3).map(
      (x): OpcionArmado => ({
        resourceId: x.resourceId,
        catalogId: x.catalogId,
        clave: x.clave,
        descripcion: x.descripcion,
        unidad: x.unidad ?? null,
        precio: x.precio ?? null,
        razon: x.razonModelo ?? null,
        mismoElemento: x.mismoElemento ?? null,
        afectaPrecio: x.diferenciaAfectaPrecio ?? null,
        mismoSistema: x.mismoSistema ?? null,
      }),
    );
    // Lo que la IA no ve como obra (un eje, simbología) no se agrega solo.
    const noSeConstruye = res.sugerencias.length === 0 ? res.nota ?? null : null;
    const eleccion: Eleccion = noSeConstruye ? 'fuera' : elegirDelCatalogo(opciones, !res.notaFaltante);
    const medida = medidaParaArmado(a);
    const sintetico = { descripcion: semillaDescripcionSintetico(a, nombre, interp), unidad: medida.unidad };
    // Sin concepto del catálogo, la medida del elemento tal cual (su foto, si cuadra con ella).
    const fotoSinConcepto = (() => {
      const ft = fotoDeElemento(a, {
        magnitud: medida.unidad === 'm2' && !a.medidas && a.areaM2 != null && a.areaM2 > 0 ? { magnitud: 'area', inputExtra: 'ninguno' } : undefined,
        unidadConcepto: medida.unidad,
        altoStr: altoEfectivoArmado,
        espesorStr: '',
        altosVanos: altosVanosStr,
        altoDeDonde: altoArmadoDeDonde,
      });
      return ft && Math.abs(ft.total - medida.cantidad) < 1e-9 ? ft : null;
    })();
    const medidoBase: CantidadArmado =
      typeof eleccion === 'number'
        ? await medirArmado(c.key, opciones[eleccion]!)
        : { cantidad: medida.cantidad, revisar: false, nota: null, foto: fotoSinConcepto };
    // El largo de las líneas no mide la obra. Si el concepto se cobra por pieza y la IA contó las piezas del
    // dibujo (la escalera de planta baja y alta es una), entra esa cuenta para revisar; si no, sin cantidad.
    const porPieza =
      porLineas && res.medidaNoSirve && res.piezas != null && typeof eleccion === 'number'
        ? (await magnitudDeConceptoArmado(opciones[eleccion]!)) === 'conteo'
        : false;
    // Para generarlo con IA, la unidad de las líneas (m) no es la suya: que la decida quien lo genere.
    if (porLineas && res.medidaNoSirve) sintetico.unidad = null;
    const medido: CantidadArmado =
      porLineas && res.medidaNoSirve
        ? porPieza
          ? {
              cantidad: res.piezas!,
              revisar: true,
              nota: `Se cuentan ${res.piezas} del dibujo (el largo de sus líneas no mide la obra): revísalo.`,
              foto: {
                version: 1,
                origen: 'plano',
                magnitud: 'conteo',
                unidad: opciones[eleccion as number]?.unidad ?? null,
                decimales: 0,
                total: res.piezas!,
                lineas: [
                  {
                    elemento: { tipo: a.seleccion.tipo, clave: a.seleccion.clave },
                    grupo: '',
                    localizacion: nombre,
                    detalle: 'Piezas contadas del dibujo (el largo de sus líneas no mide la obra)',
                    piezas: res.piezas!,
                    largo: null,
                    ancho: null,
                    alto: null,
                    parcial: res.piezas!,
                    clase: 'medida',
                  },
                ],
              },
            }
          : { cantidad: null, revisar: true, nota: 'El largo de las líneas del dibujo no es la cantidad de este elemento: escríbela (cuántas piezas o tramos).' }
        : medidoBase;
    // El más parecido (no el exacto) entra marcado para revisar.
    // Solo si la diferencia cambia el precio: una regadera genérica por «regadera» no amerita alerta.
    // El material lo supuso la IA (nadie lo contestó y el modelo no lo dice): se dice y se marca para revisar.
    const notaMaterial = res.materialSupuesto
      ? `El modelo no dice el material: supuse ${res.materialSupuesto.material}${res.materialSupuesto.porQue ? ` (${res.materialSupuesto.porQue.replace(/\.$/, '')})` : ''}. Revísalo.`
      : null;
    const cantidad: CantidadArmado =
      (typeof eleccion === 'number' && res.notaFaltante && (opciones[eleccion]!.afectaPrecio !== false || (res.noSeSabe?.length ?? 0) > 0)) || (notaMaterial && eleccion !== 'fuera')
        ? { ...medido, revisar: true }
        : medido;
    const principal: PropuestaArmado = {
      key: c.key,
      tipo: 'principal',
      elementoKey: c.key,
      padre: null,
      nombre: c.nombre,
      medida: c.medida,
      opciones,
      eleccion,
      nota: noSeConstruye
        ? `Parece que no se construye: ${noSeConstruye}`
        : [
            notaMaterial,
            res.notaFaltante
              ? typeof eleccion === 'number'
                ? notaDelMasParecido(res.notaFaltante, opciones[eleccion]!, res.noSeSabe ?? [])
                : notaOtroSistema(res.notaFaltante, opciones) ?? res.notaFaltante
              : null,
          ].filter(Boolean).join(' ') || null,
      // Lo que el modelo dice del uso (losa de azotea vs. de entrepiso): los complementarios no se juntan entre usos.
      ...(ifc ? { uso: `${ifc.clase}|${ifc.predefinido ?? ''}` } : {}),
      sintetico,
      cantidad,
    };
    if (!op.complementarios || eleccion === 'fuera') return [principal];
    return [
      principal,
      ...(await complementosDeIa({ c, a, op, opciones, eleccion, sintetico, ifc, caras: res.caras, materialSupuesto: !!res.materialSupuesto })),
    ];
  };
  // Revisión final de la hoja armada (lo que falta y lo que sobra, mirándola completa). Lo que sobra se apaga
  // con su porqué; lo que falta pasa por el mismo camino que un complementario (catálogo, cantidad con las
  // medidas de su renglón, filtros de caras y salidas). Solo cuelga de renglones que pidieron complementarios.
  const revisarArmado = async (filas: PropuestaArmado[]): Promise<PropuestaArmado[]> => {
    if (!projectId || !fileId) return filas;
    const vivas = filas.filter((f) => f.eleccion !== 'fuera');
    const idDe = new Map<string, string>(vivas.map((f, i) => [f.key, `r${i + 1}`]));
    const keyDe = new Map<string, string>([...idDe].map(([k, id]) => [id, k]));
    const r = await revisarArmadoPlano(
      projectId,
      fileId,
      vivas.map((f) => {
        const o = typeof f.eleccion === 'number' ? f.opciones[f.eleccion] : null;
        return {
          id: idDe.get(f.key)!,
          tipo: f.tipo,
          padre: f.padre ? idDe.get(f.padre) ?? null : null,
          elemento: f.nombre.slice(0, 120),
          concepto: (o?.descripcion ?? f.sintetico?.descripcion ?? '').slice(0, 700),
          unidad: o?.unidad ?? f.sintetico?.unidad ?? null,
          cantidad: f.cantidad.cantidad,
        };
      }),
    );
    const quitar = new Map(r.quitar.map((q) => [keyDe.get(q.id), q.razon] as const));
    const salida = filas.map((f) =>
      quitar.has(f.key)
        ? f.deTipos
          ? // Lo asignó la persona: la revisión lo ve, pero no lo quita; queda marcado con su razón.
            {
              ...f,
              cantidad: { ...f.cantidad, revisar: true },
              nota: `La revisión final sugería quitarlo: ${quitar.get(f.key) || 'no corresponde'}. Lo dejamos porque ya estaba asignado. ${f.nota ?? ''}`.trim(),
            }
          : { ...f, eleccion: 'fuera' as const, nota: `La revisión final lo quita: ${quitar.get(f.key) || 'no corresponde'}. ${f.nota ?? ''}`.trim() }
        : f,
    );
    // Lo que falta, por renglón padre (si el padre es un complementario, se cuelga de su principal).
    const porPadre = new Map<string, PlanoComplementario[]>();
    // Un principal con complementarios RECORDADOS no trae los datos para pedirle otros a la IA: lo que la revisión dice
    // que falta se anota en su fila (marcada para revisar) en vez de perderse.
    const faltanEnRecordados = new Map<string, string[]>();
    for (const falta of r.faltan) {
      let k = keyDe.get(falta.padre);
      const fp = filas.find((f) => f.key === k);
      if (fp?.tipo === 'complemento') k = fp.padre ?? undefined;
      if (!k) continue;
      if (!paramsComplementosArmado.current.has(k)) {
        if (filas.find((f) => f.key === k)?.deTipos) {
          const texto = falta.necesidad;
          if (texto) (faltanEnRecordados.get(k) ?? faltanEnRecordados.set(k, []).get(k)!).push(texto);
        }
        continue;
      }
      (porPadre.get(k) ?? porPadre.set(k, []).get(k)!).push(falta);
    }
    const salidaConNotas = salida.map((f) => {
      const faltan = faltanEnRecordados.get(f.key);
      return faltan?.length
        ? { ...f, cantidad: { ...f.cantidad, revisar: true }, nota: `La revisión final sugiere agregar: ${faltan.join('; ')}. ${f.nota ?? ''}`.trim() }
        : f;
    });
    const nuevas = await Promise.all(
      [...porPadre].map(([k, lista]) =>
        armarComplementarios({ ...paramsComplementosArmado.current.get(k)!, lista, sufijo: 'rev' })
          .then((xs) => xs.map((f) => ({ ...f, nota: `La revisión final lo agrega: ${f.nota}` })))
          .catch(() => [] as PropuestaArmado[]),
      ),
    );
    return [...salidaConNotas, ...nuevas.flat()];
  };
  // «Lo que no trae el plano»: la misma consulta del botón de la barra (una por plano).
  const proponerNoDibujadoArmado = async (op?: OpcionesDeArmado): Promise<PropuestaArmado[]> => {
    if (!projectId || !fileId) return [];
    // Lo que el plano ya trae (con su medida): la IA no lo repite y estima con eso (el largo de los muros).
    const yaTrae = [
      ...new Set(
        candidatosArmado.map((x) => {
          // Por planta cuando el plano lo da (la cimentación va solo bajo los muros de planta baja).
          const asig = asignableDeClave(x.key);
          const plantas = asig?.porPlanta
            ?.filter((p) => p.largoM != null)
            .map((p) => `${p.rotulo}: ${Math.round(p.largoM! * 100) / 100} m`);
          // IFC: la clase y el tipo del modelo (una «Losa 20 cm» BASESLAB es la cimentación; FLOOR, el entrepiso).
          const capaIfc = asig?.seleccion.tipo === 'capa' ? inventario?.capas.find((c) => c.capa === asig.seleccion.clave) : undefined;
          const ifcTxt = capaIfc?.categoria ? `${capaIfc.categoria}${capaIfc.ifc?.predefinido ? ` ${capaIfc.ifc.predefinido}` : ''}` : null;
          // Lo que quien cotiza dijo que es va como NOMBRE: con eso se reconoce el repetido («firme de concreto»
          // no se vuelve a proponer); el nombre de la capa queda entre paréntesis.
          const queEs = asig?.seleccion.tipo === 'capa' ? queEsArmado[asig.seleccion.clave]?.trim() : '';
          const medida = [queEs ? x.nombre : null, ifcTxt, x.medida, plantas?.length ? plantas.join(', ') : null].filter(Boolean).join('; ');
          const nombre = queEs || x.nombre;
          return medida ? `${nombre} (${medida})` : nombre;
        }),
      ),
    ];
    const lista = await proponerInstalacionesSupuesto(projectId, fileId, undefined, yaTrae.length ? yaTrae : undefined);
    const filas = await Promise.all(
      lista.map(async (it, i): Promise<PropuestaArmado> => {
        // Se busca con la descripción completa que la IA ya redactó: la frase corta («Conexión de servicios»)
        // traía una conexión de tubería; la completa dice que es el trámite con los organismos.
        const completa = it.descripcion?.trim() || it.consultaCatalogo || it.nombre;
        let consulta = completa;
        let { opciones: ops, noEsExacto } = await buscarExtraArmado(completa);
        let eleccion = elegirDelCatalogo(ops, !noEsExacto);
        // Si con la descripción completa el catálogo no trae el MISMO elemento, se busca con la frase de catálogo que
        // redactó la IA: lo que la descripción dice que «incluye» arrastra la búsqueda (una «losa de azotea … que
        // incluye impermeabilización» traía solo impermeabilizaciones, la losa iba a IA y se quedaba sin sus trabajos).
        const frase = it.consultaCatalogo?.trim();
        if (eleccion === 'ia' && frase && frase !== completa) {
          const otra = await buscarExtraArmado(frase);
          const e2 = elegirDelCatalogo(otra.opciones, !otra.noEsExacto);
          if (e2 !== 'ia') {
            consulta = frase;
            ops = otra.opciones;
            noEsExacto = otra.noEsExacto;
            eleccion = e2;
          }
        }
        return {
          key: `nodibujado:${i}`,
          tipo: 'noDibujado',
          elementoKey: null,
          padre: null,
          nombre: it.nombre,
          medida: it.deDonde,
          opciones: ops,
          consulta,
          eleccion,
          nota: [
            it.disciplina?.trim() ? `${it.disciplina.trim()}: ${it.razon}` : it.razon,
            typeof eleccion === 'number' && noEsExacto && ops[eleccion]!.afectaPrecio !== false ? 'Va lo más parecido de tu catálogo: revísalo.' : null,
          ].filter(Boolean).join(' '),
          sintetico: { descripcion: it.descripcion, unidad: it.unidadEsperada },
          cantidad: {
            cantidad: it.cantidad,
            revisar: it.cantidad == null || (typeof eleccion === 'number' && !!noEsExacto && ops[eleccion]!.afectaPrecio !== false),
            nota: it.cantidad == null ? 'Con lo que trae el plano no se puede estimar: escríbela.' : null,
            // Números generadores: no sale de piezas dibujadas; se dice de dónde sale y con qué medidas del plano.
            foto:
              it.cantidad != null
                ? fotoDeRegla({
                    origen: 'no_dibujado',
                    unidad: (typeof eleccion === 'number' ? ops[eleccion]?.unidad : null) ?? it.unidadEsperada ?? null,
                    total: it.cantidad,
                    lineas: [{ elemento: null, localizacion: it.nombre, detalle: it.deDonde || 'Supuesto con lo que trae el plano', parcial: it.cantidad }],
                    supuesto: { deDonde: it.deDonde, base: yaTrae.slice(0, 80) },
                  })
                : null,
          },
          partida: grupoDe(it).partida,
        };
      }),
    );
    if (!op?.complementarios) return filas;
    // Lo estructural que el plano no dibuja también lleva sus complementarios (la losa de azotea, su
    // impermeabilización; el entrepiso, su plafón). Solo con concepto del catálogo: su magnitud (la que
    // clasifica la IA) dice de qué medida sale la cantidad de cada trabajo. Sin tabla de unidades.
    const conHijos = await Promise.all(
      filas.map(async (f, i): Promise<PropuestaArmado[]> => {
        const it = lista[i]!;
        const grupo = grupoDe(it).id;
        // Solo lo estructural: los pisos y acabados ya son ellos mismos el acabado (sus «complementarios»
        // repetían el firme o el piso que van en su propia partida).
        if ((grupo !== 'cimentacion' && grupo !== 'estructura') || typeof f.eleccion !== 'number') return [f];
        const o = f.opciones[f.eleccion]!;
        const cant = f.cantidad.cantidad;
        const m = await magnitudDeConceptoArmado(o);
        if (cant == null || m == null) return [f];
        const contexto = [
          `LO QUE EL PLANO DICE DEL ELEMENTO: ${it.descripcion} (lo estima el plano: ${it.deDonde || 'sin dato'})` +
            (it.caras?.length ? `; sus caras: ${it.caras.join(', ')}.` : '.'),
          `EL PLANO YA TRAE COMO ELEMENTOS PROPIOS (se cotizan aparte): ${[...new Set(candidatosArmado.map((x) => x.nombre))].slice(0, 60).join('; ')}.`,
          // Las otras partidas de lo que no trae el plano también van aparte (el piso de la planta alta no
          // es complementario de la losa de entrepiso).
          `SE PRESUPUESTAN APARTE, cada una en su partida: ${lista.filter((_, j) => j !== i).map((y) => y.nombre).join('; ')}.`,
          'LAS SALIDAS DE INSTALACIONES SE COTIZAN APARTE para toda la obra: no las propongas.',
        ].join('\n');
        const comps = await armarComplementarios({
          key: f.key,
          nombrePadre: it.nombre,
          selTipo: 'capa',
          selClave: it.nombre,
          opciones: f.opciones,
          eleccion: f.eleccion,
          sintetico: f.sintetico,
          contexto,
          conceptoK: `nodibujado:${o.resourceId}|${it.nombre.toLowerCase()}`,
          mag: {
            conteo: m === 'conteo' ? cant : null,
            largo: m === 'lineal' ? cant : null,
            largoConVanos: null,
            area: m === 'area' ? cant : null,
            volumen: m === 'volumen' ? cant : null,
            sinAltoDeVano: false,
            vanos: null,
          },
          partida: f.partida,
          sinYaPropuestas: true,
          salidasAparte: true,
          ...(it.caras?.length ? { caras: it.caras } : {}),
        }).catch(() => []);
        return [f, ...comps];
      }),
    );
    return conHijos.flat();
  };
  // La cantidad con la unidad del concepto: la misma regla que el panel (`cantidadParaConcepto`), con el
  // alto de muros del asistente y los altos de puertas y ventanas que ya conoce el plano.
  const medirArmado = async (
    key: string,
    o: OpcionArmado,
    // Lo recordado dice qué se mide: no se vuelve a preguntar a la IA (sin eso, la IA decide como siempre).
    magnitudConocida?: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null,
    // true = ya se le preguntó a la IA por este concepto (aunque no supo): no se vuelve a preguntar ni se depende del estado de React.
    yaPreguntada = false,
  ): Promise<CantidadArmado> => {
    const a = asignableDeClave(key);
    if (!a || !projectId || !fileId) return { cantidad: null, revisar: true, nota: 'No se encontró en el plano.' };
    let magnitud: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null | undefined = magnitudConocida ?? magnitudCache.get(o.resourceId);
    if (magnitud === undefined && yaPreguntada) magnitud = null;
    if (magnitud === undefined) {
      const clasificada = await clasificarMagnitud(projectId, fileId, o.catalogId || catalogoArmado, o.resourceId).catch(() => null);
      magnitud = clasificada;
      const m = clasificada;
      setMagnitudCache((prev) => new Map(prev).set(o.resourceId, m));
    }
    const altosVanos: Record<string, string> = { ...altosVanosStr };
    for (const t of a.muroVanos?.tipos ?? []) {
      if (altosVanos[t.clave] != null) continue;
      const pr = proponerAltoDeVano(t, inventario?.anotaciones, a.muroVanos!.factor);
      if (pr != null) altosVanos[t.clave] = String(pr);
    }
    const vanos = cuentaDeVanosDe(a.muroVanos, altoEfectivoArmado, altosVanos);
    const info = a.medidas ? inventario?.capas.find((c) => c.capa === a.seleccion.clave) : undefined;
    const espesor = a.espesorSugerido != null ? String(a.espesorSugerido) : '';
    const txt = cantidadParaConcepto({
      magnitud,
      conceptoElegido: true,
      unidadConcepto: o.unidad ?? undefined,
      cantidadDefault: a.cantidadDefault,
      baseEsConteo: a.seleccion.tipo === 'bloque',
      medidas: a.medidas,
      mandaIfc: info?.medidas?.manda,
      areaM2: a.areaM2,
      sinEscala: a.sinEscala,
      areaMuroPorPlantas: a.areaMuroPorPlantas,
      netoVanosM2: vanos?.netoM2 ?? null,
      altoStr: altoEfectivoArmado,
      espesorStr: espesor,
    });
    const cantidad = Number(txt.replace(',', '.'));
    const sinAltoDeVano =
      !!a.muroVanos &&
      cantidadDeMuroSinAltoDeVano({ magnitud, areaM2: a.areaM2, sinEscala: a.sinEscala, tiposVanosSinAlto: vanos?.tiposSinAlto });
    const ok = Number.isFinite(cantidad) && cantidad > 0;
    const foto = ok
      ? fotoDeElemento(a, { magnitud, unidadConcepto: o.unidad, altoStr: altoEfectivoArmado, espesorStr: espesor, altosVanos, altoDeDonde: altoArmadoDeDonde })
      : null;
    return {
      cantidad: ok ? cantidad : null,
      foto: foto && Math.abs(foto.total - cantidad) < 1e-9 ? foto : null,
      revisar: !!a.revisar || sinAltoDeVano,
      nota: !ok
        ? 'Falta un dato para medirlo con la unidad de este concepto (el alto o el espesor): escribe la cantidad.'
        : sinAltoDeVano
          ? 'Hay puertas o ventanas sin alto: se descontaron de piso a techo. Entra marcado para revisar.'
          : a.revisar
            ? 'La medida del plano es dudosa: entra marcado para revisar.'
            : null,
    };
  };
  // En orden: cada principal antes que sus complementarios (cuelgan de su renglón).
  const agregarArmado = async (
    lista: FilaParaAgregar[],
    avance?: (n: number, total: number, etapa: 'agregando' | 'actualizando') => void,
    /**
     * `modo` fijo ('faltante') = ignora lo que haya elegido el asistente (nunca reemplaza la hoja); `soloSiFalta` = el servidor
     * no escribe nada si el elemento ya está en la hoja («Poner en la hoja lo asignado»: no pisa el trabajo del cotizador).
     */
    opts?: { modo?: 'faltante'; soloSiFalta?: boolean },
  ) => {
    let procesadas = 0;
    let agregados = 0;
    let conIa = 0;
    let fallidos = 0;
    let yaEnLaHoja = 0;
    let importe = 0;
    let sinCantidad = 0;
    if (!projectId || !fileId) return { agregados, conIa, fallidos: lista.length, yaEnLaHoja, importe, sinCantidad };
    // «Rehacer la hoja»: la lista entera va en UNA llamada que reemplaza la hoja del plano (o no cambia
    // nada). Los complementarios nombran a su principal por la clave de la fila, no por id.
    const rehacer = (opts?.modo ?? modoArmado) === 'rehacer';
    const filasRehacer: FilaReemplazoHoja[] = [];
    const renglonDe = new Map<string, string>();
    for (const f of lista) {
      avance?.(++procesadas, lista.length, 'agregando');
      const a = f.elementoKey ? asignableDeClave(f.elementoKey) : null;
      // Un complementario de «lo que no trae el plano» no tiene elemento del plano: cuelga de su renglón.
      if (f.tipo === 'principal' && !a) {
        fallidos++;
        continue;
      }
      // Un complementario juntado cuelga del primero de sus principales que haya entrado a la hoja.
      const idsPadres =
        f.tipo === 'complemento'
          ? [...new Set([f.padre, ...(f.padres ?? [])].map((k) => (k ? renglonDe.get(k) : undefined)).filter((x): x is string => !!x))]
          : [];
      const principalId = idsPadres[0] ?? null;
      if (f.tipo === 'complemento' && !principalId) {
        fallidos++;
        continue;
      }
      // El principal lleva la selección del plano; el complementario cuelga de él; lo que el plano no
      // dibuja no tiene selección y va en la partida de su grupo.
      // Un renglón por concepto: el principal que junta varios elementos lleva la selección del primero y,
      // en `ademas`, la de los otros (la hoja los da a todos por presupuestados).
      const otros = f.tipo === 'principal'
        ? (f.elementos ?? []).filter((k) => k !== f.elementoKey).map((k) => asignableDeClave(k)).filter((x): x is Asignable => !!x)
        : [];
      const mismo = <T,>(xs: T[]) => (xs.every((x) => x === xs[0]) ? xs[0] : undefined);
      // Números generadores: la foto de lo que midió el plano; si la persona escribió otra cantidad, se dice.
      const generador = f.foto ?? (f.cantidadEscritaAMano ? fotoAMano(f.cantidad, f.opcion?.unidad ?? f.sintetico?.unidad ?? null) : undefined);
      const comun = {
        cantidad: f.cantidad,
        revisar: f.revisar,
        ...(generador ? { generador, cantidadEscritaAMano: !!f.cantidadEscritaAMano && !!f.foto } : {}),
        ...(idsPadres.length > 1 ? { padres: idsPadres.slice(1) } : {}),
        rol: f.tipo === 'complemento' ? ('complemento' as const) : ('principal' as const),
        ...(f.tipo === 'complemento' ? { principalId } : {}),
        ...(f.tipo === 'principal' && a
          ? {
              seleccion: otros.length ? { ...a.seleccion, ademas: otros.map((o) => o.seleccion) } : a.seleccion,
              nivel: mismo([a, ...otros].map((x) => x.nivel)) ?? (otros.length ? 'Varios niveles' : a.nivel),
              ubicacion: mismo([a, ...otros].map((x) => x.ubicacion)) ?? (otros.length ? 'Varias zonas' : a.ubicacion),
            }
          : {}),
        ...(f.partida ? { partida: f.partida } : {}),
        // «Conceptos por tipo»: los ejes recordados, y lo que se mide solo mientras siga elegido el concepto recordado.
        ...(f.tipo === 'principal' && f.deTipos?.agrupacion && f.opcion?.resourceId === f.deTipos.resourceId ? { agrupacion: f.deTipos.agrupacion } : {}),
        ...(f.tipo === 'principal' && f.deTipos?.magnitud && f.opcion?.resourceId === f.deTipos.resourceId
          ? { magnitudConcepto: f.deTipos.magnitud }
          : {}),
      };
      if (rehacer) {
        const ligas = {
          ref: f.key,
          principalRef: principalId,
          ...(idsPadres.length > 1 ? { padresRefs: idsPadres.slice(1) } : {}),
        };
        const { principalId: _p, padres: _pa, ...cuerpo } = comun as typeof comun & { principalId?: string | null; padres?: string[] };
        if (f.opcion) {
          filasRehacer.push({ ...cuerpo, ...ligas, fuente: 'catalogo', catalogId: f.opcion.catalogId || catalogoArmado, resourceClave: f.opcion.clave });
        } else if (f.sintetico) {
          filasRehacer.push({ ...cuerpo, ...ligas, fuente: 'descripcion', descripcion: f.sintetico.descripcion, unidad: f.sintetico.unidad ?? undefined });
          conIa++;
        } else continue;
        if (f.tipo !== 'complemento') renglonDe.set(f.key, f.key);
        agregados++;
        continue;
      }
      try {
        const r = f.opcion
          ? await asignarPlanoRenglon(projectId, fileId, {
              ...comun,
              catalogId: f.opcion.catalogId || catalogoArmado,
              resourceClave: f.opcion.clave,
              // El renglón EXACTO del catálogo: la misma clave puede existir en dos unidades.
              resourceId: f.opcion.resourceId,
              ...(opts?.soloSiFalta && f.tipo === 'principal' ? { soloSiFalta: true } : {}),
            })
          : f.sintetico
            ? await agregarRenglonPorDescripcion(projectId, fileId, {
                ...comun,
                descripcion: f.sintetico.descripcion,
                unidad: f.sintetico.unidad ?? undefined,
              })
            : null;
        if (!r) continue;
        if (!f.opcion) conIa++;
        if (f.tipo !== 'complemento') renglonDe.set(f.key, r.id);
        agregados++;
        importe += r.importe ?? 0;
        if (f.cantidad === 0) sinCantidad++;
      } catch (e) {
        // Con «solo si falta»: el elemento ya estaba en la hoja y no se tocó (no es un fallo).
        if (e instanceof ApiClientError && e.code === 'YA_EN_LA_HOJA') yaEnLaHoja++;
        else fallidos++;
      }
    }
    if (rehacer) {
      // Todo o nada: si una fila no se pudo armar, no se reemplaza (la hoja vieja sí la tenía).
      if (fallidos > 0) {
        throw new Error(
          `${fallidos === 1 ? 'Un renglón no se pudo armar' : `${fallidos} renglones no se pudieron armar`} (su elemento ya no está o no tiene concepto). No se cambió nada: tu hoja sigue igual. Quítalos de la lista o completa su concepto.`,
        );
      }
      try {
        await reemplazarHojaPlano(projectId, fileId, { filas: filasRehacer, renglonesVistos: renglonesVistosArmado });
      } catch (e) {
        // La hoja pudo cambiar desde otra pestaña: se vuelve a leer para que el siguiente intento use lo vigente.
        void refrescarRenglones();
        if (e instanceof ApiClientError && e.code === 'FILA_INVALIDA') {
          const ref = (e.body as { ref?: string } | null)?.ref;
          const fila = lista.find((x) => x.key === ref);
          const nombre = fila?.opcion?.descripcion ?? fila?.sintetico?.descripcion;
          if (nombre) throw new Error(`No se pudo poner «${nombre.slice(0, 80)}». No se cambió nada: tu hoja anterior sigue igual.`);
        }
        throw e;
      }
      setModoArmado('faltante');
    }
    setHpAbierto(true);
    avance?.(lista.length, lista.length, 'actualizando');
    await refrescarRenglones();
    await refrescarOpciones();
    // Fuego y olvido: cuenta el uso de cada receta recordada que entró a la hoja.
    if (MEMORIA_TIPOS_ENABLED && agregados > 0) {
      // Solo cuenta si la fila sigue con el concepto recordado (si la persona lo cambió, ya no se usó lo recordado).
      const ids = [...new Set(lista.filter((f) => f.opcion?.resourceId === f.deTipos?.resourceId).map((f) => f.deTipos?.memoriaId).filter((x): x is string => !!x))];
      if (ids.length) void sumarUsosDeMemoria(ids).catch(() => undefined);
    }
    return { agregados, conIa, fallidos, yaEnLaHoja, importe, sinCantidad };
  };
  // ===== «Poner en la hoja lo asignado» (desde «Asignar conceptos a elementos») =====
  // Pone en la hoja, SIN IA para elegir conceptos, lo que la persona ya asignó (o la memoria exacta y vigente) a los elementos que
  // todavía no tienen concepto. Reutiliza el armado (`proponerDeReceta`, `agruparPorConcepto`, `agregarArmado`): no hay un segundo
  // camino de alta. Nunca rehace la hoja ni pisa un concepto que ya está en ella (el servidor lo vuelve a comprobar).
  type PrepEnvio = { plan: PlanDeEnvio; elementos: ElementoParaEnvio[]; candidatos: CandidatoArmado[]; mem: { reconocer: ReconocerTiposResponse | null; plano: TiposDelPlanoResponse | null }; yaEnLaHoja: number };
  const prepararEnvioAsignado = async (catalogIds: string[]): Promise<PrepEnvio | { bloqueo: string }> => {
    if (!MEMORIA_TIPOS_ENABLED || !inventario || !projectId || !fileId) return { bloqueo: 'No se puede agregar nada a la hoja de este plano.' };
    if (catalogIds.length === 0) return { bloqueo: 'Marca al menos un catálogo.' };
    if (!esInventarioIfc && factorDeEscala(inventario, escalaElegida).dudosa) {
      return { bloqueo: 'Antes di en qué unidad está dibujado el plano: se pregunta al abrir «Presupuestar todo el plano».' };
    }
    const candidatos = construirCandidatos(true);
    const mem = await cargarMemoriaArmado(candidatos, catalogIds);
    if (!mem) return { bloqueo: 'No se pudo leer lo asignado. Inténtalo otra vez.' };
    const elementos: ElementoParaEnvio[] = candidatos.map((c) => ({
      key: c.key,
      claves: clavesDeCandidato(c),
      gids: piezasDelTipo(c.key).flatMap((p) => (p.gid ? [p.gid] : [])),
    }));
    const plan = planDeEnvio(elementos, mem, conceptosResueltos(mem.plano, mem.reconocer));
    return { plan, elementos, candidatos, mem, yaEnLaHoja: Math.max(0, elementosDelDialogo().length - candidatos.length) };
  };
  const ponerEnLaHojaLoAsignado = async (
    prep: PrepEnvio,
    avance: (n: number, total: number, etapa: 'midiendo' | 'agregando' | 'actualizando') => void,
  ) => {
    if (!projectId || !fileId) throw new Error('No hay un plano abierto.');
    const { plan, candidatos, mem } = prep;
    const resueltos = conceptosResueltos(mem.plano, mem.reconocer);
    const op: OpcionesDeArmado = { complementarios: true, noDibujado: false };
    memoriaArmado.current = Promise.resolve(mem);
    principalesArmado.current = new Set();
    const entran = candidatos.filter((c) => plan.entran.some((e) => e.key === c.key));
    const resDe = (c: CandidatoArmado) =>
      resolverElemento(clavesDeCandidato(c), mem.plano?.asignaciones ?? [], mem.reconocer, piezasDelTipo(c.key).flatMap((p) => (p.gid ? [p.gid] : [])));

    // 1) Qué se mide, UNA consulta por concepto distinto (no por elemento): solo los principales que no recordaron qué se mide.
    const porConsultar = new Map<string, string>(); // resourceId → catálogo
    for (const c of entran) {
      const res = resDe(c);
      for (const receta of [res.receta, ...res.excepciones.map((g) => g.receta)]) {
        const lp = receta && !receta.noPresupuestar ? receta.lineas.find((l) => l.rol === 'principal') : undefined;
        const r = lp && !lp.magnitud ? resueltoDeLinea(resueltos, lp) : undefined;
        if (lp && r) porConsultar.set(r.resourceId, lp.catalogId || catalogoArmado);
      }
    }
    const magnitudes = new Map<string, Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null>();
    let preguntadas = 0;
    avance(0, porConsultar.size, 'midiendo');
    await Promise.all(
      [...porConsultar].map(([resourceId, catalogId]) =>
        limiteIaArmado(async () => {
          magnitudes.set(resourceId, await clasificarMagnitud(projectId, fileId, catalogId, resourceId).catch(() => null));
          avance(++preguntadas, porConsultar.size, 'midiendo');
        }),
      ),
    );

    // 2) Las líneas de cada elemento con su cantidad medida (la misma medición del armado).
    const opts = { sinIa: true, magnitudes };
    const propuestas: PropuestaArmado[] = [];
    for (const c of entran) {
      const e = plan.entran.find((x) => x.key === c.key)!;
      if (e.grupos > 0) propuestas.push(...(await proponerExcepciones(c, op, opts)));
      if (e.base) {
        const a = asignableDeClave(c.key);
        const filas = a ? await proponerDeReceta(c, a, resDe(c), mem, op, opts) : null;
        if (filas) propuestas.push(...filas);
      }
    }

    // 3) Una línea por concepto y a la hoja, sin tocar lo que ya hay.
    const lista = filasParaAgregarDe(agruparPorConcepto(propuestas));
    const r = await agregarArmado(lista, (n, total, etapa) => avance(n, total, etapa), { modo: 'faltante', soloSiFalta: true });
    setMemoriaTick((n) => n + 1);
    return { ...r, lineas: lista.length, revisar: lista.filter((x) => x.revisar).length };
  };
  // ===== «Dos opciones con precio» (idea 7) =====
  // Una opción de un principal: otro concepto con la MISMA medición del renglón y sus propios complementarios,
  // pedidos con el mismo motor de «Presupuestar todo el plano» (catálogo primero; la IA propone las reglas,
  // sin tabla fija). Todo lo derivado entra «para revisar». No toca la hoja: se guarda aparte.
  const buscarTextoEnCatalogos = async (q: string): Promise<ConceptoParaOpcion[]> => {
    const listas = await Promise.all(
      catalogosPrioridad.map((cid) =>
        listCatalogResources(cid, { q, tipos: [7], limit: 30, todasLasPalabras: true })
          .then((r) => r.resources.map((x) => ({ x, cid })))
          .catch(() => []),
      ),
    );
    const vistos = new Set<string>();
    const out: ConceptoParaOpcion[] = [];
    for (const { x, cid } of listas.flat()) {
      if (vistos.has(x.id) || out.length >= 30) continue;
      vistos.add(x.id);
      out.push({ resourceId: x.id, catalogId: cid, clave: x.clave, descripcion: x.descripcion, unidad: x.unidad ?? null, precio: x.precio ?? null });
    }
    return out;
  };
  const armarOpcionDeRenglon = async (
    r: PlanoRenglon,
    pedido: { elegido: ConceptoParaOpcion | null; texto: string },
    avance: (t: string) => void,
    /** En un grupo, los miembros comparten el MISMO concepto: lo que la IA propuso para él (indexado por concepto) se reusa. */
    opciones?: { reusarComplementos?: boolean },
  ): Promise<{ renglones: RenglonDeOpcion[]; nombre: string; avisos: string[] }> => {
    if (!projectId || !fileId) throw new Error('No hay un plano abierto.');
    const lado = ladoActualDeLaHoja(renglones, r.id);
    if (!lado) throw new Error('El concepto ya no está en la hoja.');
    // Cada opción es su propia corrida (como abrir «Presupuestar todo el plano»).
    cacheExtraArmado.clear();
    if (!opciones?.reusarComplementos) cacheComplementosArmado.clear();
    cacheMagnitudArmado.clear();
    yaPropuestoArmado.current = [];
    principalesArmado.current = new Set();
    paramsComplementosArmado.current = new Map();
    const avisos: string[] = [];
    const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
    try {
      // 1) El concepto: el elegido de la lista o, si no, la búsqueda de siempre (catálogo primero; IA solo si no hay).
      let opciones: OpcionArmado[];
      let eleccion: Eleccion;
      let notaFaltante: string | null = null;
      if (pedido.elegido) {
        opciones = [{ ...pedido.elegido, razon: null }];
        eleccion = 0;
      } else {
        avance('Buscando el concepto en tus catálogos…');
        const res = await conSaldo(() =>
          sugerirConceptos(projectId, fileId, { catalogId: catalogoArmado, yaEsConcepto: true, seleccion: { tipo: 'capa', clave: pedido.texto } }),
        );
        opciones = res.sugerencias.slice(0, 3).map(
          (x): OpcionArmado => ({
            resourceId: x.resourceId,
            catalogId: x.catalogId,
            clave: x.clave,
            descripcion: x.descripcion,
            unidad: x.unidad ?? null,
            precio: x.precio ?? null,
            razon: x.razonModelo ?? null,
            mismoElemento: x.mismoElemento ?? null,
            afectaPrecio: x.diferenciaAfectaPrecio ?? null,
            mismoSistema: x.mismoSistema ?? null,
          }),
        );
        notaFaltante = res.notaFaltante ?? null;
        eleccion = elegirDelCatalogo(opciones, !notaFaltante);
      }
      const o = typeof eleccion === 'number' ? opciones[eleccion]! : null;
      const sintetico = { descripcion: pedido.texto || r.conceptoDescripcion, unidad: r.unidad };
      if (o) principalesArmado.current.add(o.resourceId);
      const unidad = o ? o.unidad : sintetico.unidad;

      // 2) La MISMA medición del renglón; si el concepto se cobra en otra unidad, se mide el elemento con ella.
      avance('Midiendo con la misma medición del renglón…');
      const actual = lado.renglones[0]!;
      const asignables: (readonly [string, Asignable])[] = [];
      for (const e of elementosDeSeleccion(r.seleccion)) {
        const k = `${e.tipo}:${e.clave}`;
        const a = asignableDeClave(k);
        if (a) asignables.push([k, a] as const);
      }
      const misma = mismaMedicion(actual, unidad);
      let cantidad: CantidadArmado;
      if (misma) cantidad = { cantidad: misma.cantidad, revisar: true, nota: null, foto: misma.generador };
      else if (o && asignables.length > 0) {
        const medidas = await Promise.all(asignables.map(([k]) => conSaldo(() => medirArmado(k, o))));
        const ok = medidas.every((m) => m.cantidad != null);
        cantidad = {
          cantidad: ok ? Math.round(medidas.reduce((t, m) => t + (m.cantidad ?? 0), 0) * 1000) / 1000 : null,
          revisar: true,
          nota: medidas.find((m) => m.nota)?.nota ?? null,
          foto: ok ? juntarTodas(medidas.map((m) => m.foto)) : null,
        };
        avisos.push(`Este concepto se cobra en ${unidad ?? 'otra unidad'} y el de hoy en ${r.unidad ?? 'otra'}: su cantidad se midió del plano con esa unidad.`);
      } else {
        cantidad = { cantidad: null, revisar: true, nota: 'No se pudo medir con la unidad de este concepto: al usarla, escribe la cantidad en la hoja.' };
      }
      if (cantidad.foto && cantidad.cantidad != null && Math.abs(cantidad.foto.total - (misma?.cantidadMedida ?? cantidad.cantidad)) > 1e-3) {
        cantidad = { ...cantidad, foto: null };
      }

      // 3) Lo que acompaña a la opción, por elemento y con su medida (el mismo motor de «Presupuestar todo el plano»).
      avance('Proponiendo lo que acompaña a la opción…');
      const otros = [...new Set(renglones.filter((x) => x.rol === 'principal' && x.id !== r.id).map((x) => x.conceptoDescripcion.slice(0, 90)))];
      const hayNoDibujado = renglones.some((x) => x.rol === 'principal' && !x.seleccion);
      const conceptoK = o ? `cat:${o.resourceId}` : `ia:${sintetico.descripcion.trim().toLowerCase()}|${sintetico.unidad ?? ''}`;
      const porElemento = await Promise.all(
        asignables.map(async ([k, a]): Promise<PropuestaArmado[]> => {
          const capaInfo = a.seleccion.tipo === 'capa' ? inventario?.capas.find((x) => x.capa === a.seleccion.clave) : undefined;
          const ifc = capaInfo ? describirTipoIfcParaSugerencia(capaInfo) : null;
          const nombre = capaInfo?.categoria ? nombreLegibleTipoIfc(capaInfo) : a.seleccion.clave;
          const contexto = [
            ifc
              ? `LO QUE EL MODELO DICE DEL ELEMENTO: ${ifc.etiqueta}; clase IFC ${ifc.clase}` +
                `${ifc.predefinido ? `; tipo ${ifc.predefinido}` : ''}` +
                `; material: ${ifc.materiales?.length ? ifc.materiales.join(', ') : 'no lo dice'}` +
                `${ifc.esExterior === true ? '; exterior' : ifc.esExterior === false ? '; interior' : ''}.`
              : null,
            !ifc && a.porPlanta?.length
              ? `LO QUE EL PLANO DICE DEL ELEMENTO: está dibujado en ${a.porPlanta
                  .map((pl) => {
                    const m = [pl.largoM != null ? `largo ${Math.round(pl.largoM * 100) / 100} m` : null, pl.altoM != null ? `alto ${pl.altoM} m` : null].filter(Boolean);
                    return `«${pl.rotulo}»${m.length ? ` (${m.join(', ')})` : ''}`;
                  })
                  .join(', ')}.`
              : null,
            otros.length ? `EL PLANO YA TRAE COMO ELEMENTOS PROPIOS (se cotizan aparte): ${otros.slice(0, 60).join('; ')}.` : null,
            hayNoDibujado
              ? 'LAS SALIDAS DE INSTALACIONES (eléctricas, hidráulicas, sanitarias, de gas, de voz y datos) SE COTIZAN APARTE para toda la obra: no las propongas.'
              : null,
          ]
            .filter(Boolean)
            .join('\n');
          const principalFila: PropuestaArmado = {
            key: k,
            tipo: 'principal',
            elementoKey: k,
            padre: null,
            nombre,
            medida: '',
            opciones,
            eleccion,
            nota: null,
            ...(ifc ? { uso: `${ifc.clase}|${ifc.predefinido ?? ''}` } : {}),
            sintetico,
            cantidad: { cantidad: null, revisar: true, nota: null },
          };
          const comps = await conSaldo(() =>
            armarComplementarios({
              key: k,
              nombrePadre: nombre,
              selTipo: a.seleccion.tipo,
              selClave: a.seleccion.clave,
              opciones,
              eleccion,
              sintetico,
              contexto,
              conceptoK,
              mag: magnitudesArmado(a),
              porPlanta: a.porPlanta,
              salidasAparte: hayNoDibujado,
              porVanos: true,
            }),
          );
          return [principalFila, ...comps];
        }),
      );
      const juntas = agruparPorConcepto(porElemento.flat());

      const out: RenglonDeOpcion[] = [
        {
          rol: 'principal',
          origen: o ? 'catalogo' : 'ia',
          catalogId: o?.catalogId ?? null,
          clave: o?.clave ?? null,
          descripcion: o?.descripcion ?? sintetico.descripcion,
          unidad: unidad ?? null,
          cantidad: cantidad.cantidad ?? 0,
          ...(cantidad.cantidad == null ? { sinCantidad: true } : {}),
          cantidadMedida: misma?.cantidadMedida ?? null,
          precioUnitario: o?.precio ?? null,
          importe: o?.precio != null && cantidad.cantidad != null ? r2(o.precio * cantidad.cantidad) : null,
          generador: cantidad.cantidad != null ? cantidad.foto ?? null : null,
          revisar: true,
          nota:
            [
              o && notaFaltante ? notaDelMasParecido(notaFaltante, o) : null,
              !o ? (notaFaltante ? notaOtroSistema(notaFaltante, opciones) ?? notaFaltante : 'Tus catálogos no lo tienen: se genera con IA al usar la opción.') : null,
              cantidad.nota,
            ]
              .filter(Boolean)
              .join(' ') || null,
        },
      ];
      let apagados = 0;
      for (const f of juntas) {
        if (f.tipo !== 'complemento') continue;
        const oc = typeof f.eleccion === 'number' ? f.opciones[f.eleccion] : undefined;
        // Lo típico que tus catálogos no tienen entra para generarlo con IA (sin precio todavía); lo que depende
        // de la obra no entra (en «Presupuestar todo el plano» también va apagado).
        const nota = f.nota ?? '';
        const sinCatalogo = f.eleccion === 'ia' || (f.eleccion === 'fuera' && nota.includes('Tus catálogos no lo tienen') && !nota.includes('Depende de la obra'));
        if (!oc && !sinCatalogo) {
          apagados++;
          continue;
        }
        const q = f.cantidad.cantidad;
        out.push({
          rol: 'complemento',
          origen: oc ? 'catalogo' : 'ia',
          catalogId: oc?.catalogId ?? null,
          clave: oc?.clave ?? null,
          descripcion: oc?.descripcion ?? f.sintetico.descripcion,
          unidad: oc?.unidad ?? f.sintetico.unidad,
          cantidad: q ?? 0,
          // Sin cantidad (el plano no la da): entra marcada para escribirla, nunca como un 0 callado.
          ...(q == null ? { sinCantidad: true } : {}),
          precioUnitario: oc?.precio ?? null,
          importe: oc?.precio != null && q != null ? r2(oc.precio * q) : null,
          nombre: f.nombre,
          regla: f.cantidad.foto?.regla ?? null,
          generador: q != null && f.cantidad.foto && Math.abs(f.cantidad.foto.total - q) < 1e-3 ? f.cantidad.foto : null,
          revisar: true,
          nota: [f.nota, f.cantidad.nota].filter(Boolean).join(' ') || null,
        });
      }
      if (apagados > 0) {
        avisos.push(
          `${apagados === 1 ? 'Un trabajo que depende de la obra no se incluyó' : `${apagados} trabajos que dependen de la obra no se incluyeron`}: agrégalos en la hoja si van.`,
        );
      }
      const nombre = (o?.descripcion ?? sintetico.descripcion).replace(/\s+/g, ' ').trim().slice(0, 200);
      return { renglones: out, nombre, avisos };
    } catch (e) {
      if ((e as Error).message === SIN_SALDO) throw new SaldoAgotadoAlArmar('Ya no te queda saldo este mes: no se pudo terminar de armar la opción.');
      throw e instanceof ApiClientError ? new Error(e.message) : e;
    } finally {
      void useUsageStore.getState().refresh();
    }
  };
  // «Comparar todos los muros»: la misma opción armada, una por concepto, y guardada como un grupo que se
  // compara y se usa junta. Se arma en secuencia (cada concepto es su propia corrida: así uno no pierde los
  // adicionales que ya propuso otro). Si se acaba la bolsa a mitad, se guardan los ya armados (si son 2 o más).
  const armarGrupoDeOpciones = async (
    principal: PlanoRenglon,
    pedido: { elegido: ConceptoParaOpcion; texto: string },
    extras: PlanoRenglon[],
    avance: (t: string) => void,
  ): Promise<void> => {
    if (!projectId || !fileId) throw new Error('No hay un plano abierto.');
    const todos = [principal, ...extras];
    const miembros: { principalId: string; nombre: string; renglones: RenglonDeOpcion[] }[] = [];
    let cortado: string | null = null;
    for (let i = 0; i < todos.length; i++) {
      const m = todos[i]!;
      try {
        // Del segundo en adelante se reusa lo que la IA ya propuso para este concepto (misma consulta, mismo resultado).
        const r = await armarOpcionDeRenglon(m, pedido, (t) => avance(`${i + 1} de ${todos.length} · ${t}`), { reusarComplementos: i > 0 });
        miembros.push({ principalId: m.id, nombre: r.nombre, renglones: r.renglones });
      } catch (e) {
        if (miembros.length >= 2 && e instanceof SaldoAgotadoAlArmar) {
          cortado = `Se acabó el saldo del mes: se guardaron ${miembros.length} de ${todos.length} conceptos.`;
          break;
        }
        throw e;
      }
    }
    if (miembros.length < 2) throw new Error('No se pudo armar el grupo: hacen falta al menos 2 conceptos.');
    avance('Guardando el grupo…');
    const nombre = `${pedido.elegido.descripcion.replace(/\s+/g, ' ').trim().slice(0, 150)} · en ${miembros.length} conceptos`;
    const g = await crearGrupoOpcionesPlano(projectId, fileId, { nombre, miembros });
    await refrescarOpciones();
    setOpcionesDe(null);
    setGrupoAbierto(g.grupoId);
    if (cortado) dispatchToast(<Toast><ToastTitle>{cortado}</ToastTitle></Toast>, { intent: 'warning', timeout: 7000 });
  };
  // Un adicional que se le olvidó a un principal que ya está en la hoja (desde la fila, sin buscar el elemento
  // en el dibujo): entra como complemento de ese principal, con la misma medición o la cantidad escrita.
  const agregarAdicionalAlPrincipal = async (principal: PlanoRenglon, ren: RenglonDeOpcion): Promise<void> => {
    if (!projectId || !fileId) throw new Error('No hay un plano abierto.');
    try {
      const comun = {
        cantidad: ren.cantidad,
        unidad: ren.unidad ?? undefined,
        rol: 'complemento' as const,
        principalId: principal.id,
        revisar: true,
        nivel: principal.agrupacion?.nivel ?? null,
        ubicacion: principal.agrupacion?.ubicacion ?? null,
        ...(principal.seleccion ? { seleccion: { tipo: principal.seleccion.tipo, clave: principal.seleccion.clave, ids: principal.seleccion.ids } } : {}),
        ...(ren.generador ? { generador: ren.generador } : {}),
        ...(ren.cantidadMedida != null ? { cantidadEscritaAMano: true } : {}),
      };
      if (ren.origen === 'catalogo' && ren.catalogId && ren.clave) {
        await asignarPlanoRenglon(projectId, fileId, { catalogId: ren.catalogId, resourceClave: ren.clave, ...comun });
      } else {
        await agregarRenglonPorDescripcion(projectId, fileId, { descripcion: ren.descripcion, ...comun });
        setHpAbierto(true);
      }
      await refrescarRenglones();
      dispatchToast(
        <Toast>
          <ToastTitle>Adicional agregado a la hoja.{ren.origen === 'ia' ? ' Cotízalo con IA cuando quieras.' : ''}</ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 4000 },
      );
    } catch (e) {
      throw e instanceof ApiClientError ? new Error(e.message) : e;
    }
  };
  // Staff: borra TODA la hoja del proyecto (todos los planos). Primero detiene lo que la IA esté
  // generando; después los principales con lo que les cuelga, y al final lo que quede suelto.
  const borrarTodaLaHoja = async () => {
    if (!projectId) return;
    setBorrandoHoja(true);
    let fallidos = 0;
    try {
      const todos = modoProyecto ? await getPlanosHp(projectId) : renglones;
      const planos = [...new Set(todos.map((r) => r.sourceFileId ?? fileId).filter((x): x is string => !!x))];
      await Promise.all(planos.map((f) => detenerIaLote(projectId, f).catch(() => undefined)));
      const orden = [...todos.filter((r) => r.rol === 'principal'), ...todos.filter((r) => r.rol !== 'principal')];
      const borrados = new Set<string>();
      for (const r of orden) {
        if (borrados.has(r.id)) continue;
        const f = r.sourceFileId ?? fileId;
        if (!f) continue;
        try {
          await borrarPlanoRenglon(projectId, f, r.id, r.rol === 'principal');
          borrados.add(r.id);
          if (r.rol === 'principal') for (const h of todos) if (h.principalId === r.id) borrados.add(h.id);
        } catch (e) {
          // Ya no estaba (se fue con su principal): no es un fallo.
          if (!(e instanceof ApiClientError && e.status === 404)) fallidos++;
        }
      }
    } finally {
      setBorrandoHoja(false);
      setBorrarHojaAbierto(false);
      await refrescarRenglones();
    }
    dispatchToast(
      <Toast>
        <ToastTitle>{fallidos ? `No se pudieron borrar ${fallidos} renglones. Vuelve a intentarlo.` : 'La hoja quedó vacía.'}</ToastTitle>
      </Toast>,
      { intent: fallidos ? 'warning' : 'success', timeout: 4000 },
    );
  };
  // Regla de oro en el asistente: antes de arrancar se mira la bolsa (el mismo medidor de la barra) y,
  // si una consulta vuelve con «sin tokens» (429 del portero), la corrida se detiene en vez de seguir
  // pidiendo consultas que van a fallar.
  const revisarSaldoArmado = async (): Promise<{ alcanza: boolean; aviso: string | null }> => {
    await useUsageStore.getState().refresh();
    const t = useUsageStore.getState().data?.tokens;
    if (!t || !t.quota) return { alcanza: true, aviso: null }; // sin medidor: decide el servidor en cada consulta
    return t.remaining > 0
      ? { alcanza: true, aviso: null }
      : { alcanza: false, aviso: 'Ya no te quedan créditos este mes: no se puede buscar el concepto de cada elemento. Puedes comprar una recarga o esperar al siguiente mes.' };
  };
  const conSaldo = async <T,>(f: () => Promise<T>): Promise<T> => {
    try {
      return await f();
    } catch (e) {
      if (e instanceof ApiClientError && (e.status === 429 || e.code === 'TOKENS_EXHAUSTED')) throw new Error(SIN_SALDO);
      throw e;
    }
  };
  // Borrador del asistente, por plano y en este navegador (como un texto a medio escribir): se retoma al
  // volver a abrirlo. Lo que ya entró a la hoja vive en la hoja, no aquí.
  // El de «Rehacer la hoja» va aparte: no se mezcla con uno de «completar lo que falta».
  const claveBorrador = `supernova:armado-borrador:${fileId ?? ''}${modoArmado === 'rehacer' ? ':rehacer' : ''}`;
  // «Armar»: con la hoja de este plano vacía abre directo; con renglones, pregunta antes si se completa lo
  // que falta o se rehace toda (y dice qué se pierde al reemplazarla).
  const abrirArmar = async () => {
    if (!projectId || !fileId || renglones.length === 0) {
      setModoArmado('faltante');
      setArmarAbierto(true);
      return;
    }
    try {
      setYaArmada(await trabajoEnRiesgoPlano(projectId, fileId));
    } catch {
      setModoArmado('faltante');
      setArmarAbierto(true);
    }
  };
  const borradorArmado = {
    leer: (): BorradorArmado | null => {
      try {
        const v = JSON.parse(localStorage.getItem(claveBorrador) ?? 'null') as BorradorArmado | null;
        return v && Array.isArray(v.filas) ? v : null;
      } catch {
        return null;
      }
    },
    guardar: (b: BorradorArmado) => {
      try {
        localStorage.setItem(claveBorrador, JSON.stringify(b));
      } catch {
        /* sin almacenamiento: el borrador no se guarda, la revisión sigue */
      }
    },
    borrar: () => {
      try {
        localStorage.removeItem(claveBorrador);
      } catch {
        /* nada que borrar */
      }
    },
  };
  const conPrincipalBloqueArmado = new Set(
    renglones.filter((r) => r.rol === 'principal').flatMap((r) => seleccionesDe(r).filter((s) => s.tipo === 'bloque').map((s) => s.clave)),
  ).size;
  // Preguntas de «Antes de armar»: lo que el plano deja en duda y cambia QUÉ se busca. Nada de listas
  // fijas: salen de lo que ya dijo la clasificación (IA o estándar IFC) y de lo que mide el plano, y la
  // respuesta es la misma corrección del ⋯ de la lista (queda guardada en el plano).
  // Los grupos (clase IFC) cuyo material el modelo no dice y que se van a armar: lo que se le consulta al servidor.
  const gruposSinMaterial: { clase: string; etiqueta: string; n: number; nombres: string[] }[] = (() => {
    if (!armarAbierto || !inventario || !esInventarioIfc) return [];
    // «Conceptos por tipo»: hasta saber qué ya decidió la memoria no se pregunta nada (se evita una consulta de más).
    if (MEMORIA_TIPOS_ENABLED && memoriaLeidaDe !== inventario) return [];
    const sinMaterial = new Map<string, { n: number; nombres: string[] }>();
    // Un nombre que no dice el material (ArchiCAD pone «Radial Gradient Fill», un relleno de dibujo) cuenta
    // como sin material: lo dice la clasificación de nombres de material que ya hace ci (`desconocido`).
    const tipoDeNombre = new Map((inventario.ifc?.materialesDeducidos ?? []).map((m) => [m.nombre, m.tipo] as const));
    const diceMaterial = (c: PlanoCapa) => (c.ifc?.materiales ?? []).some((m) => tipoDeNombre.get(m) !== 'desconocido');
    // Solo lo que se va a armar: un tipo que ya tiene concepto en la hoja no se busca, y preguntar su
    // material era ruido (con la hoja completa salían preguntas y «Armar» seguía apagado).
    const porArmar = new Set(candidatosArmado.map((x) => x.key));
    // Lo que ya decidió la memoria o el plano (resuelto en la corrida anterior de «Conceptos por tipo») no se pregunta.
    const yaDecididos = decididosPorTipos;
    for (const c of inventario.capas) {
      if (!porArmar.has(`capa:${c.capa}`) || yaDecididos.has(`capa:${c.capa}`)) continue;
      if (c.categoria == null || (c.medidas?.conteo ?? 0) <= 0 || diceMaterial(c)) continue;
      if ((decisionOverride[c.capa] ?? decisionIfc(c.categoria, c.ifc?.supertipos)) !== 'cotiza') continue;
      const previo = sinMaterial.get(c.categoria) ?? { n: 0, nombres: [] };
      previo.n += 1;
      // El nombre del tipo en el modelo (p. ej. «Sparren 100×140»): «Viga, Viga, Viga» no dice nada.
      const nombreTipo = c.capa && c.capa !== nombreLegibleTipoIfc(c) ? c.capa : nombreLegibleTipoIfc(c);
      if (!previo.nombres.includes(nombreTipo)) previo.nombres.push(nombreTipo);
      sinMaterial.set(c.categoria, previo);
    }
    return Array.from(sinMaterial, ([clase, { n, nombres }]) => ({ clase, etiqueta: CATEGORIA_IFC_ES[clase] ?? clase, n, nombres }));
  })();
  const gruposParaMaterial: GrupoSinMaterial[] = gruposSinMaterial.map((g) => ({
    clase: g.clase,
    etiqueta: g.etiqueta,
    tipos: g.nombres.slice(0, MAX_TIPOS_POR_GRUPO_MATERIAL).map((t) => t.slice(0, MAX_LARGO_TIPO_MATERIAL)),
  }));
  const claveGruposMaterial = fileId ? `${claveDeGruposMaterial(fileId, gruposParaMaterial)}|${catalogoArmado}` : '';
  // Mientras el servidor revisa el catálogo para saber qué preguntar: el diálogo lo muestra y «Armar» espera.
  const esperandoMemoria = MEMORIA_TIPOS_ENABLED && armarAbierto && esInventarioIfc && !!inventario && memoriaLeidaDe !== inventario;
  const revisandoMaterial =
    esperandoMemoria ||
    (gruposSinMaterial.length > 0 &&
    !!proyectoDeRuta &&
    !!fileId &&
    (!preguntasDeMaterial || preguntasDeMaterial.clave !== claveGruposMaterial || preguntasDeMaterial.estado === 'cargando'));
  // Una línea sobre las preguntas de material: revisando el catálogo, no se pudo, o qué grupos no se preguntaron.
  const avisoMaterial: string | null = (() => {
    if (gruposSinMaterial.length === 0) return null;
    const e = preguntasDeMaterial;
    if (revisandoMaterial) return null; // el diálogo ya muestra que está trabajando
    if (!e) return null;
    if (e.estado === 'fallo') return e.aviso ?? MENSAJE_MATERIAL_NO_REVISADO;
    const sinPregunta = gruposSinMaterial.filter((g) => !e.porClase[g.clase]?.preguntar);
    if (sinPregunta.length === 0) return null;
    const nombres = sinPregunta.map((g) => g.etiqueta.toLowerCase());
    const lista = nombres.length === 1 ? nombres[0]! : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
    return `El material de ${lista} no cambia el concepto en tu catálogo: se supone con el resto del modelo y se anota en cada uno.`;
  })();
  const preguntasArmado: PreguntaArmado[] = (() => {
    if (!armarAbierto || !inventario) return [];
    const out: PreguntaArmado[] = [];
    const descartar = (k: string) => setDescartadasArmado((prev) => new Set(prev).add(k));
    const motivo = (clave: string) => (capasClasificadas?.motivos as Record<string, string> | undefined)?.[clave] ?? null;
    const fmt = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 1 });
    // 1) Lo que nadie decidió si se construye (la IA no estuvo segura; en IFC, los genéricos).
    const dudosos: { clave: string; tipo: 'capa' | 'bloque'; texto: string }[] = [];
    if (esInventarioIfc) {
      for (const c of inventario.capas) {
        if (c.categoria == null || (c.medidas?.conteo ?? 0) <= 0) continue;
        if ((decisionOverride[c.capa] ?? decisionIfc(c.categoria, c.ifc?.supertipos)) === 'dudoso')
          dudosos.push({ clave: c.capa, tipo: 'capa', texto: `${nombreLegibleTipoIfc(c)} · ${c.medidas?.conteo ?? 0} pza` });
      }
    } else {
      for (const c of inventario.capas)
        if ((decisionOverride[c.capa] ?? decisionDeCapa(c.capa, capasClasificadas)) === 'dudoso') dudosos.push({ clave: c.capa, tipo: 'capa', texto: c.capa });
      for (const b of inventario.bloques)
        if ((b.n ?? 0) > 0 && (decisionOverride[b.nombre] ?? decisionDeBloque(b.nombre, capasClasificadas)) === 'dudoso')
          dudosos.push({ clave: b.nombre, tipo: 'bloque', texto: `${b.nombre} · ${b.n} pza` });
    }
    for (const d of dudosos) {
      const k = `dudoso:${d.clave}`;
      if (descartadasArmado.has(k)) continue;
      out.push({
        key: k,
        titulo: `¿«${d.texto}» se construye?`,
        detalle:
          motivo(d.clave) ??
          (esInventarioIfc ? 'El modelo no dice qué es (pieza genérica).' : 'No se pudo saber por su nombre ni por lo que dibuja.'),
        opciones: [
          { texto: 'Sí, se construye', elegir: () => guardarDecision(d.clave, 'cotiza') },
          { texto: 'No, es solo dibujo', elegir: () => guardarDecision(d.clave, 'ruido') },
        ],
      });
    }
    if (esInventarioIfc) {
      // Solo se pregunta el material de los grupos donde el catálogo del cliente tiene el mismo elemento en
      // varios materiales; las opciones SON esos materiales (del catálogo, no una lista fija). Lo demás se supone
      // con el resto del modelo y se anota, igual que cuando no se contesta.
      for (const g of gruposSinMaterial) {
        const p = preguntasDeMaterial?.estado === 'listo' && preguntasDeMaterial.clave === claveGruposMaterial ? preguntasDeMaterial.porClase[g.clase] : undefined;
        if (!p?.preguntar || p.opciones.length < 2) continue;
        const cuales = g.nombres.slice(0, 4).join(', ') + (g.nombres.length > 4 ? ` y ${g.nombres.length - 4} más` : '');
        out.push({
          key: `material:${g.clase}`,
          titulo: `¿De qué material son las «${g.etiqueta}»${g.n > 1 ? ` (${g.n} tipos)` : ''}?`,
          detalle: `En tu catálogo hay ${g.etiqueta.toLowerCase()} de varios materiales y cada uno tiene otro precio; el modelo no dice cuál es. Tipos: ${cuales}. Toca uno o escribe otro.`,
          opciones: [],
          entrada: {
            valor: materialArmado[g.clase] ?? '',
            placeholder: 'o escríbelo (ej.: madera laminada)',
            sugerencias: p.opciones,
            siNoContestas: 'Si no eliges ninguno, el sistema lo supone con el resto del modelo y lo dice en una nota de la revisión.',
            cambiar: (v) => setMaterialArmado((prev) => ({ ...prev, [g.clase]: v })),
          },
        });
      }
      return out;
    }
    // 2) Una capa tomada como «solo dibujo» que ENCIERRA área (recintos cerrados): un contorno que
    //    solo delimita, o el piso/losa/firme. Lo decide quien cotiza.
    //    Solo la que encierra la MAYOR área de todo el plano (la candidata real a piso): unos ejes o unos
    //    muebles también cierran recintos, pero chicos, y preguntar por ellos es ruido.
    const areaDe = new Map(inventario.capas.map((c) => [c.capa, medirCapa2D(c, factorEscala, false).areaM2 ?? 0] as const));
    const mayorArea = Math.max(0, ...areaDe.values());
    for (const c of inventario.capas) {
      // Lo decidido por quien cotiza no se vuelve a preguntar. Lo que decidió la IA sí: tome la superficie
      // como dibujo o como obra, con solo su nombre no se sabe QUÉ se construye ahí.
      // Si dijo que se construye pero no QUÉ, se sigue preguntando qué (sin respuesta recomendada).
      const yaDijoQueSi = decisionOverride[c.capa] === 'cotiza';
      if (decisionOverride[c.capa] === 'ruido') continue;
      const area = areaDe.get(c.capa) ?? 0;
      const k = `area:${c.capa}`;
      if (area <= 0 || area < mayorArea || descartadasArmado.has(k)) continue;
      out.push({
        key: k,
        titulo: `«${c.capa}» encierra ${fmt(area)} m²: ¿se construye algo en esa superficie?`,
        detalle:
          (decisionDeCapa(c.capa, capasClasificadas) === 'ruido'
            ? `Se tomó como dibujo${motivo(c.capa) ? `: ${motivo(c.capa)}` : ''}.`
            : 'Se tomó como obra, pero su nombre no dice qué se construye.') +
          ' Si se construye, escribe qué ' +
          '(entra con su área); los pisos y losas que el plano no trae ya los estima «lo que no trae el plano».',
        opciones: [
          {
            texto: 'No, solo delimita',
            recomendada: !yaDijoQueSi,
            elegir: () => {
              descartar(k);
              guardarDecision(c.capa, 'ruido');
            },
          },
        ],
        entrada: {
          valor: queEsArmado[c.capa] ?? '',
          placeholder: 'Qué se construye (ej.: firme de concreto)',
          cambiar: (v) => setQueEsArmado((prev) => ({ ...prev, [c.capa]: v })),
          confirmar: () => guardarDecision(c.capa, 'cotiza'),
        },
      });
    }
    // 2b) Muros que se miden del plano: el dibujo no dice de qué son, y sin eso cada corrida elegía otro
    //     sistema (tabique, block, concreto). Se pregunta; vacío = se busca como hasta ahora.
    for (const cand of candidatosArmado) {
      const am = asignableDeClave(cand.key);
      if (!am?.esMuroDobleTrazo || am.seleccion.tipo !== 'capa') continue;
      const capa = am.seleccion.clave;
      out.push({
        key: `muro:${capa}`,
        titulo: `¿De qué son los muros de «${capa}»?`,
        detalle: 'El plano no dice el material ni el espesor de obra. Escríbelo para buscar el concepto correcto; si lo dejas vacío, se busca con lo que el plano mide.',
        opciones: [],
        entrada: {
          valor: queEsArmado[capa] ?? '',
          placeholder: 'Ej.: muro de block de concreto de 15 cm',
          cambiar: (v) => setQueEsArmado((prev) => ({ ...prev, [capa]: v })),
        },
      });
    }
    // 3) Una capa de puertas/ventanas que se construye y sobre la que están los BLOQUES de esas mismas
    //    piezas: contarlas por la capa y por los bloques las cobra dos veces.
    const vanosCls = new Set([...(capasClasificadas?.door ?? []), ...(capasClasificadas?.window ?? [])]);
    for (const c of inventario.capas) {
      if (!vanosCls.has(c.capa)) continue;
      if ((decisionOverride[c.capa] ?? decisionDeCapa(c.capa, capasClasificadas)) !== 'cotiza') continue;
      const enLaCapa = inventario.bloques.filter(
        (b) => (b.n ?? 0) > 0 && b.capas.includes(c.capa) &&
          (decisionOverride[b.nombre] ?? decisionDeBloque(b.nombre, capasClasificadas)) === 'cotiza',
      );
      const k = `doble:${c.capa}`;
      if (!enLaCapa.length || descartadasArmado.has(k)) continue;
      out.push({
        key: k,
        titulo: `«${c.capa}» y sus bloques (${enLaCapa.map((b) => `${b.nombre} · ${b.n}`).join(', ')}) cuentan las mismas piezas`,
        detalle: 'Si se cotizan las dos, esas puertas o ventanas entran dos veces. Lo normal es contarlas por sus bloques; deja la capa solo si trae piezas que no son bloque.',
        opciones: [
          { texto: 'Contar por los bloques', recomendada: true, elegir: () => guardarDecision(c.capa, 'ruido') },
          { texto: 'Dejar las dos', elegir: () => descartar(k) },
        ],
      });
    }
    return out;
  })();
  // El alto solo se pide si algún muro no lo tiene ya por planta (leído de una fachada o un corte).
  const hayMurosArmado =
    !esInventarioIfc &&
    armarAbierto &&
    candidatosArmado.some((c) => {
      const a = asignableDeClave(c.key);
      if (!a?.esMuroDobleTrazo) return false;
      const porPlanta = a.areaMuroPorPlantas != null || (a.muroVanos?.tramos.every((t) => t.altoPropioM != null) ?? false);
      return !porPlanta && a.vista?.altoM == null;
    });
  const saltar = (sig: Siguiente) => setSaltados((prev) => new Set(prev).add(`${sig.tipo}:${sig.clave}`));
  const botonSaltar = (sig: Siguiente) => (
    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <Button size="small" appearance="secondary" title="Lo dejo para el final; vuelve cuando no quede otro." onClick={() => saltar(sig)}>
        Saltar este
      </Button>
      <Button
        size="small"
        appearance="secondary"
        disabled={!puedeEditar}
        title="No se construye: pasa a «Es solo dibujo» (lo mismo que el ⋯ de su fila) y no se vuelve a proponer."
        onClick={() => guardarDecision(sig.clave, 'ruido')}
      >
        Es solo dibujo, no se construye
      </Button>
    </span>
  );
  const deshacerUltimo = async (cambiar: boolean) => {
    if (!ultimoAgregado || !projectId || !fileId) return;
    // Mismo camino que el bote de la hoja: si ya le colgaste complementarios, pregunta qué hacer con ellos.
    setDeshaciendo(true);
    try {
      await handleBorrar(ultimoAgregado.renglon, () => {
        setUltimoAgregado(null);
        if (cambiar) setConceptoElegido(null);
      });
    } finally {
      setDeshaciendo(false);
    }
  };
  // El renglón recién agregado, con lo último de la hoja (sus notas pegadas cambian al quitar una).
  const renglonUltimo = ultimoAgregado
    ? renglones.find((x) => x.id === ultimoAgregado.renglon.id) ?? ultimoAgregado.renglon
    : null;
  const bannerAgregado = ultimoAgregado && renglonUltimo ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          padding: '10px 12px', borderRadius: 8, border: '1px solid var(--ok)', background: 'var(--ok-weak)',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ok)' }}>✓ Agregado a la hoja</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.4 }} title={ultimoAgregado.renglon.conceptoDescripcion}>
          <span style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {ultimoAgregado.renglon.conceptoDescripcion}
          </span>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
            {' '}· {ultimoAgregado.renglon.cantidad.toLocaleString('es-MX')} {ultimoAgregado.renglon.unidad ?? ''}
            {ultimoAgregado.renglon.importe != null ? ` · ${fmtMoneda(ultimoAgregado.renglon.importe)}` : ''}
          </span>
        </span>
        <span style={{ display: 'flex', gap: 6 }}>
          <Button size="small" appearance="secondary" disabled={deshaciendo} onClick={() => void deshacerUltimo(true)}
            title="Quita este renglón y elige otro concepto">
            Cambiar
          </Button>
          <Button size="small" appearance="subtle" disabled={deshaciendo} onClick={() => void deshacerUltimo(false)}>
            Deshacer
          </Button>
        </span>
      </div>
      <NotasDelDetalle
        projectId={projectId}
        texto={renglonUltimo.conceptoDescripcion}
        nombre={renglonUltimo.seleccion?.clave ?? null}
        notasPegadas={renglonUltimo.specRefs}
        onQuitar={puedeEditar ? (n) => quitarNota(renglonUltimo, n) : undefined}
      />
    </div>
  ) : null;
  // "Siguiente que falta": el último paso, DESPUÉS de los complementarios (no antes).
  const botonSiguiente = ultimoAgregado && siguienteQueFalta ? (
    <>
        <button
          type="button"
          onClick={() => {
            setUltimoAgregado(null);
            irA(siguienteQueFalta);
          }}
          style={{
            textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', padding: '10px 12px',
            borderRadius: 8, border: '1px solid var(--accent)', background: 'var(--accent-weak)',
            color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 2,
          }}
        >
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--accent)' }}>Siguiente que falta</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{siguienteQueFalta.texto} →</span>
        </button>
        {botonSaltar(siguienteQueFalta)}
    </>
  ) : null;

  // Las condiciones de la obra son las MISMAS que usa la cotización (una sola página por proyecto);
  // al guardar o cancelar se vuelve a este plano.
  const irACondicionesDeObra = () => {
    if (!projectId) return;
    navigate(`/project/${projectId}/obra-context`, {
      state: { returnTo: `${window.location.pathname}${window.location.search}` },
    });
  };

  const toolbarContenido = (
    <>
        {/* Ayuda: un "?" discreto con el paso a paso (antes era un botón "Léeme" con destello). */}
        <Popover positioning="below-start" withArrow>
          <PopoverTrigger disableButtonEnhancement>
            <button
              type="button"
              className={styles.rolHelpTrigger}
              title="Cómo funciona esta pantalla"
              aria-label="Ayuda: cómo armar tu presupuesto desde el plano"
            >
              ?
            </button>
          </PopoverTrigger>
          <PopoverSurface>
            <div style={{ maxWidth: 360 }}>
              <div style={{ font: '700 15px var(--font-head, inherit)', marginBottom: 2 }}>
                Cómo armar tu presupuesto desde el plano
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Toca cada paso para ver el detalle.
              </div>
              {[
                {
                  t: '1 · Abre tu plano',
                  d: modoProyecto
                    ? 'Toca la ficha del plano (IFC, DWG, DXF o PDF) y se abre solo; arriba cambias de un plano a otro. Lo leo y detecto lo que trae —muros, puertas, pisos, instalaciones…— aunque venga en otro idioma o con nombres raros.'
                    : 'Elige tu proyecto y tu plano (IFC, DWG, DXF o PDF) aquí arriba y pulsa “Abrir”. Lo leo y detecto lo que trae —muros, puertas, pisos, instalaciones…— aunque venga en otro idioma o con nombres raros.',
                },
                { t: '2 · Elige qué se construye', d: 'De todo lo que trae el dibujo, no todo se construye. A la izquierda te muestro lo que creo que SÍ (muros, pisos, escaleras, instalaciones…) y lo que es solo dibujo (ejes, cotas, mobiliario). Tú revisas y desmarcas lo que no se construya.' },
                { t: '3 · Ponle precio a cada concepto', d: 'Tocas una capa o algo del plano y le pones precio de tres formas: (1) lo buscas tú en tus catálogos, (2) yo te lo sugiero de tus catálogos con IA, o (3) te armo el concepto con IA cuando no está en ningún catálogo. La cantidad la mido yo del plano.' },
                { t: '4 · Y sus complementarios', d: 'Un concepto arrastra otros: un muro lleva aplanado, dala, castillo, pintura…; un piso lleva su firme, etc. Cuando le pones precio a un concepto, en el panel de la derecha aparece «¿Qué más lleva?»: los propongo para que no se te escape ninguno, y a cada uno le pones precio con las mismas tres formas del paso 3.' },
                { t: '5 · Lo que no trae el plano', d: 'Hay trabajo que casi nunca se dibuja en la planta, pero la obra lo lleva: la limpieza y el trazo del terreno, la obra provisional, las instalaciones eléctrica, de agua, drenaje o gas, los permisos y conexiones, y la limpieza final. Cuando quieras, pulsa «Lo que no trae el plano» (en la barra de la hoja, abajo): miro los cuartos y la superficie del plano y te lo propongo por grupos, para que no se te olvide; tú eliges qué agregas. Dos cosas importantes: (1) solo puedo inferirlo si el plano trae esa información —sobre todo los NOMBRES de los cuartos y su superficie—; si el modelo no la tiene, no la adivino; (2) si una instalación YA está dibujada en el plano, no te la vuelvo a proponer, para no cobrarla dos veces.' },
                { t: '6 · Tu hoja de presupuesto', d: 'Abajo se va armando la hoja con todo —principales, complementarios e instalaciones—, su cantidad y su importe. En cualquier momento la puedes llevar a OPUS 27.' },
              ].map((s) => (
                <details key={s.t} style={{ borderTop: '1px solid var(--border-subtle)', padding: '7px 0' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, listStyle: 'none' }}>
                    {s.t}
                  </summary>
                  <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)', marginTop: 6 }}>
                    {s.d}
                  </div>
                </details>
              ))}
            </div>
          </PopoverSurface>
        </Popover>
        {/* /planos a secas (staff): combos de proyecto y plano + botón Abrir. Entrando por el
            proyecto no hacen falta: la tira de planos elige y el plano se abre solo. */}
        {!modoProyecto && (
          <>
              <>
                <span className={styles.toolbarLabel}>Proyecto</span>
                <Dropdown
                  aria-label="Proyecto"
                  placeholder={loadingProjects ? 'Cargando…' : 'Elige un proyecto'}
                  value={proyectoActual?.name ?? ''}
                  selectedOptions={projectId ? [projectId] : []}
                  onOptionSelect={(_, d) => setProjectId(d.optionValue ?? null)}
                  style={{ minWidth: 220 }}
                >
                  {projects.map((p) => (
                    <Option key={p.id} value={p.id} text={p.name}>
                      {p.name}
                    </Option>
                  ))}
                </Dropdown>
              </>
  
          <span className={styles.toolbarLabel}>Plano</span>
          <Dropdown
            aria-label="Plano DWG, DXF o IFC"
            placeholder={
              !projectId
                ? 'Elige un proyecto primero'
                : loadingFiles
                  ? 'Cargando…'
                  : files.length === 0
                    ? 'Sin planos DWG/DXF/IFC'
                    : 'Elige un plano'
            }
            disabled={!projectId || loadingFiles || files.length === 0}
            value={archivoActual?.originalName ?? ''}
            selectedOptions={fileId ? [fileId] : []}
            onOptionSelect={(_, d) => setFileId(d.optionValue ?? null)}
            style={{ minWidth: 240 }}
          >
            {files.map((f) => (
              <Option key={f.id} value={f.id} text={f.originalName}>
                {f.originalName}
              </Option>
            ))}
          </Dropdown>
  
          <Button
            appearance="primary"
            disabled={!projectId || !fileId || status === 'pending'}
            onClick={() => void abrirPlano()}
          >
            {status === 'pending'
              ? 'Procesando…'
              : esIfc(archivoActual)
                ? 'Abrir modelo 3D'
                : 'Abrir dibujo'}
          </Button>
          </>
        )}

        {/* Rol del archivo en la HP por-proyecto: decide si sus cantidades entran al total o si el
            plano solo enriquece los conceptos (láminas de detalle). Aparece con el plano abierto. */}
        {status === 'ready' && rolArchivo && (
          <>
            <span className={styles.toolbarLabel}>Este plano</span>
            <Dropdown
              aria-label="Rol de este plano en la hoja de presupuesto"
              value={etiquetaRolArchivo(rolArchivo)}
              selectedOptions={[rolParaSelector(rolArchivo)]}
              disabled={guardandoRol}
              onOptionSelect={(_, d) => {
                // Elegir lo que ya se ve («Suma a la hoja» con un rol guardado como sitio) no cambia nada.
                if (d.optionValue && d.optionValue !== rolParaSelector(rolArchivo)) void cambiarRolArchivo(d.optionValue as PlanoRolArchivo);
              }}
              style={{ minWidth: 200 }}
            >
              {PLANO_ROLES_ARCHIVO.map((op) => (
                <Option key={op.id} value={op.id} text={op.label}>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600 }}>{op.label}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{op.ayuda}</span>
                  </span>
                </Option>
              ))}
            </Dropdown>
            <Popover
              withArrow
              positioning={{ position: 'below', align: 'center', flipBoundary: undefined }}
            >
              <PopoverTrigger disableButtonEnhancement>
                <button
                  type="button"
                  className={styles.rolHelpTrigger}
                  aria-label="Ayuda: El rol del plano"
                >
                  ?
                </button>
              </PopoverTrigger>
              <PopoverSurface className={styles.rolHelpSurface} aria-label="El rol del plano">
                <h3 className={styles.rolHelpTitle}>El rol del plano</h3>
                <p className={styles.rolHelpBody}>{ROL_PLANO_AYUDA}</p>
              </PopoverSurface>
            </Popover>
            {rolSugerido && (
              <span
                title={rolSugerido.razon}
                style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 220, lineHeight: 1.3 }}
              >
                {rolParaSelector(rolArchivo) === rolParaSelector(rolSugerido.rol)
                  ? '✓ Rol sugerido por el contenido del plano'
                  : `Sugerido: ${etiquetaRolArchivo(rolSugerido.rol)}`}
              </span>
            )}
            {puedeEditar && inventario && !esPdfEstimado && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Button
                  size="small"
                  appearance="primary"
                  icon={<Sparkle16Regular />}
                  title="Propone de una vez el concepto de todo lo que se construye en este plano. Lo revisas antes de que entre a la hoja."
                  onClick={() => void abrirArmar()}
                >
                  Presupuestar todo el plano
                </Button>
                <FieldHelp helpKey="planos.armar_presupuesto" />
              </span>
            )}
            {chequeoDelPlano && fileId && (
              <ChequeoDelPlanoPastilla
                chequeo={chequeoDelPlano}
                fileId={fileId}
                diagnostico={
                  esIfcParaDiagnostico
                    ? {
                        diagnostico: diagnosticoModelo
                          ? {
                              entidadesRevisadas: diagnosticoModelo.piezasRevisadas,
                              parcial: false,
                              // El mismo contrato de lista que el dibujo: las piezas viajan como «handles» (su número en el visor).
                              avisos: diagnosticoModelo.avisos.map((a) => ({ ...a, clave: 'piezas_repetidas' as const, capas: a.tipos, bloques: [], handles: a.ids.map(String) })),
                            }
                          : null,
                        leyendo: false,
                        titulo: 'Revisar el modelo',
                        textoVer: 'Ver en el modelo',
                        textoSinAvisos: 'No encontramos piezas repetidas, desmedidas ni lejos del resto.',
                        onVer: (a) => {
                          const n = (piezasVer?.n ?? 0) + 1;
                          setPiezasVer({ n, ids: a.handles.map(Number), titulo: a.titulo });
                          setOrdenCamara({ n: Date.now(), vista: 'resaltadas' });
                        },
                        verActivo: piezasVer != null,
                        onQuitarResaltado: () => {
                          setPiezasVer(null);
                          setOrdenCamara({ n: Date.now(), vista: 'todo' });
                        },
                      }
                    : esDwgParaDiagnostico
                    ? {
                        diagnostico: diagnosticoDibujo,
                        leyendo: leyendoDibujo,
                        onVer: (a) => setAvisoVisto((v) => ({ n: (v?.n ?? 0) + 1, handles: a.handles, titulo: a.titulo })),
                        verActivo: avisoVisto != null,
                        onQuitarResaltado: () => setAvisoVisto(null),
                      }
                    : null
                }
              />
            )}
            <LoQueSeDedujo
              materiales={inventario?.ifc?.materialesDeducidos}
              avisoMateriales={inventario?.ifc?.avisoMateriales}
              materialResaltado={instColoresMaterial ? materialVer?.nombre ?? null : null}
              onVerMaterial={esInventarioIfc ? (m) => { if (m) limpiarSeleccion(); setMaterialVer(m); } : undefined}
              nivelesDeducidos={nivelesIfc.filter((n) => n.deducido).length}
              altura={alturaProp}
              rolSugerido={rolSugerido}
              capasClasificadas={capasClasificadas}
              corregidas={decisionOverride}
              conFormaDeMuro={esInventarioIfc ? [] : capasQueParecenMuro(inventario, capasClasificadas, factorEscala, decisionOverride)}
              falloCapas={esInventarioIfc ? null : inventario?.clasificacion_capas_fallo ?? null}
            />
          </>
        )}

        <span className={styles.wipTag} title="Esta función todavía está en pruebas">BETA</span>
    </>
  );

  // Acciones de la HOJA en la fila del breadcrumb (entrando por el proyecto): la barra de la hoja de
  // abajo queda solo con su título, su conteo y su total.
  // Qué acciones de «Cliente y obra» hay disponibles (las mismas condiciones que tenían como botones sueltos).
  const hayCompartir = filasHoja.length > 0 && !!projectId && !!fileId && puedeEditar;
  const hayRevision = isStaff && formatoCambioDePlano != null && filasHoja.length > 0 && !!projectId && !!fileId && puedeEditar;
  const hayAvance = isStaff && formatoCambioDePlano != null && filasHoja.length > 0 && !!projectId && !!fileId;
  // Un IFC cuyo visor 3D aún no abrió: estas dos acciones existen pero no se pueden usar todavía. Se muestran
  // deshabilitadas y con el motivo (antes desaparecían sin decir nada y parecía que no existían).
  const faltaAbrirModelo =
    isStaff && formatoCambioDePlano == null && !!archivoDeLaHoja && esIfc(archivoDeLaHoja) &&
    filasHoja.length > 0 && !!projectId && !!fileId && puedeEditar;
  const accionesHoja = (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 12.5 }}>Mostrar en el presupuesto:</span>
        <span className={styles.segmentado} role="group" aria-label="Qué renglones ver en la hoja de presupuesto">
          <button type="button" className={mergeClasses(styles.segBtn, vistaProyecto && styles.segBtnOn)} aria-pressed={vistaProyecto} onClick={() => setVistaProyecto(true)}>
            Todos los planos
          </button>
          <button type="button" className={mergeClasses(styles.segBtn, !vistaProyecto && styles.segBtnOn)} aria-pressed={!vistaProyecto} onClick={() => setVistaProyecto(false)}>
            Solo este plano
          </button>
        </span>
        <FieldHelp helpKey="planos.hp_proyecto" />
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 12.5 }}>Partidas:</span>
        <Dropdown
          size="small"
          aria-label="Cómo se agrupan las partidas de la hoja"
          value={etiquetaAgrupacion}
          selectedOptions={[tipoAgrupacion]}
          onOptionSelect={(_, d) => void alElegirAgrupacion((d.optionValue as TipoAgrupacion) ?? 'familia')}
          style={{ minWidth: 130 }}
        >
          {AGRUPACIONES.map((a) => (
            <Option key={a.valor} value={a.valor} text={a.label} title={a.valor === 'obra' && vistaProyecto ? 'Solo acomoda los conceptos del plano abierto; los de otros planos quedan «Sin clasificar».' : undefined}>
              {a.label}
            </Option>
          ))}
        </Dropdown>
        {agrupando ? <Spinner size="extra-tiny" /> : <FieldHelp helpKey="planos.tipo_partidas" />}
      </span>
      <span className={styles.accionAncha} style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={motivoNoDibujadoApagado ?? undefined}>
          <Button size="small" appearance="secondary" className={styles.botonBarra} icon={<PlugConnectedRegular />} disabled={instalCargando || !fileId || !inventario} onClick={() => void abrirInstalaciones()}>
            {instalCargando ? 'Pensando…' : 'Lo que no trae el plano'}
          </Button>
          <FieldHelp helpKey="planos.instalaciones_faltan" variante="accion" />
        </span>
        {MEMORIA_TIPOS_ENABLED && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Button
              size="small"
              appearance="secondary"
              className={styles.botonBarra}
              disabled={!fileId || !inventario}
              title="Ve y cambia qué concepto lleva cada tipo de elemento, y lo que recuerdas de otros proyectos"
              onClick={() => setTiposAbierto(true)}
            >
              {resumenPlano && resumenPlano.total > 0 ? `Asignar conceptos a elementos · ${resumenPlano.reconocidos} de ${resumenPlano.total}` : 'Asignar conceptos a elementos'}
            </Button>
            <FieldHelp helpKey="planos.asignar_conceptos" variante="accion" />
          </span>
        )}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Button
            size="small"
            appearance="secondary"
            className={styles.botonBarra}
            icon={<Print20Regular />}
            disabled={renglones.length === 0}
            title="Todos los conceptos de la hoja con de dónde sale su cantidad, pieza por pieza, y su croquis; desde ahí se imprimen"
            onClick={() => setImprimiendoGeneradores(true)}
          >
            Números generadores
          </Button>
          <FieldHelp helpKey="planos.numeros_generadores" variante="accion" />
        </span>
        <Tooltip
          content="Altitud, clima, lluvia, horario de trabajo… Ajustan el rendimiento de los análisis de precio que se generan con IA."
          relationship="description"
        >
          <Button size="small" appearance="secondary" className={styles.botonBarra} icon={<WeatherPartlyCloudyDay20Regular />} disabled={!projectId} onClick={irACondicionesDeObra}>
            Contexto de proyecto
          </Button>
        </Tooltip>
      </span>
      {(hayCompartir || hayRevision || hayAvance || faltaAbrirModelo) && (
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <MenuButton size="small" appearance="secondary" className={styles.botonBarra}>
              Cliente y obra
            </MenuButton>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {hayAvance && (
                <MenuItem onClick={() => setAvanceAbierto(true)}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Avance de obra</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{`Cómo fue avanzando la obra sobre el ${formatoCambioDePlano === 'dwg' ? 'dibujo' : 'modelo'}.`}</div>
                  </div>
                </MenuItem>
              )}
              {hayCompartir && (
                <MenuItem onClick={() => setCompartirAbierto(true)}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Compartir con mi cliente</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Un enlace de solo lectura, sin crear cuenta.</div>
                  </div>
                </MenuItem>
              )}
              {hayRevision && (
                <MenuItem onClick={() => setCambioAbierto(true)}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Cambió el plano (subir revisión)</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{`Compara la revisión nueva del ${formatoCambioDePlano === 'dwg' ? 'dibujo' : 'modelo'} con tu hoja y dice cuánto cambia.`}</div>
                  </div>
                </MenuItem>
              )}
              {faltaAbrirModelo && (
                <>
                  <MenuItem disabled>
                    <div>
                      <div style={{ fontWeight: 600 }}>Avance de obra</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Disponible cuando el modelo 3D termine de cargar.</div>
                    </div>
                  </MenuItem>
                  <MenuItem disabled>
                    <div>
                      <div style={{ fontWeight: 600 }}>Cambió el plano (subir revisión)</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Disponible cuando el modelo 3D termine de cargar.</div>
                    </div>
                  </MenuItem>
                </>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
      <span className={styles.accionMas}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button size="small" appearance="secondary" icon={<MoreHorizontal20Regular />}>
              Más
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem disabled={instalCargando || !fileId || !inventario} onClick={() => void abrirInstalaciones()}>
                Lo que no trae el plano
              </MenuItem>
              <MenuItem disabled={!projectId} onClick={irACondicionesDeObra}>
                Contexto de proyecto
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </span>
      <span aria-hidden style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '0 2px' }} />
      <span className={styles.segmentado} role="group" aria-label="Cuánto espacio le toca a la hoja">
        {(
          [
            { k: 'plano', t: 'Solo el plano', icon: <LayerRegular /> },
            { k: 'mitad', t: 'Mitad y mitad', icon: <SplitHorizontal20Regular /> },
            { k: 'hoja', t: 'Solo la hoja', icon: <Table20Regular /> },
          ] as const
        ).map((v) => (
          <Tooltip key={v.k} content={v.t} relationship="label">
            <button type="button" className={styles.segBtn} onClick={() => aplicarPresetHp(v.k)}>
              {v.icon}
            </button>
          </Tooltip>
        ))}
      </span>
    </>
  );

  // GALERÍA de planos del proyecto (/project/:id/planos, sin plano abierto): elegir cuál abrir.
  if (modoProyecto && !archivoDeRuta) {
    const fichaIfc = miniaturaIfcEnCurso;
    return (
      <div className={styles.page} style={{ overflow: 'auto', height: 'auto', minHeight: '100vh' }}>
        <AppTopBar />
        <Breadcrumbs items={planosCrumbs!} />
        <main style={{ padding: '24px var(--space-xxl) 48px', maxWidth: 1400, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700 }}>Planos de la obra</h1>
          <p style={{ margin: '0 0 20px', fontSize: 13.5, color: 'var(--text-secondary)' }}>
            Toca un plano para abrirlo y medir. Todos suman en una sola hoja de presupuesto.
          </p>
          <PlanosGaleria
            planos={galeria.planos}
            error={galeria.error}
            onReintentar={() => void galeria.recargar()}
            canMutate={puedeSubirPlanos}
            subiendo={subiendoPlano}
            progreso={progresoSubida}
            onSubir={subirPlanos}
            onAbrir={(fid) => navigate(`/project/${proyectoDeRuta}/planos/${fid}`)}
            onElegirUnidad={elegirUnidadDelPlano}
          />
          {fichaIfc && proyectoDeRuta && (
            <MiniaturaIfcEnSegundoPlano
              key={fichaIfc}
              projectId={proyectoDeRuta}
              fileId={fichaIfc}
              onTermino={(ok) => {
                setMiniaturasIfcIntentadas((s) => new Set(s).add(fichaIfc));
                if (ok) void galeria.recargar();
              }}
            />
          )}
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Dialog open={obrasDialogAbierto} onOpenChange={(_, d) => setObrasDialogAbierto(d.open)}>
        <DialogSurface style={{ maxWidth: 480 }}>
          <DialogBody>
            <DialogTitle>Obra de referencia</DialogTitle>
            <DialogContent>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.45 }}>
                Elige una obra con sus partidas. La IA acomoda cada concepto de tu hoja en la partida que le corresponde; lo que no quepa en ninguna queda en «Sin partida».
              </div>
              {obrasListaError ? (
                <MessageBar intent="error"><MessageBarBody>No se pudo traer la lista de obras.</MessageBarBody></MessageBar>
              ) : obrasLista === null ? (
                <Spinner size="small" label="Cargando obras…" />
              ) : obrasLista.length === 0 ? (
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>No hay obras de referencia disponibles.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {obrasLista.map((o) => {
                    const arbol = arbolesDeObra[o.id];
                    return (
                      <div
                        key={o.id}
                        style={{
                          borderRadius: 8, background: 'var(--bg-surface)',
                          border: `1px solid ${o.id === obraIdPref ? 'var(--accent)' : 'var(--border-subtle)'}`,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => void elegirObra(o.id)}
                          style={{
                            textAlign: 'left', cursor: 'pointer', padding: '10px 12px', width: '100%',
                            background: 'transparent', border: 'none', color: 'var(--text-primary)',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{o.nombre}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                            {o.partidasElegibles} partidas · {o.niveles} {o.niveles === 1 ? 'nivel' : 'niveles'}
                          </div>
                        </button>
                        <div style={{ padding: '0 12px 8px' }}>
                          {(
                            <div style={{ float: 'right', display: 'flex', alignItems: 'center', gap: 6 }}>
                              {obraPorBorrar === o.id ? (
                                <>
                                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>¿Borrar las partidas de esta obra?</span>
                                  <Button size="small" appearance="primary" disabled={borrandoObra} onClick={() => void borrarObra(o.id)}>Sí, borrar</Button>
                                  <Button size="small" appearance="secondary" disabled={borrandoObra} onClick={() => setObraPorBorrar(null)}>No</Button>
                                </>
                              ) : (
                                <Button
                                  size="small"
                                  appearance="subtle"
                                  icon={<DeleteRegular />}
                                  aria-label="Borrar las partidas de esta obra"
                                  title="Borrar las partidas de esta obra"
                                  onClick={() => setObraPorBorrar(o.id)}
                                />
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => void alternarArbolDeObra(o.id)}
                            aria-expanded={obraDesplegada === o.id}
                            style={{
                              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                              fontSize: 11.5, color: 'var(--accent)',
                            }}
                          >
                            {obraDesplegada === o.id ? 'Ocultar partidas' : 'Ver partidas'}
                          </button>
                          {obraDesplegada === o.id && (
                            <div style={{ marginTop: 6, maxHeight: 260, overflowY: 'auto', fontSize: 12 }}>
                              {arbol === undefined ? (
                                <Spinner size="tiny" label="Cargando partidas…" />
                              ) : arbol === 'error' ? (
                                <span style={{ color: 'var(--text-secondary)' }}>No se pudo traer el árbol de partidas.</span>
                              ) : (
                                <ArbolDePartidas obra={arbol} />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setObrasDialogAbierto(false)}>Cancelar</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {planosCrumbs && <Breadcrumbs items={planosCrumbs} derecha={accionesHoja} />}
      {modoProyecto ? (
        <TiraDePlanos
          planos={galeria.planos ?? []}
          activoId={fileId}
          onTodos={() => navigate(`/project/${proyectoDeRuta}/planos`)}
          onElegir={(fid) => navigate(`/project/${proyectoDeRuta}/planos/${fid}`)}
          onSubir={puedeSubirPlanos ? subirPlanos : undefined}
          subiendo={subiendoPlano}
          derecha={toolbarContenido}
        />
      ) : (
        <div className={styles.toolbar}>{toolbarContenido}</div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}
        onClick={(e) => {
          // Panel de análisis abierto: un clic en una «zona muerta» (ni renglón ni control) lo cierra,
          // como en la cotización. Pasar a otro renglón lo maneja el renglón.
          if (!panelRenglon) return;
          const t = e.target as HTMLElement;
          if (t.closest('[data-renglon-id]')) return;
          if (t.closest('button, a, input, select, textarea, label, [role="menuitem"], [role="button"], [role="tab"], [role="option"], [role="checkbox"], [role="switch"], [contenteditable="true"], .fui-Combobox, .fui-Dropdown')) return;
          setPanelRenglon(null);
          setPanelConcepto(null);
        }}
      >
      <div
        className={styles.body}
        ref={setCuerpoEl}
        style={{
          ['--right-w' as string]: `${rightW}px`,
          ['--left-w' as string]: `${leftW}px`,
          // Catálogos: 256px para que los nombres no se partan a media palabra ("INDUSTRI AL"); mismo
          // lugar y mismo componente que en cotización (ahí la columna mide 286px).
          // Modo «eligiendo»: los catálogos se pliegan, Capas no pasa de 240 px (220 en pantalla angosta) y el
          // panel derecho de 400 (320), para que la lista ancha tenga lugar (el visor no lo nota: queda como
          // miniatura con su tamaño de antes).
          gridTemplateColumns: `${sinPlano ? '0px' : catAbiertoEff && !modoLista ? '256px' : '44px'} ${leftAbiertoEff ? (modoLista ? `min(var(--left-w, 280px), ${cuerpoAngosto ? 220 : 240}px)` : 'var(--left-w, 280px)') : '44px'} minmax(0, 1fr) ${
            derechaAbiertaEff ? (modoLista ? `min(var(--right-w, 390px), ${cuerpoAngosto ? 320 : 400}px)` : 'var(--right-w, 390px)') : '44px'
          }`,
        }}
      >
        {/* CATÁLOGOS: panel PROPIO y COLAPSABLE, a la izquierda de Capas (la "fuente" de conceptos).
            Abierto = aside con la barra; colapsado = rail con botón para reabrir; sin plano = <div/>
            (para no descuadrar las columnas del grid). */}
        {sinPlano ? <div /> : catAbiertoEff && !modoLista ? panelCatalogos : railCatalogos}

        {/* IZQUIERDA: capas (selector primario) + bloques */}
        {leftAbiertoEff ? (
        <aside className={styles.sidePanel} style={{ position: 'relative' }}>
          {/* Manija para ensanchar/angostar la lista (arrastrar el borde derecho). */}
          <div
            className={styles.resizer}
            style={{ left: 'auto', right: 0 }}
            onMouseDown={startResizeLeft}
            role="separator"
            aria-orientation="vertical"
            aria-label="Ensanchar la lista"
          />
          {/* El botón de colapsar va EN la fila del encabezado "Capas", no en una franja propia
              (ahorra la fila que ocupaba). */}
          <div className={styles.section}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                className={styles.sectionHead}
                onClick={() => setOpenCapas((v) => !v)}
                style={{ flex: 1, minWidth: 0 }}
              >
                {openCapas ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                <LayerRegular />
                {esInventarioIfc ? 'Lo que trae el modelo' : 'Capas'}
                <span className={styles.sectionCount}>{inventario?.capas.length ?? 0}</span>
              </button>
              <BtnContraer lado="izq" onClick={() => setLeftAbierto(false)} />
            </div>
            {openCapas && (
              <div className={styles.list}>
                {esInventarioIfc && (nivelesIfc.length > 0 || obrasIfc.length > 1) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 14px 6px 14px', fontSize: 12 }}>
                    <span style={{ color: 'var(--text-tertiary)', marginRight: 2 }}>Agrupar por</span>
                    {(['tipo', ...(nivelesIfc.length > 0 ? ['piso'] : []), ...(obrasIfc.length > 1 ? ['obra'] : [])] as ('tipo' | 'piso' | 'obra')[]).map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={styles.iconBtn}
                        aria-pressed={panelIfcPor === v}
                        title={
                          v === 'tipo'
                            ? 'Lista las piezas por su tipo.'
                            : v === 'piso'
                              ? 'Lista las piezas por piso: piso → categoría → tipo → pieza.'
                              : 'Lista las piezas por obra (cada puente, carretera o edificio del modelo): obra → categoría → tipo → pieza.'
                        }
                        onClick={() => setPanelIfcPor(v)}
                        style={{
                          width: 'auto', height: 24, padding: '0 8px', fontSize: 12,
                          ...(panelIfcPor === v ? { background: 'var(--accent-weak)', color: 'var(--accent)' } : {}),
                        }}
                      >
                        {v === 'tipo' ? 'Tipo' : v === 'piso' ? 'Piso' : 'Obra'}
                      </button>
                    ))}
                  </div>
                )}
                {!inventario && (
                  <div className={styles.emptyHint}>Abre un plano para ver sus capas.</div>
                )}
                {inventario && inventario.capas.length === 0 && (
                  <div className={styles.emptyHint}>El plano no trae geometría por capa.</div>
                )}
                {/* Fila de capa (tipo), reusada en la vista plana (DWG) y agrupada (IFC). */}
                {(() => {
                  // Decisión efectiva = corrección del usuario si la hay, si no la de la IA (DWG) o la de
                  // la categoría estándar del elemento (IFC, vía decisionIfc de módulo). Coherencia DWG↔IFC.
                  const decisionEfectiva = (capa: string, categoria?: string, supertipos?: readonly string[]): DecisionDeCapa | null =>
                    decisionOverride[capa] ?? (esInventarioIfc ? decisionIfc(categoria, supertipos) : decisionDeCapa(capa, capasClasificadas));
                  const moverCapa = (capa: string, d: DecisionDeCapa) =>
                    guardarDecision(capa, d);
                  // El ⋯ del usuario mueve DIRECTO entre los dos destinos reales (un paso). "¿Esto se
                  // construye?" (dudoso) NO es un destino que el usuario elija: lo propone la IA con su duda.
                  const CUBETAS: { d: DecisionDeCapa; label: string }[] = [
                    { d: 'cotiza', label: 'Se construye' },
                    { d: 'ruido', label: 'Es solo dibujo' },
                  ];
                  // `piso`: en el árbol por piso, solo las piezas de ese piso (undefined = el tipo entero).
                  // `grupoDe`: de qué grupo es cada pieza en ese árbol (piso por defecto; obra en "Agrupar por obra").
                  const filaCapa = (c: PlanoCapa, piso?: number | null, grupoDe: ReadonlyMap<number, number> = nivelDeElemento) => {
                    const todasDelTipo = c.instancias ?? [];
                    // IFC: con el tipo activo se listan sus INSTANCIAS para poder sacar/reingresar cada
                    // una desde el panel (camino confiable para las piezas tapadas en el 3D). Clic en la
                    // fila = misma acción que clic en la pieza en la escena; la cantidad se recalcula.
                    const instancias =
                      piso === undefined
                        ? todasDelTipo
                        : todasDelTipo.filter((x) => (grupoDe.get(x.id) ?? null) === piso);
                    const sel =
                      capasSel.has(c.capa) &&
                      (piso === undefined || instancias.some((x) => !instExcluidas.has(x.id)));
                    const mostrarInstancias = sel && esInventarioIfc && instancias.length > 1;
                    const incluidas = instancias.filter((x) => !instExcluidas.has(x.id)).length;
                    // Estado de asignación de la capa (para el contador "N total · A asignadas · P sin asignar").
                    const conteo = esInventarioIfc ? contarAsignadas(c) : null;
                    const capaEntera = asignacionPorCapa.completas.has(c.capa);
                    const idsAsignados = asignacionPorCapa.ids.get(c.capa);
                    // Decisión efectiva de la capa (para el control de corregir cubeta). DWG: de la IA.
                    // IFC: por categoría estándar del elemento (mobiliario/anotación → ruido, resto → obra).
                    const decEf = decisionEfectiva(c.capa, c.categoria, c.ifc?.supertipos);
                    const nTotal = piso === undefined ? c.n_entidades : instancias.length;
                    return (
                      <div key={piso === undefined ? c.capa : `${c.capa}@${piso}`}>
                        {/* "¿Se construye?" va en su propia línea: al lado del nombre lo tapaba. */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <button
                            type="button"
                            className={mergeClasses(styles.row, sel && !(vistaSel?.capa === c.capa) && styles.rowSel)}
                            onClick={() =>
                              piso === undefined
                                ? vistaSel?.capa === c.capa
                                  ? setVistaSel(null) // de una planta a la capa completa
                                  : toggleCapa(c.capa)
                                : seleccionarPiezasDelTipo(c, instancias.map((x) => x.id))
                            }
                            title={`${c.capa} · ${c.tipos.join(', ')}`}
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            {esInventarioIfc && c.categoria ? (
                              // IFC: arriba el nombre legible (clase + exterior + espesor medido), abajo en
                              // chico el nombre del modelo y las piezas; a la derecha la cantidad que manda.
                              <>
                                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                  <span className={styles.rowName} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                    {capasConPrincipal.has(c.capa) && (
                                      <CheckmarkCircle16Filled style={{ color: 'var(--ok)', flexShrink: 0 }} aria-label="Ya tiene concepto" />
                                    )}
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{nombreDeclaradoDelTipoIfc(c)?.tipo ?? nombreLegibleTipoIfc(c)}</span>
                                  </span>
                                  <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {nombreDeclaradoDelTipoIfc(c) ? `${nombreLegibleTipoIfc(c)}${nombreDeclaradoDelTipoIfc(c)?.familia ? ` · ${nombreDeclaradoDelTipoIfc(c)?.familia}` : ''}` : c.capa} · {mostrarInstancias && incluidas < instancias.length ? `${incluidas} de ${nTotal}` : nTotal}{' '}
                                    {nTotal === 1 ? 'pieza' : 'piezas'}
                                  </span>
                                </span>
                                <span className={styles.rowMeta} style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {(() => {
                                    const mp = medidaPrincipalIfc(c.medidas, c.categoria, c.ifc?.predefinido, c.ifc?.supertipos);
                                    return mp ? fmtMedidaIfc(mp) : '';
                                  })()}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className={styles.rowName}>{c.capa}</span>
                                <span className={styles.rowMeta}>
                                  {mostrarInstancias && incluidas < instancias.length
                                    ? `${incluidas}/${nTotal}`
                                    : nTotal}
                                </span>
                              </>
                            )}
                          </button>
                          {MEMORIA_TIPOS_ENABLED && esInventarioIfc && c.categoria && (
                            <button
                              type="button"
                              title="Asignar o cambiar el concepto de este tipo (se recuerda para tus próximos planos)"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTipoInicialDelDialogo(`capa:${c.capa}`);
                                setTiposAbierto(true);
                              }}
                              style={{ flex: 'none', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 11, padding: '2px 4px', borderRadius: 4 }}
                            >
                              Asignar
                            </button>
                          )}
                          {/* Ojo: apaga/prende esta capa (DWG) o las piezas de este tipo (IFC). Solo vista. */}
                          {(() => {
                            const ids = esInventarioIfc ? instancias.map((x) => x.id) : [];
                            if (esInventarioIfc ? !ifcAbierto || ids.length === 0 : !usarMlightcad) return null;
                            const oculta = esInventarioIfc ? todosOcultos(ocultasOjo, ids) : capasOcultasDwg.has(c.capa);
                            const nombreOjo = esInventarioIfc ? nombreLegibleTipoIfc(c) : c.capa;
                            return (
                              <button
                                type="button"
                                aria-pressed={oculta}
                                aria-label={`${oculta ? 'Mostrar' : 'Ocultar'} ${nombreOjo}`}
                                title={oculta ? 'Oculta en la vista: clic para mostrarla. Sigue contando en la hoja.' : 'Ocultar en la vista (sigue contando en la hoja).'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (esInventarioIfc) setOcultasOjo((prev) => alternarGrupoOculto(prev, ids));
                                  else setCapasOcultasDwg((prev) => alternarCapaOculta(prev, c.capa));
                                }}
                                style={{
                                  flex: 'none', border: 'none', background: 'none', cursor: 'pointer', display: 'inline-flex',
                                  color: oculta ? 'var(--accent)' : 'var(--text-tertiary)', padding: '2px 4px', borderRadius: 4,
                                }}
                              >
                                {oculta ? <EyeOff16Regular /> : <Eye16Regular />}
                              </button>
                            );
                          })()}
                          {/* "¿Esto se construye?": la IA no supo → pregunta CLARA Sí/No.
                              En las demás cubetas: el ⋯ para corregir si quedó mal. */}
                          {/* Mismo "⋯" en TODAS las filas (también las dudosas): la decisión en lote va
                              en el encabezado de la sección; aquí solo se corrige la excepción. */}
                          {decEf ? (
                            <Popover positioning="below-end" withArrow>
                              <PopoverTrigger disableButtonEnhancement>
                                <button
                                  type="button"
                                  onClick={(e) => e.stopPropagation()}
                                  title="¿Mal clasificada? Muévela de cubeta"
                                  style={{
                                    flex: 'none', border: 'none', background: 'none', cursor: 'pointer',
                                    color: 'var(--text-tertiary)', fontSize: 16, lineHeight: 1,
                                    padding: '2px 6px', borderRadius: 4,
                                  }}
                                >
                                  ⋯
                                </button>
                              </PopoverTrigger>
                              <PopoverSurface>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, maxWidth: 180 }}>
                                  ¿Dónde va <b>{c.capa}</b>?
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {CUBETAS.map((b) => (
                                    <Button
                                      key={b.d}
                                      size="small"
                                      appearance={decEf === b.d ? 'primary' : 'secondary'}
                                      onClick={() => moverCapa(c.capa, b.d)}
                                    >
                                      {b.label}
                                    </Button>
                                  ))}
                                </div>
                              </PopoverSurface>
                            </Popover>
                          ) : null}
                        </div>
                        {/* DWG con varias plantas: la capa desglosada por planta, con el rótulo que trae el
                            dibujo, su largo, su altura y su área. Tocar una = trabajar solo esa planta; tocar
                            la capa = la capa completa. Las fachadas y cortes no suman y no se listan. */}
                        {!esInventarioIfc && (() => {
                          const vs = inventario?.vistas ?? [];
                          const plantas = (c.por_vista ?? []).filter((x) => vs.find((v) => v.id === x.vista)?.suma === true);
                          if (plantas.length < 2 || escalaSinResolver) return null;
                          const asigC = asignables.find((x) => x.seleccion.tipo === 'capa' && x.seleccion.clave === c.capa && !x.seleccion.vista);
                          // Muro con huecos: el área de cada planta es la neta (misma cuenta que el panel), no
                          // largo sin huecos × alto. Tramo i = planta i (las mismas plantas que suman, mismo orden).
                          // Se arma aquí (no del asignable: la capa puede no estar seleccionada), igual que el panel.
                          const muroVanosC =
                            factorEscala != null && factorEscala > 0 &&
                            medirCapa2D(c, factorEscala, capaSeMideComoMuro(c.capa, capasClasificadas, decisionOverride[c.capa])).dobleTrazo
                              ? armarMuroVanos(
                                  plantas.map((x) => ({ eje: x.eje_bruto, vanos: x.vanos, altoPropioM: alturaPorVista.get(x.vista)?.altoM ?? null })),
                                  factorEscala,
                                )
                              : undefined;
                          const cuentaC = cuentaPorPlantasConVanos(muroVanosC, altosVanosStr);
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', padding: '0 0 4px 14px' }}>
                              {plantas.map((x, i) => {
                                const v = vs.find((y) => y.id === x.vista);
                                const selV = vistaSel?.capa === c.capa && vistaSel.vista === x.vista;
                                const pl = asigC?.porPlanta?.find((y) => y.vista === x.vista);
                                const mv = medirCapa2D(x, factorEscala, capaSeMideComoMuro(c.capa, capasClasificadas, decisionOverride[c.capa]));
                                const largoV = mv.baseLargo;
                                // El alto y el área solo tienen sentido en un muro (medido por su eje).
                                const altoV = mv.dobleTrazo ? alturaPorVista.get(x.vista)?.altoM ?? null : null;
                                const m0 = pl ?? {
                                  rotulo: rotuloDeVista(v, vs.findIndex((y) => y.id === x.vista)),
                                  largoM: largoV,
                                  altoM: altoV,
                                  areaM2: largoV != null && altoV != null ? Math.round(largoV * altoV * 100) / 100 : null,
                                };
                                const netoV = cuentaC && cuentaC.tiposSinAlto === 0 && cuentaC.porTramo.length === plantas.length
                                  ? cuentaC.porTramo[i]?.netoM2 ?? null
                                  : null;
                                const m = netoV != null ? { ...m0, areaM2: Math.round(netoV * 100) / 100 } : m0;
                                return (
                                  <button
                                    key={x.vista}
                                    type="button"
                                    className={mergeClasses(styles.row, selV && styles.rowSel)}
                                    onClick={() => elegirVistaDeCapa(c.capa, x.vista)}
                                    title={
                                      netoV != null && cuentaC
                                        ? `${c.capa} · solo ${m.rotulo}: ${fmtM(cuentaC.porTramo[i]!.brutoM2)} m² (largo con sus huecos × alto) − ${fmtM(cuentaC.porTramo[i]!.vanosM2)} m² de puertas y ventanas`
                                        : `${c.capa} · solo ${m.rotulo}`
                                    }
                                    style={{ paddingTop: 3, paddingBottom: 3 }}
                                  >
                                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                      <span className={styles.rowName} style={{ fontSize: 12 }}>{m.rotulo}</span>
                                      <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {m.largoM != null ? `${fmtM(m.largoM)} m` : '—'}
                                        {m.altoM != null ? ` · alto ${fmtM(m.altoM)} m` : mv.dobleTrazo ? ' · sin alto' : ''}
                                        {m.areaM2 != null ? ` · ${fmtM(m.areaM2)} m²${netoV != null ? ' sin puertas ni ventanas' : ''}` : ''}
                                      </span>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {/* Elegiste parte del tipo (p. ej. UNA puerta con clic): cuántas iguales hay y un botón
                            para elegirlas todas (quita las exclusiones; la cantidad se recalcula). */}
                        {sel && esInventarioIfc && instancias.length > 1 && incluidas < instancias.length && (
                          <div
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                              padding: '0 14px 6px 30px', fontSize: 11.5, color: 'var(--text-secondary)',
                            }}
                          >
                            <span>
                              {incluidas === 1 ? 'Elegiste 1 pieza' : `Elegiste ${incluidas} piezas`} · hay{' '}
                              {instancias.length} iguales
                            </span>
                            <Button
                              size="small"
                              appearance="secondary"
                              title="Elige todas las piezas de este tipo."
                              onClick={(e) => {
                                e.stopPropagation();
                                seleccionarPiezasDelTipo(c, instancias.map((x) => x.id));
                              }}
                            >
                              Seleccionar las {instancias.length}
                            </Button>
                          </div>
                        )}
                        {/* Contador de asignación: cuántas piezas del tipo ya tienen concepto y cuántas
                            faltan, para ver de un vistazo lo que resta. Solo cuando ya se asignó algo. */}
                        {conteo && conteo.asignadas > 0 && (
                          <div
                            style={{
                              padding: '0 14px 4px 30px', fontSize: 11, lineHeight: 1.3,
                              color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {conteo.total} total · {conteo.asignadas} asignadas ·{' '}
                            <span style={{ color: conteo.sinAsignar > 0 ? 'var(--accent)' : 'var(--text-tertiary)', fontWeight: 600 }}>
                              {conteo.sinAsignar} sin asignar
                            </span>
                          </div>
                        )}
                        {mostrarInstancias && (
                          <div>
                            {/* La casilla decide si la pieza CUENTA para el concepto; el resto de la
                                fila la ENFOCA en el 3D (magenta) para ubicarla. */}
                            {instancias.map((inst, i) => {
                              const fuera = instExcluidas.has(inst.id);
                              const enfocada = instEnfoque === inst.id;
                              const asignada = capaEntera || idsAsignados?.has(inst.id) === true;
                              // Nombre/marca real de la pieza; si el modelo no lo trae, tipo + índice.
                              // "Muro 3 · Nivel 1 · 10.43 m²": nunca el GUID ni el número interno.
                              const nivelId = nivelDeElemento.get(inst.id);
                              const medidaPieza = medidaPrincipalIfc({ conteo: 1, ...inst, manda: c.medidas?.manda }, c.categoria, c.ifc?.predefinido, c.ifc?.supertipos);
                              const nombrePieza = nombrePiezaIfc({
                                nombre: inst.nombre,
                                clase: c.categoria,
                                indice: i,
                                nivel: nivelId != null ? etiquetaNivel.get(nivelId)?.corto : null,
                                medida: medidaPieza && medidaPieza.magnitud !== 'conteo' ? medidaPieza : null,
                              });
                              return (
                                <div
                                  key={inst.id}
                                  onClick={() => enfocarInstancia(inst.id)}
                                  title="Clic para resaltarla en el modelo 3D. Usa la casilla para incluirla o no en el concepto."
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                                    cursor: 'pointer', padding: '4px 14px 4px 28px', textAlign: 'left',
                                    color: 'var(--text-tertiary)',
                                    // Enfocada: fondo de ALTO CONTRASTE (magenta, mismo color que el foco en 3D),
                                    // no un pastel que se pierde sobre superficies claras/oscuras.
                                    background: enfocada ? 'rgba(255, 43, 214, 0.22)' : 'transparent',
                                    boxShadow: enfocada ? 'inset 3px 0 0 #ff2bd6' : 'none',
                                  }}
                                >
                                  <Checkbox
                                    checked={!fuera}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleInstancia(inst.id)}
                                    aria-label={`Contar ${nombrePieza} para el concepto`}
                                    title={fuera ? 'No cuenta para el concepto' : 'Cuenta para el concepto'}
                                  />
                                  <span
                                    className={styles.rowName}
                                    style={{
                                      fontSize: 12,
                                      color: enfocada ? '#ff2bd6' : undefined,
                                      fontWeight: enfocada ? 700 : undefined,
                                      opacity: fuera ? 0.5 : 1,
                                      textDecoration: fuera ? 'line-through' : 'none',
                                    }}
                                  >
                                    {nombrePieza}
                                  </span>
                                  {asignada && (
                                    <CheckmarkCircle16Filled
                                      style={{ color: 'var(--ok)', flexShrink: 0, marginLeft: 'auto' }}
                                      aria-label="Ya tiene concepto"
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  };
                  const capas = inventario?.capas ?? [];
                  // Agrupa las capas en 3 cubetas por su DECISIÓN, IGUAL en DWG e IFC (coherencia): la IA
                  // decide en DWG (por clasificación) y en IFC por la CATEGORÍA estándar del elemento
                  // (decisionIfc, pasando c.categoria). "Se construye" / "¿Esto se construye?" (la IA dudó,
                  // solo DWG) / "Es solo dibujo". Sin clasificación (DWG viejo/sin saldo) → lista plana.
                  const dec = (c: PlanoCapa) => decisionEfectiva(c.capa, c.categoria);
                  const hayClasif = capas.some((c) => dec(c) != null);
                  // Árbol POR PISO (IFC): Piso → Categoría → Tipo → Pieza. Clic en el tipo elige sus piezas
                  // de ESE piso; "Aislar" deja a la vista solo una categoría del piso; "Ver" muestra el piso
                  // cortado como plano. Las filas de tipo y pieza son las mismas de la vista por tipo.
                  // Árbol POR OBRA (modelo con varias IfcFacility: un puente carretero y dos ferroviarios):
                  // Obra → Categoría → Tipo → Pieza, con el nombre de la obra tal como lo trae el modelo.
                  const porObra = esInventarioIfc && panelIfcPor === 'obra' && obrasIfc.length > 1;
                  if (porObra || (esInventarioIfc && panelIfcPor === 'piso' && nivelesIfc.length > 0)) {
                    const grupoDe: ReadonlyMap<number, number> = porObra ? obraDeElemento : nivelDeElemento;
                    const grupos: { key: string; id: number | null; nombre: string; delModelo: string | null }[] = porObra
                      ? [
                          ...obrasIfc.map((o) => ({ key: `obra:${o.id}`, id: o.id, nombre: o.nombre, delModelo: CATEGORIA_IFC_ES[o.clase] ?? null })),
                          { key: 'obra:sin', id: null, nombre: 'Sin obra', delModelo: null },
                        ]
                      : [
                          ...[...nivelesIfc]
                            .sort((x, y) => (etiquetaNivel.get(x.id)?.titulo ?? '').localeCompare(etiquetaNivel.get(y.id)?.titulo ?? '', 'es', { numeric: true }))
                            .map((n) => ({ key: String(n.id), id: n.id, nombre: etiquetaNivel.get(n.id)?.titulo ?? n.nombre, delModelo: n.nombre as string | null })),
                          { key: 'sin-piso', id: null, nombre: 'Sin piso', delModelo: null },
                        ];
                    return grupos.map((g) => {
                      const porCat = new Map<string, PlanoCapa[]>();
                      let total = 0;
                      for (const c of capas) {
                        const n = (c.instancias ?? []).filter((i) => (grupoDe.get(i.id) ?? null) === g.id).length;
                        if (!n) continue;
                        total += n;
                        const cat = c.categoria ?? 'Otros';
                        (porCat.get(cat) ?? porCat.set(cat, []).get(cat)!).push(c);
                      }
                      if (!total) return null;
                      const abierto = pisosAbiertos.has(g.key);
                      const viendo = g.id != null && nivelVista === g.id;
                      return (
                        <div key={g.key}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <button
                              type="button"
                              className={styles.row}
                              onClick={() =>
                                setPisosAbiertos((prev) => {
                                  const n = new Set(prev);
                                  if (n.has(g.key)) n.delete(g.key);
                                  else n.add(g.key);
                                  return n;
                                })
                              }
                              style={{ flex: 1, minWidth: 0, fontWeight: 700 }}
                            >
                              {abierto ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                              <span className={styles.rowName}>
                                {g.nombre}
                                {g.delModelo && (
                                  <span style={{ display: 'block', fontSize: 10.5, fontWeight: 400, color: 'var(--text-tertiary)' }}>{g.delModelo}</span>
                                )}
                              </span>
                              <span className={styles.rowMeta}>{total}</span>
                            </button>
                            {g.id != null && !porObra && (
                              <button
                                type="button"
                                className={styles.iconBtn}
                                aria-pressed={viendo}
                                title="Ver solo este piso, cortado como un plano."
                                onClick={() => setNivelVista(viendo ? null : g.id)}
                                style={{
                                  width: 'auto', padding: '0 8px', fontSize: 11, marginRight: 6,
                                  ...(viendo ? { background: 'var(--accent-weak)', color: 'var(--accent)' } : {}),
                                }}
                              >
                                Ver
                              </button>
                            )}
                          </div>
                          {abierto &&
                            [...porCat].map(([cat, cs]) => {
                              const ids = cs.flatMap((c) =>
                                (c.instancias ?? []).filter((i) => (grupoDe.get(i.id) ?? null) === g.id).map((i) => i.id),
                              );
                              const aislada =
                                aisladasArbol != null && ids.length === aisladasArbol.size && ids.every((id) => aisladasArbol.has(id));
                              return (
                                <div key={cat} style={{ paddingLeft: 10 }}>
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div className={styles.capaCatHead} style={{ flex: 1, minWidth: 0 }}>
                                      <span>{CATEGORIA_IFC_ES[cat] ?? categoriaSingular(cat)}</span>
                                      <span className={styles.rowMeta}>{ids.length}</span>
                                    </div>
                                    {ifcAbierto && ids.length > 0 && (() => {
                                      const oculta = todosOcultos(ocultasOjo, ids);
                                      const nombreCat = CATEGORIA_IFC_ES[cat] ?? categoriaSingular(cat);
                                      return (
                                        <button
                                          type="button"
                                          aria-pressed={oculta}
                                          aria-label={`${oculta ? 'Mostrar' : 'Ocultar'} ${nombreCat}`}
                                          title={oculta ? 'Oculta en la vista: clic para mostrarla. Sigue contando en la hoja.' : 'Ocultar en la vista (sigue contando en la hoja).'}
                                          onClick={() => setOcultasOjo((prev) => alternarGrupoOculto(prev, ids))}
                                          style={{
                                            flex: 'none', border: 'none', background: 'none', cursor: 'pointer', display: 'inline-flex',
                                            color: oculta ? 'var(--accent)' : 'var(--text-tertiary)', padding: '2px 4px', borderRadius: 4,
                                          }}
                                        >
                                          {oculta ? <EyeOff16Regular /> : <Eye16Regular />}
                                        </button>
                                      );
                                    })()}
                                    <button
                                      type="button"
                                      className={styles.iconBtn}
                                      aria-pressed={aislada}
                                      title="Deja a la vista solo estas piezas (aislar). Otro clic las vuelve a mostrar todas."
                                      onClick={() => setAisladasArbol(aislada ? null : new Set(ids))}
                                      style={{
                                        width: 'auto', padding: '0 8px', fontSize: 11, marginRight: 6,
                                        ...(aislada ? { background: 'var(--accent-weak)', color: 'var(--accent)' } : {}),
                                      }}
                                    >
                                      Aislar
                                    </button>
                                  </div>
                                  {cs.map((c) => filaCapa(c, g.id, grupoDe))}
                                </div>
                              );
                            })}
                        </div>
                      );
                    });
                  }
                  if (!hayClasif) return capas.map((c) => filaCapa(c));
                  // IFC: "Se construye" agrupado por lo que es (Muros, Losas, Techo, Puertas y ventanas,
                  // Otros), por la clase del estándar — no por el nombre del tipo.
                  // `conTitulo=false` cuando TODO se construye: el encabezado no distinguiría nada.
                  const seccionIfcAgrupada = (grupo: PlanoCapa[], conTitulo = true) =>
                    grupo.length === 0 ? null : (
                      <div key="se-construye">
                        {conTitulo && <button
                          type="button"
                          className={styles.capaCatHead}
                          title="Se construye"
                          onClick={() => alternarGrupo('sec:Se construye')}
                          aria-expanded={!gruposCerrados.has('sec:Se construye')}
                          style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {gruposCerrados.has('sec:Se construye') ? <ChevronRight16Regular /> : <ChevronDown16Regular />}
                            Se construye
                          </span>
                          <span className={styles.rowMeta}>{grupo.length}</span>
                        </button>}
                        {(!conTitulo || !gruposCerrados.has('sec:Se construye')) && GRUPOS_IFC.map((g) => {
                          const deG = grupo.filter((c) => grupoDeCapaIfc(c) === g);
                          if (deG.length === 0) return null;
                          const cerrado = gruposCerrados.has(`ifc:${g}`);
                          return (
                            <div key={g}>
                              <button
                                type="button"
                                onClick={() => alternarGrupo(`ifc:${g}`)}
                                aria-expanded={!cerrado}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'space-between', padding: '8px 14px 2px 8px', fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                              >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  {cerrado ? <ChevronRight16Regular /> : <ChevronDown16Regular />}
                                  {g}
                                </span>
                                <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>
                                  {deG.length} {deG.length === 1 ? 'tipo' : 'tipos'}
                                </span>
                              </button>
                              {!cerrado && deG.map((c) => filaCapa(c))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  const presup = capas.filter((c) => dec(c) === 'cotiza');
                  const revisar = capas.filter((c) => dec(c) === 'dudoso');
                  const ruido = capas.filter((c) => dec(c) === 'ruido');
                  const seccionCapas = (titulo: string, grupo: typeof capas, hint?: string, enLote?: boolean) =>
                    grupo.length === 0 ? null : (
                      <div key={titulo}>
                        <button
                          type="button"
                          className={styles.capaCatHead}
                          title={titulo}
                          onClick={() => alternarGrupo(`sec:${titulo}`)}
                          aria-expanded={!gruposCerrados.has(`sec:${titulo}`)}
                          style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {gruposCerrados.has(`sec:${titulo}`) ? <ChevronRight16Regular /> : <ChevronDown16Regular />}
                            {titulo}
                          </span>
                          <span className={styles.rowMeta}>{grupo.length}</span>
                        </button>
                        {!gruposCerrados.has(`sec:${titulo}`) && hint && (
                          <div style={{ padding: '2px 14px 6px 14px', fontSize: 11, lineHeight: 1.35, color: 'var(--text-tertiary)' }}>
                            {hint}
                          </div>
                        )}
                        {/* Decisión EN LOTE para lo que ci no supo clasificar; la excepción se corrige con el ⋯ de su fila. */}
                        {!gruposCerrados.has(`sec:${titulo}`) && enLote && (
                          <div style={{ display: 'flex', gap: 6, padding: '0 14px 8px' }}>
                            <Button size="small" appearance="primary" onClick={() => grupo.forEach((c) => moverCapa(c.capa, 'cotiza'))}>
                              Todos se construyen
                            </Button>
                            <Button size="small" appearance="secondary" onClick={() => grupo.forEach((c) => moverCapa(c.capa, 'ruido'))}>
                              Todos son dibujo
                            </Button>
                          </div>
                        )}
                        {!gruposCerrados.has(`sec:${titulo}`) && grupo.map((c) => filaCapa(c))}
                      </div>
                    );
                  // Si TODO se construye, el encabezado "Se construye" no distingue nada: va directo la lista.
                  const soloObra = revisar.length === 0 && ruido.length === 0;
                  if (soloObra && !esInventarioIfc) return presup.map((c) => filaCapa(c));
                  return [
                    esInventarioIfc ? seccionIfcAgrupada(presup, !soloObra) : seccionCapas('Se construye', presup),
                    esInventarioIfc
                      ? seccionCapas('Revisar: genéricos', revisar,
                          'El modelo no dice qué son (elementos genéricos o del entorno): pueden ser obra, o personas, árboles y autos del render. Decide todos de una vez; si alguno es distinto, corrígelo con su ⋯.', true)
                      : seccionCapas('¿Esto se construye? — la IA no está segura', revisar,
                          'La IA no supo si estas se construyen. Decide todas de una vez; si alguna es distinta, corrígela con su ⋯.', true),
                    seccionCapas('Es solo dibujo (no se cotiza)', ruido),
                  ];
                })()}
              </div>
            )}
          </div>

          {/* «¿Qué me falta?»: cuánto del plano ya está en la hoja y qué falta (IFC y DWG). */}
          {faltaDelPlano && faltaDelPlano.avance.elementos > 0 && (
            <QueMeFaltaPanel
              avance={faltaDelPlano.avance}
              sinDecidir={faltaDelPlano.sinDecidir}
              verFalta={verFalta}
              onVerFalta={setVerFalta}
              onElegir={(f) => irA({ tipo: f.tipo, clave: f.clave, texto: f.nombre })}
            />
          )}

          {/* Bloques es un concepto de CAD (INSERTs del DWG); el IFC no tiene esa dicotomía —sus
              elementos ya vienen tipados—, así que en IFC esta sección no aplica y se oculta. */}
          {!esInventarioIfc && (
          <div className={styles.section}>
            <button
              type="button"
              className={styles.sectionHead}
              onClick={() => setOpenBloques((v) => !v)}
            >
              {openBloques ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
              <CubeRegular />
              Bloques
              <span className={styles.sectionCount}>{inventario?.bloques.length ?? 0}</span>
            </button>
            {openBloques && (
              <div className={styles.list}>
                {!inventario && (
                  <div className={styles.emptyHint}>Abre un plano para ver sus bloques.</div>
                )}
                {inventario && inventario.bloques.length === 0 && (
                  <div className={styles.emptyHint}>
                    El plano no trae bloques con nombre. Puede estar dibujado a mano.
                  </div>
                )}
                {(() => {
                  const bloques = inventario?.bloques ?? [];
                  const decB = (nombre: string) => decisionOverride[nombre] ?? decisionDeBloque(nombre, capasClasificadas);
                  const moverB = (nombre: string, d: DecisionDeCapa) => guardarDecision(nombre, d);
                  const CUB: { d: DecisionDeCapa; label: string }[] = [
                    { d: 'cotiza', label: 'Se construye' }, { d: 'ruido', label: 'Es solo dibujo' },
                  ];
                  const filaBloque = (b: PlanoInventario['bloques'][number]) => {
                    const seleccionadas = b.ids.filter((id) => idsSel.has(id)).length;
                    const sel = seleccionadas > 0;
                    const dEf = decB(b.nombre);
                    return (
                      <div key={b.nombre + b.nombre_crudo} style={{ display: 'flex', alignItems: 'center' }}>
                        <button
                          type="button"
                          className={mergeClasses(styles.row, sel && styles.rowSel)}
                          onClick={() => toggleBloque(b.nombre)}
                          title={b.anonimo ? `${b.nombre} (bloque dinámico: ${b.nombre_crudo})` : b.nombre}
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <span className={styles.rowName}>{b.nombre}</span>
                          <span className={styles.rowMeta}>{sel ? `${seleccionadas}/${b.n}` : b.n}</span>
                        </button>
                        {dEf ? (
                          <Popover positioning="below-end" withArrow>
                            <PopoverTrigger disableButtonEnhancement>
                              <button type="button" onClick={(e) => e.stopPropagation()} title="¿Mal clasificado? Muévelo de cubeta"
                                style={{ flex: 'none', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 16, lineHeight: 1, padding: '2px 6px', borderRadius: 4 }}>⋯</button>
                            </PopoverTrigger>
                            <PopoverSurface>
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, maxWidth: 180 }}>¿Dónde va <b>{b.nombre}</b>?</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {CUB.map((x) => (
                                  <Button key={x.d} size="small" appearance={dEf === x.d ? 'primary' : 'secondary'} onClick={() => moverB(b.nombre, x.d)}>{x.label}</Button>
                                ))}
                              </div>
                            </PopoverSurface>
                          </Popover>
                        ) : null}
                      </div>
                    );
                  };
                  const hayClasif = bloques.some((b) => decB(b.nombre) != null);
                  if (!hayClasif) return bloques.map(filaBloque);
                  const grupo = (d: DecisionDeCapa) => bloques.filter((b) => decB(b.nombre) === d);
                  const secc = (titulo: string, g: typeof bloques) =>
                    g.length === 0 ? null : (
                      <div key={titulo}>
                        <div className={styles.capaCatHead} title={titulo}><span>{titulo}</span><span className={styles.rowMeta}>{g.length}</span></div>
                        {g.map(filaBloque)}
                      </div>
                    );
                  return [secc('Se construye', grupo('cotiza')), secc('¿Esto se construye? — la IA no está segura', grupo('dudoso')), secc('Es solo dibujo (no se cotiza)', grupo('ruido'))];
                })()}
              </div>
            )}
          </div>
          )}
        </aside>
        ) : (
          sinPlano ? <div /> : (
          <RailColapsar etiqueta="Capas y bloques" lado="izq" onExpandir={() => setLeftAbierto(true)} />
          )
        )}

        {/* CENTRO: el plano */}
        <div ref={visorWrapRef} className={styles.viewerWrap}>
          {/* La barra de zoom/atenuar es del SVG 2D; el visor 3D del IFC trae sus propios controles.
              Se oculta mientras el visor 3D EXPERIMENTAL está a la vista (tiene sus propios controles). */}
          {status === 'ready' && !ifcAbierto && !ifcExpUrl && !usarMlightcad && !esPdfEstimado && !modoLista && visorConEspacio && (
            <div className={styles.viewerToolbar}>
              <button type="button" className={styles.iconBtn} onClick={() => zoomBtn(1 / 1.25)} title="Acercar" aria-label="Acercar">
                <ZoomInRegular />
              </button>
              <button type="button" className={styles.iconBtn} onClick={() => zoomBtn(1.25)} title="Alejar" aria-label="Alejar">
                <ZoomOutRegular />
              </button>
              <button type="button" className={styles.iconBtn} onClick={resetView} title="Ajustar a la vista" aria-label="Ajustar a la vista">
                <ArrowResetRegular />
              </button>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setAtenuarAsignados((v) => !v)}
                title="Atenuar en el plano los elementos ya asignados (para ver qué falta)"
                aria-pressed={atenuarAsignados}
                style={{
                  width: 'auto',
                  padding: '0 10px',
                  fontSize: 12,
                  ...(atenuarAsignados ? { background: 'var(--accent-weak)', color: 'var(--accent)' } : {}),
                }}
              >
                Atenuar asignados
              </button>
              {/* Cotas y nombres de local: enciende la capa de anotaciones que el SVG trae oculta.
                  Solo se ofrece si el plano trae algo que mostrar (cotas o textos). BORRADOR de texto. */}
              {((inventario?.anotaciones?.textos?.length ?? 0) > 0 ||
                (inventario?.anotaciones?.cotas?.n ?? 0) > 0) && (
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => setMostrarAnotaciones((v) => !v)}
                  title="Mostrar sobre el plano las cotas y los nombres de local (como en un CAD). No afecta la selección ni la medición."
                  aria-pressed={mostrarAnotaciones}
                  style={{
                    width: 'auto',
                    padding: '0 10px',
                    fontSize: 12,
                    ...(mostrarAnotaciones ? { background: 'var(--accent-weak)', color: 'var(--accent)' } : {}),
                  }}
                >
                  Cotas y nombres
                </button>
              )}
              {/* Vista 3D EXPERIMENTAL del plano (DWG/DXF → IFC). BORRADOR de texto (Sebastián redacta).
                  Es solo visualización: no afecta la HP ni las cantidades. */}
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => void verEn3dExperimental()}
                disabled={ifcExpStatus === 'generando'}
                title="Genera una vista 3D aproximada de este plano (experimental). Es solo para visualizar: no cambia la Hoja de Presupuesto ni las cantidades."
                style={{ width: 'auto', padding: '0 10px', fontSize: 12, opacity: ifcExpStatus === 'generando' ? 0.6 : 1 }}
              >
                <CubeRegular style={{ marginRight: 4 }} />
                {ifcExpStatus === 'generando' ? 'Generando 3D…' : 'Ver en 3D (experimental)'}
              </button>
            </div>
          )}

          {error && (
            <MessageBar intent="error" className={styles.msg}>
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}

          {/* Aviso de error del visor 3D experimental (aparte del error del pipeline 2D). */}
          {ifcExpError && !ifcExpUrl && (
            <MessageBar intent="warning" className={styles.msg}>
              <MessageBarBody>{ifcExpError}</MessageBarBody>
            </MessageBar>
          )}

          {status === 'idle' && !error && (
            <div className={styles.centerState}>
              <div className={styles.stateBox}>
                <div style={{ fontSize: '19px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Convierte tu plano en presupuesto
                </div>
                <div style={{ fontSize: '14px' }}>
                  {modoProyecto ? (
                    // Entrando por el proyecto el plano se abre solo; si llegamos aquí es que el plano de
                    // la ruta ya no está (se quitó) o todavía se está cargando la lista.
                    errorArchivos ? (
                      <>
                        No pude cargar el plano.{' '}
                        <Button size="small" appearance="primary" onClick={() => setArchivosVersion((v) => v + 1)}>
                          Reintentar
                        </Button>
                      </>
                    ) : loadingFiles || fileId ? (
                      'Abriendo el plano…'
                    ) : (
                      <>
                        Este plano ya no está en el proyecto. Elige otro arriba o vuelve a{' '}
                        <b>Todos los planos</b>.
                      </>
                    )
                  ) : (
                    <>
                      Arriba, elige tu <b>proyecto</b> y su <b>plano</b>, y pulsa <b>Abrir</b>.
                    </>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '7px',
                    textAlign: 'left',
                    fontSize: '13px',
                    marginTop: '6px',
                  }}
                >
                  <div><b>1.</b>&nbsp; Leo el dibujo y detecto sus capas y bloques.</div>
                  <div><b>2.</b>&nbsp; Te propongo el concepto de cada uno y reviso contigo lo dudoso.</div>
                  <div><b>3.</b>&nbsp; Abajo se arma tu hoja de presupuesto.</div>
                </div>
              </div>
            </div>
          )}

          {/* El IFC no se "procesa" (se ve en 3D directo): durante el breve `ensure-ifc` el visor 3D
              ya está montado, así que no mostramos aquí el spinner de conversión para no taparlo. */}
          {status === 'pending' && !ifcAbierto && (
            <div className={styles.centerState}>
              <div className={styles.stateBox}>
                <Spinner size="large" label="Procesando el plano…" />
                Esto puede tardar unos segundos mientras se convierte el dibujo.
              </div>
            </div>
          )}

          {/* PDF leído por visión: no hay dibujo que mostrar (un PDF no tiene geometría). Se muestra el
              AVISO de que la medición es estimada + las cantidades que se leyeron de las láminas.
              Sebastián, opción (a): un plano en PDF sí cuantifica, pero avisando que puede no ser exacto. */}
          {status === 'ready' && !ifcAbierto && esPdfEstimado && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'auto', padding: 16, gap: 12 }}>
              <MessageBar intent="warning">
                <MessageBarBody>
                  Este plano es un <b>PDF</b>: copié las cantidades que vienen escritas en las láminas (cotas,
                  tablas, notas); <b>el dibujo no se mide</b>. Tómalas como una estimación y revísalas
                  antes de cotizar.
                </MessageBarBody>
              </MessageBar>
              {(inventario?.cantidadesEstimadas?.length ?? 0) > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Cantidades leídas del PDF (estimadas)
                  </div>
                  <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%', maxWidth: 640 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)' }}>Concepto</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right' }}>Cantidad</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)' }}>Unidad</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right' }}>Hoja</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(inventario?.cantidadesEstimadas ?? []).map((c, i) => (
                        <tr key={`${c.que}-${i}`}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>{c.que}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right', color: 'var(--text-primary)' }}>{c.valor}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>{c.unidad ?? '—'}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right', color: 'var(--text-secondary)' }}>{c.laminaPagina ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  No se leyeron cantidades en las láminas de este PDF. Puedes revisarlo o usar un plano en formato CAD/DWG para medir sobre el dibujo.
                </div>
              )}
            </div>
          )}

          {/* ESCENARIO del visor (IFC 3D, CAD mlightcad, SVG): siempre montado. En el modo «eligiendo» se
              vuelve la MINIATURA de arriba a la izquierda: conserva su tamaño y se encoge con `transform`,
              así el canvas no cambia de tamaño y la cámara, el zoom y la selección quedan igual al volver.
              Sin interacción (pointer-events: none); el clic cae en la miniatura y vuelve al dibujo. */}
          <div
            ref={setEscenarioEl}
            aria-hidden={modoLista ? true : undefined}
            style={
              modoLista && tamEscenario && mini
                ? {
                    position: 'absolute', top: mini.top, left: mini.left, width: tamEscenario.w, height: tamEscenario.h,
                    transform: `scale(${mini.s})`, transformOrigin: '0 0', clipPath: mini.clip, zIndex: 31,
                    pointerEvents: 'none', display: hayEscenario ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden',
                  }
                : {
                    position: 'relative', flex: 1, minHeight: 0,
                    display: hayEscenario ? 'flex' : 'none', flexDirection: 'column',
                  }
            }
          >
            {/* IFC (BIM): visor 3D en el centro, en lugar del SVG 2D. El visor arma el inventario
                por tipo de elemento (llena el panel izquierdo) y deja seleccionar por clic en 3D. */}
            {ifcAbierto && projectId && (() => {
              const mb = (files.find((f) => f.id === ifcAbierto)?.sizeBytes ?? 0) / (1024 * 1024);
              return ifcAvisoTamanoMb == null || (mb >= ifcAvisoTamanoMb && !ifcGrandeConfirmados.has(ifcAbierto));
            })() ? (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 24 }}>
                {ifcAvisoTamanoMb == null ? (
                  <Spinner size="small" />
                ) : (
                  <div style={{ maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'center' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                      Este modelo pesa {Math.round((files.find((f) => f.id === ifcAbierto)?.sizeBytes ?? 0) / (1024 * 1024))} MB
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Se abre y se mide en tu navegador. Uno así de grande puede tardar varios minutos o dejar la
                      pestaña sin memoria, sobre todo en una computadora con poca memoria. Cierra otras pestañas
                      antes de abrirlo.
                    </span>
                    <div>
                      <Button
                        appearance="primary"
                        onClick={() => setIfcGrandeConfirmados((s) => new Set(s).add(ifcAbierto))}
                      >
                        Abrirlo igual
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : ifcAbierto && projectId && !visorTapado && (
              <IfcViewer3D
                key={ifcAbierto}
                projectId={projectId}
                fileId={ifcAbierto}
                onInventario={setInventario}
                sinBarraDeEstado={!visorConEspacio}
                onPrimeraVista={onPrimeraVistaIfc}
                capasSel={capasSel}
                onPickCapa={toggleCapa}
                instExcluidas={instExcluidas}
                onToggleInstancia={toggleInstancia3D}
                onPickVacio={limpiarSeleccion}
                instAsignadas={instAsignadas}
                instEnfoque={instEnfoque}
                instOcultas={instOcultas}
                instAPresupuestar={instAPresupuestar}
                instColores={instColoresVer ?? instColoresMaterial}
                onPickPieza={seleccionarPieza3D}
                instSoloVisibles={instSoloVisibles}
                nivelVista={nivelVista}
                alturaCorte={cortarPiso ? alturaCorte : null}
                ocultarTecho={ocultarTecho}
                onNiveles={(ns, nd, techo, ob) => {
                  setNivelesIfc(ns);
                  setNivelDeElemento(nd);
                  setObrasIfc(ob?.obras ?? []);
                  setObraDeElemento(ob?.obraDeElemento ?? new Map());
                  setHayTechoIfc(techo);
                }}
                onOcultarPieza={(eid) => setOcultasClic((prev) => new Set(prev).add(eid))}
                ordenCamara={ordenCamara}
                onCajaEnPantalla={registrarCajaMini}
                onCapturador={registrarCroquisIfc}
              />
            )}

            {/* «Ver en el plano / modelo» de la revisión: qué se está viendo y cómo volver, a la vista sobre el visor. */}
            {(avisoVisto || piezasVer) && (
              <div
                role="status"
                style={{
                  position: 'absolute', top: 8, left: 8, right: 8, zIndex: 20,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '10px 14px',
                  borderRadius: 8, background: 'var(--bg-surface)', border: '2px solid var(--accent)', boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{piezasVer ? 'Te muestro en el modelo' : 'Te muestro en el plano'}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{piezasVer?.titulo ?? avisoVisto?.titulo}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Lo del aviso está resaltado; lo demás se ve tenue.</div>
                </div>
                <Button
                  appearance="primary"
                  onClick={() => {
                    setAvisoVisto(null);
                    if (piezasVer) {
                      setPiezasVer(null);
                      setOrdenCamara({ n: Date.now(), vista: 'todo' });
                    }
                  }}
                >
                  Volver a ver todo
                </Button>
              </div>
            )}

            {/* VISOR CAD (mlightcad, WebGL) detrás del flag: dibuja el DXF y emite la MISMA
                PlanoSeleccion que el SVG. Solo para DWG/DXF 2D; el IFC sigue con su visor 3D. */}
            {usarMlightcad && projectId && fileId && !visorTapado && (
              <div style={{ position: 'absolute', inset: 0 }}>
                <Suspense
                  fallback={
                    <div className={styles.centerState}>
                      <div className={styles.stateBox}>
                        <Spinner size="large" label="Cargando el visor…" />
                      </div>
                    </div>
                  }
                >
                  <PlanoViewerMlightcad
                    key={`${projectId}:${fileId}`}
                    projectId={projectId}
                    fileId={fileId}
                    renglones={renglones}
                    idsSel={idsSel}
                    capasSel={capasSel}
                    onSeleccion={onSeleccionMlightcad}
                    vistas={inventario?.vistas}
                    vistaSel={vistaSel}
                    sinControles={!!modoLista || !visorConEspacio}
                    onCajaEnPantalla={registrarCajaMini}
                    queMeFalta={queMeFaltaDwg}
                    onCenso={DIAGNOSTICO_DIBUJO_ENABLED ? setCensoDwg : undefined}
                    comparacionPorEntidad={comparacionDiagnostico}
                    enfocar={avisoVisto ? { n: avisoVisto.n, capas: [], bloques: [], handles: avisoVisto.handles } : null}
                    onCapturador={registrarCroquisDwg}
                    capasOcultas={capasOcultasDwg}
                    tramasOcultas={tramasOcultasDwg}
                    onAlternarTramas={() => setTramasOcultasDwg((v) => !v)}
                    onRestablecerOjos={() => {
                      setCapasOcultasDwg(new Set());
                      setTramasOcultasDwg(false);
                    }}
                  />
                </Suspense>
              </div>
            )}

            {/* El contenedor del SVG existe siempre que haya (o vaya a haber) plano,
                para que el ref esté montado cuando se inyecte. Con el visor CAD nuevo queda oculto. */}
            <div
              ref={canvasRef}
              className={mergeClasses(styles.canvas, grabbing && styles.canvasGrabbing)}
              style={{ display: status === 'ready' && !ifcAbierto && !usarMlightcad && !esPdfEstimado ? 'flex' : 'none' }}
              onClick={onCanvasClick}
              onWheel={onWheel}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={endDrag}
              onMouseLeave={() => {
                endDrag();
                if (tipRef.current) tipRef.current.style.display = 'none';
                hoverKeyRef.current = null;
              }}
            />
          </div>

          {/* VISOR 3D EXPERIMENTAL (DWG/DXF → IFC): capa que cubre el plano 2D. Solo visualización;
              NO se conecta al inventario ni a la HP (por eso IfcViewer3D va sin onInventario/selección).
              El IFC generado se sirve por `srcUrl` (ruta del propio API, cookie de sesión). */}
          {ifcExpUrl && (
            <div
              style={{
                position: 'absolute', inset: 0, zIndex: 8, display: 'flex', flexDirection: 'column',
                background: 'var(--bg-page)',
              }}
            >
              <MessageBar intent="warning" style={{ borderRadius: 0 }}>
                <MessageBarBody>
                  {/* BORRADOR de texto — Sebastián redacta. */}
                  Vista 3D <b>experimental</b>: es una reconstrucción aproximada del plano, solo para
                  visualizar. No afecta la Hoja de Presupuesto ni las cantidades.
                </MessageBarBody>
                <Button size="small" appearance="transparent" icon={<DismissRegular />} onClick={cerrarVisor3dExp}>
                  Volver al plano 2D
                </Button>
              </MessageBar>
              <IfcViewer3D key={ifcExpUrl} srcUrl={ifcExpUrl} />
            </div>
          )}

          {/* Mientras se genera el 3D experimental (antes de que el visor tome el relevo). */}
          {ifcExpStatus === 'generando' && !ifcExpUrl && (
            <div className={styles.centerState} style={{ position: 'absolute', inset: 0, zIndex: 9, background: 'var(--bg-page)' }}>
              <div className={styles.stateBox}>
                <Spinner size="large" label="Generando la vista 3D…" />
                Esto es experimental y puede tardar; el plano 2D sigue disponible al cerrar.
                <Button size="small" appearance="secondary" onClick={cerrarVisor3dExp}>Cancelar</Button>
              </div>
            </div>
          )}

          {/* Barra de visibilidad del visor 3D (IFC): ocultar/aislar lo seleccionado, ocultar lo ya
              asignado para ver solo lo pendiente, y volver a mostrar todo. Es solo VISTA: no cambia la
              selección ni la asignación. */}
          {ifcAbierto && status === 'ready' && esInventarioIfc && !modoLista && visorConEspacio && (
            <div className={styles.viewerToolbar} style={{ flexWrap: 'wrap', maxWidth: 'calc(100% - 24px)' }}>
              {(
                [
                  { modo: 'ocultar-sel', label: 'Ocultar seleccionadas (H)', activa: hayTipoActivo,
                    title: 'Oculta las piezas que elegiste (H).' },
                  { modo: 'aislar-sel', label: 'Aislar seleccionadas (I)', activa: hayTipoActivo,
                    title: 'Deja a la vista solo las piezas que elegiste (I).' },
                  { modo: 'ocultar-asig', label: 'Ocultar asignadas', activa: instAsignadas.size > 0,
                    title: 'Oculta lo que ya tiene concepto → queda a la vista solo lo pendiente.' },
                ] as const
              ).map((b) => {
                const on = visMode === b.modo;
                return (
                  <button
                    key={b.modo}
                    type="button"
                    className={styles.iconBtn}
                    disabled={!b.activa && !on}
                    aria-pressed={on}
                    title={b.title}
                    onClick={() => setVisMode((v) => (v === b.modo ? 'todo' : b.modo))}
                    style={{
                      width: 'auto', padding: '0 10px', fontSize: 12,
                      opacity: !b.activa && !on ? 0.5 : 1,
                      ...(on ? { background: 'var(--accent-weak)', color: 'var(--accent)' } : {}),
                    }}
                  >
                    {b.label}
                  </button>
                );
              })}
              <button
                type="button"
                className={styles.iconBtn}
                disabled={!hayAlgoOculto}
                title="Vuelve a mostrar todo el modelo: lo oculto, el techo y todos los pisos (Esc, sin nada elegido)."
                onClick={mostrarTodo}
                style={{ width: 'auto', padding: '0 10px', fontSize: 12, opacity: hayAlgoOculto ? 1 : 0.5 }}
              >
                Mostrar todo (Esc)
              </button>
              {/* Acciones de CÁMARA: también actúan sobre la vista, por eso van en esta barra y no en la
                  de abajo (que queda para qué parte del edificio se ve: piso, corte, techo). */}
              <button
                type="button"
                className={styles.iconBtn}
                title="Mira el modelo desde arriba, como un plano."
                onClick={() => pedirCamara('arriba')}
                style={{ width: 'auto', padding: '0 10px', fontSize: 12 }}
              >
                Vista desde arriba
              </button>
              <button
                type="button"
                className={styles.iconBtn}
                disabled={!hayTipoActivo}
                title="Acerca la cámara a lo que elegiste (F)."
                onClick={() => pedirCamara('seleccion')}
                style={{ width: 'auto', padding: '0 10px', fontSize: 12, opacity: hayTipoActivo ? 1 : 0.5 }}
              >
                Acercar a lo elegido (F)
              </button>
            </div>
          )}

          {/* Barra de VISTA del IFC (abajo a la izquierda): ver un piso cortado como plano y ocultar el
              techo. El clic derecho oculta una pieza. */}
          {ifcAbierto && status === 'ready' && esInventarioIfc && !modoLista && visorConEspacio && (
            <div
              className={styles.viewerToolbar}
              style={{
                // bottom 38: deja libre la barra de estado del visor (al pie).
                top: 'auto', bottom: 38, right: 'auto', left: 12, alignItems: 'center', gap: 8,
                padding: '4px 8px', flexWrap: 'wrap', maxWidth: '78%',
              }}
            >
              {/* Sin niveles en el modelo: se dice, en vez de desaparecer el control sin avisar. */}
              {nivelesIfc.length === 0 && inventario != null && (
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Este modelo no trae niveles.</span>
              )}
              {nivelesIfc.length > 0 && (
                <>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                    {/* Infraestructura (IFC 4.3): el modelo se divide en PARTES (tramo, pila, calzada), no en pisos. */}
                    {soloPartesIfc ? 'Parte' : 'Piso'} <FieldHelp helpKey="planos.piso_ifc" variante="accion" />
                  </span>
                  <Dropdown
                    size="small"
                    aria-label={soloPartesIfc ? 'Ver una parte' : 'Ver un piso'}
                    value={nivelVista == null ? (soloPartesIfc ? 'Todo el modelo' : 'Todo el edificio') : (etiquetaNivel.get(nivelVista)?.titulo ?? '')}
                    selectedOptions={[nivelVista == null ? 'todo' : String(nivelVista)]}
                    onOptionSelect={(_, d) =>
                      setNivelVista(d.optionValue && d.optionValue !== 'todo' ? Number(d.optionValue) : null)
                    }
                    style={{ minWidth: 150 }}
                  >
                    <Option value="todo" text={soloPartesIfc ? 'Todo el modelo' : 'Todo el edificio'}>
                      {soloPartesIfc ? 'Todo el modelo' : 'Todo el edificio'}
                    </Option>
                    {/* "Nivel 1 · +0.00 m" por elevación real; el nombre del modelo en chico debajo. */}
                    {[...nivelesIfc]
                      .sort((a, b) => (etiquetaNivel.get(a.id)?.titulo ?? '').localeCompare(etiquetaNivel.get(b.id)?.titulo ?? '', 'es', { numeric: true }))
                      .map((n) => {
                        const et = etiquetaNivel.get(n.id);
                        return (
                          <Option key={n.id} value={String(n.id)} text={et?.titulo ?? n.nombre}>
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span>{et?.titulo ?? n.nombre}</span>
                              {n.tipo !== 'parte' && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{n.nombre}</span>}
                            </span>
                          </Option>
                        );
                      })}
                  </Dropdown>
                </>
              )}
              {/* "Cortar a" SIEMPRE a la vista (antes solo aparecía al elegir un nivel y no se
                  descubría). Con "Todo el edificio" va deshabilitado. */}
              {/* Una parte de infraestructura no tiene altura de piso: no se corta. */}
              {nivelesIfc.length > 0 && !soloPartesIfc && (
                <>
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)', opacity: nivelVista == null ? 0.6 : 1 }}
                    title={nivelVista == null ? 'Elige un nivel para cortarlo' : undefined}
                  >
                    <Switch
                      checked={cortarPiso}
                      disabled={nivelVista == null}
                      onChange={(_, d) => setCortarElegido(d.checked)}
                      label="Cortar a"
                      title={cortarPiso ? 'Apágalo para ver el nivel completo (columnas, vigas y losas enteras)' : 'Préndelo para ver el nivel cortado como planta'}
                    />
                    <FieldHelp helpKey="planos.corte_ifc" variante="accion" />
                    <Slider
                      size="small"
                      disabled={nivelVista == null || !cortarPiso}
                      min={0.5}
                      max={2.5}
                      step={0.1}
                      value={alturaCorte}
                      onChange={(_, d) => setAlturaCorte(d.value)}
                      aria-label="Altura del corte sobre el piso"
                      style={{ width: 110 }}
                    />
                    <span style={{ minWidth: 42, fontVariantNumeric: 'tabular-nums' }}>
                      {alturaCorte.toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m
                    </span>
                  </span>
                </>
              )}
              {hayTechoIfc && (
                <Switch
                  checked={ocultarTecho}
                  onChange={(_, d) => setOcultarTecho(d.checked)}
                  label={<span style={{ fontSize: 12 }}>Ocultar techo</span>}
                  title="Quita el techo para ver lo de adentro."
                />
              )}
              {ocultasClic.size > 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  {ocultasClic.size} {ocultasClic.size === 1 ? 'pieza oculta' : 'piezas ocultas'} (con clic derecho)
                </span>
              )}
              {hayAlgoOculto && (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Lo oculto sigue contando en la hoja</span>
              )}
            </div>
          )}

          {/* DWG con escala dudosa: la pregunta de unidad, arriba del dibujo (la misma de la ficha de la
              galería). Hasta que se conteste no se muestra ninguna cantidad de este plano. */}
          {status === 'ready' && preguntarUnidadAbierto && fichaAbierta?.escala && fileId && puedeEditar && (
            <div style={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 6, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ pointerEvents: 'auto', maxWidth: 720, width: '100%', boxShadow: '0 6px 18px rgba(0,0,0,0.25)', borderRadius: 8, background: 'var(--bg-surface)' }}>
                <PreguntaUnidad escala={fichaAbierta.escala} onElegir={(u) => elegirUnidadDelPlano(fileId, u)} />
              </div>
            </div>
          )}

          {/* Modo «eligiendo»: la lista ancha en el centro (el dibujo queda en la miniatura). */}
          {modoLista && (target || modoLista === 'instalaciones') && vistaAncha}

          {/* Barra de estado al pie del plano (hover de escritorio, actualizada por DOM directo). */}
          <div ref={tipRef} className={styles.statusBar} style={{ display: 'none' }} />
        </div>

        {/* DERECHA: acordeón (una sección abierta a la vez) — Propiedades / Asignar concepto */}
        {derechaAbiertaEff ? (
        <aside className={mergeClasses(styles.sidePanel, styles.rightPanel)}>
          {/* Manija para ensanchar/angostar el panel (arrastrar el borde). */}
          <div
            className={styles.resizer}
            onMouseDown={startResize}
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionar panel"
          />
          {/* Botón de colapsar FLOTANTE en la esquina superior derecha (altura 0, sticky): no ocupa
              una fila propia y se queda visible al hacer scroll. La esquina der. del contenido va libre. */}
          <div style={{ position: 'sticky', top: 0, height: 0, zIndex: 5, display: 'flex', justifyContent: 'flex-end', paddingRight: 4, paddingTop: 4, overflow: 'visible' }}>
            <BtnContraer lado="der" onClick={() => setDerechaAbierta(false)} />
          </div>
          {/* --- Estado VACÍO: qué hacer y por dónde empezar --- */}
          {nadaSeleccionado && modoLista !== 'instalaciones' && (
            <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {ultimoAgregado && bannerAgregado}
              {botonSiguiente}
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  ¿Qué quieres presupuestar?
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Elige algo del plano; aquí te muestro su medida y le pones concepto y precio.
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { n: 'a', t: 'Un tipo de elemento', d: 'Tócalo en la lista de la izquierda. Se marcan todos los iguales.' },
                  { n: 'b', t: 'Algo del plano', d: 'Haz clic sobre el dibujo. Se marcan todos los de su tipo.' },
                  { n: 'c', t: 'Solo una pieza', d: 'Ctrl + clic sobre esa pieza.' },
                ].map((s) => (
                  <div key={s.n} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span
                      aria-hidden
                      style={{
                        flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                        background: 'var(--accent-weak)', color: 'var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, marginTop: 1,
                      }}
                    >
                      {s.n}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{s.t}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
              {siguienteQueFalta && !ultimoAgregado && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button
                  type="button"
                  onClick={() => irA(siguienteQueFalta)}
                  style={{
                    textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', padding: '12px 14px',
                    borderRadius: 10, border: '1px solid var(--accent)', background: 'var(--accent-weak)',
                    color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 3,
                  }}
                >
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--accent)' }}>Empieza por lo grande</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{siguienteQueFalta.texto} →</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                    Después de cada concepto te llevo al siguiente que falta.
                  </span>
                </button>
                {botonSaltar(siguienteQueFalta)}
                </div>
              )}
            </div>
          )}

          {/* --- 1) En el modo «eligiendo» la medida se pliega a una línea («Ver medida» vuelve al dibujo). --- */}
          {!nadaSeleccionado && modoLista && modoLista !== 'instalaciones' && target && (
            <div
              className={styles.section}
              style={{ padding: '10px 40px 10px 14px', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}
            >
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, minWidth: 0 }}>
                <b style={{ color: 'var(--text-primary)' }}>1 · {nombreSel}</b>
                {resumenMedida ? ` · ${resumenMedida}` : ''}
              </span>
              <Button size="small" appearance="transparent" onClick={salirModoLista} style={{ flexShrink: 0 }}>
                Ver medida
              </Button>
            </div>
          )}

          {/* --- 1) Verificar la selección (solo cuando HAY algo seleccionado) --- */}
          {!nadaSeleccionado && !modoLista && (
          <div className={styles.section}>
            <div className={styles.sectionHeadStatic} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1 }}>1 · Lo que seleccionaste{totalPiezas ? ` · ${totalPiezas} pza` : ''}</span>
              <Button size="small" appearance="subtle" onClick={limpiarSeleccion} title="Deja de tener seleccionado esto en el plano (también con Esc)" style={{ marginRight: 26 }}>
                Quitar selección
              </Button>
            </div>
            {(
              <div className={styles.propGroup}>
                {/* Nota: la "unidad del dibujo" y el "Altura detectada" separado se quitaron. El dato duro
                    (Largo del muro) va primero, en el bloque de la capa; el Alto (pre-llenado con la altura
                    leída de la elevación) se captura ahí mismo. Así no hay altura duplicada ni fuera de orden. */}

                {/* Estado de asignación de lo seleccionado: si ya tiene concepto y —en tipos con
                    piezas contables— cuántas están hechas y cuántas faltan. [textos BORRADOR] */}
                {target && estadoTarget && (() => {
                  const { conceptos, total, asignadas, tienePiezas } = estadoTarget;
                  const sinAsignar = total != null ? Math.max(0, total - asignadas) : 0;
                  const parcial = tienePiezas && asignadas > 0 && sinAsignar > 0;
                  const nada = conceptos.length === 0;
                  // Sin concepto todavía no hay NADA que verificar → no mostramos el cuadro "Sin concepto
                  // asignado" (ruido); el usuario va directo a asignar abajo. Solo aparece cuando ya hay uno.
                  // Recién agregado: el panel ya lo dice abajo ("✓ Agregado a la hoja"); no se repite aquí.
                  if (nada || ultimoAgregado?.key === target.key) return null;
                  // Conceptos distintos (dedup por descripción+rol).
                  const listaConceptos = [
                    ...new Map(conceptos.map((c) => [`${c.descripcion}|${c.rol}`, c])).values(),
                  ];
                  return (
                    <div
                      className={styles.noShrink}
                      style={{
                        padding: '10px 12px', borderRadius: 8,
                        border: '1px solid var(--border-subtle)',
                        background: nada ? 'var(--bg-raised)' : 'var(--accent-weak)',
                        display: 'flex', flexDirection: 'column', gap: 6,
                      }}
                    >
                      {nada ? (
                        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                          Sin concepto asignado.
                        </span>
                      ) : (
                        <>
                          <span
                            style={{
                              fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                              letterSpacing: '0.05em', color: 'var(--text-tertiary)',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {parcial
                              ? `${asignadas} de ${total} piezas ya con concepto · faltan ${sinAsignar}`
                              : 'Concepto asignado'}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {listaConceptos.map((c, i) => (
                              <span
                                key={i}
                                style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.35 }}
                              >
                                {c.rol === 'complemento' ? '↳ ' : ''}
                                <b>{c.descripcion}</b>
                                {c.esIa ? ' · IA' : ''}
                                {` · ${c.cantidad} ${c.unidad ?? ''}`.trimEnd()}
                              </span>
                            ))}
                          </div>
                          {parcial && (
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                              Las piezas ya asignadas se ven atenuadas en el plano.
                            </span>
                          )}
                        </>
                      )}
                      {/* Un clic para quedarte solo con lo pendiente (reversible). Solo cuando el tipo
                          tiene piezas y ya hay algo asignado. */}
                      {tienePiezas && asignadas > 0 && sinAsignar > 0 && (
                        <Button
                          size="small"
                          appearance={soloFaltantesActivo ? 'primary' : 'secondary'}
                          onClick={() =>
                            soloFaltantesActivo ? incluirTodasLasPiezas() : soloLasQueFaltan()
                          }
                          style={{ alignSelf: 'flex-start', marginTop: 2 }}
                        >
                          {soloFaltantesActivo
                            ? 'Mostrar todas las piezas'
                            : `Trabajar solo las ${sinAsignar} que faltan`}
                        </Button>
                      )}
                      {/* Capa de geometría continua (DWG): no hay sub-piezas que separar. */}
                      {!tienePiezas && !nada && target.seleccion.tipo === 'capa' && (
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                          Esta capa es geometría continua (sin piezas individuales); se asigna completa.
                        </span>
                      )}
                      {/* Pieza enfocada (clic en el visor 3D o en la lista): su estado individual. */}
                      {piezaEnfocadaInfo && (
                        <div
                          style={{
                            marginTop: 4, paddingTop: 6, borderTop: '1px solid var(--border-subtle)',
                            fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4,
                          }}
                        >
                          <b style={{ color: '#ff2bd6' }}>{piezaEnfocadaInfo.nombre}</b>{' '}
                          {piezaEnfocadaInfo.asignada ? (
                            piezaEnfocadaInfo.conceptos.length > 0 ? (
                              <>
                                ya tiene concepto:{' '}
                                <b style={{ color: 'var(--text-primary)' }}>
                                  {piezaEnfocadaInfo.conceptos.join(', ')}
                                </b>
                                .
                              </>
                            ) : (
                              'ya tiene concepto.'
                            )
                          ) : (
                            'todavía sin concepto.'
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* BLOQUE seleccionado: identificación + dimensiones + atributos del plano. */}
                {resumenBloques.map((b) => {
                  const attrs = (inventario?.anotaciones?.atributos ?? [])
                    .filter((a) => a.bloque === b.nombre)
                    .slice(0, 8);
                  return (
                    <div key={b.nombre} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <TarjetasMedida
                        titulo={b.nombre}
                        subtitulo={`${b.sel === b.total ? b.total : `${b.sel} de ${b.total}`} ${b.total === 1 ? 'pieza' : 'piezas'}`}
                        filas={[
                          { k: 'conteo', label: 'Piezas', value: b.sel === b.total ? String(b.total) : `${b.sel} de ${b.total}` },
                          b.ancho != null ? { k: 'ancho', label: 'Ancho', value: `${fmtM(b.ancho)} m` } : null,
                          b.alto != null ? { k: 'alto', label: 'Alto', value: `${fmtM(b.alto)} m` } : null,
                        ].filter((x): x is { k: string; label: string; value: string } => x != null)}
                        manda="conteo"
                      />
                      <div>
                      {b.capas[0] ? <FilaProp label="Capa" value={b.capas[0]} /> : null}
                      {attrs.map((a) => (
                        <FilaProp key={`${a.tag}:${a.valor}`} label={a.tag} value={a.valor} />
                      ))}
                      </div>
                    </div>
                  );
                })}

                {/* CAPA seleccionada: qué es + su MEDIDA. Se muestra el LARGO DEL EJE (corrido, el mismo
                    número que usa la cantidad de abajo), NO el crudo que cuenta las dos caras — así no hay
                    dos "largos" distintos. Se quitó el "área de recintos" confusa y el ruido de CAD. */}
                {capasSeleccionadas.map((capa) => {
                  const info = inventario?.capas.find((c) => c.capa === capa);
                  // IFC (BIM): el modelo YA trae las medidas (área/longitud/volumen). No se captura
                  // alto/ancho ni se deriva nada — se muestran tal cual, reflejando las piezas que queden
                  // incluidas si el usuario sacó instancias en el visor 3D (medidas ya recalculadas en
                  // `asignables`). Esto es lo que se veía perdido en un plano IFC: el bloque asumía DWG
                  // (largo × alto/ancho) y pedía "captura el alto" en vez de mostrar lo medido por el modelo.
                  const asigCapa = asignables.find((a) => a.key === `capa:${capa}`);
                  const md = asigCapa?.medidas ?? info?.medidas;
                  if (md) {
                    const instSel = asigCapa?.instSel;
                    const parcial = instSel != null && instSel.sel < instSel.total;
                    // La medida que MANDA: la de la unidad del concepto elegido; si aún no hay concepto,
                    // la principal del tipo. Se resalta, y se marca la que el modelo no declara.
                    const principal = medidaPrincipalIfc(md && { ...md, manda: info?.medidas?.manda }, info?.categoria, info?.ifc?.predefinido, info?.ifc?.supertipos);
                    const manda =
                      magnitudElegida?.magnitud === 'lineal' ? 'longitud'
                        : magnitudElegida?.magnitud === 'area' ? 'area'
                          : magnitudElegida?.magnitud === 'volumen' ? 'volumen'
                            : magnitudElegida?.magnitud === 'conteo' ? 'conteo'
                              : magnitudElegida?.magnitud === 'peso' ? 'peso'
                              : principal?.magnitud ?? null;
                    const estimadas = new Set<string>(
                      (md as { magnitudesEstimadas?: string[] }).magnitudesEstimadas ?? info?.medidas?.magnitudesEstimadas ?? [],
                    );
                    const filas = [
                      { k: 'conteo', label: 'Piezas', value: parcial ? `${instSel!.sel} de ${instSel!.total}` : String(md.conteo) },
                      md.area != null ? { k: 'area', label: 'Área', value: `${md.area.toLocaleString('es-MX')} m²` } : null,
                      md.longitud != null && muestraLargo(info?.categoria, info?.ifc?.supertipos, info?.medidas?.manda)
                        ? { k: 'longitud', label: 'Largo', value: `${md.longitud.toLocaleString('es-MX')} m` }
                        : null,
                      md.volumen != null ? { k: 'volumen', label: 'Volumen', value: `${md.volumen.toLocaleString('es-MX')} m³` } : null,
                      // Peso (kg): solo si se pudo pesar; ausente = no se muestra (nunca un 0).
                      (() => {
                        // El de lo INCLUIDO (md ya viene recalculado si se sacaron piezas); sin caer al del tipo entero.
                        const peso = (md as { peso?: number }).peso;
                        return peso != null && peso > 0
                          ? { k: 'peso', label: 'Peso', value: `${peso.toLocaleString('es-MX', { maximumFractionDigits: 0 })} kg` }
                          : null;
                      })(),
                    ].filter(Boolean) as { k: string; label: string; value: string }[];
                    return (
                      <div key={capa} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <TarjetasMedida
                          titulo={info ? nombreLegibleTipoIfc(info) : capa}
                          subtitulo={`${parcial ? `${instSel!.sel} de ${instSel!.total}` : md.conteo} ${md.conteo === 1 ? 'pieza' : 'piezas'}`}
                          filas={filas}
                          manda={manda}
                          estimadas={estimadas}
                        />
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                          Medido del modelo 3D. No hace falta capturar nada.
                        </span>
                        {info?.medidas?.origenesVolumen?.includes('perfil') && (
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                            Parte del volumen sale de su sección × largo: el modelo no la trae como sólido.
                          </span>
                        )}
                        {info?.medidas?.sinVolumen && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                            {info.medidas.sinVolumen.piezas === 1 ? '1 pieza' : `${info.medidas.sinVolumen.piezas} piezas`} —{' '}
                            {info.medidas.sinVolumen.motivo}: no se miden ni su volumen ni su peso.
                          </span>
                        )}
                        {info?.medidas?.pesosDeducidos?.map((d) => (
                          <span key={d.material} style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                            {motivoDelPesoDeducido(d.material, d.tipo)}
                          </span>
                        ))}
                      </div>
                    );
                  }
                  // Lo MISMO que se va a asignar (misma medición, misma vista): el asignable de esta capa.
                  const asig2d = asignables.find((x) => x.seleccion.tipo === 'capa' && x.seleccion.clave === capa);
                  const largoM = asig2d?.medidoEnMetros ? asig2d.cantidadDefault : null;
                  // El plano da el LARGO; alto y ancho los completa el usuario (pre-llenados con lo que el
                  // plano trae: alto de la fachada de esa planta, ancho del muro de doble trazo). Con
                  // largo×alto sale el m², y ×ancho el m³ — se muestran calculados en vivo aquí mismo.
                  const alto = Number(altoStr.replace(',', '.')) || 0;
                  const ancho = Number(espesorStr.replace(',', '.')) || 0;
                  const porPlanta = asig2d?.porPlanta;
                  const areaPlantas = asig2d?.areaMuroPorPlantas ?? null;
                  // Descontar puertas y ventanas: solo muro de un DWG con vanos, y solo si el concepto es por
                  // área o volumen. Con todos los altos, el área es la neta.
                  const muroVanos =
                    asig2d?.key === target?.key &&
                    (magnitudElegida?.magnitud === 'area' || magnitudElegida?.magnitud === 'volumen')
                      ? asig2d?.muroVanos
                      : undefined;
                  // La cuenta del muro con vanos manda en el ÁREA de las tarjetas aunque el concepto todavía no
                  // se haya elegido (o sea lineal): antes la tarjeta decía largo medido × alto (= los vanos de
                  // piso a techo) y al elegir un concepto en m² pasaba al neto con los altos de los vanos, así
                  // que "descontar" parecía SUBIR el área. Mismo número antes y después; el bloque de abajo lo
                  // explica solo cuando el concepto es por área o volumen.
                  const cuentaAqui = asig2d?.muroVanos && asig2d.key === target?.key ? cuentaVanos : null;
                  // Desglose por planta con la MISMA cuenta (tramo i = planta i: los dos salen de las plantas
                  // que suman, en el mismo orden). Solo si cada planta tiene su alto propio.
                  const tramosVanos = asig2d?.muroVanos?.tramos;
                  const vanosDePlanta = (i: number) =>
                    cuentaAqui?.netoM2 != null && tramosVanos && porPlanta && tramosVanos.length === porPlanta.length &&
                    tramosVanos[i]?.altoPropioM != null
                      ? { ejeBrutoM: tramosVanos[i]!.ejeBrutoM, ...cuentaAqui.porTramo[i]! }
                      : null;
                  const areaM2 =
                    cuentaAqui?.netoM2 != null
                      ? cuentaAqui.netoM2
                      : areaPlantas != null ? areaPlantas : largoM != null && largoM > 0 && alto > 0 ? largoM * alto : null;
                  const volM3 = areaM2 != null && ancho > 0 ? areaM2 * ancho : null;
                  // Con huecos y sus altos, el área NO es largo × alto: se dice la cuenta que sí es.
                  const fraseAreaConHuecos =
                    cuentaAqui?.netoM2 != null
                      ? `el área es ${fmtM(cuentaAqui.brutoM2)} m² (el largo con sus huecos × alto) menos ${fmtM(cuentaAqui.brutoM2 - cuentaAqui.netoM2)} m² de puertas y ventanas`
                      : null;
                  const faltaAlto = areaPlantas == null && !altoStr.trim();
                  const vista = asig2d?.vista;
                  if (asig2d?.sinEscala) {
                    return (
                      <div key={capa} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <TarjetasMedida titulo={capa} subtitulo="capa del dibujo" filas={[]} manda={null} />
                        <span style={{ fontSize: 11.5, color: 'var(--warn)', lineHeight: 1.45 }}>
                          Todavía no sé en qué unidad está dibujado este plano, así que no te muestro medidas.
                          Contesta la pregunta de arriba del dibujo y aquí aparecen.
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div key={capa} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {/* Mismas tarjetas que un modelo 3D; la que manda depende de la unidad del concepto. */}
                      <TarjetasMedida
                        titulo={vista ? `${capa} · ${vista.rotulo}` : capa}
                        subtitulo={vista ? 'solo esta planta' : porPlanta ? `${porPlanta.length} plantas` : 'capa del dibujo'}
                        filas={[
                          largoM != null && largoM > 0 ? { k: 'longitud', label: 'Largo', value: `${fmtM(largoM)} m` } : null,
                          areaM2 != null ? { k: 'area', label: 'Área', value: `${fmtM(areaM2)} m²` } : null,
                          volM3 != null ? { k: 'volumen', label: 'Volumen', value: `${fmtM(volM3)} m³` } : null,
                        ].filter((x): x is { k: string; label: string; value: string } => x != null)}
                        manda={
                          magnitudElegida?.magnitud === 'lineal' ? 'longitud'
                            : magnitudElegida?.magnitud === 'area' ? 'area'
                              : magnitudElegida?.magnitud === 'volumen' ? 'volumen'
                                : 'longitud'
                        }
                      />
                      {/* Capa completa en varias plantas: cada una con su largo, su altura y su área. El área
                          de la capa suma planta por planta (cada una con SU altura). */}
                      {porPlanta && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {porPlanta.map((pl, i) => {
                            const cv = vanosDePlanta(i);
                            return (
                            <button
                              key={pl.vista}
                              type="button"
                              onClick={() => elegirVistaDeCapa(capa, pl.vista)}
                              title="Trabajar solo esta planta"
                              style={{
                                textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', padding: '6px 8px',
                                borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'transparent',
                                color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 1,
                              }}
                            >
                              <span style={{ fontSize: 12, fontWeight: 600 }}>
                                {pl.rotulo}
                                <span style={{ fontWeight: 400, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                                  {cv && cv.netoM2 != null
                                    ? ` · ${fmtM(cv.ejeBrutoM)} m × ${fmtM(pl.altoM!)} m de alto − ${fmtM(cv.vanosM2)} m² de puertas y ventanas = ${fmtM(cv.netoM2)} m²`
                                    : <>
                                        {' '}· {pl.largoM != null ? `${fmtM(pl.largoM)} m` : 'sin largo'}
                                        {pl.altoM != null ? ` × ${fmtM(pl.altoM)} m de alto` : ''}
                                        {pl.areaM2 != null ? ` = ${fmtM(pl.areaM2)} m²` : ''}
                                      </>}
                                </span>
                              </span>
                              {cv && pl.largoM != null && (
                                <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', lineHeight: 1.35 }}>
                                  Largo con los huecos: {fmtM(pl.largoM)} m de muro + {fmtM(Math.max(0, cv.ejeBrutoM - pl.largoM))} m de puertas y ventanas.
                                </span>
                              )}
                              {pl.origen && (
                                <span style={{ fontSize: 10.5, color: pl.altoM == null ? 'var(--warn)' : 'var(--text-tertiary)', lineHeight: 1.35 }}>
                                  Alto {pl.origen}.
                                </span>
                              )}
                            </button>
                            );
                          })}
                        </div>
                      )}
                      {areaPlantas == null ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <span className={styles.fieldLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              Alto (m) <FieldHelp helpKey="planos.alto_muro" />
                            </span>
                            <Input
                              size="small" type="number" value={altoStr}
                              onChange={(_, d) => setAltoStr(d.value)} placeholder="ej. 2.50"
                              style={faltaAlto ? { outline: '2px solid var(--accent)', borderRadius: 4 } : undefined}
                            />
                            {alturaPropError && !altoStr.trim() && (
                              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>{alturaPropError}</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <span className={styles.fieldLabel}>Ancho (m)</span>
                            <Input
                              size="small" type="number" value={espesorStr}
                              onChange={(_, d) => setEspesorStr(d.value)} placeholder="ej. 0.15"
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '50%' }}>
                          <span className={styles.fieldLabel}>Ancho (m)</span>
                          <Input
                            size="small" type="number" value={espesorStr}
                            onChange={(_, d) => setEspesorStr(d.value)} placeholder="ej. 0.15"
                          />
                        </div>
                      )}
                      <span style={{ fontSize: 11, color: faltaAlto ? 'var(--accent)' : 'var(--text-tertiary)', fontWeight: faltaAlto ? 600 : 400, lineHeight: 1.4 }}>
                        {faltaAlto
                          ? vista
                            ? `Escribe el alto de esta planta para tener el área: ${vista.origen}.`
                            : 'Escribe el alto para tener el área: el dibujo en planta solo mide el largo.'
                          : areaPlantas != null
                            ? 'El área suma cada planta con su propia altura. Para cambiar una altura, elige esa planta.'
                            : vista
                              ? `El alto de esta planta sale ${vista.origen.startsWith('de ') ? vista.origen : `del plano (${vista.origen})`}; ${fraseAreaConHuecos ?? 'el área es largo × alto'}. Revísalo.`
                              : `El largo sale del dibujo; ${fraseAreaConHuecos ?? 'el área es largo × alto'} y el volumen, × ancho. El alto y el ancho no vienen en la planta: revísalos.`}
                      </span>
                      {/* «Descontar puertas y ventanas» no va aquí: depende del concepto elegido, así que
                          se pide en el paso 2, junto a la cantidad. Aquí solo se refleja el área neta. */}
                      {asig2d?.vistasQueNoSuman && (
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                          No incluye lo que esta capa trae en {asig2d.vistasQueNoSuman.map((x) => `«${x}»`).join(' y ')}:
                          una fachada o un corte no suma.
                        </span>
                      )}
                    </div>
                  );
                })}

                {(resumenBloques.length > 0 || capasSeleccionadas.length > 0) && (
                  <>
                    {/* "Piezas" solo aplica a bloques/piezas contables; en una capa suelta siempre es 0 → se oculta. */}
                    {totalPiezas > 0 && (
                      <div className={styles.totalRow}>
                        <span>Piezas seleccionadas</span>
                        <b>{totalPiezas}</b>
                      </div>
                    )}
                    {/* "Soltar" (arriba, en el encabezado) ya suelta la selección. */}
                  </>
                )}
              </div>
            )}
          </div>
          )}

          {/* --- 2) Asignar concepto (cuando hay una selección activa) --- */}
          {asignables.length > 0 && modoLista !== 'adicionales' && modoLista !== 'instalaciones' && (
            <div className={styles.section}>
              <div className={styles.sectionHeadStatic}>
                <TagRegular />
                2 · ¿Qué concepto es?
              </div>
              {target && target.esRuido ? (
                // "Es solo dibujo" = no se presupuesta → no se le asigna concepto. Si en realidad se
                // construye, el usuario lo mueve a "Se construye" con el ⋯ de la capa/bloque (panel izq).
                <div style={{ padding: '12px 14px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
                  <b>{target.etiqueta}</b> está marcado como <b>solo dibujo</b> (texto, ejes, mobiliario…),
                  así que no se presupuesta y no se le pone concepto.
                  <br />
                  ¿Sí se construye? Muévelo a <b>«Se construye»</b> con el <b>⋯</b> de su fila, en el panel
                  de la izquierda.
                </div>
              ) : target && ultimoAgregado?.key === target.key ? (
                <div className={styles.assignGroup}>{bannerAgregado}</div>
              ) : target && principalActual && !cambiandoPrincipal ? (
                // Ya tiene concepto en la hoja (se ve arriba): no se muestra el buscador vacío; el paso
                // que sigue es el 3 (lo que lo acompaña). Cambiarlo es un clic.
                <div className={styles.assignGroup}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                    Ya está en la hoja con el concepto de arriba. Abajo le agregas lo que lo acompaña.
                  </span>
                  {notasDelPrincipal}
                  <Button size="small" appearance="secondary" style={{ alignSelf: 'flex-start' }} onClick={() => setCambiandoPrincipal(true)}>
                    Cambiar concepto
                  </Button>
                </div>
              ) : target && (
                <div className={styles.assignGroup}>
                  {/* A qué selección se le asigna (si hay varias, se puede elegir) */}
                  {asignables.length > 1 ? (
                    <>
                      <span className={styles.fieldLabel}>Asignar a</span>
                      <div className={styles.targetChips}>
                        {asignables.map((a) => (
                          <button
                            key={a.key}
                            type="button"
                            className={mergeClasses(
                              styles.targetChip,
                              a.key === target.key && styles.targetChipSel,
                            )}
                            onClick={() => setTargetKey(a.key)}
                            title={a.detalle}
                          >
                            {a.etiqueta}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    // Lo seleccionado ya se nombra arriba (paso 1), en 3D y en dibujo: no se repite aquí.
                    null
                  )}

                  {/* El estado completo (qué concepto, cuántas piezas) se ve arriba en "Verificar la
                      selección"; aquí solo el recordatorio de que reasignar reemplaza el principal. */}
                  {estadoTarget && estadoTarget.conceptos.some((c) => c.rol === 'principal') && (
                    <div
                      className={styles.noShrink}
                      style={{
                        padding: '8px 10px', borderRadius: 8, background: 'var(--accent-weak)',
                        border: '1px solid var(--border-subtle)', fontSize: 12, lineHeight: 1.4,
                        color: 'var(--text-primary)',
                      }}
                    >
                      Este tipo ya tiene un concepto principal. Asignar otro lo reemplaza.
                    </div>
                  )}

                  {/* La barra de catálogos se movió al panel IZQUIERDO (arriba de Capas): los catálogos son
                      la "fuente" y van con las capas. El diálogo de selección (abajo) sigue siendo global. */}

                  {/* Selector de catálogos de /planos (multi + prioridad, como en la cotización). */}
                  <Dialog
                    open={catalogosDialogAbierto}
                    onOpenChange={(_, d) => setCatalogosDialogAbierto(d.open)}
                  >
                    <DialogSurface style={{ maxWidth: 460 }}>
                      <DialogBody>
                        <DialogTitle>Catálogos para este plano</DialogTitle>
                        <DialogContent>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.45 }}>
                            Elige y ordena los catálogos que se usarán para buscar y sugerir
                            conceptos aquí. Es independiente de la cotización del proyecto.
                          </div>
                          <ProjectCatalogsPicker
                            available={catalogosDisponibles}
                            initialSelected={catalogosBorrador}
                            onChange={setCatalogosBorrador}
                            disabled={guardandoCatalogos}
                          />
                        </DialogContent>
                        <DialogActions>
                          <Button
                            appearance="secondary"
                            disabled={guardandoCatalogos}
                            onClick={() => setCatalogosDialogAbierto(false)}
                          >
                            Cancelar
                          </Button>
                          <Button
                            appearance="primary"
                            disabled={guardandoCatalogos}
                            onClick={() => void guardarCatalogos(catalogosBorrador)}
                          >
                            {guardandoCatalogos ? 'Guardando…' : 'Guardar'}
                          </Button>
                        </DialogActions>
                      </DialogBody>
                    </DialogSurface>
                  </Dialog>

                  {/* Tres formas de la MISMA pregunta: recomendados (bajo demanda: gasta saldo), buscar en tus
                      catálogos, o generar uno nuevo con IA. Las tres abren la vista ANCHA en el centro (modo
                      «eligiendo»); aquí quedan el concepto elegido, su cantidad y Agregar. */}
                  {!modoLista && (
                    <>
                      {/* Con el panel angosto los tres no caben en una línea: cada botón (con su «?») pasa
                          a la siguiente en vez de recortar su texto. */}
                      <div className={styles.noShrink} style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 auto' }}>
                          <Button
                            size="small"
                            style={{ flex: 1, whiteSpace: 'nowrap', minWidth: 0, paddingInline: 8 }}
                            appearance={formaConcepto === 'buscar' ? 'primary' : 'secondary'}
                            disabled={!catalogSel}
                            onClick={() => entrarModoPrincipal('buscar')}
                          >
                            Buscar
                          </Button>
                          <FieldHelp helpKey="planos.buscar" variante="accion" />
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 auto' }}>
                          <Button
                            size="small"
                            style={{ flex: 1, whiteSpace: 'nowrap', minWidth: 0, paddingInline: 8 }}
                            appearance={formaConcepto === 'sugeridos' ? 'primary' : 'secondary'}
                            disabled={!catalogSel}
                            onClick={() => entrarModoPrincipal('sugeridos')}
                          >
                            Recomendados
                          </Button>
                          <FieldHelp helpKey="planos.recomendados" variante="accion" />
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 auto' }}>
                          {/* El que más resalta: borde e ícono de IA (✨, el mismo que usa ai), en el morado de IA. */}
                          <Button
                            size="small"
                            icon={<Sparkle24Regular />}
                            style={{
                              flex: 1, whiteSpace: 'nowrap', minWidth: 0, paddingInline: 8,
                              ...(formaConcepto === 'crear'
                                ? {}
                                : { border: '1px solid var(--privado)', color: 'var(--privado)', fontWeight: 600 }),
                            }}
                            appearance={formaConcepto === 'crear' ? 'primary' : 'outline'}
                            onClick={() => entrarModoPrincipal('crear')}
                          >
                            Generar con IA
                          </Button>
                          <FieldHelp helpKey="planos.generar_ia" variante="accion" />
                        </span>
                      </div>
                      {!catalogSel ? (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>Elige un catálogo.</span>
                      ) : !conceptoElegido && formaConcepto !== 'crear' && (
                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
                          La lista se abre grande donde está el dibujo, para que compares con calma.
                        </span>
                      )}
                    </>
                  )}

                  {/* El concepto elegido (en la lista ancha). Fuera del modo, tocarlo vuelve a la lista. */}
                  {formaConcepto !== 'crear' && (conceptoElegido ? (
                    <button
                      type="button"
                      className={styles.noShrink}
                      onClick={() => entrarModoPrincipal(formaConcepto)}
                      title={modoLista ? undefined : 'Volver a la lista para cambiarlo'}
                      disabled={!!modoLista}
                      style={{
                        textAlign: 'left', fontFamily: 'inherit', cursor: modoLista ? 'default' : 'pointer',
                        padding: '10px 12px', borderRadius: 8, marginTop: 4,
                        background: 'var(--accent-weak)', border: '1px solid var(--accent)',
                        color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}>{conceptoElegido.descripcion}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                        {conceptoElegido.clave}
                        {conceptoElegido.unidad ? ` · ${conceptoElegido.unidad}` : ''}
                        {` · ${conceptoElegido.precio != null ? fmtMoneda(conceptoElegido.precio) : 'sin precio'}`}
                      </span>
                      {!modoLista && <span style={{ fontSize: 11, color: 'var(--accent)' }}>Cambiar</span>}
                    </button>
                  ) : modoLista ? (
                    <div
                      className={styles.noShrink}
                      style={{
                        padding: '12px', borderRadius: 8, border: '1px dashed var(--border-subtle)', marginTop: 4,
                        color: 'var(--text-secondary)', fontSize: 12.5, lineHeight: 1.45,
                      }}
                    >
                      Elige un concepto de la lista. Aquí ves su cantidad y lo agregas a la hoja.
                    </div>
                  ) : null)}
                  {modoLista && formaConcepto === 'crear' && (
                    <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                      Escribe el concepto en la lista de la izquierda y agrégalo desde ahí.
                    </span>
                  )}

                  {/* Las notas de las láminas «Solo referencia» que se le pegan al elegido (y si lo contradicen). */}
                  {formaConcepto !== 'crear' && conceptoElegido && (
                    <NotasDelDetalle
                      projectId={projectId}
                      texto={conceptoElegido.descripcion}
                      nombre={target?.seleccion.clave ?? null}
                    />
                  )}

                  {/* Concepto propio (no está en catálogo): mismo bloque que en la lista ancha. */}
                  {!modoLista && formaConcepto === 'crear' && bloqueCrear}

                  {/* La unidad del plano ya NO se ofrece: la escala sale de las cotas del dibujo (confiable),
                      así que un ingeniero no tiene por qué elegirla. La medición usa esa escala en silencio. */}

                  {/* Fase 2: la IA clasifica el concepto → nota + campos alto/espesor si aplica */}
                  {conceptoElegido && clasificando && !magnitudElegida && (
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
                        fontSize: 12, color: 'var(--text-tertiary)',
                      }}
                    >
                      <Spinner size="tiny" /> Analizando cómo se cuantifica este concepto…
                    </div>
                  )}
                  {/* La explicación "largo del plano × alto" es del DWG 2D; en IFC la cantidad sale
                      directa del modelo, así que no aplica (lo dice la caja "Medido en el modelo BIM"). */}
                  {/* La cantidad quedó vacía porque el concepto pide una medida que no hay (o en otra unidad,
                      p. ej. ton contra el peso en kg): se dice por qué. Igual en modelo 3D y en dibujo; la
                      medida que SÍ se usa ya se ve resaltada arriba, en el paso 1. */}
                  {conceptoElegido && magnitudElegida && cantidadCalculada === '' && (
                    <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--warn)', lineHeight: 1.4 }}>
                      {magnitudElegida.magnitud === 'peso'
                        ? target.medidas?.peso != null && target.medidas.peso > 0
                          ? `Este concepto se cobra en ${conceptoElegido.unidad ?? 'otra unidad'} y el modelo pesa en kg (${target.medidas.peso.toLocaleString('es-MX', { maximumFractionDigits: 0 })} kg). Escribe la cantidad en la unidad del concepto.`
                          : 'Este concepto se cobra por peso y el plano no trae el peso. Escribe la cantidad.'
                        : target.medidas
                          ? 'Este concepto pide una medida que el modelo no trae para este tipo. Escribe la cantidad.'
                          : 'Este concepto pide el alto (o el ancho) que la planta no dibuja: complétalo arriba o escribe la cantidad.'}
                    </div>
                  )}
                  {conceptoElegido && magnitudElegida === null && (
                    <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                      {magnitudErrorPorId.get(conceptoElegido.id) ??
                        'No se pudo saber cómo se mide este concepto, por eso la cantidad quedó vacía. Escríbela a mano.'}
                    </div>
                  )}

                  {/* Piso/losa: m² directo del área de recintos cerrados del plano (sin altura) */}
                  {esAreaDePiso && !target.medidas && (
                    <div
                      className={styles.noShrink}
                      style={{
                        marginTop: 10, padding: '8px 10px', borderRadius: 8,
                        background: 'var(--accent-weak)', border: '1px solid var(--border-subtle)',
                        fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.4,
                      }}
                    >
                      Área medida del recinto: <b style={{ color: 'var(--text-primary)' }}>{target.areaM2} m²</b>,
                      tomada directo del plano. Revísala antes de asignar.
                    </div>
                  )}

                  {/* El alto y el ancho/espesor ahora se capturan ARRIBA, en el bloque de la medida (paso
                      Medir): ahí se ven el m² (largo×alto) y el m³ (×ancho) en vivo. Aquí ya no se piden;
                      la cantidad de abajo usa esos valores según la unidad del concepto. */}

                  {/* Cantidad + agregar (solo al elegir de Sugeridos o Buscar) */}
                  {formaConcepto !== 'crear' && (conceptoElegido || modoLista) && (
                  <>
                  {/* Descontar puertas y ventanas: aparece por el CONCEPTO (se cobra por área o volumen) y
                      cambia la cantidad, así que va justo arriba de ella. La planta da el ancho de cada
                      hueco, no su alto: por eso se pide el alto por tipo. */}
                  {conceptoElegido && target.muroVanos &&
                    (magnitudElegida?.magnitud === 'area' || magnitudElegida?.magnitud === 'volumen') && (
                    <div className={styles.noShrink} style={{ marginTop: 10, marginBottom: 4 }}>
                      <BloqueDescontarVanos
                        tipos={target.muroVanos.tipos}
                        valores={altosVanosStr}
                        onChange={(clave, v) => setAltosVanosStr((prev) => ({ ...prev, [clave]: v }))}
                        onGuardar={guardarAltosVanos}
                        cuenta={cuentaVanos}
                      />
                    </div>
                  )}
                  <span className={styles.fieldLabel}>
                    Cantidad para la hoja
                    {conceptoElegido?.unidad
                      ? ` (${conceptoElegido.unidad})` // la unidad en que se cobra el concepto elegido
                      : target.seleccion.tipo === 'bloque'
                      ? ' (piezas)'
                      : target.medidas && !magnitudElegida
                        ? (() => {
                            const info = inventario?.capas.find((c) => c.capa === target.seleccion.clave);
                            const mp = medidaPrincipalIfc(target.medidas, info?.categoria, info?.ifc?.predefinido, info?.ifc?.supertipos);
                            return mp ? ` (${mp.unidad})` : '';
                          })()
                      : magnitudElegida
                        ? ` (${unidadDeMagnitud(magnitudElegida.magnitud)})`
                        : target.medidoEnMetros
                          ? ' (metros)'
                          : ''}
                  </span>
                  <div className={styles.cantidadRow}>
                    <Input
                      aria-label="Cantidad"
                      type="number"
                      className={styles.cantidadInput}
                      value={cantidadStr}
                      onChange={(_, d) => setCantidadStr(d.value)}
                    />
                  </div>
                  {/* El botón dice lo que pasa: la hoja recibe este concepto con este importe. */}
                  <Button
                    appearance="primary"
                    className={styles.noShrink}
                    disabled={!conceptoElegido || asignando || !(cantidadNum > 0)}
                    onClick={() => void handleAsignar()}
                  >
                    {asignando
                      ? 'Agregando…'
                      : !conceptoElegido
                        ? 'Elige un concepto de la lista'
                        : !(cantidadNum > 0)
                          ? 'Escribe la cantidad'
                          : `Agregar a la hoja${importeAgregar != null ? ` · ${fmtMoneda(importeAgregar)}` : ''}`}
                  </Button>
                  </>
                  )}
                  {target.notaMedicion && (
                    <span
                      style={{
                        display: 'block', fontSize: 11, color: 'var(--text-tertiary)',
                        lineHeight: 1.4, marginTop: 6,
                      }}
                    >
                      {target.notaMedicion}
                    </span>
                  )}
                  {target.revisar && (
                    <span
                      className={styles.noShrink}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6,
                        padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: 'var(--warning-weak, rgba(240,180,40,0.15))',
                        color: 'var(--warning, #d9a400)',
                        border: '1px solid var(--warning, #d9a400)',
                      }}
                    >
                      ⚠ Medición dudosa — se marcará para revisar
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* --- 3) Conceptos complementarios / previos (la IA los propone; se resuelven EN LÍNEA, uno a
              la vez, con las mismas vías que el principal: buscar, sugerir del catálogo o generar con IA).
              Cada uno se cuelga de su principal (principal_id), así que primero va el principal a la hoja. --- */}
          {(conceptoElegido || principalActual) && !modoLista && (
            <div className={styles.section}>
              <div className={styles.sectionHeadStatic}>3 · También lleva</div>
              {!principalActual ? (
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', margin: '0 14px 10px', lineHeight: 1.4 }}>
                  Primero agrega el concepto a la hoja. Después te propongo lo que suele acompañarlo
                  (aplanado, pintura, dala…) y cada uno entra a la hoja en su partida.
                </div>
              ) : (
              <>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', margin: '0 14px 8px', lineHeight: 1.4 }}>
                <span
                  title={principalActual.descripcion}
                  style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: 'var(--text-primary)', fontWeight: 600 }}
                >
                  {principalActual.descripcion}
                </span>
                {principalActual.esIa ? 'Se cotiza con IA. ' : ''}Te proponemos lo que suele acompañarlo; tú
                decides cuáles van. Cada uno entra a la hoja en su partida.
              </div>
              <div className={styles.assignGroup}>
                {/* Cada adicional se resuelve en la lista ancha (modo «eligiendo»): tocarlo la abre. */}
                {complementarios.map((cmp, i) => renderFilaAdic(cmp, i))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: complementarios.length > 0 ? 4 : 0 }}>
                  <Button
                    appearance={complementarios.length > 0 ? 'secondary' : 'primary'}
                    icon={<LightbulbRegular />}
                    disabled={sugiriendoComp}
                    onClick={() => void pedirAdicionales()}
                  >
                    {sugiriendoComp
                      ? 'Consultando…'
                      : complementarios.length > 0
                        ? 'Proponer más'
                        : '¿Qué más lleva?'}
                  </Button>
                  <FieldHelp helpKey="planos.que_mas_lleva" />
                </div>
                {compNota && (
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.4 }}>
                    {compNota}
                  </span>
                )}
              </div>
              </>
              )}
            </div>
          )}

          {/* --- Modo «eligiendo», paso 3: el adicional activo (la lista de adicionales y sus candidatos están
              en el centro). --- */}
          {modoLista === 'adicionales' && target && panelAdicional}
          {modoLista === 'instalaciones' && panelInstal}
          {/* Último paso: ir al siguiente elemento que falta (después de los complementarios). */}
          {!modoLista && target && ultimoAgregado?.key === target.key && botonSiguiente && (
            <div style={{ padding: '4px 14px 16px' }}>{botonSiguiente}</div>
          )}
        </aside>
        ) : (
          sinPlano ? <div /> : (
          <RailColapsar
            etiqueta="Propiedades y asignación"
            lado="der"
            onExpandir={() => setDerechaAbierta(true)}
          />
          )
        )}
      </div>

      {/* HP: cajón inferior colapsable (barra fina contraído · tabla por partidas expandido) */}
      <div className={styles.hpDrawer}>
        {hpAbierto && (
          <div
            className={styles.hpResizer}
            onMouseDown={startResizeHp}
            role="separator"
            aria-orientation="horizontal"
            aria-label="Redimensionar la hoja de presupuesto"
          />
        )}
        <div className={styles.hpBar}>
          <button
            type="button"
            className={styles.hpBarToggle}
            onClick={() => setHpAbierto((v) => !v)}
            aria-expanded={hpAbierto}
          >
            {hpAbierto ? <ChevronDown16Regular /> : <ChevronUp16Regular />}
            <span className={styles.hpBarTitle}>
              <ReceiptRegular />
              Hoja de presupuesto
            </span>
            <span className={styles.hpBarStat}>
              {vistaProyecto ? (
                <>
                  {renglonesProyecto.length} {renglonesProyecto.length === 1 ? 'renglón' : 'renglones'}
                  {planosEnProyecto > 0 && ` · de ${planosEnProyecto} ${planosEnProyecto === 1 ? 'plano' : 'planos'}`}
                </>
              ) : (
                <>
                  {renglones.length} {renglones.length === 1 ? 'renglón' : 'renglones'} · {partidas.length} partida
                  {partidas.length === 1 ? '' : 's'}
                </>
              )}
            </span>
          </button>
          <span className={styles.hpBarTotal}>{fmtMoneda(totalHoja)}</span>
          {hpAbierto && filasHoja.length > 0 && barraFiltros}
          {/* Entrando por el proyecto estas acciones viven arriba, en la fila del breadcrumb; aquí solo
              quedan el título, el conteo y el total. En /planos (staff) siguen aquí. */}
          {!modoProyecto && (<>
          {/* Ver la HP de TODO el proyecto (todos los planos juntos) vs solo el archivo abierto. La
              vista de proyecto es de lectura: cada renglón muestra de qué plano viene y su rol. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Switch
              checked={vistaProyecto}
              onChange={(_, d) => setVistaProyecto(Boolean(d.checked))}
              label={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Ver toda la obra (todos los planos juntos)</span>}
            />
            <FieldHelp helpKey="planos.hp_proyecto" />
          </span>
          {/* Lo que no trae el plano (preliminares, obra provisional, instalaciones, trámites, cierre): la
              IA lo deduce de los cuartos y la superficie y el usuario elige qué agregar. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8 }} title={motivoNoDibujadoApagado ?? undefined}>
            <Button
              size="small"
              appearance="secondary"
              className={styles.botonBarra}
              icon={<PlugConnectedRegular />}
              disabled={instalCargando || !fileId || !inventario}
              onClick={() => void abrirInstalaciones()}
            >
              {instalCargando ? 'Pensando…' : 'Lo que no trae el plano'}
            </Button>
            <FieldHelp helpKey="planos.instalaciones_faltan" variante="accion" />
          </span>
          <Button
            size="small"
            appearance="secondary"
            className={styles.botonBarra}
            icon={<Print20Regular />}
            disabled={renglones.length === 0}
            title="Todos los conceptos de la hoja con de dónde sale su cantidad, pieza por pieza, y su croquis; desde ahí se imprimen"
            onClick={() => setImprimiendoGeneradores(true)}
          >
            Números generadores
          </Button>
          {/* Presets rápidos de reparto plano/HP para el demo (plano grande · mitad · hoja grande). */}
          <div className={styles.hpPresets}>
            <button type="button" className={styles.hpPresetBtn} title="Plano en grande" aria-label="Plano en grande" onClick={() => aplicarPresetHp('plano')}>
              <LayerRegular /> Plano
            </button>
            <button type="button" className={styles.hpPresetBtn} title="Mitad plano, mitad hoja" aria-label="Mitad y mitad" onClick={() => aplicarPresetHp('mitad')}>
              50 / 50
            </button>
            <button type="button" className={styles.hpPresetBtn} title="Hoja de presupuesto en grande" aria-label="Hoja en grande" onClick={() => aplicarPresetHp('hoja')}>
              <ReceiptRegular /> Hoja
            </button>
          </div>
          </>)}
        </div>

        {/* El lote de IA se cortó porque el servicio no respondía: aviso VISIBLE (no solo el
            tooltip del renglón), con el MISMO texto que la cotización. Lo que faltaba quedó
            pendiente para el próximo lote. */}
        {hpAbierto && avisoIa === 'ai_unavailable' &&
          !renglones.some((r) => r.apuIaStatus === 'queued' || r.apuIaStatus === 'running') && (
          <MessageBar intent="warning" style={{ borderRadius: 0 }}>
            <MessageBarBody>
              {MENSAJE_IA_NO_RESPONDE}
              {marcadosParaIa > 0 && ' Los conceptos que faltaron quedan marcados para el próximo «Generar con IA».'}
            </MessageBarBody>
          </MessageBar>
        )}

        {hpAbierto && loteIaEnCurso.enCola + loteIaEnCurso.generandose > 0 && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '6px 12px', borderBottom: '1px solid var(--border-subtle)',
              background: 'var(--accent-weak)',
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Spinner size="extra-tiny" />
              Generando con IA: {loteIaEnCurso.generandose} en proceso
              {loteIaEnCurso.enCola > 0 ? `, ${loteIaEnCurso.enCola} esperando turno` : ''}.
            </span>
            {loteIaEnCurso.enCola > 0 && (
              <Button
                size="small"
                appearance="secondary"
                disabled={deteniendoLote || !projectId || !fileId}
                title="Lo que espera turno no se genera ni se cobra; lo que ya está en proceso termina"
                onClick={() => {
                  if (!projectId || !fileId) return;
                  setDeteniendoLote(true);
                  void detenerIaLote(projectId, fileId)
                    .then(async (r) => {
                      await refrescarRenglones();
                      dispatchToast(
                        <Toast>
                          <ToastTitle>
                            Detenido. {r.detenidos} concepto{r.detenidos === 1 ? '' : 's'} quedaron marcados para la próxima vez
                            {r.generandose > 0 ? `; ${r.generandose} que ya se estaba${r.generandose === 1 ? '' : 'n'} generando termina${r.generandose === 1 ? '' : 'n'}` : ''}.
                          </ToastTitle>
                        </Toast>,
                        { intent: 'info', timeout: 6000 },
                      );
                    })
                    .catch(() =>
                      dispatchToast(
                        <Toast><ToastTitle>No se pudo detener. Intenta de nuevo.</ToastTitle></Toast>,
                        { intent: 'error', timeout: 4000 },
                      ),
                    )
                    .finally(() => setDeteniendoLote(false));
                }}
              >
                Detener
              </Button>
            )}
          </div>
        )}

        {projectId && fileId && (
          <CompartirConCliente
            abierto={compartirAbierto}
            onCerrar={() => setCompartirAbierto(false)}
            projectId={projectId}
            fileId={fileId}
            nombreObra={nombreProyectoRuta ?? proyectoActual?.name}
          />
        )}
        {isStaff && projectId && fileId && (
          <CambioDePlano
            formato={formatoCambioDePlano ?? 'ifc'}
            abierto={cambioAbierto}
            onCerrar={() => setCambioAbierto(false)}
            projectId={projectId}
            fileId={fileId}
            formatoPrecio={(n) => fmtMoneda(n)}
            onHojaCambiada={(archivo) => {
              if (!archivo || archivo === fileId) {
                void refrescarRenglones();
                return;
              }
              // La hoja pasó a otro archivo (aplicar / volver): se recarga la lista (el nuevo puede no estar) y se abre.
              abrirTrasRecargarRef.current = archivo;
              if (modoProyecto && proyectoDeRuta) navigate(`/project/${proyectoDeRuta}/planos/${archivo}`);
              setArchivosVersion((v) => v + 1);
            }}
          />
        )}
        {isStaff && projectId && fileId && formatoCambioDePlano != null && (
          <AvanceDeObra abierto={avanceAbierto} onCerrar={() => setAvanceAbierto(false)} projectId={projectId} fileId={fileId} formato={formatoCambioDePlano} />
        )}
        <Dialog open={borrarHojaAbierto} onOpenChange={(_, d) => !borrandoHoja && setBorrarHojaAbierto(d.open)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>¿Borrar toda la hoja de presupuesto?</DialogTitle>
              <DialogContent>
                {modoProyecto
                  ? 'Se borran todos los conceptos de la hoja de este proyecto, de todos sus planos'
                  : 'Se borran todos los conceptos de la hoja de este plano'}
                , con sus análisis de precio generados con IA y sus cantidades corregidas a mano. Lo que la IA esté
                generando se detiene. No se puede deshacer.
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--privado, #6c54d6)' }}>Solo Ecosoft.</div>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" disabled={borrandoHoja} onClick={() => setBorrarHojaAbierto(false)}>
                  Cancelar
                </Button>
                <Button
                  appearance="primary"
                  style={{ background: 'var(--bad)' }}
                  disabled={borrandoHoja}
                  icon={borrandoHoja ? <Spinner size="tiny" /> : undefined}
                  onClick={() => void borrarTodaLaHoja()}
                >
                  {borrandoHoja ? 'Borrando…' : 'Borrar toda la hoja'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {/* La hoja de este plano ya está armada: completar lo que falta o rehacerla toda. */}
        <Dialog open={yaArmada != null && !armarAbierto} onOpenChange={(_, d) => !d.open && setYaArmada(null)}>
          <DialogSurface style={{ maxWidth: 520 }}>
            <DialogBody>
              <DialogTitle>Este plano ya tiene su hoja armada</DialogTitle>
              <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13.5 }}>
                {yaArmada && (
                  <>
                    <div>
                      Tiene {yaArmada.renglones === 1 ? '1 renglón' : `${yaArmada.renglones} renglones`}. Puedes completar solo lo que
                      todavía no tiene concepto, o rehacer toda la hoja.
                    </div>
                    <div style={{ color: 'var(--text-secondary)' }}>
                      Si la rehaces, se arma de nuevo y <b>tu hoja actual no se toca</b> hasta que, al final, pulses «Reemplazar la hoja».
                      Si cancelas a la mitad, queda como está.
                    </div>
                    {lineasQueSePierden(yaArmada).length > 0 && (
                      <div style={{ color: 'var(--warn)', background: 'var(--warn-weak)', borderRadius: 6, padding: '6px 10px' }}>
                        Al reemplazarla se pierde:
                        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                          {lineasQueSePierden(yaArmada).map((l) => (
                            <li key={l}>{l}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {bloqueaRehacer(yaArmada) && <div style={{ color: 'var(--bad)' }}>{bloqueaRehacer(yaArmada)}</div>}
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setYaArmada(null)}>
                  Cancelar
                </Button>
                <Button
                  appearance="secondary"
                  onClick={() => {
                    setModoArmado('faltante');
                    setYaArmada(null);
                    setArmarAbierto(true);
                  }}
                >
                  Completar lo que falta
                </Button>
                <Button
                  appearance="primary"
                  disabled={!yaArmada || bloqueaRehacer(yaArmada) != null}
                  onClick={() => {
                    // Los ids vienen de la misma lectura que el aviso (no de la lista de la página, que pudo quedar vieja).
                    setRenglonesVistosArmado(yaArmada!.ids);
                    setRehacerInfo(yaArmada);
                    setModoArmado('rehacer');
                    setYaArmada(null);
                    setArmarAbierto(true);
                  }}
                >
                  Rehacer toda la hoja
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {armarAbierto && esInventarioIfc && proyectoDeRuta && fileId && (
          <CargadorDePreguntasDeMaterial
            activo={gruposParaMaterial.length > 0}
            projectId={proyectoDeRuta}
            fileId={fileId}
            grupos={gruposParaMaterial}
            catalogId={catalogoArmado}
            onEstado={setPreguntasDeMaterial}
          />
        )}
        {armarAbierto && (
          <ArmarPresupuesto
            abierto={armarAbierto}
            onCerrar={() => {
              setArmarAbierto(false);
              setModoArmado('faltante');
              setYaArmada(null);
              setRehacerInfo(null);
            }}
            rehacer={
              modoArmado === 'rehacer' && rehacerInfo
                ? {
                    // Lo asignado a un elemento que el asistente no vuelve a proponer (una capa que no se cotiza, algo
                    // que se saltó) también se va con la hoja vieja. Lo que no tiene elemento del plano (lo que el plano
                    // no dibuja) sí se vuelve a armar.
                    pierde: (() => {
                      const propone = new Set(candidatosArmado.map((x) => x.key));
                      const sueltos = renglones.filter((r) => {
                        if (r.rol !== 'principal') return false;
                        const sels = seleccionesDe(r);
                        return sels.length > 0 && !sels.some((sel) => propone.has(`${sel.tipo}:${sel.clave}`));
                      }).length;
                      return [
                        ...lineasQueSePierden(rehacerInfo),
                        ...(sueltos > 0
                          ? [`${sueltos === 1 ? '1 renglón principal' : `${sueltos} renglones principales`} de elementos que el asistente no vuelve a proponer`]
                          : []),
                      ];
                    })(),
                    renglones: rehacerInfo.renglones,
                  }
                : null
            }
            previo={{
              chequeo: chequeoDelPlano,
              catalogos: catalogos.map(etiquetaCatalogo),
              escala:
                escalaSinResolver && fileId && fichaAbierta?.escala?.opciones?.length
                  ? { opciones: fichaAbierta.escala.opciones, elegir: (u) => elegirUnidadDelPlano(fileId, u) }
                  : null,
              altura: hayMurosArmado
                ? {
                    valor: altoArmado,
                    deDonde:
                      alturaProp?.alturaM != null && altoArmado === String(alturaProp.alturaM)
                        ? alturaProp.razon || 'La leímos de las anotaciones del plano.'
                        : null,
                    cambiar: setAltoArmado,
                  }
                : null,
              sinDecidir: faltaDelPlano?.sinDecidir ?? 0,
              yaConConcepto: capasConPrincipal.size + conPrincipalBloqueArmado,
              tipos: MEMORIA_TIPOS_ENABLED && resumenTipos && memoriaLeidaDe === inventario ? { ...resumenTipos, abrir: () => setTiposAbierto(true) } : null,
              esIfc: esInventarioIfc,
              puedeNoDibujado: motivoNoDibujadoApagado == null,
              preguntas: preguntasArmado,
              avisoMaterial,
              revisandoMaterial,
            }}
            candidatos={candidatosArmado}
            proponer={(c, op) => conSaldo(() => proponerArmado(c, op))}
            proponerNoDibujado={(op) => conSaldo(() => proponerNoDibujadoArmado(op))}
            revisar={(filas) => conSaldo(() => revisarArmado(filas))}
            revisarSaldo={revisarSaldoArmado}
            buscarOpciones={(q) => conSaldo(() => buscarExtraArmado(q))}
            buscarTexto={async (q) => {
              // Mismo buscador que «Buscar» del panel: conceptos, TODAS las palabras, en todos los catálogos
              // del plano por prioridad. Gratis.
              const listas = await Promise.all(
                catalogosPrioridad.map((cid) =>
                  listCatalogResources(cid, { q, tipos: [7], limit: 30, todasLasPalabras: true })
                    .then((r) => r.resources.map((x) => ({ x, cid })))
                    .catch(() => []),
                ),
              );
              const vistos = new Set<string>();
              const out: OpcionArmado[] = [];
              for (const { x, cid } of listas.flat()) {
                if (vistos.has(x.id) || out.length >= 30) continue;
                vistos.add(x.id);
                out.push({ resourceId: x.id, catalogId: cid, clave: x.clave, descripcion: x.descripcion, unidad: x.unidad ?? null, precio: x.precio ?? null, razon: null });
              }
              return out;
            }}
            borrador={borradorArmado}
            tituloImpresion={nombrePlanoAbierto || archivoActual?.originalName || 'Plano'}
            alTerminar={() => void useUsageStore.getState().refresh()}
            medir={medirArmado}
            agregar={agregarArmado}
            generarConIa={() => {
              setArmarAbierto(false);
              setConfirmarLoteOpen(true);
            }}
            costoStaff={isStaff && projectId && fileId ? (desde) => costoIaDelPlano(projectId, fileId, desde) : null}
          />
        )}
        {MEMORIA_TIPOS_ENABLED && tiposAbierto && projectId && fileId && (
          <TiposDelPlano
            abierto={tiposAbierto}
            onCerrar={() => { setTiposAbierto(false); setTipoInicialDelDialogo(null); }}
            tipoInicial={tipoInicialDelDialogo}
            projectId={projectId}
            fileId={fileId}
            esIfc={esInventarioIfc}
            elementos={elementosDelDialogo()}
            catalogos={catalogosDisponibles.map((c) => ({ id: c.id, nombre: (c.displayName ?? '').trim() || c.name }))}
            catalogosMarcados={catalogosPrioridad}
            formatoPrecio={(n) => fmtMoneda(n)}
            onAplicado={(_r, ids) => {
              if (ids) guardarCatalogosInline(ids);
              setMemoriaTick((n) => n + 1);
            }}
            onMemoriaCambiada={() => setMemoriaTick((n) => n + 1)}
            sugerir={async (key, catalogId, acompanaA) => {
              if (!projectId || !fileId) return { opciones: [], nota: null };
              const i = key.indexOf(':');
              const tipo = key.slice(0, i) === 'bloque' ? 'bloque' : 'capa';
              const clave = key.slice(i + 1);
              const capaInfo = tipo === 'capa' ? inventario?.capas.find((c) => c.capa === clave) : undefined;
              const ifc = capaInfo ? describirTipoIfcParaSugerencia(capaInfo) : null;
              const res = await sugerirConceptos(projectId, fileId, {
                catalogId,
                ...(acompanaA ? { acompanaA } : {}),
                seleccion: { tipo, clave, ...(ifc ? { ifc } : {}) },
              });
              return {
                opciones: res.sugerencias.map((x) => ({ catalogId: x.catalogId, clave: x.clave, descripcion: x.descripcion, unidad: x.unidad, precio: x.precio })),
                nota: res.nota ?? res.notaFaltante ?? null,
              };
            }}
            ponerEnLaHoja={{
              preparar: async (catalogIds) => {
                const prep = await prepararEnvioAsignado(catalogIds);
                if ('bloqueo' in prep) return prep;
                const c = prep.plan.conteos;
                // «Líneas»: las de la hoja, ya juntadas por concepto (la misma cuenta que hará el envío, sin medir).
                const lineas = prep.plan.entran.reduce((n, e) => n + e.lineas, 0);
                return {
                  resumen: {
                    elementos: prep.plan.elementos,
                    principales: prep.plan.principales,
                    complementarios: prep.plan.complementarios,
                    lineas,
                    sinConcepto: c.sinConcepto + c.noVigentes, // lo que ya no sirve no se muestra: cuenta como sin concepto
                    yaEnLaHoja: prep.yaEnLaHoja,
                    noPresupuestar: c.noPresupuestar,
                    sugeridos: c.sugeridos,
                    conflictos: c.conflictos,
                    noVigentes: 0,
                  },
                  datos: prep,
                };
              },
              poner: (datos, avance) => ponerEnLaHojaLoAsignado(datos as Parameters<typeof ponerEnLaHojaLoAsignado>[0], avance),
            }}
          />
        )}

        {/* Confirmación antes de generar el lote: es lento y consume la bolsa de tokens del mes,
            y el botón se puede apretar sin querer (igual que en las cotizaciones). */}
        <Dialog open={confirmarLoteOpen} onOpenChange={(_, d) => setConfirmarLoteOpen(d.open)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Generar {marcadosParaIa} concepto{marcadosParaIa === 1 ? '' : 's'} con IA</DialogTitle>
              <DialogContent>
                Se armará el análisis de precio de {marcadosParaIa === 1 ? 'este concepto' : `los ${marcadosParaIa} conceptos`} que
                marcaste con IA. Toma un rato y consume de tu bolsa de tokens del mes. Puedes revisar cada
                análisis cuando termine.
                {reemplazanPrecio > 0 && (
                  <div style={{ marginTop: 8, fontWeight: 600 }}>
                    {reemplazanPrecio === 1
                      ? '1 de ellos ya tiene precio: el de la IA lo reemplaza.'
                      : `${reemplazanPrecio} de ellos ya tienen precio: el de la IA lo reemplaza.`}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setConfirmarLoteOpen(false)} disabled={generandoLote}>
                  Cancelar
                </Button>
                <Button
                  appearance="primary"
                  icon={<LightbulbRegular />}
                  onClick={() => void generarLote()}
                  disabled={generandoLote || marcadosParaIa === 0}
                >
                  {generandoLote ? 'Generando…' : `Generar ${marcadosParaIa}`}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {hpAbierto && (
          <div className={styles.hpBody} style={{ height: `${hpH}px` }}>
            {vistaProyecto && cargandoProyecto && renglonesProyecto.length === 0 ? (
              <div className={styles.hpEmpty} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Spinner size="tiny" /> Cargando la hoja de la obra…
              </div>
            ) : filasHoja.length === 0 ? (
              <>
                {!modoProyecto && barraAgrupacion}
                <div className={styles.hpEmpty}>
                  {vistaProyecto
                    ? 'Tu hoja todavía está vacía. Elige algo del plano y agrégale un concepto: aquí se junta todo lo de todos los planos de la obra.'
                    : 'Este plano todavía no tiene renglones. Elige algo del plano y agrégale un concepto.'}
                </div>
              </>
            ) : (
              <>
                {!modoProyecto && barraAgrupacion}
                {vistaProyecto && renglonesProyecto.some((r) => !r.aportaCantidad) && (
                  <div style={{ padding: '6px 0 4px', fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                    Lo que viene de un plano de “solo referencia” describe mejor el concepto, pero no suma al total.
                  </div>
                )}
                {/* La tabla va en un marco con borde redondeado, como la hoja de cotización. */}
                <div className={styles.hpTabla}>
                {/* Encabezado de columnas PEGADO arriba (como el thead de la hoja de cotización); mismas
                    columnas en las dos vistas; "Viene de" solo en toda la obra. */}
                <div ref={hpColHeadRef} className={styles.hpColHead} aria-hidden>
                  <div
                    className={styles.hpRow}
                    style={{
                      cursor: 'default', background: 'transparent', padding: '6px 14px', borderBottom: 'none',
                      fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                      color: 'var(--text-tertiary)',
                      ...(vistaProyecto ? { gridTemplateColumns: HP_COLUMNAS_OBRA } : {}),
                    }}
                  >
                    <span>Concepto</span>
                    {vistaProyecto && <span>Viene de</span>}
                    <span className={styles.hpUnitCell}>Unidad</span>
                    <span className={styles.hpNumCell}>Cantidad</span>
                    <span className={styles.hpNumCell}>P. unitario</span>
                    <span className={styles.hpNumCell}>Importe</span>
                    <span style={{ textAlign: 'center', whiteSpace: 'normal', lineHeight: 1.15 }} title="Crear el análisis de precio unitario con inteligencia artificial">Analizar con IA</span>
                    <span />
                  </div>
                </div>
                {/* Partida general = nombre del proyecto (nivel 0), pegada bajo las columnas; debajo
                    van las partidas del eje elegido (nivel 1), cada una pegada mientras se recorre. */}
                <div
                  ref={partidaGeneralRef}
                  className={styles.partidaGeneral}
                  style={{ top: hpColHeadH, display: 'grid', gap: 10, alignItems: 'center', gridTemplateColumns: vistaProyecto ? HP_COLUMNAS_OBRA : HP_COLUMNAS }}
                >
                  <span style={{ gridColumn: '1 / -4', minWidth: 0 }}>{(tipoAgrupacion === 'obra' && raizDeObra) || (proyectoActual?.name ?? 'Presupuesto')}</span>
                  <span className={styles.partidaTotal}>{fmtMoneda(totalHoja)}</span>
                </div>
                {filtrando && partidasVisibles.length === 0 && (
                  <div className={styles.hpEmpty}>Ningún renglón cumple el filtro.</div>
                )}
                {partidasVisibles.map((p) => (
                <div key={p.clave}>
                  <div
                    className={styles.partidaHead}
                    style={{
                      top: hpColHeadH + partidaGeneralH,
                      ...(vistaProyecto ? { gridTemplateColumns: HP_COLUMNAS_OBRA } : {}),
                      // Partida de la obra sin conceptos: sale (la estructura va completa) pero atenuada y sin pegarse
                      // al recorrer, para que no se amontonen cabeceras vacías bajo las columnas.
                      ...(p.items.length === 0 ? { position: 'static' as const, opacity: 0.6 } : {}),
                    }}
                  >
                    <span style={{ gridColumn: '1 / -4', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, paddingLeft: p.nivel * 18 }}>
                      <span aria-hidden style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-100)', flexShrink: 0 }}>›</span>
                      <span>{p.etiqueta}</span>
                    </span>
                    <span className={styles.partidaTotal}>{fmtMoneda(p.totalConHijos)}</span>
                  </div>
                  {p.visibles.map((r) => {
                    const deOtroPlano = r.sourceFileId != null && r.sourceFileId !== fileId;
                    const cuenta = cuentaAlTotal(r);
                    return (
                    <div
                      key={r.id}
                      data-renglon-id={r.id}
                      className={mergeClasses(
                        styles.hpRow,
                        ((!deOtroPlano && renglonesResaltados.has(r.id)) || panelRenglon?.id === r.id) && styles.hpRowSel,
                        destacados.has(r.id) && styles.hpRowDestacado,
                      )}
                      style={{
                        ...(vistaProyecto ? { gridTemplateColumns: HP_COLUMNAS_OBRA } : {}),
                        ...(!cuenta ? { opacity: 0.78 } : {}),
                      }}
                      onClick={() => handleClickRenglon(r)}
                    >
                      <span className={styles.hpDescCell}>
                        {esHijo(r) && (
                          <Popover withArrow positioning="below-start">
                            <PopoverTrigger disableButtonEnhancement>
                              <button
                                type="button"
                                className={styles.leyendaLink}
                                title="Es un adicional: lo lleva ese concepto (lo que salió en «¿Qué más lleva?»). Tócalo para verlo."
                                onClick={(e) => e.stopPropagation()}
                              >
                                Adicional de {nombreDelPrincipal(r)}
                              </button>
                            </PopoverTrigger>
                            <PopoverSurface onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                                Es adicional de
                              </div>
                              {(() => {
                                const pr = filasHoja.find((x) => x.id === r.principalId);
                                if (!pr) return <span style={{ fontSize: 12.5 }}>Su concepto principal ya no está en la hoja.</span>;
                                return (
                                  <button
                                    type="button"
                                    onClick={() => irARenglones([pr.id])}
                                    title="Ir a este renglón en la hoja"
                                    style={{
                                      textAlign: 'left', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
                                      color: 'var(--text-primary)', fontSize: 12.5, lineHeight: 1.4,
                                    }}
                                  >
                                    {pr.conceptoDescripcion}
                                    <span style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>
                                      {` · ${pr.cantidad.toLocaleString('es-MX', { maximumFractionDigits: 3 })} ${pr.unidad ?? ''}`}
                                      {pr.importe != null ? ` · ${fmtMoneda(pr.importe)}` : ''}
                                    </span>
                                  </button>
                                );
                              })()}
                            </PopoverSurface>
                          </Popover>
                        )}
                        {!esHijo(r) && (hijosDe.get(r.id)?.length ?? 0) > 0 && (
                          <Popover withArrow positioning="below-start">
                            <PopoverTrigger disableButtonEnhancement>
                              <button
                                type="button"
                                className={styles.leyendaLink}
                                title="Lo que este concepto lleva además (de «¿Qué más lleva?»). Tócalo para verlo."
                                onClick={(e) => e.stopPropagation()}
                              >
                                Lleva {hijosDe.get(r.id)!.length} {hijosDe.get(r.id)!.length === 1 ? 'adicional' : 'adicionales'}
                              </button>
                            </PopoverTrigger>
                            <PopoverSurface onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                                Lo que lleva este concepto
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {hijosDe.get(r.id)!.map((h) => (
                                  <button
                                    key={h.id}
                                    type="button"
                                    onClick={() => irARenglones([h.id])}
                                    title="Ir a este renglón en la hoja"
                                    style={{
                                      textAlign: 'left', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
                                      color: 'var(--text-primary)', fontSize: 12.5, lineHeight: 1.4,
                                    }}
                                  >
                                    {h.conceptoDescripcion}
                                    <span style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>
                                      {` · ${h.cantidad.toLocaleString('es-MX', { maximumFractionDigits: 3 })} ${h.unidad ?? ''}`}
                                      {h.importe != null ? ` · ${fmtMoneda(h.importe)}` : ''}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </PopoverSurface>
                          </Popover>
                        )}
                        {r.rol === 'complemento' && !esHijo(r) && (
                          <span
                            title="Era adicional de un concepto que ya no está en la hoja"
                            style={marcaDeRenglon('var(--accent)')}
                          >
                            adicional
                          </span>
                        )}
                        {!(Number(r.cantidad) > 0) ? (
                          <span
                            title="El plano no da esta cantidad: entró en 0. Escríbela para que el concepto cuente."
                            style={marcaDeRenglon('var(--bad)')}
                          >
                            ⚠ sin cantidad
                          </span>
                        ) : r.revisar ? (
                          (() => {
                            // El motivo exacto no se guarda; sí se sabe de dónde salió el renglón, y con eso la marca
                            // dice QUÉ revisar (antes solo decía «revisar», sin decir qué).
                            const origenFoto = r.generador?.origen;
                            const m =
                              origenFoto === 'no_dibujado'
                                ? {
                                    texto: '⚠ cantidad supuesta',
                                    ayuda: `El plano no trae esto; la cantidad es una estimación${r.generador?.supuesto?.deDonde ? ` (${r.generador.supuesto.deDonde})` : ''}. Confírmala o corrígela antes de cotizar.`,
                                  }
                                : r.rol === 'complemento'
                                  ? {
                                      texto: '⚠ confirmar adicional',
                                      ayuda: 'Se agregó solo, porque lo pide otro concepto de la hoja. Confirma que aplica a tu obra y que su cantidad es la correcta.',
                                    }
                                  : {
                                      texto: '⚠ revisar concepto o cantidad',
                                      ayuda: 'La medición del plano es dudosa o el concepto elegido es el más parecido, no uno igual. Confirma que el concepto es el que quieres y que la cantidad es la correcta.',
                                    };
                            return (
                              <span title={m.ayuda} style={marcaDeRenglon('var(--warning, #d9a400)')}>
                                {m.texto}
                              </span>
                            );
                          })()
                        ) : null}
                        {/* Precio hecho con IA: se marca en el renglón. De qué catálogo salió lo dice el panel. */}
                        {r.origen === 'apu_ia' && (
                          <span
                            title="Precio construido con inteligencia artificial"
                            style={marcaDeRenglon('var(--privado, #6c54d6)')}
                          >
                            ✦ IA
                          </span>
                        )}
                        {/* DWG con varias plantas: de qué planta es este renglón (A-MUROS · Planta baja). */}
                        {r.seleccion?.vista && !deOtroPlano && (() => {
                          const vs = inventario?.vistas ?? [];
                          const i = vs.findIndex((v) => v.id === r.seleccion!.vista);
                          return i >= 0 ? (
                            <span
                              title="Solo lo de esta planta"
                              style={marcaDeRenglon('var(--text-secondary)', 'var(--border-subtle)')}
                            >
                              {rotuloDeVista(vs[i], i)}
                            </span>
                          ) : null;
                        })()}
                        {(opcionesPorPrincipal.get(r.id)?.length ?? 0) > 0 && (
                          <button
                            type="button"
                            title="Tiene opciones guardadas: tócalo para compararlas (no suman a la hoja)"
                            style={{ ...marcaDeRenglon('var(--accent)'), cursor: 'pointer', background: 'transparent' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpcionesDe(r.id);
                            }}
                          >
                            {opcionesPorPrincipal.get(r.id)!.length === 1 ? '1 opción' : `${opcionesPorPrincipal.get(r.id)!.length} opciones`}
                          </button>
                        )}
                        <IndicadorNotas specs={r.specRefs} onQuitar={puedeEditar ? (n) => quitarNota(r, n) : undefined} />
                        {editandoDescId === r.id ? (
                          <Input
                            size="small"
                            autoFocus
                            value={editDescStr}
                            style={{ width: '100%' }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(_, d) => setEditDescStr(d.value)}
                            onBlur={() => void guardarDescripcionEditada()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); void guardarDescripcionEditada(); }
                              if (e.key === 'Escape') setEditandoDescId(null);
                            }}
                          />
                        ) : (
                          <>
                            {r.conceptoDescripcion}
                            {r.origen === 'apu_ia' &&
                              r.precioUnitario == null &&
                              r.apuIaStatus !== 'queued' &&
                              r.apuIaStatus !== 'running' && (
                                <button
                                  type="button"
                                  title="Editar la descripción antes de cotizar con IA"
                                  aria-label="Editar descripción"
                                  className={styles.editDescBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditandoDescId(r.id);
                                    setEditDescStr(r.conceptoDescripcion);
                                  }}
                                >
                                  <EditRegular />
                                </button>
                              )}
                            {puedeEditar && r.apuIaStatus !== 'queued' && r.apuIaStatus !== 'running' && (
                              <button
                                type="button"
                                title="Cambiar por otro concepto del catálogo (se queda la cantidad)"
                                aria-label="Cambiar concepto"
                                className={styles.editDescBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCambiarConceptoDe(r);
                                }}
                              >
                                <ArrowSwapRegular /> <span style={{ fontSize: 11 }}>Cambiar concepto</span>
                              </button>
                            )}
                            {/* «Dos opciones con precio» (idea 7): otra forma de construir este concepto, con precio. */}
                            {puedeEditar && r.rol === 'principal' && !deOtroPlano && r.apuIaStatus !== 'queued' && r.apuIaStatus !== 'running' && (
                              <button
                                type="button"
                                title="Comparar con otra forma de construirlo (otro concepto con la misma medición), con su precio"
                                aria-label="Otra opción"
                                className={styles.editDescBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpcionesDe(r.id);
                                }}
                              >
                                <ScalesRegular /> <span style={{ fontSize: 11 }}>Otra opción</span>
                              </button>
                            )}
                            {/* ¿Se olvidó un adicional? Se agrega desde la fila, sin buscar el elemento en el dibujo. */}
                            {puedeEditar && r.rol === 'principal' && !deOtroPlano && r.apuIaStatus !== 'queued' && r.apuIaStatus !== 'running' && (
                              <button
                                type="button"
                                title="Agregar algo que falta a este concepto (un adicional: cimbra, impermeabilizante…)"
                                aria-label="Agregar algo que falta"
                                className={styles.editDescBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAdicionalDe(r);
                                }}
                              >
                                <AddRegular /> <span style={{ fontSize: 11 }}>Agregar algo que falta</span>
                              </button>
                            )}
                          </>
                        )}
                      </span>
                      {/* De qué plano viene el renglón (y si suma o solo describe). */}
                      {vistaProyecto && (
                        <span
                          title={`${r.sourceFileName ?? 'Plano'} · ${etiquetaRolArchivo(r.planoRol)}`}
                          style={{
                            fontSize: 11, color: deOtroPlano ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                          }}
                        >
                          {r.sourceFileName ?? 'Plano'}
                          <span style={{ display: 'block', fontSize: 10, opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {deOtroPlano ? etiquetaRolArchivo(r.planoRol) : 'el plano abierto'}
                          </span>
                        </span>
                      )}
                      <span className={styles.hpUnitCell}>{r.unidad ?? '—'}</span>
                      <span className={styles.hpNumCell}>
                        {editandoCantId === r.id ? (
                          <Input
                            size="small"
                            autoFocus
                            value={editCantStr}
                            style={{ width: '100%', minWidth: 0 }}
                            input={{ style: { textAlign: 'right' } }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(_, d) => setEditCantStr(d.value)}
                            onBlur={() => void guardarCantidadEditada(r)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); void guardarCantidadEditada(r); }
                              if (e.key === 'Escape') setEditandoCantId(null);
                            }}
                          />
                        ) : (
                          <span
                            role={puedeEditar ? 'button' : undefined}
                            tabIndex={puedeEditar ? 0 : undefined}
                            style={{ cursor: puedeEditar ? 'text' : undefined }}
                            title={
                              r.cantidadMedida != null
                                ? `Corregida a mano. El plano midió ${r.cantidadMedida.toLocaleString('es-MX')} ${r.unidad ?? ''}; para volver, escribe esa cantidad.`
                                : puedeEditar
                                  ? 'Clic para corregir la cantidad a mano'
                                  : undefined
                            }
                            onClick={(e) => {
                              if (!puedeEditar) return;
                              e.stopPropagation();
                              setEditandoCantId(r.id);
                              setEditCantStr(String(r.cantidad));
                            }}
                          >
                            {r.cantidadMedida != null && (
                              <span aria-label="corregida a mano" style={{ color: 'var(--accent)', marginRight: 3 }}>✎</span>
                            )}
                            {r.cantidad.toLocaleString('es-MX', { maximumFractionDigits: 3 })}
                          </span>
                        )}
                      </span>
                      <span className={mergeClasses(styles.hpNumCell, r.precioUnitario == null && styles.hpSinPrecio)}>
                        {r.apuIaStatus === 'queued' || r.apuIaStatus === 'running' ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontStyle: 'italic', color: 'var(--accent)' }}>
                            <Spinner size="extra-tiny" /> generando…
                          </span>
                        ) : r.precioUnitario == null ? (
                          r.apuIaStatus === 'failed' ? (
                            // Por qué, en palabras simples (el MISMO texto que la cotización). El
                            // detalle técnico solo para staff Ecosoft, nunca a la vista del cliente.
                            <span
                              title={
                                mensajeDeFalloIa(r.apuIaFailReason) +
                                ' Queda marcado para el próximo «Generar con IA».' +
                                (isStaff && r.apuIaError ? `

Detalle técnico (solo Ecosoft): ${r.apuIaError}` : '')
                              }
                              style={{ fontSize: 10.5, fontWeight: 700, fontStyle: 'normal', color: 'var(--bad, #d13438)' }}
                            >
                              {r.apuIaFailReason === 'ai_unavailable' ? 'la IA no respondió' : 'no se pudo generar'}
                            </span>
                          ) : (
                            's/precio'
                          )
                        ) : (
                          // Precio (texto plano). El semáforo del sintético queda como punto informativo;
                          // el desglose se abre con el botón "Análisis" de la derecha (no con el precio).
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                            {r.origen === 'apu_ia' && r.apuIaConfianza && (
                              <span
                                aria-hidden
                                title={`Análisis por IA (confianza ${r.apuIaConfianza})`}
                                style={{ width: 8, height: 8, borderRadius: '50%', background: semaforoColor(r.apuIaConfianza), flexShrink: 0 }}
                              />
                            )}
                            {fmtMoneda(r.precioUnitario)}
                          </span>
                        )}
                      </span>
                      <span
                        className={mergeClasses(styles.hpNumCell, (!cuenta || r.importe == null) && styles.hpSinPrecio)}
                        title={!cuenta ? 'Viene de un plano de solo referencia: no suma al total.' : undefined}
                      >
                        {!cuenta ? 'no suma' : r.importe == null ? 's/precio' : fmtMoneda(r.importe)}
                      </span>
                      {/* Columna IA propia (como la cotización): cualquier renglón se marca, también el de
                          catálogo —generarlo con IA reemplaza su precio—. Solo marca; «Generar con IA» genera. */}
                      <span style={{ display: 'flex', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {r.apuIaStatus === 'queued' || r.apuIaStatus === 'running' ? null : (
                          <InterruptorIa
                            checked={r.apuIaRequested}
                            onChange={(siguiente) => void toggleIaRenglon(r, siguiente)}
                            title={
                              r.precioUnitario != null
                                ? 'Marcar para generar su análisis con IA (reemplaza el precio que tiene)'
                                : 'Marcar para generar su análisis con IA'
                            }
                            ariaLabel="Generar con IA"
                          />
                        )}
                      </span>
                      <button
                        type="button"
                        className={styles.delBtn}
                        title="Quitar de la hoja"
                        aria-label="Quitar de la hoja"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleBorrar(r);
                        }}
                      >
                        <DeleteRegular />
                      </button>
                    </div>
                    );
                  })}
                </div>
                ))}
                </div>
              </>
            )}
          </div>
        )}

        {imprimiendoGeneradores && (
          <VistaGeneradores
            renglones={vistaProyecto ? renglonesProyecto : renglones}
            inicial={null}
            capturar={capturarCroquisRenglon}
            obra={proyectoActual?.name ?? null}
            planoDe={(r) => r.sourceFileName ?? archivoActual?.originalName ?? null}
            fileIdAbierto={fileId}
            onCerrar={() => {
              setImprimiendoGeneradores(false);
            }}
          />
        )}
        {opcionesDe && projectId && fileId && (() => {
          const principal = renglones.find((x) => x.id === opcionesDe);
          if (!principal) return null;
          return (
            <OpcionesDelConcepto
              key={principal.id}
              principal={principal}
              hoja={renglones}
              opciones={opcionesPorPrincipal.get(principal.id) ?? []}
              formatoPrecio={(n) => fmtMoneda(n)}
              puedeEditar={puedeEditar && !proyectoActual?.isExample}
              obra={proyectoActual?.name ?? null}
              plano={nombrePlanoAbierto || archivoActual?.originalName || null}
              buscarTexto={buscarTextoEnCatalogos}
              recomendar={() => recomendarParaRenglon(principal)}
              revisarSaldo={revisarSaldoArmado}
              armar={(pedido, avance) => armarOpcionDeRenglon(principal, pedido, avance)}
              guardar={async (nombre, rs) => {
                const o = await crearOpcionPlano(projectId, fileId, { principalId: principal.id, nombre, renglones: rs });
                await refrescarOpciones();
                return o;
              }}
              usar={async (o) => {
                const r = await usarOpcionPlano(projectId, fileId, o.id);
                await refrescarRenglones();
                await refrescarOpciones();
                setOpcionesDe(r.principalId);
                dispatchToast(
                  <Toast>
                    <ToastTitle>
                      {o.estado === 'anterior' ? 'Tu hoja volvió a lo que tenía antes.' : 'Tu hoja ya usa esta opción.'} Lo que había quedó como opción para volver.
                      {r.conIa > 0 ? ` ${r.conIa === 1 ? '1 concepto quedó marcado' : `${r.conIa} conceptos quedaron marcados`} para «Generar con IA».` : ''}
                    </ToastTitle>
                  </Toast>,
                  { intent: 'success', timeout: 6000 },
                );
              }}
              descartar={async (o) => {
                await descartarOpcionPlano(projectId, fileId, o.id);
                await refrescarOpciones();
              }}
              editar={async (o, rs) => {
                try {
                  const nueva = await editarOpcionPlano(projectId, fileId, o.id, { renglones: rs });
                  await refrescarOpciones();
                  return nueva;
                } catch (e) {
                  throw e instanceof ApiClientError ? new Error(e.message) : e;
                }
              }}
              armarGrupo={(pedido, extras, avance) => armarGrupoDeOpciones(principal, pedido, extras, avance)}
              abrirGrupo={(gid) => {
                setOpcionesDe(null);
                setGrupoAbierto(gid);
              }}
              onCerrar={() => setOpcionesDe(null)}
            />
          );
        })()}
        {grupoAbierto && projectId && fileId && (() => {
          const miembros = opcionesPlano.filter((o) => o.grupoId === grupoAbierto);
          if (miembros.length === 0) return null;
          return (
            <OpcionesDelGrupo
              key={grupoAbierto}
              opciones={miembros}
              hoja={renglones}
              formatoPrecio={(n) => fmtMoneda(n)}
              puedeEditar={puedeEditar && !proyectoActual?.isExample}
              obra={proyectoActual?.name ?? null}
              plano={nombrePlanoAbierto || archivoActual?.originalName || null}
              usar={async (gid, volver) => {
                try {
                  const r = await usarGrupoOpcionesPlano(projectId, fileId, gid);
                  await refrescarRenglones();
                  await refrescarOpciones();
                  setGrupoAbierto(r.anteriorGrupoId);
                  dispatchToast(
                    <Toast>
                      <ToastTitle>
                        {volver ? 'Tu hoja volvió a lo que tenía antes.' : 'Tu hoja ya usa estas opciones.'} Lo que había quedó guardado para volver.
                        {r.conIa > 0 ? ` ${r.conIa === 1 ? '1 concepto quedó marcado' : `${r.conIa} conceptos quedaron marcados`} para «Generar con IA».` : ''}
                      </ToastTitle>
                    </Toast>,
                    { intent: 'success', timeout: 6000 },
                  );
                } catch (e) {
                  throw e instanceof ApiClientError ? new Error(`${e.message} Tu hoja no cambió.`) : e;
                }
              }}
              descartar={async (gid) => {
                await descartarGrupoOpcionesPlano(projectId, fileId, gid);
                await refrescarOpciones();
                setGrupoAbierto(null);
              }}
              onCerrar={() => setGrupoAbierto(null)}
            />
          );
        })()}
        {adicionalDe && projectId && fileId && (
          <AgregarAdicional
            principal={{
              descripcion: adicionalDe.conceptoDescripcion,
              unidad: adicionalDe.unidad,
              cantidad: adicionalDe.cantidad,
              cantidadMedida: adicionalDe.cantidadMedida ?? null,
              generador: adicionalDe.generador ?? null,
            }}
            buscarTexto={buscarTextoEnCatalogos}
            recomendar={async (frase) => {
              const cid = catalogosPrioridad[0];
              if (!projectId || !fileId || !cid) return [];
              // La frase ya es un concepto de obra (la escribió la persona): no se interpreta, y se busca lo que acompaña al concepto.
              const res = await sugerirConceptos(projectId, fileId, {
                catalogId: cid,
                yaEsConcepto: true,
                acompanaA: adicionalDe.conceptoDescripcion,
                seleccion: { tipo: 'capa', clave: frase },
              });
              return res.sugerencias.map((x) => ({ resourceId: x.resourceId, catalogId: x.catalogId, clave: x.clave, descripcion: x.descripcion, unidad: x.unidad ?? null, precio: x.precio ?? null }));
            }}
            formatoPrecio={(n) => fmtMoneda(n)}
            onAgregar={(ren) => agregarAdicionalAlPrincipal(adicionalDe, ren)}
            onCerrar={() => setAdicionalDe(null)}
          />
        )}
        <CambiarConceptoDialog
          abierto={cambiarConceptoDe != null}
          descripcionActual={cambiarConceptoDe?.conceptoDescripcion ?? ''}
          unidadActual={cambiarConceptoDe?.unidad ?? null}
          catalogos={catalogosPrioridad}
          guardando={cambiandoConcepto}
          formatoPrecio={(n) => fmtMoneda(n)}
          recomendar={() => recomendarParaRenglon(cambiarConceptoDe)}
          onElegir={(c) => void confirmarCambioDeConcepto(c)}
          onCerrar={() => setCambiarConceptoDe(null)}
        />

        {/* Quitar un concepto que lleva complementarios colgados: tres salidas claras (cerrar el
            diálogo NO borra nada). */}
        <Dialog open={borrarPendiente != null} onOpenChange={(_, d) => { if (!d.open) setBorrarPendiente(null); }}>
          <DialogSurface style={{ maxWidth: 480 }}>
            <DialogBody>
              <DialogTitle>Quitar el concepto</DialogTitle>
              <DialogContent>
                {borrarPendiente && (
                  <>
                    <div
                      title={borrarPendiente.renglon.conceptoDescripcion}
                      style={{ fontWeight: 600, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 8 }}
                    >
                      {borrarPendiente.renglon.conceptoDescripcion}
                    </div>
                    {borrarPendiente.hijos === 1
                      ? 'Este concepto lleva 1 concepto que lo acompaña en la hoja. ¿También lo quito?'
                      : `Este concepto lleva ${borrarPendiente.hijos} conceptos que lo acompañan en la hoja. ¿También los quito?`}
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-tertiary)' }}>
                      {borrarPendiente.hijos === 1
                        ? 'Si quitas solo este, el otro se queda en la hoja con su importe.'
                        : 'Si quitas solo este, los otros se quedan en la hoja con su importe.'}
                    </div>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setBorrarPendiente(null)}>Cancelar</Button>
                <Button
                  appearance="secondary"
                  onClick={() => borrarPendiente && void ejecutarBorrado(borrarPendiente.renglon, false, borrarPendiente.alTerminar)}
                >
                  Quitar solo este
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => borrarPendiente && void ejecutarBorrado(borrarPendiente.renglon, true, borrarPendiente.alTerminar)}
                >
                  {borrarPendiente?.hijos === 1 ? 'Quitar los dos' : `Quitar los ${(borrarPendiente?.hijos ?? 0) + 1}`}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
      </div>
      {panelRenglon && panelConcepto && (() => {
        // ← → recorren los renglones de la hoja que se está viendo (con sus filtros), como en la cotización.
        const visibles = partidasVisibles.flatMap((p) => p.visibles);
        const i = visibles.findIndex((x) => x.id === panelRenglon.id);
        const nav =
          i >= 0 && visibles.length > 1
            ? {
                pos: i + 1,
                total: visibles.length,
                onPrev: () => { const x = visibles[i - 1]; if (x) handleClickRenglon(x); },
                onNext: () => { const x = visibles[i + 1]; if (x) handleClickRenglon(x); },
              }
            : undefined;
        return (
          <ConceptPricePanel
            nav={nav}
            moneda="MXN"
            quoteId={panelRenglon.quoteId}
            concepto={panelConcepto}
            catalogName={panelRenglon.catalogId ? catalogoNombrePorId.get(panelRenglon.catalogId) ?? null : null}
            llmVeredicto={null}
            isActive={false}
            // Se corrige igual que en la cotización (descripción, unidad, cantidad); el servidor busca
            // en los catálogos de Planos y conserva la cantidad que midió el plano.
            canEdit={puedeEditar && !proyectoActual?.isExample}
            onEdited={(r) => {
              setPanelConcepto(r.concepto);
              void refrescarRenglones();
              const msg = !r.rebuscado
                ? 'Renglón corregido.'
                : r.resultado === 'con_precio'
                  ? 'Renglón corregido. Le encontramos precio en tus catálogos.'
                  : r.resultado === 'sin_catalogos'
                    ? 'Renglón corregido. No hay catálogos de referencia para buscarle precio.'
                    : 'Renglón corregido. Todavía no encontramos un concepto equivalente en tus catálogos.';
              dispatchToast(
                <Toast><ToastTitle>{msg}</ToastTitle></Toast>,
                { intent: r.rebuscado && r.resultado !== 'con_precio' ? 'warning' : 'success', timeout: 5000 },
              );
            }}
            onClose={() => { setPanelRenglon(null); setPanelConcepto(null); }}
            onChanged={() => { void cargarPanelConcepto(panelRenglon); void refrescarRenglones(); }}
            onGoToEquivalentes={() => setCambiarConceptoDe(panelRenglon)}
            onRegenerar={async () => {
              if (!projectId) return;
              await cotizarRenglonConIa(projectId, panelRenglon.sourceFileId ?? fileId ?? '', panelRenglon.id);
              await refrescarRenglones();
            }}
            mode="derecha"
            size={panelW}
            onResize={cambiarPanelW}
          />
        );
      })()}
      </div>
    </div>
  );
}
